import { useState, useEffect, useRef, useCallback, useMemo } from "react";

/* ══════════════════════════════════════════════════════════
   SOUNDLIFE CMS v5.2
   - MySQL + PHP backend (no Firebase, no Cloudinary)
   - Session-based auth via /api/auth.php
   - File uploads to /uploads/ on Hostinger
   ══════════════════════════════════════════════════════════ */

const API_BASE = "https://slipms.com/api";
// Images → lightweight image.php (no session/DB — fast parallel serving)
//   thumb=true → pre-generated 400px thumbnail (~15-30KB) — used in gallery grids
//   thumb=false → full-res original — used in lightbox/viewer
// PDFs/TXT → download.php (needs auth + Content-Disposition + X-Frame-Options)
const imgSrc = (url, thumb = false) => {
  if (!url) return url;
  const filename = url.split('/').pop();
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp','heic','heif'].includes(ext)) {
    return `${API_BASE}/image.php?f=${encodeURIComponent(filename)}${thumb ? '&thumb=1' : ''}`;
  }
  return `${API_BASE}/download.php?file=${encodeURIComponent(filename)}`;
};

// ── CSRF token (set after auth check, used in all state-changing requests) ──
let _csrfToken = '';
const apiCall = async (url, options = {}) => {
  const isStateChanging = ['POST', 'PUT', 'DELETE'].includes(options.method?.toUpperCase());
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(isStateChanging ? { 'X-CSRF-Token': _csrfToken } : {}),
      ...(options.headers || {}),
    },
  });
};

// ── Fetch patients with full visits+records — server-side filtered ──
async function fetchFullPatients(filters = {}) {
  const p = new URLSearchParams({ mode:'full' });
  if (filters.from)                  p.set('from', filters.from);
  if (filters.to)                    p.set('to',   filters.to);
  if (filters.clinics?.length)       filters.clinics.forEach(c  => p.append('clinics[]',  c));
  if (filters.statuses?.length)      filters.statuses.forEach(s => p.append('statuses[]', s));
  if (filters.sources?.length)       filters.sources.forEach(s  => p.append('sources[]',  s));
  if (filters.doctor?.trim())        p.set('doctor', filters.doctor.trim());
  const res = await fetch(`${API_BASE}/patients.php?${p}`, { credentials:'include' });
  if (!res.ok) throw new Error('Failed to fetch patient data');
  return res.json();
}

// ── Fonts ──────────────────────────────────────────────────
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
document.head.appendChild(fontLink);

// ── Global styles ──────────────────────────────────────────
const styleEl = document.createElement("style");
styleEl.textContent = `
  *{box-sizing:border-box;margin:0;padding:0;}
  input,select,textarea,button{font-family:'DM Sans',sans-serif;}
  :root {
    --bg:#f4f3ff;--bg2:#ffffff;--bg3:#ede9fe;--border:#ddd6fe;--border2:#c4b5fd;
    --text1:#1e1b4b;--text2:#4c1d95;--text3:#7c3aed;--muted:#8b7ec8;--muted2:#a89fd4;
    --accent:#7c3aed;--accent2:#6d28d9;--accentbg:#ede9fe;--accentbg2:#ddd6fe;
    --danger:#e84c3d;--green:#059669;--sidebar:rgba(255,255,255,0.82);--sideborder:rgba(196,181,253,0.45);
    --topbar:#ffffff;--card:#ffffff;--cardborder:#e9e3ff;
    --cardshadow:0 2px 12px #7c3aed12;--rowhover:#f5f3ff;--inputbg:#faf9ff;
    --scrolltrack:#ede9fe;--scrollthumb:#c4b5fd;
  }
  [data-theme="dark"] {
    --bg:#0d0b1a;--bg2:#120f22;--bg3:#1a1530;--border:#2a2250;--border2:#3d3070;
    --text1:#e8e4ff;--text2:#c4b5fd;--text3:#a78bfa;--muted:#6d5fa8;--muted2:#4a3d7a;
    --accent:#8b5cf6;--accent2:#7c3aed;--accentbg:#1e1540;--accentbg2:#2d2060;
    --danger:#f87171;--green:#34d399;--sidebar:rgba(13,11,26,0.86);--sideborder:rgba(42,34,80,0.7);
    --topbar:#120f22;--card:#120f22;--cardborder:#2a2250;
    --cardshadow:0 2px 20px #00000040;--rowhover:#1a1535;--inputbg:#0d0b1a;
    --scrolltrack:#1a1535;--scrollthumb:#3d3070;
  }
  body{background:var(--bg);color:var(--text1);font-family:'DM Sans',sans-serif;transition:background 0.3s,color 0.3s;}
  ::-webkit-scrollbar{width:5px;height:5px;}
  ::-webkit-scrollbar-track{background:var(--scrolltrack);}
  ::-webkit-scrollbar-thumb{background:var(--scrollthumb);border-radius:99px;}
  input,select,textarea{background:var(--inputbg) !important;color:var(--text1) !important;border:1px solid var(--border) !important;transition:border-color 0.15s,box-shadow 0.18s;}
  input:focus,select:focus,textarea:focus{outline:none !important;border-color:var(--accent) !important;box-shadow:0 0 0 3px var(--accentbg2) !important;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes spin{to{transform:rotate(360deg)}}
  [data-placeholder]:empty:before{content:attr(data-placeholder);color:#9ca3af;pointer-events:none;font-style:italic;}
  @keyframes pulseBtn{0%,100%{box-shadow:0 4px 14px #7c3aed33}50%{box-shadow:0 4px 28px #7c3aed77,0 0 0 5px #7c3aed18}}
  @keyframes rowFadeIn{from{opacity:0;transform:translateX(-4px)}to{opacity:1;transform:translateX(0)}}
  @keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
  .slide-in-right{animation:slideInRight .28s cubic-bezier(.22,1,.36,1) both;}
  .fadeUp{animation:fadeUp 0.4s cubic-bezier(.16,1,.3,1) both;}
  .fadeIn{animation:fadeIn 0.25s ease both;}
  .card-hover{transition:transform 0.2s,box-shadow 0.2s;}
  .card-hover:hover{transform:translateY(-2px);box-shadow:0 12px 32px #7c3aed22 !important;}
  .row-hover{transition:background 0.12s;}
  .row-hover:hover{background:var(--rowhover) !important;}
  .slink:hover{background:var(--accentbg) !important;color:var(--accent) !important;}
  .rec-card{transition:all 0.18s;cursor:pointer;}
  .rec-card:hover{border-color:var(--accent) !important;transform:scale(1.02);}
  .btn-p{transition:all 0.18s;}
  .btn-p:hover{opacity:0.88;box-shadow:0 4px 20px #7c3aed44;}
  @media(max-width:768px){
    .sidebar-full{width:64px !important;}.sidebar-label{display:none !important;}
    .main-pad{padding:14px !important;}.grid-3{grid-template-columns:1fr !important;}
    .grid-2{grid-template-columns:1fr !important;}.table-row{grid-template-columns:1fr 1fr !important;}
  }
  @media print {
    body { background: white !important; }
    .no-print { display: none !important; }
  }
`;
document.head.appendChild(styleEl);

// ── xlsx-js-style for styled Excel export ─────────────────
async function loadSheetJS() {
  const mod = await import("xlsx-js-style");
  return mod.default || mod;
}

// ── jsPDF for PDF export ───────────────────────────────────
function loadJsPDF() {
  return new Promise((resolve) => {
    if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = () => {
      const s2 = document.createElement("script");
      s2.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
      s2.onload = () => resolve(window.jspdf.jsPDF);
      document.head.appendChild(s2);
    };
    document.head.appendChild(s);
  });
}

// ── Export helpers ─────────────────────────────────────────
async function exportToExcel(patients, filename = "SoundLife_Patients", filterLabel = "") {
  const XLSX = await loadSheetJS();

  // ── Style helpers ────────────────────────────────────────
  const thin = { style:"thin", color:{ rgb:"e5e7eb" } };
  const brd  = { top:thin, bottom:thin, left:thin, right:thin };

  function mkCell(v, s = {}) {
    return { v: v ?? "", t: typeof v === "number" ? "n" : "s", s };
  }
  function hdrCell(v, bg = "7c3aed", sz = 10) {
    return mkCell(v, {
      fill: { fgColor:{ rgb:bg } },
      font: { bold:true, color:{ rgb:"ffffff" }, sz },
      alignment: { horizontal:"center", vertical:"center", wrapText:true },
      border: brd,
    });
  }
  function dataCell(v, isOdd, extra = {}) {
    return mkCell(v ?? "", {
      fill: { fgColor:{ rgb: isOdd ? "f5f3ff" : "ffffff" } },
      font: { color:{ rgb:"1f1535" }, sz:10 },
      alignment: { horizontal:"left", vertical:"center" },
      border: brd,
      ...extra,
    });
  }
  const STATUS_FILL = {
    "HA Trial":       { fill:{ fgColor:{ rgb:"fef3c7" } }, font:{ color:{ rgb:"92400e" }, sz:10 } },
    "Booked":         { fill:{ fgColor:{ rgb:"d1fae5" } }, font:{ color:{ rgb:"065f46" }, sz:10 } },
    "Fitting Done":   { fill:{ fgColor:{ rgb:"dcfce7" } }, font:{ color:{ rgb:"14532d" }, sz:10 } },
    "Not Interested": { fill:{ fgColor:{ rgb:"fee2e2" } }, font:{ color:{ rgb:"991b1b" }, sz:10 } },
    "PTA":            { fill:{ fgColor:{ rgb:"eff6ff" } }, font:{ color:{ rgb:"1e40af" }, sz:10 } },
    "IA":             { fill:{ fgColor:{ rgb:"f0f9ff" } }, font:{ color:{ rgb:"0c4a6e" }, sz:10 } },
    "Counselling":    { fill:{ fgColor:{ rgb:"fdf4ff" } }, font:{ color:{ rgb:"6b21a8" }, sz:10 } },
    "Follow Up":      { fill:{ fgColor:{ rgb:"fff7ed" } }, font:{ color:{ rgb:"9a3412" }, sz:10 } },
  };
  function sourceFill(src) {
    if (["Google","Google Direct","Google WhatsApp","Hear.com"].includes(src))
      return { fill:{ fgColor:{ rgb:"e8f5e9" } }, font:{ color:{ rgb:"1b5e20" }, sz:10 } };
    if (["Doctor Referral","Chirag Sir's Referral"].includes(src))
      return { fill:{ fgColor:{ rgb:"e3f2fd" } }, font:{ color:{ rgb:"0d47a1" }, sz:10 } };
    if (src === "Walk-in")
      return { fill:{ fgColor:{ rgb:"fce4ec" } }, font:{ color:{ rgb:"880e4f" }, sz:10 } };
    if (["Facebook / Instagram"].includes(src))
      return { fill:{ fgColor:{ rgb:"f3e5f5" } }, font:{ color:{ rgb:"4a148c" }, sz:10 } };
    return null;
  }

  const genStr = `Generated: ${new Date().toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}${filterLabel ? "   |   Filters: "+filterLabel : ""}`;
  const footerStyle = {
    fill:{ fgColor:{ rgb:"f3f4f6" } },
    font:{ italic:true, color:{ rgb:"6b7280" }, sz:9 },
    alignment:{ horizontal:"center", vertical:"center" },
  };

  function buildHeaderRows(ws, numCols, subTitle) {
    // Row 0: company name
    ws[XLSX.utils.encode_cell({r:0,c:0})] = mkCell("SoundLife Speech & Hearing Clinic", {
      fill:{ fgColor:{ rgb:"7c3aed" } }, font:{ bold:true, color:{ rgb:"ffffff" }, sz:14 },
      alignment:{ horizontal:"center", vertical:"center" },
    });
    // Row 1: report title
    ws[XLSX.utils.encode_cell({r:1,c:0})] = mkCell(subTitle, {
      fill:{ fgColor:{ rgb:"4c1d95" } }, font:{ bold:true, color:{ rgb:"ffffff" }, sz:11 },
      alignment:{ horizontal:"center", vertical:"center" },
    });
    // Row 2: generated / filters
    ws[XLSX.utils.encode_cell({r:2,c:0})] = mkCell(genStr, {
      fill:{ fgColor:{ rgb:"ede9fe" } }, font:{ color:{ rgb:"4c1d95" }, sz:10 },
      alignment:{ horizontal:"center", vertical:"center" },
    });
    // Row 3: spacer
    ws[XLSX.utils.encode_cell({r:3,c:0})] = mkCell("", { fill:{ fgColor:{ rgb:"ede9fe" } } });
    // Merges for rows 0-3
    return [
      { s:{r:0,c:0}, e:{r:0,c:numCols-1} },
      { s:{r:1,c:0}, e:{r:1,c:numCols-1} },
      { s:{r:2,c:0}, e:{r:2,c:numCols-1} },
      { s:{r:3,c:0}, e:{r:3,c:numCols-1} },
    ];
  }

  // ── Sheet 1: Patients ───────────────────────────────────────────
  const maxVisits = patients.reduce((m,p) => Math.max(m,(p.visits||[]).length), 0);
  const visitCols = Array.from({ length:maxVisits }, (_,i) => `Visit ${i+1}`);
  const colHeaders1 = ["Date","Name","Phone Number","Clinic","Source","Status","Audiologist Name","Doctor / Hospital Name","Records",...visitCols];
  const numCols1 = colHeaders1.length;

  const ws1 = {};
  const merges1 = buildHeaderRows(ws1, numCols1, "Patient Export Report");

  // Column header row (row 4)
  colHeaders1.forEach((h,c) => { ws1[XLSX.utils.encode_cell({r:4,c})] = hdrCell(h,"7c3aed",10); });

  // Data rows from row 5
  patients.forEach((p, ri) => {
    const r = ri + 5;
    const isOdd = ri % 2 === 0;
    const visits = [...(p.visits||[])].sort((a,b)=>new Date(a.ts)-new Date(b.ts));
    const latest = visits[visits.length-1];
    const status = p.patientStatus || "";
    const source = p.source || "";

    const stStyle = STATUS_FILL[status];
    const srStyle = sourceFill(source);

    const recs = p.records || [];
    const imgCount = recs.filter(r => r.type === 'image').length;
    const pdfCount = recs.filter(r => r.type === 'pdf' || (r.name||'').toLowerCase().endsWith('.pdf')).length;
    const otherCount = recs.length - imgCount - pdfCount;
    const recSummary = recs.length === 0 ? "" : [
      imgCount  > 0 ? `${imgCount} img`  : "",
      pdfCount  > 0 ? `${pdfCount} PDF`  : "",
      otherCount> 0 ? `${otherCount} other` : "",
    ].filter(Boolean).join(", ");

    const vals = [
      p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-IN") : "",
      p.name || "",
      p.phone || "",
      CLINICS[p.clinicId]?.label || p.clinicId || "",
      source,
      status,
      latest?.attendedBy || "",
      p.doctorName || "",
      recSummary,
      ...visitCols.map((_,vi) => visits[vi]?.note || ""),
    ];
    vals.forEach((v, c) => {
      let cellStyle;
      if (c === 4 && srStyle) {
        cellStyle = { ...srStyle, border:brd, alignment:{ horizontal:"left", vertical:"center" } };
      } else if (c === 5 && stStyle) {
        cellStyle = { ...stStyle, border:brd, alignment:{ horizontal:"left", vertical:"center" } };
      }
      ws1[XLSX.utils.encode_cell({r,c})] = cellStyle
        ? mkCell(v, cellStyle)
        : dataCell(v, isOdd);
    });
  });

  // Footer
  const foot1 = patients.length + 5;
  ws1[XLSX.utils.encode_cell({r:foot1,c:0})] = mkCell("Confidential — SoundLife Internal Use Only", footerStyle);

  ws1["!ref"]    = XLSX.utils.encode_range({ s:{r:0,c:0}, e:{r:foot1,c:numCols1-1} });
  ws1["!merges"] = [...merges1, { s:{r:foot1,c:0}, e:{r:foot1,c:numCols1-1} }];
  ws1["!rows"]   = [{hpt:28},{hpt:20},{hpt:16},{hpt:6},{hpt:20}];
  ws1["!cols"]   = [
    {wch:14},{wch:24},{wch:14},{wch:20},{wch:24},{wch:18},{wch:20},{wch:22},{wch:18},
    ...visitCols.map(()=>({wch:42})),
  ];
  ws1["!freeze"] = { xSplit:0, ySplit:5 };

  // ── Sheet 2: Visit Logs ─────────────────────────────────────────
  const allVisits = [];
  for (const p of patients) {
    for (const v of (p.visits||[])) {
      allVisits.push({
        dateVal: new Date(v.ts),
        patient: p.name || "",
        phone:   p.phone || "",
        code:    p.id || "",
        clinic:  CLINICS[v.clinicId]?.label || v.clinicId || "—",
        by:      v.attendedBy || "—",
        note:    v.note || "",
      });
    }
  }
  allVisits.sort((a,b) => b.dateVal - a.dateVal);

  const colHeaders2 = ["Date","Patient Name","Phone Number","Case Code","Clinic","Audiologist","Visit Note / Remark"];
  const numCols2 = colHeaders2.length;
  const ws2 = {};
  const merges2 = buildHeaderRows(ws2, numCols2, "Visit Logs Report");

  colHeaders2.forEach((h,c) => { ws2[XLSX.utils.encode_cell({r:4,c})] = hdrCell(h,"4c1d95",10); });

  const dFmt = { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" };
  allVisits.forEach((v, ri) => {
    const r = ri + 5;
    const isOdd = ri % 2 === 0;
    const bg = isOdd ? "faf5ff" : "ffffff";
    [
      v.dateVal.toLocaleString("en-IN", dFmt),
      v.patient, v.phone, v.code, v.clinic, v.by, v.note,
    ].forEach((val, c) => {
      ws2[XLSX.utils.encode_cell({r,c})] = mkCell(val, {
        fill: { fgColor:{ rgb:bg } },
        font: { color:{ rgb:"1f1535" }, sz:10 },
        alignment: { horizontal:"left", vertical:"center", wrapText: c===6 },
        border: brd,
      });
    });
  });

  const foot2 = (allVisits.length||1) + 5;
  ws2[XLSX.utils.encode_cell({r:foot2,c:0})] = mkCell("Confidential — SoundLife Internal Use Only", footerStyle);

  ws2["!ref"]    = XLSX.utils.encode_range({ s:{r:0,c:0}, e:{r:foot2,c:numCols2-1} });
  ws2["!merges"] = [...merges2, { s:{r:foot2,c:0}, e:{r:foot2,c:numCols2-1} }];
  ws2["!rows"]   = [{hpt:28},{hpt:20},{hpt:16},{hpt:6},{hpt:20}];
  ws2["!cols"]   = [{wch:14},{wch:24},{wch:14},{wch:16},{wch:20},{wch:22},{wch:60}];
  ws2["!freeze"] = { xSplit:0, ySplit:5 };

  // ── Workbook ─────────────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, "Patients");
  XLSX.utils.book_append_sheet(wb, ws2, "Visit Logs");

  // Tab colors
  if (!wb.Workbook) wb.Workbook = { Sheets:[] };
  if (!wb.Workbook.Sheets) wb.Workbook.Sheets = [];
  wb.Workbook.Sheets[0] = { ...(wb.Workbook.Sheets[0]||{}), TabColor:{ rgb:"7c3aed" } };
  wb.Workbook.Sheets[1] = { ...(wb.Workbook.Sheets[1]||{}), TabColor:{ rgb:"4c1d95" } };

  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0,10)}.xlsx`);
}

async function exportToPDF(patients, title = "SoundLife Patient Report", filterLabel = "") {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const headerH = filterLabel ? 22 : 18;

  function drawHeader(doc) {
    doc.setFillColor(124, 58, 237);
    doc.rect(0, 0, 297, headerH, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("SoundLife Speech & Hearing Clinic", 14, 8);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(title, 14, 14);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", {day:"numeric",month:"long",year:"numeric"})}`, 200, 14);
    if (filterLabel) {
      doc.setFontSize(7.5);
      doc.text(`Filters: ${filterLabel}`, 14, 20);
    }
  }

  drawHeader(doc);

  // Page 1: Patients table (12 columns, no visit logs text column)
  doc.autoTable({
    startY: headerH + 4,
    head: [["Code","Name","Phone","Age","Gender","Clinic","Source","Status","Visits","Records","Registered","Notes"]],
    body: patients.map(p => [
      p.id,
      p.name,
      p.phone,
      p.age || "—",
      p.gender || "—",
      CLINICS[p.clinicId]?.label || p.clinicId,
      p.source || "—",
      p.patientStatus || "—",
      p.visits?.length || 0,
      p.records?.length || 0,
      new Date(p.createdAt).toLocaleDateString("en-IN"),
      p.notes || "—",
    ]),
    headStyles: { fillColor: [109, 40, 217], textColor: 255, fontSize: 6.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 6.5, textColor: [30, 27, 75] },
    alternateRowStyles: { fillColor: [237, 233, 254] },
    columnStyles: {
      0: {cellWidth:18}, 1: {cellWidth:26}, 2: {cellWidth:20},
      3: {cellWidth:8},  4: {cellWidth:10}, 5: {cellWidth:22},
      6: {cellWidth:22}, 7: {cellWidth:16}, 8: {cellWidth:10},
      9: {cellWidth:10}, 10: {cellWidth:18}, 11: {cellWidth:50},
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) drawHeader(doc);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(
        `Page ${data.pageNumber} · SoundLife PMS · Confidential`,
        14, doc.internal.pageSize.height - 5
      );
    },
  });

  // Page 2: Visit History table
  const visitRows = [];
  for (const p of patients) {
    for (const v of (p.visits || [])) {
      visitRows.push([
        p.name,
        p.id,
        new Date(v.ts).toLocaleDateString("en-IN", {day:"numeric",month:"short",year:"numeric"}),
        CLINICS[v.clinicId]?.label || v.clinicId || "—",
        v.attendedBy || "—",
        v.note || "—",
      ]);
    }
  }

  doc.addPage();
  drawHeader(doc);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(109, 40, 217);
  doc.text("Visit History", 14, headerH + 8);

  doc.autoTable({
    startY: headerH + 12,
    head: [["Patient Name","Case Code","Visit Date","Visit Clinic","Audiologist","Note"]],
    body: visitRows.length
      ? visitRows
      : [["No visit logs for selected patients","","","","",""]],
    headStyles: { fillColor: [109, 40, 217], textColor: 255, fontSize: 6.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 6.5, textColor: [30, 27, 75] },
    alternateRowStyles: { fillColor: [237, 233, 254] },
    columnStyles: {
      0: {cellWidth:32}, 1: {cellWidth:22}, 2: {cellWidth:24},
      3: {cellWidth:26}, 4: {cellWidth:26}, 5: {cellWidth:120},
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) drawHeader(doc);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(
        `Page ${data.pageNumber} · SoundLife PMS · Confidential`,
        14, doc.internal.pageSize.height - 5
      );
    },
  });

  // Final page numbers pass
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(
      `Page ${i} of ${pageCount} · SoundLife PMS · Confidential`,
      14, doc.internal.pageSize.height - 5
    );
  }

  doc.save(`${title.replace(/ /g,"_")}_${new Date().toISOString().slice(0,10)}.pdf`);
}

// ── Constants ──────────────────────────────────────────────
const QUOTES = [
  { q:"I asked my patient if they could hear me. They said 'of course!' Their family disagreed.", a:"— SoundLife" },
  { q:"Warning: After getting hearing aids, you will hear things you were perfectly happy not hearing before.", a:"— SoundLife" },
  { q:"We once had a patient who thought everyone mumbled. Turns out, they didn't.", a:"— SoundLife" },
  { q:"Hearing loss doesn't happen overnight. It happens over years of saying 'huh?' and hoping for the best.", a:"— SoundLife" },
  { q:"The world is full of beautiful sounds. Birds. Laughter. Rain. Your name being called. Don't miss them.", a:"— SoundLife" },
  { q:"They say silence is golden. Our patients disagree — they want every sound back.", a:"— SoundLife" },
  { q:"Ears don't come with a warranty. But we're pretty close.", a:"— SoundLife" },
  { q:"Some people turn up the volume. Some people get it fixed. Guess which ones sleep better.", a:"— SoundLife" },
  { q:"Lip reading is impressive. Hearing aids are easier. Just saying.", a:"— SoundLife" },
  { q:"You can hear a notification from across the room. But not your name being called. Interesting priorities.", a:"— SoundLife" },
  { q:"There are two types of people — those who fixed their hearing early and those who wish they had.", a:"— SoundLife" },
  { q:"Hearing loss is the only condition where everyone around you notices before you do.", a:"— SoundLife" },
  { q:"Beethoven composed his 9th Symphony completely deaf. Imagine what he could've done with a good audiologist.", a:"— SoundLife" },
  { q:"Life is too short to say 'What?' twice. Come on in.", a:"— SoundLife" },
];

const CLINICS = {
  shyamal:      { label:"Prahladnagar",      city:"Ahmedabad",   region:"Gujarat",     color:"#7c3aed" },
  sciencecity:  { label:"Science City",      city:"Ahmedabad",   region:"Gujarat",     color:"#6d28d9" },
  maninagar:    { label:"Maninagar",         city:"Ahmedabad",   region:"Gujarat",     color:"#8b5cf6" },
  bapunagar:    { label:"Bapunagar",         city:"Ahmedabad",   region:"Gujarat",     color:"#7c3aed" },
  bopal:        { label:"Bopal",             city:"Ahmedabad",   region:"Gujarat",     color:"#6d28d9" },
  naroda:       { label:"Naroda",            city:"Ahmedabad",   region:"Gujarat",     color:"#8b5cf6" },
  naranpura:    { label:"Naranpura",         city:"Ahmedabad",   region:"Gujarat",     color:"#7c3aed" },
  chandkheda:   { label:"Chandkheda",        city:"Ahmedabad",   region:"Gujarat",     color:"#6d28d9" },
  wadaj:        { label:"Vadaj",             city:"Ahmedabad",   region:"Gujarat",     color:"#8b5cf6" },
  lalbagh:      { label:"Lalbagh",           city:"Vadodara",    region:"Gujarat",     color:"#7c3aed" },
  alkapuri:     { label:"Baroda-Alkapuri",   city:"Vadodara",    region:"Gujarat",     color:"#6d28d9" },
  anand:        { label:"Anand",             city:"Anand",       region:"Gujarat",     color:"#8b5cf6" },
  nadiad:       { label:"Nadiad",            city:"Nadiad",      region:"Gujarat",     color:"#7c3aed" },
  bhuj:         { label:"Bhuj",              city:"Bhuj",        region:"Gujarat",     color:"#6d28d9" },
  rajkot:       { label:"Rajkot",            city:"Rajkot",      region:"Gujarat",     color:"#7c3aed" },
  bharuch:      { label:"Bharuch",           city:"Bharuch",     region:"Gujarat",     color:"#6d28d9" },
  indiranagar:  { label:"Indiranagar",       city:"Bengaluru",   region:"Karnataka",   color:"#7c3aed" },
  yelahanka:    { label:"Yelahanka",         city:"Bengaluru",   region:"Karnataka",   color:"#6d28d9" },
  juhu:         { label:"Juhu",              city:"Mumbai",      region:"Maharashtra", color:"#8b5cf6" },
  versova:      { label:"Versova",           city:"Mumbai",      region:"Maharashtra", color:"#7c3aed" },
  dadar:        { label:"Dadar",             city:"Mumbai",      region:"Maharashtra", color:"#6d28d9" },
  khorda:             { label:"Khorda",            city:"Bhubaneswar", region:"Odisha",      color:"#7c3aed" },
  niyati:             { label:"Niyati Hospital",   city:"Ahmedabad",   region:"Gujarat",     color:"#8b5cf6" },
  'pune-kalyaninagar':{ label:"Kalyaninagar",      city:"Pune",        region:"Maharashtra", color:"#7c3aed" },
  'pune-kothrud':     { label:"Kothrud",           city:"Pune",        region:"Maharashtra", color:"#6d28d9" },
};

const REGIONS = {
  "Gujarat":     ["shyamal","sciencecity","maninagar","bapunagar","bopal","naroda","naranpura","chandkheda","wadaj","lalbagh","alkapuri","anand","nadiad","bhuj","rajkot","bharuch","niyati"],
  "Karnataka":   ["indiranagar","yelahanka"],
  "Maharashtra": ["juhu","versova","dadar","pune-kalyaninagar","pune-kothrud"],
  "Odisha":      ["khorda"],
};

// ── Number formatter — always use this for display, never raw numbers ──
const n = v => (+(v) || 0).toLocaleString('en-IN');

// ── Avatar gradient — unique per user, based on username ──
function avatarGradient(str = "") {
  const gradients = [
    "linear-gradient(135deg,#7c3aed,#4f46e5)",
    "linear-gradient(135deg,#2563eb,#0891b2)",
    "linear-gradient(135deg,#059669,#0d9488)",
    "linear-gradient(135deg,#d97706,#ea580c)",
    "linear-gradient(135deg,#db2777,#9333ea)",
    "linear-gradient(135deg,#0284c7,#7c3aed)",
    "linear-gradient(135deg,#16a34a,#2563eb)",
    "linear-gradient(135deg,#b45309,#d97706)",
  ];
  const idx = (str.charCodeAt(0) || 0) % gradients.length;
  return gradients[idx];
}

const SOURCE_OPTIONS = [
  "Google","Google Direct","Hear.com","Google WhatsApp","Facebook / Instagram",
  "Just Dial","IndiaMart","Newspaper","Pamphlets","Patient Referral",
  "Old Patient","Doctor Referral","Hospital","Chirag Sir's Referral","Walk-in","Other",
];

const STATUS_OPTIONS = [
  "PTA","IA","Berra/Tinnitus/OAE","HA Trial","Fine Tuning","Service","Inquiry","Fitting Done","Booked","Follow Up","Not Interested","Others",
];

const STATUS_COLORS = {
  "PTA":               { bg:"#ede9fe", color:"#7c3aed" },
  "IA":                { bg:"#dbeafe", color:"#1d4ed8" },
  "Berra/Tinnitus/OAE":{ bg:"#fef9c3", color:"#854d0e" },
  "HA Trial":          { bg:"#ffedd5", color:"#c2410c" },
  "Fine Tuning":       { bg:"#e0f2fe", color:"#0369a1" },
  "Service":           { bg:"#f0fdf4", color:"#15803d" },
  "Inquiry":           { bg:"#faf5ff", color:"#7c3aed" },
  "Fitting Done":      { bg:"#d1fae5", color:"#065f46" },
  "Booked":            { bg:"#dcfce7", color:"#15803d" },
  "Follow Up":         { bg:"#fef9c3", color:"#a16207" },
  "Not Interested":    { bg:"#fee2e2", color:"#dc2626" },
  "Others":            { bg:"#f3f4f6", color:"#6b7280" },
};

const CLINIC_CODES = {
  shyamal:     "PN",  // Prahladnagar
  sciencecity: "SC",  // Science City
  maninagar:   "MN",  // Maninagar
  bapunagar:   "BN",  // Bapunagar
  bopal:       "BP",  // Bopal
  naroda:      "NR",  // Naroda
  naranpura:   "AE",  // AEC Naranpura
  chandkheda:  "CK",  // Chandkheda
  wadaj:       "VD",  // Vadaj
  lalbagh:     "BB",  // Baroda Lalbaug
  alkapuri:    "BR",  // Baroda Alkapuri
  anand:       "AD",  // Anand
  nadiad:      "ND",  // Nadiad
  bhuj:        "BJ",  // Bhuj
  rajkot:      "RJ",  // Rajkot
  bharuch:     "BH",  // Bharuch
  indiranagar: "BI",  // Bengaluru Indranagar
  yelahanka:   "BL",  // Bengaluru
  juhu:        "MJ",  // Mumbai Juhu
  versova:     "MV",  // Mumbai Versova
  dadar:       "MD",  // Mumbai Dadar
  khorda:      "BK",  // Bhubaneswar Khordha
};

// Clinics with Appointments tab access
const APPT_CLINICS = ['indiranagar','yelahanka','juhu','versova','dadar','alkapuri','lalbagh'];

// Clinics with This Month + Stock features
const THIS_MONTH_CLINICS = ['shyamal'];


function genCode(cid) {
  const prefix = CLINIC_CODES[cid] || cid.slice(0,2).toUpperCase();
  return `${prefix}-${Math.floor(100000+Math.random()*900000)}`;
}

// ── Compress image before upload (max 1200px wide, 0.78 quality) ──
// Handles HEIC/HEIF by converting to JPEG first via heic2any
async function compressImage(file, maxWidth = 1200, quality = 0.78) {
  if (!file.type.startsWith("image/")) return file;
  // Convert HEIC/HEIF to JPEG blob first
  let inputFile = file;
  const isHeic = /\.(heic|heif)$/i.test(file.name) || file.type === "image/heic" || file.type === "image/heif";
  if (isHeic) {
    try {
      const { default: heic2any } = await import("heic2any");
      const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
      const blob = Array.isArray(converted) ? converted[0] : converted;
      inputFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
    } catch {
      return file; // fallback: upload as-is
    }
  }
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => resolve(new File([blob], inputFile.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })),
          "image/jpeg", quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(inputFile);
  });
}

async function uploadToServer(file, patientId) {
  const compressed = await compressImage(file);
  const formData = new FormData();
  formData.append("file", compressed);
  formData.append("patient_id", patientId);
  formData.append("type", file.type.startsWith("image/") ? "image" : "document");
  formData.append("name", file.type.startsWith("image/") ? file.name.replace(/\.[^.]+$/, ".jpg") : file.name); // preserve ext for non-images
  const res = await fetch(`${API_BASE}/records.php`, {
    method: "POST",
    body: formData,
    credentials: "include",
    headers: { 'X-CSRF-Token': _csrfToken },
  });
  if (!res.ok) throw new Error("Upload failed");
  return await res.json();
}

async function writeAudit(user, action, detail = {}) {
  try {
    await apiCall(`${API_BASE}/audit.php`, {
      method: "POST",
      body: JSON.stringify({
        username:    user.username,
        name:        user.name,
        clinicId:    user.clinic || "mis",
        clinicLabel: user.clinicLabel,
        action, detail,
        ts: new Date().toISOString(),
      }),
    });
  } catch(e) { console.warn("Audit log failed:", e); }
}

// ══════════════════════════════════════════════════════════
// STORE  (MySQL-backed, fetch-based)
// ══════════════════════════════════════════════════════════
function useStore(ready) {
  const [patients, setPatients] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const refresh = useCallback(async () => {
    if (!ready) return;
    try {
      const res = await fetch(`${API_BASE}/patients.php`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPatients(Array.isArray(data) ? data : []);
      }
    } catch(e) { console.error("Patients fetch:", e); }
    setLoading(false);
  }, [ready]);

  useEffect(() => {
    if (ready) refresh();
    else       setLoading(false);
  }, [ready, refresh]);

  const addPatient = async (d, user) => {
    const p = { ...d, id:genCode(d.clinicId), createdAt:new Date().toISOString(), records:[], visits:[], recordCount:0, visitCount:0 };
    const res = await apiCall(`${API_BASE}/patients.php`, {
      method: "POST",
      body: JSON.stringify(p),
    });
    if (!res.ok) {
      let msg = `Save failed (${res.status})`;
      try {
        const body = await res.json();
        if (body.error === 'duplicate_phone') msg = body.message || 'duplicate_phone';
        else if (body.error) msg = body.error;
      } catch {}
      throw new Error(msg);
    }
    await writeAudit(user, "PATIENT_CREATED", { patientId:p.id, patientName:p.name, clinicId:p.clinicId });
    setPatients(prev => [p, ...prev]);
    return p;
  };

  const editPatient = async (pid, updates, user) => {
    const allowed = ["name","phone","age","gender","address","source","doctorName","patientStatus","notes"];
    const clean = Object.fromEntries(Object.entries(updates).filter(([k]) => allowed.includes(k)));
    await apiCall(`${API_BASE}/patients.php?id=${encodeURIComponent(pid)}`, {
      method: "PUT",
      body: JSON.stringify(clean),
    });
    const patient = patients.find(p => p.id === pid);
    await writeAudit(user, "PATIENT_EDITED", { patientId:pid, patientName:patient?.name, fields:Object.keys(clean) });
    setPatients(prev => prev.map(x => x.id === pid ? {...x, ...clean} : x)); // optimistic
  };

  const addRecord = async (pid, r, user) => {
    if (r.file) {
      await uploadToServer(r.file, pid);
    }
    await writeAudit(user, "RECORD_UPLOADED", { patientId:pid, fileName:r.name });
    setPatients(prev => prev.map(x => x.id === pid ? {...x, recordCount:(x.recordCount||0)+1} : x));
  };

  const deleteRecord = async (pid, rid, user) => {
    await apiCall(`${API_BASE}/records.php?pid=${encodeURIComponent(pid)}&rid=${encodeURIComponent(rid)}`, {
      method: "DELETE",
    });
    await writeAudit(user, "RECORD_DELETED", { patientId:pid, rid });
    setPatients(prev => prev.map(x => x.id === pid ? {...x, recordCount:Math.max(0,(x.recordCount||1)-1)} : x));
  };

  const addVisit = async (pid, v, user) => {
    const res = await apiCall(`${API_BASE}/visits.php`, {
      method: "POST",
      body: JSON.stringify({ patient_id:pid, ...v }),
    });
    if (!res.ok) {
      let msg = `Visit log failed (${res.status})`;
      try { const body = await res.json(); if (body.error) msg = body.error; } catch {}
      throw new Error(msg);
    }
    const patient = patients.find(p => p.id === pid);
    await writeAudit(user, "VISIT_LOGGED", { patientId:pid, patientName:patient?.name });
    setPatients(prev => prev.map(x => x.id === pid ? {...x, visitCount:(x.visitCount||0)+1} : x));
  };

  const deleteVisit = async (pid, vid, user) => {
    await apiCall(`${API_BASE}/visits.php?pid=${encodeURIComponent(pid)}&vid=${encodeURIComponent(vid)}`, {
      method: "DELETE",
    });
    await writeAudit(user, "VISIT_DELETED", { patientId:pid, vid });
    setPatients(prev => prev.map(x => x.id === pid ? {...x, visitCount:Math.max(0,(x.visitCount||1)-1)} : x));
  };

  const editVisit = async (pid, vid, updates, user) => {
    await apiCall(`${API_BASE}/visits.php?vid=${encodeURIComponent(vid)}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    await writeAudit(user, "VISIT_EDITED", { patientId:pid, vid });
    // no count change on edit — no reload needed
  };

  const deletePatient = async (pid, user) => {
    const patient = patients.find(p => p.id === pid);
    await apiCall(`${API_BASE}/patients.php?id=${encodeURIComponent(pid)}`, {
      method: "DELETE",
    });
    await writeAudit(user, "PATIENT_DELETED", { patientId:pid, patientName:patient?.name });
    setPatients(prev => prev.filter(x => x.id !== pid)); // optimistic remove
  };

  const searchPhone       = (q) => patients.filter(p => p.phone?.includes(q));
  const searchCode        = (q) => patients.filter(p => p.id?.toLowerCase().includes(q.toLowerCase()));
  const searchName        = (q) => patients.filter(p => p.name?.toLowerCase().includes(q.toLowerCase()));
  return { patients, loading, refresh, addPatient, editPatient, deletePatient, addRecord, deleteRecord, addVisit, deleteVisit, editVisit, searchPhone, searchCode, searchName };
}

// ══════════════════════════════════════════════════════════
// ROOT
// ══════════════════════════════════════════════════════════
export default function App() {
  const [user,          setUser]          = useState(null);
  const [authReady,     setAuthReady]     = useState(false);
  const [theme,         setTheme]         = useState("light");
  const [showWarning,   setShowWarning]   = useState(false);
  const [warnCountdown, setWarnCountdown] = useState(1800); // 30 min in seconds
  const store = useStore(authReady && !!user);

  const lastActivity = useRef(Date.now());
  const warningShown = useRef(false);
  const intervalRef  = useRef(null);
  const warnTimerRef = useRef(null);

  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); }, [theme]);

  // Restore session on page load
  useEffect(() => {
    fetch(`${API_BASE}/auth.php?action=check`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (data.user) setUser(data.user);
        if (data.csrf) { _csrfToken = data.csrf; }
      })
      .catch(() => {})
      .finally(() => setAuthReady(true));
  }, []);

  const sendOtp = async (username, password) => {
    try {
      const res = await fetch(`${API_BASE}/otp.php?action=send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || "Login failed. Please try again." };
      if (data.cooldown) return { cooldown: true, secondsLeft: data.secondsLeft, maskedEmail: data.maskedEmail };
      return { success: true, maskedEmail: data.maskedEmail };
    } catch(e) {
      return { error: "Network error. Check your connection and try again." };
    }
  };

  const verifyOtp = async (username, otp) => {
    try {
      const res = await fetch(`${API_BASE}/otp.php?action=verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, otp }),
      });
      const data = await res.json();
      if (!res.ok) return data.error || "Invalid or expired OTP.";
      // Fetch fresh CSRF token for the new session
      fetch(`${API_BASE}/auth.php?action=check`, { credentials: "include" })
        .then(r => r.json())
        .then(d => { if (d.csrf) { _csrfToken = d.csrf; } })
        .catch(() => {});
      setUser(data.user);
      return null;
    } catch(e) {
      return "Network error. Check your connection and try again.";
    }
  };

  const logout = async () => {
    // Clear inactivity timers
    if (intervalRef.current)  { clearInterval(intervalRef.current);  intervalRef.current  = null; }
    if (warnTimerRef.current)  { clearInterval(warnTimerRef.current); warnTimerRef.current = null; }
    setShowWarning(false);
    warningShown.current = false;
    await apiCall(`${API_BASE}/auth.php?action=logout`, { method: "POST" }).catch(() => {});
    _csrfToken = '';
    setUser(null);
  };

  const toggleTheme = () => setTheme(t => t === "light" ? "dark" : "light");

  // ── Inactivity logic ──────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // Reset activity timestamp on any user interaction
    const resetActivity = () => {
      lastActivity.current  = Date.now();
      warningShown.current  = false;
    };
    const EVENTS = ["mousemove", "mousedown", "keypress", "scroll", "touchstart"];
    EVENTS.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    // Check inactivity every 60 seconds
    intervalRef.current = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= 28800000) {
        // 8 hours — auto logout
        logout();
      } else if (idle >= 27000000 && !warningShown.current) {
        // 7.5 hours — show warning modal
        warningShown.current = true;
        setWarnCountdown(1800);
        setShowWarning(true);
      }
    }, 60000);

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, resetActivity));
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Warning modal countdown tick
  useEffect(() => {
    if (!showWarning) return;
    if (warnCountdown <= 0) { logout(); return; }
    warnTimerRef.current = setInterval(() => {
      setWarnCountdown(c => {
        if (c <= 1) { clearInterval(warnTimerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => { if (warnTimerRef.current) clearInterval(warnTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWarning]);

  // Auto-logout when countdown hits 0
  useEffect(() => {
    if (showWarning && warnCountdown === 0) logout();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warnCountdown, showWarning]);

  const stayLoggedIn = () => {
    lastActivity.current = Date.now();
    warningShown.current = false;
    if (warnTimerRef.current) { clearInterval(warnTimerRef.current); warnTimerRef.current = null; }
    setShowWarning(false);
    setWarnCountdown(1800);
  };

  const fmtCountdown = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!authReady || (user && store.loading)) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg)",flexDirection:"column",gap:16}}>
      <SLogo/>
      <div style={{display:"flex",alignItems:"center",gap:10,color:"var(--muted)",fontSize:13,marginTop:8}}>
        <span style={{width:16,height:16,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>
        Connecting…
      </div>
    </div>
  );

  return (
    <>
      {/* Inactivity warning modal */}
      {showWarning && (
        <div style={{position:"fixed",inset:0,background:"#00000080",zIndex:99999,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:16,padding:32,maxWidth:400,width:"90%",boxShadow:"0 24px 64px #00000040"}}>
            <div style={{fontSize:18,fontWeight:700,color:"var(--text1)",marginBottom:10}}>Session Expiring Soon</div>
            <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.6,marginBottom:20}}>
              You've been inactive for 7.5 hours. You will be automatically logged out in 30 minutes.
            </div>
            <div style={{fontSize:36,fontWeight:800,color:"#7c3aed",textAlign:"center",fontFamily:"monospace",marginBottom:24,letterSpacing:2}}>
              {fmtCountdown(warnCountdown)}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={stayLoggedIn} style={{flex:1,padding:"12px",background:"linear-gradient(135deg,#7c3aed,#6d28d9)",border:"none",borderRadius:10,color:"#fff",fontSize:14,fontWeight:700,cursor:"pointer",boxShadow:"0 4px 14px #7c3aed33"}}>
                Stay Logged In
              </button>
              <button onClick={logout} style={{flex:1,padding:"12px",background:"transparent",border:"1px solid var(--border)",borderRadius:10,color:"var(--muted)",fontSize:14,fontWeight:600,cursor:"pointer"}}>
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
      {!user && <Login onSendOtp={sendOtp} onVerifyOtp={verifyOtp} theme={theme} toggleTheme={toggleTheme}/>}
      {user && (user.role === "mis" || user.role === "owner" || user.role === "regional" || user.role === "office") && <MIS user={user} store={store} onLogout={logout} theme={theme} toggleTheme={toggleTheme}/>}
      {user && user.role !== "mis" && user.role !== "owner" && user.role !== "regional" && user.role !== "office" && <Clinic user={user} store={store} onLogout={logout} theme={theme} toggleTheme={toggleTheme}/>}
    </>
  );
}


// ══════════════════════════════════════════════════════════
// LOGIN
// ══════════════════════════════════════════════════════════
function fmtLastLogin(ts) {
  if (!ts) return null;
  const d = new Date(Number(ts));
  const now = new Date();
  const todayStr = now.toDateString();
  const yesterStr = new Date(now - 86400000).toDateString();
  const time = d.toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit', hour12:true});
  if (d.toDateString() === todayStr) return `Today at ${time}`;
  if (d.toDateString() === yesterStr) return `Yesterday at ${time}`;
  return d.toLocaleDateString('en-IN', {day:'numeric', month:'short'}) + ` at ${time}`;
}

function Login({ onSendOtp, onVerifyOtp, theme, toggleTheme }) {
  const [u,           setU]           = useState("");
  const [pw,          setPw]          = useState("");
  const [err,         setErr]         = useState("");
  const [busy,        setBusy]        = useState(false);
  const [show,        setShow]        = useState(false);
  const [step,        setStep]        = useState("credentials");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp,         setOtp]         = useState("");
  const [countdown,   setCountdown]   = useState(0);
  const [patientCount, setPatientCount] = useState(null);
  const [pulsing, setPulsing]         = useState(true);
  const lastLoginLabel = fmtLastLogin(localStorage.getItem('sl_last_login'));
  const quote = QUOTES[Math.floor(Date.now()/86400000) % QUOTES.length];

  useEffect(() => {
    fetch(`${API_BASE}/patients.php?count=1`).then(r=>r.json()).then(d=>{ if(d.count!=null) setPatientCount(d.count); }).catch(()=>{});
    const t = setTimeout(() => setPulsing(false), 6000);
    return () => clearTimeout(t);
  }, []);

  // Countdown tick (for resend cooldown)
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const fmtCountdown = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const go = async () => {
    if(!u.trim()||!pw.trim()){setErr("Please enter both username and password.");return;}
    setBusy(true); setErr("");
    const result = await onSendOtp(u.trim(), pw);
    setBusy(false);
    if (result.error) { setErr(result.error); return; }
    setMaskedEmail(result.maskedEmail || "");
    setCountdown(result.cooldown ? (result.secondsLeft || 300) : 300);
    setStep("otp");
  };

  const verify = async () => {
    if (otp.trim().length !== 6) { setErr("Enter the 6-digit OTP."); return; }
    setBusy(true); setErr("");
    const error = await onVerifyOtp(u.trim(), otp.trim());
    setBusy(false);
    if (error) { setErr(error); return; }
    localStorage.setItem('sl_last_login', Date.now().toString());
  };

  const resend = async () => {
    setBusy(true); setErr("");
    const result = await onSendOtp(u.trim(), pw);
    setBusy(false);
    if (result.error) { setErr(result.error); return; }
    setOtp(""); setCountdown(300);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",background:"var(--bg)"}}>
      {/* Left panel */}
      <div style={{flex:"0 0 46%",background:"linear-gradient(160deg,#4c1d95 0%,#6d28d9 50%,#7c3aed 100%)",display:"flex",flexDirection:"column",padding:"52px 56px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-100,left:-100,width:450,height:450,borderRadius:"50%",border:"1px solid #ffffff12"}}/>
        <div style={{position:"absolute",top:-50,left:-50,width:280,height:280,borderRadius:"50%",border:"1px solid #ffffff18"}}/>
        <div style={{position:"absolute",bottom:-80,right:-60,width:380,height:380,borderRadius:"50%",border:"1px solid #ffffff0a"}}/>
        <div style={{position:"absolute",bottom:80,left:0,right:0,display:"flex",alignItems:"center",justifyContent:"center",gap:4,opacity:0.15}}>
          {[10,24,16,38,20,48,28,42,18,32,14,28,22,44,12,36,24,42,16,30].map((h,i)=>(
            <div key={i} style={{width:3,height:h,background:"#fff",borderRadius:99}}/>
          ))}
        </div>
        <div style={{marginBottom:"auto"}}><SLogo white/></div>
        <div style={{marginBottom:52}}>
          <div style={{fontSize:10,color:"#c4b5fd",fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",marginBottom:18}}>Today's Note</div>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:"#fff",lineHeight:1.5,fontStyle:"italic",marginBottom:14}}>"{quote.q}"</div>
          <div style={{fontSize:12,color:"#c4b5fd"}}>{quote.a}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:12,width:"100%"}}>
            <div style={{height:1,flex:1,background:"linear-gradient(90deg,#ffffff30,transparent)"}}/>
            <div style={{fontSize:10,color:"#a78bfa",letterSpacing:1.5}}>23 CLINICS · 4 STATES</div>
            <div style={{height:1,flex:1,background:"linear-gradient(90deg,transparent,#ffffff30)"}}/>
          </div>
          {patientCount!=null&&<div style={{fontSize:11,color:"#c4b5fd",fontWeight:600,letterSpacing:0.5}}>{patientCount.toLocaleString('en-IN')} Patients Registered</div>}
        </div>
      </div>

      {/* Right panel */}
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 56px",background:"var(--bg)",position:"relative"}}>
        <button onClick={toggleTheme} style={{position:"absolute",top:20,right:24,background:"var(--card)",border:"1px solid var(--border)",borderRadius:99,padding:"7px 14px",fontSize:13,cursor:"pointer",color:"var(--text1)",boxShadow:"var(--cardshadow)"}}>
          {theme==="light"?"🌙 Dark":"☀️ Light"}
        </button>

        <div className="fadeUp" style={{width:"100%",maxWidth:400}}>
          <div style={{marginBottom:28}}>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:"var(--text1)",letterSpacing:-0.5}}>Welcome back</div>
            <div style={{fontSize:13,color:"var(--muted)",marginTop:6}}>Sign in to your SoundLife clinic portal</div>
          </div>

          <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:18,padding:"30px",boxShadow:"var(--cardshadow)"}}>
            {step === "credentials" ? (<>
              <label style={S.label}>Username</label>
              <input style={S.inp} value={u} onChange={e=>{setU(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="e.g. sl-mis, sl-shyamal" autoFocus autoComplete="username"/>
              <label style={{...S.label,marginTop:16}}>Password</label>
              <div style={{position:"relative"}}>
                <input style={{...S.inp,paddingRight:44}} type={show?"text":"password"} value={pw} onChange={e=>{setPw(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&go()} placeholder="Enter password" autoComplete="current-password"/>
                <button onClick={()=>setShow(s=>!s)} style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:14,padding:4}}>{show?"🙈":"👁"}</button>
              </div>
              {err && <ErrBox msg={err}/>}
              <button className="btn-p" onClick={go} disabled={busy} style={{marginTop:20,width:"100%",padding:"13px",background:busy?"var(--border)":"linear-gradient(135deg,#7c3aed,#6d28d9)",border:"none",borderRadius:11,color:"#fff",fontSize:14,fontWeight:700,cursor:busy?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxShadow:"0 4px 14px #7c3aed33",animation:(!busy&&pulsing)?"pulseBtn 1.8s ease-in-out 0.4s 3":undefined}}>
                {busy?<Spin/>:"Sign In →"}
              </button>
              {lastLoginLabel&&<div style={{marginTop:10,textAlign:"center",fontSize:11,color:"var(--muted2)"}}>🕐 Last login: {lastLoginLabel}</div>}
              <div style={{marginTop:10,padding:"10px 13px",background:"var(--accentbg)",borderRadius:9,border:"1px solid var(--border)",fontSize:11,color:"var(--muted)",lineHeight:1.6}}>
                👆 Use your assigned username (e.g. <strong>sl-shyamal</strong>) and password. Contact MIS admin if you don't have one.
              </div>
            </>) : (<>
              {maskedEmail && <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>OTP sent to <strong>{maskedEmail}</strong></div>}
              <label style={S.label}>Enter OTP</label>
              <input style={{...S.inp,letterSpacing:10,textAlign:"center",fontSize:22,fontWeight:700}} value={otp} onChange={e=>{setOtp(e.target.value.replace(/\D/g,"").slice(0,6));setErr("");}} onKeyDown={e=>e.key==="Enter"&&verify()} placeholder="••••••" maxLength={6} inputMode="numeric" autoFocus/>
              {err && <ErrBox msg={err}/>}
              <button className="btn-p" onClick={verify} disabled={busy} style={{marginTop:20,width:"100%",padding:"13px",background:busy?"var(--border)":"linear-gradient(135deg,#7c3aed,#6d28d9)",border:"none",borderRadius:11,color:"#fff",fontSize:14,fontWeight:700,cursor:busy?"not-allowed":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,boxShadow:"0 4px 14px #7c3aed33"}}>
                {busy?<Spin/>:"Verify OTP →"}
              </button>
              <div style={{marginTop:14,textAlign:"center",fontSize:12,color:"var(--muted)"}}>
                {countdown > 0
                  ? `Resend OTP in ${fmtCountdown(countdown)}`
                  : <button onClick={resend} disabled={busy} style={{background:"none",border:"none",color:"#7c3aed",cursor:"pointer",fontSize:12,fontWeight:600,padding:0}}>Resend OTP</button>
                }
              </div>
              <div style={{marginTop:10,textAlign:"center"}}>
                <button onClick={()=>{setStep("credentials");setOtp("");setErr("");setCountdown(0);}} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:12,padding:0}}>← Back to login</button>
              </div>
            </>)}
          </div>

          <div style={{textAlign:"center",marginTop:8,fontSize:11,color:"var(--muted2)"}}>&copy; {new Date().getFullYear()} SoundLife Speech &amp; Hearing Clinic</div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SHELL
// ══════════════════════════════════════════════════════════
function Shell({ user, navItems, children, onLogout, theme, toggleTheme, pageKey }) {
  const acl = "#7c3aed";
  return (
    <div style={{display:"flex",minHeight:"100vh",background:"var(--bg)",color:"var(--text1)"}}>
      <aside className="sidebar-full" style={{width:224,background:"var(--sidebar)",backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",borderRight:"1px solid var(--sideborder)",display:"flex",flexDirection:"column",flexShrink:0,position:"sticky",top:0,height:"100vh",overflow:"auto",boxShadow:"2px 0 24px #7c3aed12"}}>
        <div style={{padding:"22px 18px 14px"}}>
          <SLogo/>
          <div style={{marginTop:12,background:acl+"18",border:`1px solid ${acl}30`,borderRadius:8,padding:"5px 10px",fontSize:10,color:acl,fontWeight:700,letterSpacing:0.5,lineHeight:1.5}}>{user.clinicLabel}</div>
        </div>
        <nav style={{flex:1,padding:"4px 10px",display:"flex",flexDirection:"column",gap:1}}>
          {navItems.map(item=>(
            <button key={item.id} className="slink" onClick={item.onClick}
              style={{display:"flex",alignItems:"center",gap:9,padding:"9px 11px",borderRadius:9,border:"none",background:item.active?`linear-gradient(90deg,${acl}20,${acl}08)`:"transparent",color:item.active?acl:"var(--muted)",fontSize:12.5,cursor:"pointer",textAlign:"left",width:"100%",borderLeft:item.active?`3px solid ${acl}`:"3px solid transparent",fontWeight:item.active?600:400,transition:"all 0.15s",boxShadow:item.active?`inset 0 0 0 1px ${acl}18`:"none"}}>
              <span style={{fontSize:16,width:20,textAlign:"center",flexShrink:0,color:"inherit"}}>{item.icon}</span>
              <span className="sidebar-label" style={{flex:1}}>{item.label}</span>
              {item.badge>0&&<span className="sidebar-label" style={{background:acl+"20",color:acl,borderRadius:99,padding:"1px 7px",fontSize:9,fontWeight:700}}>{item.badge}</span>}
            </button>
          ))}
        </nav>
        <div style={{padding:"12px 14px",borderTop:"1px solid var(--sideborder)"}}>
          <button onClick={toggleTheme} style={{width:"100%",marginBottom:10,padding:"8px",background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:8,color:"var(--text2)",fontSize:11,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            {theme==="light"?"🌙 Dark Mode":"☀️ Light Mode"}
          </button>
          <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:10}}>
            <div style={{width:32,height:32,borderRadius:9,background:avatarGradient(user.username),display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:11,flexShrink:0,letterSpacing:0.5}}>
              {user.name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase()}
            </div>
            <div style={{minWidth:0}}>
              <div className="sidebar-label" style={{fontSize:12,fontWeight:600,color:"var(--text1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5}}>
                {user.name}
                {user.clinic&&CLINICS[user.clinic]&&<span style={{width:7,height:7,borderRadius:"50%",background:CLINICS[user.clinic].color,flexShrink:0,display:"inline-block"}}/>}
              </div>
              <div className="sidebar-label" style={{fontSize:10,color:"var(--muted2)"}}>{user.username}</div>
            </div>
          </div>
          <button onClick={onLogout} style={{width:"100%",padding:"7px",background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:8,color:"var(--muted)",fontSize:11,cursor:"pointer"}}>← Sign Out</button>
        </div>
      </aside>
      <div key={pageKey} className="fadeIn" style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,overflow:"auto"}}>{children}</div>
    </div>
  );
}

function useCountUp(target, duration=900, delay=0) {
  const [val,setVal]=useState(0);
  useEffect(()=>{
    const num=typeof target==='number'?target:parseInt(target)||0;
    let raf=null, timer=null;
    const startAnim=()=>{
      let t0=null;
      raf=requestAnimationFrame(function step(ts){
        if(!t0)t0=ts;
        const p=Math.min((ts-t0)/duration,1);
        const ease=1-Math.pow(1-p,3);
        setVal(Math.round(num*ease));
        if(p<1)raf=requestAnimationFrame(step);
      });
    };
    if(delay>0){timer=setTimeout(startAnim,delay);}else{startAnim();}
    return()=>{ if(raf)cancelAnimationFrame(raf); if(timer)clearTimeout(timer); };
  },[target,duration,delay]);
  return val;
}
function CountUpNum({value,suffix="",delay=0}){
  const num=useCountUp(typeof value==='string'?parseInt(value)||0:value, 900, delay);
  return <>{num.toLocaleString('en-IN')}{suffix}</>;
}
function useLiveClock() {
  const fmt = () => new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
  const [clock,setClock] = useState(fmt);
  useEffect(() => { const t = setInterval(()=>setClock(fmt()),60000); return ()=>clearInterval(t); },[]);
  return clock;
}
function TopBar({ title, sub, actions }) {
  const clock = useLiveClock();
  return (
    <div style={{padding:"16px 26px",borderBottom:"1px solid var(--sideborder)",background:"var(--topbar)",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10,position:"sticky",top:0,zIndex:20,boxShadow:"0 1px 0 var(--border)"}}>
      <div>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,color:"var(--text1)",letterSpacing:-0.3}}>{title}</div>
        {sub&&<div style={{fontSize:11,color:"var(--muted)",marginTop:1}}>{sub}</div>}
      </div>
      <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"var(--muted)",fontVariantNumeric:"tabular-nums",background:"var(--accentbg)",padding:"4px 10px",borderRadius:7,border:"1px solid var(--border)",fontWeight:600,letterSpacing:0.3}}>🕐 {clock}</span>
        {actions&&<div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>{actions}</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ADMIN PANEL (owner only)
// ══════════════════════════════════════════════════════════
function AdminPanel({ user }) {
  const [users,setUsers]=useState([]); const [busy,setBusy]=useState(false); const [loading,setLoading]=useState(true);
  const load=()=>{
    setLoading(true);
    fetch(`${API_BASE}/admin.php?action=users`,{credentials:'include'})
      .then(r=>r.json()).then(d=>{if(Array.isArray(d))setUsers(d);}).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{load();},[]);
  const doAction=async(username,action)=>{
    if(busy)return;
    setBusy(username+action);
    try{
      await apiCall(`${API_BASE}/admin.php?action=${action}`,{method:'POST',body:JSON.stringify({username})});
      load();
    }catch(e){alert("Error: "+e.message);}
    setBusy(false);
  };
  const ROLE_COLOR={owner:"#7c3aed",mis:"#2563eb",office:"#0891b2",clinic:"#059669"};
  return (
    <div>
      <TopBar title="Admin Panel" sub="User management — owner only"/>
      <div className="main-pad" style={{padding:"22px 26px",maxWidth:800}}>
        <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"22px",boxShadow:"var(--cardshadow)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text1)"}}>System Users</div>
            <button onClick={load} style={{padding:"6px 14px",borderRadius:8,border:"1px solid var(--border)",background:"var(--accentbg)",color:"var(--accent)",fontSize:12,cursor:"pointer",fontWeight:600}}>↻ Refresh</button>
          </div>
          {loading&&<div style={{textAlign:"center",padding:32,color:"var(--muted)"}}>Loading…</div>}
          {!loading&&users.map(u=>(
            <div key={u.username} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
              <div style={{width:36,height:36,borderRadius:10,background:`linear-gradient(135deg,${ROLE_COLOR[u.role]||"#7c3aed"},${ROLE_COLOR[u.role]||"#6d28d9"})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:14,flexShrink:0}}>{u.name?.[0]?.toUpperCase()}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,color:"var(--text1)",fontSize:13}}>{u.name} <span style={{color:"var(--muted)",fontWeight:400}}>({u.username})</span></div>
                <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>
                  <span style={{background:ROLE_COLOR[u.role]||"#7c3aed",color:"#fff",borderRadius:4,padding:"1px 7px",fontSize:10,fontWeight:700,textTransform:"uppercase",marginRight:6}}>{u.role}</span>
                  {u.clinic_label||""}
                </div>
              </div>
              <div style={{flexShrink:0,display:"flex",gap:8,alignItems:"center"}}>
                {u.is_locked
                  ?<span style={{fontSize:11,color:"#dc2626",fontWeight:700,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:6,padding:"3px 10px"}}>🔒 Locked</span>
                  :<span style={{fontSize:11,color:"#059669",fontWeight:700,background:"#f0fdf4",border:"1px solid #86efac",borderRadius:6,padding:"3px 10px"}}>✓ Active</span>
                }
                {u.username!==user.username&&(u.is_locked
                  ?<button onClick={()=>doAction(u.username,"unlock_user")} disabled={!!busy} style={{padding:"5px 12px",borderRadius:7,border:"1px solid #86efac",background:"#f0fdf4",color:"#059669",fontSize:11,cursor:"pointer",fontWeight:600}}>{busy===u.username+"unlock_user"?"…":"Unlock"}</button>
                  :<button onClick={()=>doAction(u.username,"lock_user")} disabled={!!busy} style={{padding:"5px 12px",borderRadius:7,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontSize:11,cursor:"pointer",fontWeight:600}}>{busy===u.username+"lock_user"?"…":"Lock"}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MIS DASHBOARD
// ══════════════════════════════════════════════════════════
function MIS({ user, store, onLogout, theme, toggleTheme }) {
  const [tab,setTab]=useState("overview"); const [selected,setSel]=useState(null); const [lightbox,setLb]=useState(null);
  const isRegional = user.role === "regional";
  // Clinics this user is allowed to see (empty = all)
  const allowedClinics = isRegional && user.region ? (REGIONS[user.region] || []) : [];
  // Filtered store for regional users — analytics/overview see only their region; search sees all
  const regionPatients = allowedClinics.length>0 ? store.patients.filter(p=>allowedClinics.includes(p.clinicId)) : store.patients;
  const filteredStore = allowedClinics.length>0 ? {...store, patients: regionPatients} : store;
  const todayStr = new Date().toISOString().slice(0,10);
  const todayCount = regionPatients.filter(p=>(p.createdAt||"").slice(0,10)===todayStr).length;
  const isBlrRegional = isRegional;
  const [overduePopup, dismissOverdue] = useOverdueCheck(isBlrRegional);
  const [apptBadge,setApptBadge]=useState(0);
  useEffect(()=>{
    if(!isBlrRegional) return;
    const today=fmtDateKey(new Date());
    apiCall(`${API_BASE}/appointments.php?start=${today}&end=${today}`)
      .then(r=>r.json())
      .then(data=>{if(Array.isArray(data)) setApptBadge(data.filter(a=>a.status==='pending').length);})
      .catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isBlrRegional]);
  const nav=[
    {id:"overview",  icon:"◈", label:"Overview",        active:tab==="overview",  onClick:()=>setTab("overview"), badge:todayCount},
    {id:"search",    icon:"⌕", label:"Search Patient",  active:tab==="search",    onClick:()=>setTab("search")},
    {id:"all",       icon:"☰", label:"All Patients",    active:tab==="all",       onClick:()=>setTab("all"), badge:store.patients.length},
    ...(isRegional?[{id:"add", icon:"+", label:"Add Patient", active:tab==="add", onClick:()=>setTab("add")}]:[]),
    ...(isBlrRegional?[{id:"appointments",icon:"📅",label:"Appointments",badge:apptBadge,active:tab==="appointments",onClick:()=>{setTab("appointments");setApptBadge(0);}}]:[]),
    ...(['mis','office','owner'].includes(user.role)?[{id:"appts-mis",icon:"📅",label:"Appointments",active:tab==="appts-mis",onClick:()=>setTab("appts-mis")}]:[]),
    ...(['mis','office','owner'].includes(user.role)?[{id:"stock",icon:"📦",label:"Stock",active:tab==="stock",onClick:()=>setTab("stock")}]:[]),
    {id:"gallery",   icon:"⬚", label:"Records Gallery", active:tab==="gallery",   onClick:()=>setTab("gallery")},
    {id:"analytics", icon:"◉", label:"Analytics",       active:tab==="analytics", onClick:()=>setTab("analytics")},
    {id:"master",    icon:"📈", label:"Master Analytics",active:tab==="master",    onClick:()=>setTab("master")},
    ...(!isRegional?[{id:"audit", icon:"🔒", label:"Audit Log", active:tab==="audit", onClick:()=>setTab("audit")}]:[]),
    ...(user.role==="owner"?[{id:"admin", icon:"⚙", label:"Admin", active:tab==="admin", onClick:()=>setTab("admin")}]:[]),
  ];
  return (
    <Shell user={user} navItems={nav} onLogout={onLogout} theme={theme} toggleTheme={toggleTheme} pageKey={tab}>
      <CmdKPalette patients={store.patients} nav={nav} onSelect={p=>{setSel(p);}} />
      {tab==="overview"  && <MISOverview store={filteredStore} onSelect={setSel} setLb={setLb} user={user}/>}
      {tab==="search"    && <SearchView  store={store} onSelect={setSel} isMIS user={user}/>}
      {tab==="all"       && <AllPatients store={filteredStore} onSelect={setSel} allowedClinics={allowedClinics} user={user}/>}
      {tab==="add"       && isRegional && <AddPatient  user={user} store={store} onDone={()=>setTab("overview")} onCancel={()=>setTab("overview")} allowedClinics={allowedClinics}/>}
      {tab==="appointments" && isBlrRegional && <AppointmentsView user={user} onPatientCreated={store.refresh}/>}
      {tab==="appts-mis" && ['mis','office','owner'].includes(user.role) && <AppointmentsMISView/>}
      {tab==="stock"     && ['mis','office','owner'].includes(user.role) && <ShyamalStockView user={user}/>}
      {tab==="gallery"   && <Gallery     store={store} setLb={setLb} allowedClinics={allowedClinics}/>}
      {tab==="analytics" && <Analytics   store={filteredStore} allowedClinics={allowedClinics} onSelect={setSel}/>}
      {tab==="master"    && <MasterAnalytics store={filteredStore} allowedClinics={allowedClinics}/>}
      {tab==="audit"     && !isRegional && <AuditLog user={user}/>}
      {tab==="admin"     && user.role==="owner" && <AdminPanel user={user}/>}
      {selected && <PatientModal p={selected} store={store} onClose={()=>setSel(null)} readOnly={!isRegional} setLb={setLb} user={user}/>}
      {lightbox  && <Lightbox {...lightbox} onClose={()=>setLb(null)}/>}
      <OverduePopup appts={overduePopup} onDismiss={dismissOverdue}/>
    </Shell>
  );
}

function MISOverview({ store, onSelect, setLb, user }) {
  const [galleryData,setGalleryData]=useState([]);
  const [recentAudio,setRecentAudio]=useState([]);

  useEffect(()=>{
    fetch(`${API_BASE}/patients.php?mode=gallery&thumb=1`,{credentials:'include'})
      .then(r=>r.json())
      .then(data=>{ if(Array.isArray(data)) setGalleryData(data); })
      .catch(()=>{});
    fetch(`${API_BASE}/audiometry.php?mode=recent&limit=8`,{credentials:'include'})
      .then(r=>r.json())
      .then(data=>{ if(Array.isArray(data)) setRecentAudio(data); })
      .catch(()=>{});
  },[]);

  const total=store.patients.length;
  const totalRec=store.patients.reduce((a,p)=>a+(p.recordCount||p.records?.length||0),0);
  const today=store.patients.filter(p=>new Date(p.createdAt).toDateString()===new Date().toDateString()).length;
  const _allowedClinics = (user?.role==="regional"&&user?.region) ? (REGIONS[user.region]||[]) : [];
  const _regionEntries = _allowedClinics.length>0
    ? Object.entries(REGIONS).filter(([,ids])=>ids.some(id=>_allowedClinics.includes(id)))
    : Object.entries(REGIONS);
  const regions=_regionEntries.map(([r,ids])=>({r,count:store.patients.filter(p=>ids.includes(p.clinicId)).length,n:ids.length}));
  const recent=store.patients.slice(0,6);
  const recentImgs=galleryData.filter(r=>r.type==="image").slice(0,8).map(r=>({...r,patient:{name:r.patient_name,clinicId:r.clinic_id}}));
  return (
    <div>
      <TopBar title={user?.role==="regional"?`${user.region} Region Overview`:"SoundLife MIS"} sub={new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}/>
      <div className="main-pad" style={{padding:"22px 26px"}}>
        <div className="grid-3" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
          {[[total,"Total Patients","👥"],[totalRec,"Total Records","📁"],[_allowedClinics.length>0?_allowedClinics.length:Object.keys(CLINICS).length,"Active Clinics","🏥"],[today,"Registered Today","✅"]].map(([v,l,ic],i)=>(
            <div key={l} className="card-hover fadeUp" style={{animationDelay:`${i*0.07}s`,background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"18px 20px",boxShadow:"var(--cardshadow)",borderTop:"3px solid var(--accent)"}}>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:8}}>{ic} {l}</div>
              <div style={{fontSize:30,fontWeight:800,color:"var(--accent)"}}><CountUpNum value={v} delay={i*70+420}/></div>
            </div>
          ))}
        </div>
        <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:18,marginBottom:18}}>
          <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"18px 20px",boxShadow:"var(--cardshadow)"}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text1)",marginBottom:14}}>Clinics by Region</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {regions.map(r=>(
                <div key={r.r} style={{background:"var(--accentbg)",borderRadius:10,padding:"10px 13px",border:"1px solid var(--border)"}}>
                  <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{r.r}</div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginTop:6}}>
                    <span style={{fontSize:22,fontWeight:800,color:"var(--accent)"}}><CountUpNum value={r.count} delay={700}/></span>
                    <span style={{fontSize:10,color:"var(--muted2)"}}>{r.n} clinics</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"18px 20px",boxShadow:"var(--cardshadow)",overflow:"auto",maxHeight:300}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text1)",marginBottom:12}}>Recent Patients</div>
            {recent.length===0&&<Empty msg="No patients yet"/>}
            {recent.map(p=><PRow key={p.id} p={p} onClick={()=>onSelect(p)}/>)}
          </div>
        </div>
        {recentImgs.length>0&&(
          <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"18px 20px",marginBottom:18,boxShadow:"var(--cardshadow)"}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text1)",marginBottom:14}}>Latest Uploaded Records</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(148px,1fr))",gap:10}}>
              {recentImgs.map(r=><RCard key={r.rid} r={r} onView={()=>setLb({src:imgSrc(r.data),name:r.name,patient:r.patient})}/>)}
            </div>
          </div>
        )}
        {recentAudio.length>0&&(
          <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"18px 20px",boxShadow:"var(--cardshadow)"}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text1)",marginBottom:14}}>🦻 Recent Audiogram Uploads</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
              {recentAudio.map(entry=>{
                const rSvg=auGenAudiogram('right',entry.points||[]);
                const lSvg=auGenAudiogram('left',entry.points||[]);
                const cc=CLINICS[entry.clinic_id];
                const dt=entry.created_at?new Date(entry.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}):'';
                const patient=store.patients.find(p=>p.caseCode===entry.case_code||p.case_code===entry.case_code);
                return(
                <div key={entry.id}
                  onClick={()=>patient&&onSelect(patient)}
                  style={{background:"var(--bg)",border:"1.5px solid var(--border)",borderRadius:11,overflow:"hidden",cursor:patient?'pointer':'default',transition:"border-color .15s,box-shadow .15s"}}
                  onMouseEnter={e=>{if(patient){e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.boxShadow='0 4px 16px #7c3aed22';}}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.boxShadow='none';}}>
                  <div style={{display:"flex",gap:1,padding:"6px 6px 2px",background:"var(--accentbg)"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:6,fontWeight:800,color:"#dc2626",textAlign:"center",marginBottom:1,letterSpacing:.5}}>RIGHT</div>
                      <div dangerouslySetInnerHTML={{__html:rSvg.replace('style="display:block;width:100%;height:auto;"','style="display:block;width:100%;height:auto;max-height:68px;"')}}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:6,fontWeight:800,color:"#2563eb",textAlign:"center",marginBottom:1,letterSpacing:.5}}>LEFT</div>
                      <div dangerouslySetInnerHTML={{__html:lSvg.replace('style="display:block;width:100%;height:auto;"','style="display:block;width:100%;height:auto;max-height:68px;"')}}/>
                    </div>
                  </div>
                  <div style={{padding:"7px 10px",borderTop:"1px solid var(--border)"}}>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--text1)",marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{entry.patient_name||'—'}</div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:4}}>
                      {cc&&<span style={{background:cc.color+'18',color:cc.color,borderRadius:4,padding:"1px 6px",fontSize:9,fontWeight:700,flexShrink:0}}>{cc.label}</span>}
                      <span style={{fontSize:9,color:"var(--muted2)",whiteSpace:"nowrap"}}>{dt}</span>
                    </div>
                    {entry.notes&&<div style={{fontSize:9,color:"var(--muted)",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{entry.notes}</div>}
                  </div>
                </div>
              );})}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// AUDIT LOG
// ══════════════════════════════════════════════════════════
function AuditLog({ user }) {
  const [logs, setLogs]         = useState([]);
  const [ids, setIds]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("ALL");
  const [clearing, setClearing] = useState(false);
  const [page, setPage]         = useState(1);
  const PER_PAGE = 50;

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/audit.php`, { credentials: "include" });
      const data = await res.json();
      setLogs(data.logs || []);
      setIds(data.ids   || []);
    } catch(e) { console.error("Audit fetch:", e); }
    setLoading(false);
  };

  useEffect(() => { loadLogs(); }, []);

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${ids.length} audit log entries? This cannot be undone.`)) return;
    setClearing(true);
    try {
      await apiCall(`${API_BASE}/audit.php`, {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
    } catch(e) { console.error(e); }
    setClearing(false);
    loadLogs();
  };

  const ACTION_LABELS = {
    LOGIN:           { label:"Login",            icon:"🔐", color:"#059669" },
    LOGOUT:          { label:"Logout",           icon:"🚪", color:"#6d28d9" },
    PATIENT_CREATED: { label:"Patient Added",    icon:"👤", color:"#7c3aed" },
    PATIENT_EDITED:  { label:"Patient Edited",   icon:"✏️", color:"#0891b2" },
    PATIENT_DELETED: { label:"Patient Deleted",  icon:"🗑️", color:"#e84c3d" },
    RECORD_UPLOADED: { label:"Record Uploaded",  icon:"📎", color:"#0891b2" },
    RECORD_DELETED:  { label:"Record Deleted",   icon:"🗑️", color:"#e84c3d" },
    VISIT_LOGGED:    { label:"Visit Logged",     icon:"📝", color:"#d97706" },
  };

  const filtered = filter === "ALL" ? logs : logs.filter(l => l.action === filter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [filter]);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div>
      <TopBar title="Audit Log" sub={`Every action — ${logs.length} entries`} actions={
        ids.length > 0 && (user?.role === "owner" || user?.role === "mis") && (
          <button onClick={clearAll} disabled={clearing} style={{padding:"7px 14px",borderRadius:8,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            {clearing ? "Clearing…" : `🗑 Clear All (${ids.length})`}
          </button>
        )
      }/>
      <div className="main-pad" style={{padding:"22px 26px"}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
          <FBtn active={filter==="ALL"} onClick={()=>setFilter("ALL")}>All</FBtn>
          {Object.entries(ACTION_LABELS).map(([k,v])=>(
            <FBtn key={k} active={filter===k} onClick={()=>setFilter(k)}>{v.icon} {v.label}</FBtn>
          ))}
        </div>
        <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,overflow:"hidden",boxShadow:"var(--cardshadow)"}}>
          <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr 1fr 1.5fr 1.5fr",padding:"10px 18px",background:"var(--accentbg)",fontSize:9,fontWeight:700,color:"var(--muted)",letterSpacing:1.2,textTransform:"uppercase",borderBottom:"1px solid var(--border)"}}>
            <div>Time</div><div>User</div><div>Clinic</div><div>Action</div><div>Detail</div>
          </div>
          {loading && <div style={{padding:20,textAlign:"center",color:"var(--muted)",fontSize:12}}>Loading…</div>}
          {!loading && filtered.length===0 && <Empty msg="No entries yet"/>}
          {paginated.map((l,i) => {
            const al = ACTION_LABELS[l.action] || { label:l.action, icon:"•", color:"var(--muted)" };
            const detail = l.detail?.patientName?`Patient: ${l.detail.patientName}`:l.detail?.fileName?`File: ${l.detail.fileName}`:l.detail?.fields?`Fields: ${l.detail.fields?.join(", ")}`:"—";
            return (
              <div key={i} className="row-hover" style={{display:"grid",gridTemplateColumns:"1.2fr 1fr 1fr 1.5fr 1.5fr",padding:"10px 18px",borderTop:"1px solid var(--border)",fontSize:11,alignItems:"center"}}>
                <div style={{color:"var(--muted)",fontSize:10}}>{new Date(l.ts).toLocaleDateString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                <div style={{fontWeight:600,color:"var(--text1)"}}>{l.username}</div>
                <div><span style={{background:"#7c3aed18",color:"#7c3aed",padding:"2px 7px",borderRadius:5,fontSize:9,fontWeight:600}}>{l.clinicLabel?.split("·")[0]?.trim()||"MIS"}</span></div>
                <div><span style={{background:al.color+"18",color:al.color,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:600}}>{al.icon} {al.label}</span></div>
                <div style={{color:"var(--muted)",fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{detail}</div>
              </div>
            );
          })}
          {totalPages>1&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"14px 18px",borderTop:"1px solid var(--border)"}}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{padding:"5px 11px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:page===1?"var(--muted2)":"var(--muted)",fontSize:10,cursor:page===1?"default":"pointer",fontWeight:600,opacity:page===1?0.5:1}}>← Prev</button>
              <span style={{fontSize:11,color:"var(--muted)"}}>Page {page} of {totalPages} · {filtered.length} total entries</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{padding:"5px 11px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:page===totalPages?"var(--muted2)":"var(--muted)",fontSize:10,cursor:page===totalPages?"default":"pointer",fontWeight:600,opacity:page===totalPages?0.5:1}}>Next →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// CLINIC DASHBOARD
// ── Overdue appointment pop-up (shared by Clinic + MIS) ───
function OverduePopup({ appts, onDismiss }) {
  if (!appts || appts.length === 0) return null;
  return (
    <div style={{position:'fixed',inset:0,background:'#0007',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
      onClick={e=>{if(e.target===e.currentTarget)onDismiss();}}>
      <div className="fadeUp" style={{background:'var(--card)',border:'2px solid #ef4444',borderRadius:18,padding:'28px 28px 22px',maxWidth:440,width:'100%',boxShadow:'0 20px 60px #0004',maxHeight:'80vh',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18}}>
          <div style={{fontSize:32}}>⏰</div>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:'#dc2626'}}>Overdue Appointments</div>
            <div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>
              {appts.length} appointment{appts.length>1?'s':''} still pending — over 1 hour past scheduled time
            </div>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:8,marginBottom:18}}>
          {appts.map(a=>(
            <div key={a.id} style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'10px 14px',display:'flex',alignItems:'center',gap:12}}>
              <div style={{background:'#ef4444',color:'#fff',borderRadius:7,padding:'4px 8px',fontSize:11,fontWeight:700,flexShrink:0}}>
                {fmtApptTime(a.appointment_time)}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:'var(--text1)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.patient_name}</div>
                <div style={{fontSize:11,color:'var(--muted)',marginTop:1}}>
                  📞 {a.phone}{a.purpose?` · ${a.purpose}`:''}{a.visit_type==='home'?' · 🏠 Home':''}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button className="btn-p" onClick={onDismiss}
          style={{...S.btn,width:'100%',justifyContent:'center',background:'#ef4444',borderColor:'#ef4444'}}>
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ── Hook: poll for overdue appointments every 5 min ────────
function useOverdueCheck(enabled) {
  const [overduePopup, setOverduePopup] = useState([]);
  const notifiedIds = useRef(new Set());

  const check = useCallback(async () => {
    if (!enabled) return;
    const today = fmtDateKey(new Date());
    try {
      const res = await apiCall(`${API_BASE}/appointments.php?start=${today}&end=${today}`);
      const data = await res.json();
      if (!Array.isArray(data)) return;
      const now = new Date();
      const nowMins = now.getHours() * 60 + now.getMinutes();
      const overdue = data.filter(a => {
        if (a.status !== 'pending' || a.appointment_date !== today) return false;
        const parts = (a.appointment_time || '').split(':');
        const apptMins = parseInt(parts[0] || 0) * 60 + parseInt(parts[1] || 0);
        return nowMins - apptMins >= 60 && !notifiedIds.current.has(a.id);
      });
      if (overdue.length > 0) {
        overdue.forEach(a => notifiedIds.current.add(a.id));
        setOverduePopup(overdue);
      }
    } catch {}
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    check();
    const t = setInterval(check, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(t);
  }, [enabled, check]);

  return [overduePopup, () => setOverduePopup([])];
}

// ══════════════════════════════════════════════════════════
// THIS MONTH VIEW
// ══════════════════════════════════════════════════════════
function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const esc = v => '"' + String(v??'').replace(/"/g,'""') + '"';
  const csv = [keys.join(','), ...rows.map(r=>keys.map(k=>esc(r[k])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csv);
  a.download = filename;
  a.click();
}
function ThisMonthView({ user, isMIS }) {
  const now = new Date();
  const todayYM = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  // mis + owner see any month; clinic, office locked to current month
  const canSeeAnyMonth = user.role === 'mis' || user.role === 'owner';

  // For MIS, default to first allowed clinic; for clinic user, use their own
  const defaultClinic = isMIS ? THIS_MONTH_CLINICS[0] : user.clinic;

  const [month, setMonth] = useState(todayYM);
  const [clinicId, setClinicId] = useState(defaultClinic);
  const [subtab, setSubtab] = useState('appt');
  const [dsrData, setDsrData] = useState([]);
  const [pettyCashData, setPettyCashData] = useState([]);
  const [cashDeposits, setCashDeposits] = useState([]);
  const [apptData, setApptData] = useState([]);
  const [loading, setLoading] = useState(false);

  // DSR modal
  const [dsrModal, setDsrModal] = useState(false);
  // Petty cash modal
  const [pettyModal, setPettyModal] = useState(false);
  // Cash deposit modal
  const [cashModal, setCashModal] = useState(false);
  // ADR inline edit
  const [adrEditId, setAdrEditId] = useState(null);
  const [adrOutcome, setAdrOutcome] = useState('');
  const [adrFuDate, setAdrFuDate] = useState('');
  const [adrSaving, setAdrSaving] = useState(false);

  // Edit states for each type
  const [editDsrItem,  setEditDsrItem]  = useState(null);
  const [editPettyItem,setEditPettyItem]= useState(null);
  const [editCashItem, setEditCashItem] = useState(null);
  // Visit note from DSR
  const [visitNoteItem,setVisitNoteItem]= useState(null);

  // Filters — arrays for multi-select
  const [dsrFilter, setDsrFilter] = useState({ sources:[], purposes:[], visitTypes:[], paymentModes:[], audiologists:[] });
  const [adrFilter, setAdrFilter] = useState({ purposes:[], audiologists:[], outcomes:[] });
  const [apptFilter, setApptFilter] = useState({ source:'', status:'', purpose:'', visitType:'', clinic:'' });
  const toggleArr = (arr, val) => arr.includes(val) ? arr.filter(x=>x!==val) : [...arr, val];

  // ── Month navigation ────────────────────────────────────
  const prevMonth = () => {
    const [y,m] = month.split('-').map(Number);
    const d = new Date(y, m-2, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const nextMonth = () => {
    const [y,m] = month.split('-').map(Number);
    const d = new Date(y, m, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };
  const isCurrentMonth = month === todayYM;
  const monthLabel = (() => {
    const [y,m] = month.split('-').map(Number);
    return new Date(y,m-1,1).toLocaleDateString('en-IN',{month:'long',year:'numeric'});
  })();

  // ── Fetch data when clinic or month changes ─────────────
  useEffect(() => {
    if (!clinicId) return;
    setLoading(true);

    const p = `clinic_id=${encodeURIComponent(clinicId)}&month=${encodeURIComponent(month)}`;
    const [y,mo] = month.split('-').map(Number);
    const lastDay = new Date(y,mo,0).getDate();
    const start = `${month}-01`, end = `${month}-${String(lastDay).padStart(2,'0')}`;

    Promise.all([
      apiCall(`${API_BASE}/dsr.php?${p}`).then(r=>r.json()).catch(()=>[]),
      apiCall(`${API_BASE}/petty_cash.php?${p}`).then(r=>r.json()).catch(()=>[]),
      apiCall(`${API_BASE}/cash_deposits.php?${p}`).then(r=>r.json()).catch(()=>[]),
      apiCall(`${API_BASE}/appointments.php?start=${start}&end=${end}`).then(r=>r.json()).catch(()=>[]),
    ]).then(([dsr,petty,cash,appt]) => {
      const byDate = (a,b)=>(b.entry_date||b.deposit_date||'').localeCompare(a.entry_date||a.deposit_date||'');
      const byAppt = (a,b)=>(b.appointment_date||'').localeCompare(a.appointment_date||'');
      setDsrData(Array.isArray(dsr)?[...dsr].sort(byDate):[]);
      setPettyCashData(Array.isArray(petty)?[...petty].sort(byDate):[]);
      setCashDeposits(Array.isArray(cash)?[...cash].sort(byDate):[]);
      setApptData(Array.isArray(appt)?[...appt].sort(byAppt):[]);
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, month]);

  // ── Save ADR outcome ────────────────────────────────────
  const saveAdrOutcome = async (id) => {
    setAdrSaving(true);
    try {
      await apiCall(`${API_BASE}/dsr.php?id=${id}`, {
        method: 'PUT',
        body: JSON.stringify({ adr_outcome: adrOutcome, adr_fu_date: adrFuDate||null }),
      });
      setDsrData(prev => prev.map(e => e.id===id ? {...e, adr_outcome:adrOutcome, adr_fu_date:adrFuDate||null} : e));
      setAdrEditId(null);
    } catch(e) { alert('Save failed'); }
    setAdrSaving(false);
  };

  // ── Delete helpers ──────────────────────────────────────
  const deleteDsr = async (id) => {
    if (!window.confirm('Delete this DSR entry?')) return;
    await apiCall(`${API_BASE}/dsr.php?id=${id}`, { method:'DELETE', body:'{}' });
    setDsrData(prev => prev.filter(e=>e.id!==id));
  };
  const deletePetty = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await apiCall(`${API_BASE}/petty_cash.php?id=${id}`, { method:'DELETE', body:'{}' });
    setPettyCashData(prev => prev.filter(e=>e.id!==id));
  };
  const deleteCash = async (id) => {
    if (!window.confirm('Delete this deposit?')) return;
    await apiCall(`${API_BASE}/cash_deposits.php?id=${id}`, { method:'DELETE', body:'{}' });
    setCashDeposits(prev => prev.filter(e=>e.id!==id));
  };

  // ── Filtered data ───────────────────────────────────────
  const filteredDsr = dsrData.filter(e => {
    if (dsrFilter.sources.length     && !dsrFilter.sources.includes(e.source||''))       return false;
    if (dsrFilter.purposes.length    && !dsrFilter.purposes.includes(e.purpose||''))     return false;
    if (dsrFilter.visitTypes.length  && !dsrFilter.visitTypes.includes(e.visit_type||''))return false;
    if (dsrFilter.paymentModes.length&& !dsrFilter.paymentModes.includes(e.payment_mode||'')) return false;
    if (dsrFilter.audiologists.length&& !dsrFilter.audiologists.includes(e.audiologist||'')) return false;
    return true;
  });
  const adrData = dsrData.filter(e =>
    ['PTA & HAT','HAT Only','HA Inquiry'].includes(e.purpose)
  );
  const filteredAdr = adrData.filter(e => {
    if (adrFilter.purposes.length    && !adrFilter.purposes.includes(e.purpose||''))      return false;
    if (adrFilter.audiologists.length&& !adrFilter.audiologists.includes(e.audiologist||'')) return false;
    if (adrFilter.outcomes.length    && !adrFilter.outcomes.includes(e.adr_outcome||''))  return false;
    return true;
  });
  const filteredAppt = apptData.filter(e => {
    if (isMIS && apptFilter.clinic && e.clinic_id !== apptFilter.clinic) return false;
    if (apptFilter.source && e.source !== apptFilter.source) return false;
    if (apptFilter.status && e.status !== apptFilter.status) return false;
    if (apptFilter.purpose && e.purpose !== apptFilter.purpose) return false;
    if (apptFilter.visitType && e.visit_type !== apptFilter.visitType) return false;
    return true;
  });

  // ── Stats ───────────────────────────────────────────────
  // Full-dataset stats (for cash report — needs all cash regardless of DSR filter)
  const dsrCashCollected = dsrData.filter(e=>e.payment_mode==='Cash'&&!parseInt(e.nr)).reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const totalDeposited = cashDeposits.reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const cashBalance = dsrCashCollected - totalDeposited;
  // Filtered stats — respect current DSR filters
  const filteredRevenue  = filteredDsr.filter(e=>!parseInt(e.nr)).reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const filteredCash     = filteredDsr.filter(e=>e.payment_mode==='Cash'&&!parseInt(e.nr)).reduce((s,e)=>s+parseFloat(e.amount||0),0);
  const filteredNR       = filteredDsr.filter(e=>parseInt(e.nr)).length;
  const totalDsrRevenue  = dsrData.filter(e=>!parseInt(e.nr)).reduce((s,e)=>s+parseFloat(e.amount||0),0);

  // ── Style shorthands ────────────────────────────────────
  const inp = {width:'100%',padding:'9px 12px',background:'var(--inputbg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text1)',fontSize:12};
  const lbl = {fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,display:'block',marginBottom:5,textTransform:'uppercase'};
  const thStyle = {padding:'9px 12px',fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,borderBottom:'2px solid var(--border)',background:'var(--card)',textAlign:'left',whiteSpace:'nowrap'};
  const tdStyle = {padding:'9px 12px',fontSize:12,color:'var(--text1)',borderBottom:'1px solid var(--border)',verticalAlign:'middle'};

  const formatINR = (v) => '₹' + Number(v||0).toLocaleString('en-IN',{minimumFractionDigits:0,maximumFractionDigits:0});
  const fmtDate = (d) => { if(!d) return '—'; const dt=new Date(d+'T00:00:00'); return dt.toLocaleDateString('en-IN',{day:'numeric',month:'short'}); };

  // Badge helpers
  const sourceBadge = (s) => {
    const map = {'Walk-in':['#fce7f3','#be185d'],'Google':['#d1fae5','#065f46'],'Doctor Referral':['#dbeafe','#1e40af'],'Facebook / Instagram':['#f3e8ff','#6b21a8'],'Hear.com':['#d1fae5','#065f46']};
    const [bg,c] = map[s]||['#f3f4f6','#6b7280'];
    return <span style={{background:bg,color:c,borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>{s||'—'}</span>;
  };
  const purposeBadge = (p) => {
    const map={'PTA & HAT':['#fef3c7','#92400e'],'HAT Only':['#ede9fe','#5b21b6'],'HA Inquiry':['#e0f2fe','#0369a1'],'Follow Up':['#fff7ed','#9a3412'],'Counselling':['#fdf4ff','#6b21a8'],'Repair':['#fef9c3','#713f12']};
    const [bg,c] = map[p]||['#f3f4f6','#6b7280'];
    return <span style={{background:bg,color:c,borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>{p||'—'}</span>;
  };
  const payBadge = (m) => {
    const map={'Cash':['#d1fae5','#065f46'],'Card':['#dbeafe','#1e40af'],'UPI':['#ede9fe','#5b21b6'],'NEFT/RTGS':['#e0f2fe','#0369a1']};
    const [bg,c] = map[m]||['#f3f4f6','#6b7280'];
    return <span style={{background:bg,color:c,borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>{m||'—'}</span>;
  };
  const statusBadge = (s) => {
    const map={'pending':['#fef3c7','#92400e','⏳'],'done':['#d1fae5','#065f46','✅'],'missed':['#fee2e2','#dc2626','❌']};
    const [bg,c,ic] = map[s]||['#f3f4f6','#6b7280',''];
    return <span style={{background:bg,color:c,borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>{ic} {s}</span>;
  };
  const outcomeBadge = (o) => {
    const map={'Follow Up':['#fff7ed','#9a3412'],'Booked':['#d1fae5','#065f46'],'Fitting Done':['#dcfce7','#14532d'],'Not Interested':['#fee2e2','#991b1b']};
    const [bg,c] = map[o]||['#f3f4f6','#6b7280'];
    return o ? <span style={{background:bg,color:c,borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>{o}</span> : <span style={{color:'var(--muted)',fontSize:11}}>—</span>;
  };

  // ── Unique filter option builders ───────────────────────
  const uniq = (arr,key) => [...new Set(arr.map(e=>e[key]).filter(Boolean))].sort();

  // ── Stat card ───────────────────────────────────────────
  const StatCard = ({label,value,color,icon,delay=0}) => (
    <div className="card-hover fadeUp" style={{animationDelay:`${delay}s`,background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,padding:'16px 18px',boxShadow:'var(--cardshadow)',borderTop:`3px solid ${color}`}}>
      <div style={{fontSize:10,color:'var(--muted)',marginBottom:6}}>{icon} {label}</div>
      <div style={{fontSize:26,fontWeight:800,color}}>{value}</div>
    </div>
  );

  // ── Sub-tab pill ────────────────────────────────────────
  const TabPill = ({id,label}) => (
    <button onClick={()=>setSubtab(id)} style={{padding:'7px 16px',borderRadius:99,border:`1px solid ${subtab===id?'var(--accent)':'var(--border)'}`,background:subtab===id?'var(--accent)':'transparent',color:subtab===id?'#fff':'var(--muted)',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all 0.15s',whiteSpace:'nowrap'}}>
      {label}
    </button>
  );

  // ── Spinner ─────────────────────────────────────────────
  const Spinner = () => (
    <div style={{textAlign:'center',padding:40,color:'var(--muted)'}}>
      <div style={{width:24,height:24,border:'2px solid #c4b5fd',borderTopColor:'#7c3aed',borderRadius:'50%',animation:'spin 0.7s linear infinite',margin:'0 auto 10px'}}/>
      Loading…
    </div>
  );

  // ════════════════════════════════════════════════════════
  return (
    <div>
      {/* ── Top bar ── */}
      <div style={{background:'var(--topbar)',borderBottom:'1px solid var(--border)',padding:'14px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:18,fontWeight:800,color:'var(--text1)'}}>📊 This Month</div>
          <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{isMIS && subtab==='appt' ? 'All Clinics' : (CLINICS[clinicId]?.label||clinicId)} · {monthLabel}</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          {/* Clinic picker — MIS only, hidden on Appointments tab (has its own clinic filter) */}
          {isMIS && subtab !== 'appt' && (
            <select value={clinicId} onChange={e=>setClinicId(e.target.value)}
              style={{...inp,width:'auto',fontSize:12,padding:'7px 10px'}}>
              {THIS_MONTH_CLINICS.map(c=>(
                <option key={c} value={c}>{CLINICS[c]?.label||c}</option>
              ))}
            </select>
          )}
          {/* Month navigation — mis/owner see any month; others locked to current */}
          <div style={{display:'flex',alignItems:'center',gap:6,background:'var(--card)',border:'1px solid var(--border)',borderRadius:10,padding:'5px 10px'}}>
            {canSeeAnyMonth && <button onClick={prevMonth} style={{background:'transparent',border:'none',cursor:'pointer',color:'var(--muted)',fontSize:15,padding:'0 4px'}}>‹</button>}
            <span style={{fontSize:12,fontWeight:700,color:'var(--text1)',minWidth:110,textAlign:'center'}}>{monthLabel}</span>
            {canSeeAnyMonth && <button onClick={nextMonth} disabled={isCurrentMonth} style={{background:'transparent',border:'none',cursor:isCurrentMonth?'default':'pointer',color:isCurrentMonth?'var(--border)':'var(--muted)',fontSize:15,padding:'0 4px'}}>›</button>}
          </div>
        </div>
      </div>

      <div className="main-pad" style={{padding:'22px 26px'}}>
        {/* ── Sub-tabs ── */}
        <div style={{display:'flex',gap:8,marginBottom:22,flexWrap:'wrap'}}>
          <TabPill id="appt"  label="Appointments"/>
          <TabPill id="dsr"   label="DSR"/>
          <TabPill id="cash"  label="Cash Report"/>
          <TabPill id="petty" label="Petty Cash"/>
          <TabPill id="adr"   label="ADR"/>
        </div>

        {loading && <Spinner/>}

        {/* ═══════════════════════════════════════════════
            DSR TAB
        ═══════════════════════════════════════════════ */}
        {!loading && subtab==='dsr' && (
          <div className="fadeUp">
            {/* Stats */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:22}}>
              <StatCard label="Total Footfall"    value={filteredDsr.length}           color="#7c3aed" icon="👥" delay={0}/>
              <StatCard label="Revenue"           value={formatINR(filteredRevenue)}   color="#059669" icon="💰" delay={0.06}/>
              <StatCard label="Cash Collected"    value={formatINR(filteredCash)}      color="#0ea5e9" icon="💵" delay={0.12}/>
              <StatCard label="NR / Repeat"       value={filteredNR}                   color="#f59e0b" icon="↩" delay={0.18}/>
            </div>

            {/* Filter bar — multi-select pills */}
            {(() => {
              const pill = (label, active, onClick) => (
                <button key={label} onClick={onClick} style={{padding:'4px 10px',borderRadius:99,border:`1px solid ${active?'var(--accent)':'var(--border)'}`,background:active?'var(--accent)':'transparent',color:active?'#fff':'var(--muted)',fontSize:11,fontWeight:active?700:400,cursor:'pointer',whiteSpace:'nowrap',transition:'all 0.12s'}}>
                  {label}
                </button>
              );
              const grp = (label) => <span style={{fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.6,whiteSpace:'nowrap',alignSelf:'center'}}>{label}</span>;
              const hasFilter = dsrFilter.sources.length||dsrFilter.purposes.length||dsrFilter.visitTypes.length||dsrFilter.paymentModes.length||dsrFilter.audiologists.length;
              return (
                <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:12,padding:'12px 16px',marginBottom:16}}>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:uniq(dsrData,'audiologist').length?8:0}}>
                    {grp('Purpose')}
                    {uniq(dsrData,'purpose').map(v=>pill(v, dsrFilter.purposes.includes(v), ()=>setDsrFilter(f=>({...f,purposes:toggleArr(f.purposes,v)}))))}
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:8}}>
                    {grp('Audiologist')}
                    {uniq(dsrData,'audiologist').map(v=>pill(v, dsrFilter.audiologists.includes(v), ()=>setDsrFilter(f=>({...f,audiologists:toggleArr(f.audiologists,v)}))))}
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    {grp('Source')}
                    {uniq(dsrData,'source').map(v=>pill(v, dsrFilter.sources.includes(v), ()=>setDsrFilter(f=>({...f,sources:toggleArr(f.sources,v)}))))}
                    <span style={{margin:'0 4px',color:'var(--border)'}}>|</span>
                    {grp('Visit')}
                    {uniq(dsrData,'visit_type').map(v=>pill(v, dsrFilter.visitTypes.includes(v), ()=>setDsrFilter(f=>({...f,visitTypes:toggleArr(f.visitTypes,v)}))))}
                    <span style={{margin:'0 4px',color:'var(--border)'}}>|</span>
                    {grp('Payment')}
                    {uniq(dsrData,'payment_mode').map(v=>pill(v, dsrFilter.paymentModes.includes(v), ()=>setDsrFilter(f=>({...f,paymentModes:toggleArr(f.paymentModes,v)}))))}
                    {hasFilter ? <button onClick={()=>setDsrFilter({sources:[],purposes:[],visitTypes:[],paymentModes:[],audiologists:[]})} style={{marginLeft:6,padding:'4px 10px',borderRadius:99,border:'1px solid var(--border)',background:'transparent',color:'var(--muted)',fontSize:11,cursor:'pointer'}}>✕ Clear</button> : null}
                    <button className="btn-p" onClick={()=>setDsrModal(true)} style={{marginLeft:'auto',...S.btn,fontSize:12,padding:'7px 14px'}}>+ Add Entry</button>
                  </div>
                </div>
              );
            })()}

            {/* Table */}
            <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,overflow:'auto',boxShadow:'var(--cardshadow)'}}>
              {filteredDsr.length===0
                ? <div style={{textAlign:'center',padding:'50px 20px',color:'var(--muted)'}}>No DSR entries for this month.</div>
                : (
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
                  <thead>
                    <tr>
                      {['Date','Patient','Phone','Source','Purpose','Visit','Audiologist','Inv #','Amount','Payment','Remarks',''].map(h=>(
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDsr.map(e=>(
                      <tr key={e.id} className="row-hover">
                        <td style={tdStyle}>{fmtDate(e.entry_date)}<div style={{fontSize:10,color:'var(--muted)'}}>{e.entry_time||''}</div></td>
                        <td style={{...tdStyle,fontWeight:600}}>{e.patient_name}{parseInt(e.nr)?<span style={{marginLeft:5,fontSize:9,background:'#fef3c7',color:'#92400e',borderRadius:4,padding:'1px 5px'}}>NR</span>:null}</td>
                        <td style={{...tdStyle,color:'var(--muted)'}}>{e.phone||'—'}</td>
                        <td style={tdStyle}>{sourceBadge(e.source)}</td>
                        <td style={tdStyle}>{purposeBadge(e.purpose)}</td>
                        <td style={{...tdStyle,color:'var(--muted)',fontSize:11}}>{e.visit_type||'—'}</td>
                        <td style={{...tdStyle,color:'var(--muted)',fontSize:11}}>{e.audiologist||'—'}</td>
                        <td style={{...tdStyle,fontSize:11}}>{e.invoice_no||'—'}</td>
                        <td style={{...tdStyle,fontWeight:700,color:'#059669'}}>{e.amount>0?formatINR(e.amount):'—'}</td>
                        <td style={tdStyle}>{payBadge(e.payment_mode)}</td>
                        <td style={{...tdStyle,fontSize:11,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.remarks||'—'}</td>
                        <td style={{...tdStyle,whiteSpace:'nowrap',textAlign:'right'}}>
                          <button onClick={()=>setVisitNoteItem(e)} title="Add visit note to patient" style={{background:'transparent',border:'none',color:'#7c3aed',cursor:'pointer',fontSize:13,marginRight:4}}>📝</button>
                          <button onClick={()=>setEditDsrItem(e)} title="Edit" style={{background:'transparent',border:'none',color:'#0891b2',cursor:'pointer',fontSize:13,marginRight:4}}>✏️</button>
                          <button onClick={()=>deleteDsr(e.id)} title="Delete" style={{background:'transparent',border:'none',color:'#dc2626',cursor:'pointer',fontSize:13,opacity:0.7}}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            ADR TAB
        ═══════════════════════════════════════════════ */}
        {!loading && subtab==='adr' && (
          <div className="fadeUp">
            {/* Stats — based on filteredAdr */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:22}}>
              <StatCard label="Total HA Enquiries" value={filteredAdr.length}                                              color="#7c3aed" icon="🦻" delay={0}/>
              <StatCard label="Booked"              value={filteredAdr.filter(e=>e.adr_outcome==='Booked').length}         color="#059669" icon="✅" delay={0.06}/>
              <StatCard label="Follow Up"           value={filteredAdr.filter(e=>e.adr_outcome==='Follow Up').length}      color="#f59e0b" icon="📞" delay={0.12}/>
              <StatCard label="Not Interested"      value={filteredAdr.filter(e=>e.adr_outcome==='Not Interested').length} color="#ef4444" icon="❌" delay={0.18}/>
            </div>

            {/* ADR multi-select pill filters */}
            {(() => {
              const pill = (label, active, onClick) => (
                <button key={label} onClick={onClick} style={{padding:'4px 10px',borderRadius:99,border:`1px solid ${active?'var(--accent)':'var(--border)'}`,background:active?'var(--accent)':'transparent',color:active?'#fff':'var(--muted)',fontSize:11,fontWeight:active?700:400,cursor:'pointer',whiteSpace:'nowrap',transition:'all 0.12s'}}>
                  {label}
                </button>
              );
              const grp = (label) => <span style={{fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.6,whiteSpace:'nowrap',alignSelf:'center'}}>{label}</span>;
              const hasFilter = adrFilter.purposes.length||adrFilter.audiologists.length||adrFilter.outcomes.length;
              return (
                <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:12,padding:'12px 16px',marginBottom:16}}>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:8}}>
                    {grp('Purpose')}
                    {uniq(adrData,'purpose').map(v=>pill(v, adrFilter.purposes.includes(v), ()=>setAdrFilter(f=>({...f,purposes:toggleArr(f.purposes,v)}))))}
                    <span style={{margin:'0 4px',color:'var(--border)'}}>|</span>
                    {grp('Outcome')}
                    {['Follow Up','Booked','Fitting Done','Not Interested'].map(v=>pill(v, adrFilter.outcomes.includes(v), ()=>setAdrFilter(f=>({...f,outcomes:toggleArr(f.outcomes,v)}))))}
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    {grp('Audiologist')}
                    {uniq(adrData,'audiologist').map(v=>pill(v, adrFilter.audiologists.includes(v), ()=>setAdrFilter(f=>({...f,audiologists:toggleArr(f.audiologists,v)}))))}
                    {hasFilter ? <button onClick={()=>setAdrFilter({purposes:[],audiologists:[],outcomes:[]})} style={{marginLeft:6,padding:'4px 10px',borderRadius:99,border:'1px solid var(--border)',background:'transparent',color:'var(--muted)',fontSize:11,cursor:'pointer'}}>✕ Clear</button> : null}
                  </div>
                </div>
              );
            })()}

            <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,overflow:'auto',boxShadow:'var(--cardshadow)'}}>
              {filteredAdr.length===0
                ? <div style={{textAlign:'center',padding:'50px 20px',color:'var(--muted)'}}>No matching ADR entries.</div>
                : (
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:820}}>
                  <thead>
                    <tr>
                      {['Date','Patient','Phone','Purpose','Audiologist','Outcome','FU Date',''].map(h=>(
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdr.map(e=>(
                      <tr key={e.id} className="row-hover">
                        <td style={tdStyle}>{fmtDate(e.entry_date)}</td>
                        <td style={{...tdStyle,fontWeight:600}}>{e.patient_name}</td>
                        <td style={{...tdStyle,color:'var(--muted)'}}>{e.phone||'—'}</td>
                        <td style={tdStyle}>{purposeBadge(e.purpose)}</td>
                        <td style={{...tdStyle,color:'var(--muted)',fontSize:11}}>{e.audiologist||'—'}</td>
                        <td style={tdStyle}>
                          {adrEditId===e.id ? (
                            <select style={{...inp,width:140,fontSize:11}} value={adrOutcome} onChange={ev=>setAdrOutcome(ev.target.value)}>
                              <option value="">Select…</option>
                              {['Follow Up','Booked','Fitting Done','Not Interested'].map(o=>(
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          ) : (
                            <button onClick={()=>{setAdrEditId(e.id);setAdrOutcome(e.adr_outcome||'');setAdrFuDate(e.adr_fu_date||'');}}
                              style={{background:'transparent',border:'1px dashed var(--border)',borderRadius:6,padding:'3px 8px',cursor:'pointer',color:'var(--muted)',fontSize:11}}>
                              {outcomeBadge(e.adr_outcome)}
                            </button>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {adrEditId===e.id && adrOutcome==='Follow Up' ? (
                            <input type="date" style={{...inp,width:130,fontSize:11}} value={adrFuDate} onChange={ev=>setAdrFuDate(ev.target.value)}/>
                          ) : (
                            e.adr_fu_date ? <span style={{fontSize:11,color:'var(--muted)'}}>{fmtDate(e.adr_fu_date)}</span> : <span style={{color:'var(--muted)',fontSize:11}}>—</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {adrEditId===e.id ? (
                            <div style={{display:'flex',gap:5}}>
                              <button onClick={()=>saveAdrOutcome(e.id)} disabled={adrSaving||!adrOutcome}
                                style={{padding:'4px 10px',borderRadius:6,border:'none',background:'#7c3aed',color:'#fff',fontSize:11,cursor:adrSaving?'default':'pointer',fontWeight:700}}>
                                {adrSaving?'…':'Save'}
                              </button>
                              <button onClick={()=>setAdrEditId(null)} style={{padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)',background:'transparent',color:'var(--muted)',fontSize:11,cursor:'pointer'}}>
                                ✕
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            APPOINTMENTS TAB
        ═══════════════════════════════════════════════ */}
        {!loading && subtab==='appt' && (
          <div className="fadeUp">
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:22}}>
              <StatCard label="Total"      value={filteredAppt.length}                                color="#7c3aed" icon="📅" delay={0}/>
              <StatCard label="Show"       value={filteredAppt.filter(a=>a.status==='done').length}   color="#059669" icon="✅" delay={0.06}/>
              <StatCard label="No Show"    value={filteredAppt.filter(a=>a.status==='missed').length} color="#ef4444" icon="❌" delay={0.12}/>
              <StatCard label="Pending"    value={filteredAppt.filter(a=>a.status==='pending').length} color="#f59e0b" icon="⏳" delay={0.18}/>
              <StatCard label="Home Visit" value={filteredAppt.filter(a=>a.visit_type==='home').length} color="#0ea5e9" icon="🏠" delay={0.24}/>
            </div>

            {/* Filter bar */}
            <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
              {isMIS && (
                <select style={{...inp,width:'auto',fontSize:11}} value={apptFilter.clinic} onChange={e=>setApptFilter(f=>({...f,clinic:e.target.value}))}>
                  <option value="">All Clinics</option>
                  {[...new Set(apptData.map(a=>a.clinic_id).filter(Boolean))].sort().map(c=>(
                    <option key={c} value={c}>{CLINICS[c]?.label||c}</option>
                  ))}
                </select>
              )}
              <select style={{...inp,width:'auto',fontSize:11}} value={apptFilter.source} onChange={e=>setApptFilter(f=>({...f,source:e.target.value}))}>
                <option value="">All Sources</option>
                {uniq(filteredAppt,'source').map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <select style={{...inp,width:'auto',fontSize:11}} value={apptFilter.status} onChange={e=>setApptFilter(f=>({...f,status:e.target.value}))}>
                <option value="">All Statuses</option>
                {['pending','done','missed'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <select style={{...inp,width:'auto',fontSize:11}} value={apptFilter.purpose} onChange={e=>setApptFilter(f=>({...f,purpose:e.target.value}))}>
                <option value="">All Purposes</option>
                {uniq(filteredAppt,'purpose').map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <select style={{...inp,width:'auto',fontSize:11}} value={apptFilter.visitType} onChange={e=>setApptFilter(f=>({...f,visitType:e.target.value}))}>
                <option value="">All Visit Types</option>
                {['clinic','home'].map(s=><option key={s} value={s} style={{textTransform:'capitalize'}}>{s}</option>)}
              </select>
              {(apptFilter.source||apptFilter.status||apptFilter.purpose||apptFilter.visitType||apptFilter.clinic)&&(
                <button onClick={()=>setApptFilter({source:'',status:'',purpose:'',visitType:'',clinic:''})}
                  style={{padding:'6px 12px',borderRadius:7,border:'1px solid var(--border)',background:'transparent',color:'var(--muted)',fontSize:11,cursor:'pointer'}}>
                  Clear
                </button>
              )}
            </div>

            <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,overflow:'auto',boxShadow:'var(--cardshadow)'}}>
              {filteredAppt.length===0
                ? <div style={{textAlign:'center',padding:'50px 20px',color:'var(--muted)'}}>No appointments for this month.</div>
                : (
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:isMIS?900:800}}>
                  <thead>
                    <tr>
                      {[...(isMIS?['Clinic']:[]),'Date','Time','Patient','Phone','Source','Purpose','Visit','Status'].map(h=>(
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppt.map(a=>(
                      <tr key={a.id} className="row-hover">
                        {isMIS && <td style={tdStyle}><span style={{background:'var(--accentbg)',color:'var(--accent)',borderRadius:5,padding:'2px 8px',fontSize:10,fontWeight:700,whiteSpace:'nowrap'}}>{CLINICS[a.clinic_id]?.label||a.clinic_id}</span></td>}
                        <td style={tdStyle}>{fmtDate(a.appointment_date)}</td>
                        <td style={{...tdStyle,color:'var(--muted)',fontSize:11}}>{a.appointment_time?.slice(0,5)||'—'}</td>
                        <td style={{...tdStyle,fontWeight:600}}>{a.patient_name}</td>
                        <td style={{...tdStyle,color:'var(--muted)'}}>{a.phone||'—'}</td>
                        <td style={tdStyle}>{sourceBadge(a.source)}</td>
                        <td style={tdStyle}>{purposeBadge(a.purpose)}</td>
                        <td style={{...tdStyle,fontSize:11,color:'var(--muted)',textTransform:'capitalize'}}>{a.visit_type||'—'}</td>
                        <td style={tdStyle}>{statusBadge(a.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            PETTY CASH TAB
        ═══════════════════════════════════════════════ */}
        {!loading && subtab==='petty' && (
          <div className="fadeUp">
            {(() => {
              const cfEntry  = pettyCashData.find(e=>e.entry_type==='carry_forward');
              const expenses = pettyCashData.filter(e=>e.entry_type!=='carry_forward');
              const openBal  = parseFloat(cfEntry?.amount||0);
              const totalExp = expenses.reduce((s,e)=>s+parseFloat(e.amount||0),0);
              const closeBal = openBal - totalExp;
              return (<>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:22}}>
                  <StatCard label="Opening Balance"  value={formatINR(openBal)}             color="#7c3aed" icon="🏦" delay={0}/>
                  <StatCard label="Total Expenses"   value={formatINR(totalExp)}            color="#ef4444" icon="💸" delay={0.06}/>
                  <StatCard label="Closing Balance"  value={formatINR(closeBal)}            color={closeBal<0?'#ef4444':'#059669'} icon="⚖️" delay={0.12}/>
                  <StatCard label="Entries"          value={expenses.length}                color="#f59e0b" icon="📋" delay={0.18}/>
                </div>

                <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:12,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'flex-end'}}>
                  <button className="btn-p" onClick={()=>setPettyModal(true)} style={{...S.btn,fontSize:12,padding:'8px 16px'}}>
                    + Add Expense
                  </button>
                </div>

                <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,overflow:'auto',boxShadow:'var(--cardshadow)'}}>
                  {pettyCashData.length===0
                    ? <div style={{textAlign:'center',padding:'50px 20px',color:'var(--muted)'}}>No petty cash entries this month.</div>
                    : (
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead>
                        <tr>
                          {['Date','Type','Category','Description','Amount','Balance','By',''].map(h=>(
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let running = 0;
                          return pettyCashData.map(e=>{
                            const isCF = e.entry_type==='carry_forward';
                            if (isCF) running = parseFloat(e.amount||0);
                            else running -= parseFloat(e.amount||0);
                            return (
                              <tr key={e.id} className="row-hover" style={isCF?{background:'var(--accentbg)'}:{}}>
                                <td style={tdStyle}>{fmtDate(e.entry_date)}</td>
                                <td style={tdStyle}>
                                  <span style={{background:isCF?'#d1fae5':'#fee2e2',color:isCF?'#065f46':'#dc2626',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>
                                    {isCF?'Carry Fwd':'Expense'}
                                  </span>
                                </td>
                                <td style={tdStyle}><span style={{background:'var(--accentbg)',color:'var(--accent)',borderRadius:5,padding:'2px 7px',fontSize:10,fontWeight:700}}>{e.category||'—'}</span></td>
                                <td style={{...tdStyle,maxWidth:240}}>{e.description}</td>
                                <td style={{...tdStyle,fontWeight:700,color:isCF?'#059669':'#ef4444'}}>{isCF?'+':'-'}{formatINR(e.amount)}</td>
                                <td style={{...tdStyle,fontWeight:700,color:running<0?'#ef4444':'#059669'}}>{formatINR(running)}</td>
                                <td style={{...tdStyle,fontSize:11,color:'var(--muted)'}}>{e.created_by||'—'}</td>
                                <td style={{...tdStyle,whiteSpace:'nowrap'}}>
                                  <button onClick={()=>setEditPettyItem(e)} title="Edit" style={{background:'transparent',border:'none',color:'#0891b2',cursor:'pointer',fontSize:13,marginRight:4}}>✏️</button>
                                  <button onClick={()=>deletePetty(e.id)} title="Delete" style={{background:'transparent',border:'none',color:'#dc2626',cursor:'pointer',fontSize:13,opacity:0.7}}>🗑</button>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                        <tr style={{background:'var(--topbar)',fontWeight:700}}>
                          <td colSpan={4} style={{...tdStyle,fontWeight:700,color:'var(--muted)',fontSize:11}}>CLOSING BALANCE</td>
                          <td style={{...tdStyle,fontWeight:800,color:closeBal<0?'#ef4444':'#059669'}}>{formatINR(closeBal)}</td>
                          <td colSpan={3} style={tdStyle}></td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </>);
            })()}
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            CASH REPORT TAB
        ═══════════════════════════════════════════════ */}
        {!loading && subtab==='cash' && (
          <div className="fadeUp">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:22}}>
              <StatCard label="Cash Collected (DSR)" value={formatINR(dsrCashCollected)} color="#059669" icon="💵" delay={0}/>
              <StatCard label="Total Deposited"       value={formatINR(totalDeposited)}   color="#0ea5e9" icon="🏦" delay={0.06}/>
              <StatCard label="Balance (Undeposited)" value={formatINR(cashBalance)}       color={cashBalance<0?'#ef4444':'#f59e0b'} icon="⚖" delay={0.12}/>
            </div>

            <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,padding:'18px 20px',marginBottom:18,boxShadow:'var(--cardshadow)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{fontSize:13,fontWeight:700,color:'var(--text1)'}}>Cash Deposits</div>
                <button className="btn-p" onClick={()=>setCashModal(true)} style={{...S.btn,fontSize:12,padding:'8px 16px'}}>
                  + Add Deposit
                </button>
              </div>
              {cashDeposits.length===0
                ? <div style={{textAlign:'center',padding:'30px 0',color:'var(--muted)',fontSize:12}}>No deposits recorded this month.</div>
                : (
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr>
                      {['Date','Amount','Bank','Reference','Remarks',''].map(h=>(
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cashDeposits.map(e=>(
                      <tr key={e.id} className="row-hover">
                        <td style={tdStyle}>{fmtDate(e.deposit_date)}</td>
                        <td style={{...tdStyle,fontWeight:700,color:'#059669'}}>{formatINR(e.amount)}</td>
                        <td style={{...tdStyle,color:'var(--muted)'}}>{e.bank_name||'—'}</td>
                        <td style={{...tdStyle,fontSize:11}}>{e.reference||'—'}</td>
                        <td style={{...tdStyle,fontSize:11,color:'var(--muted)'}}>{e.remarks||'—'}</td>
                        <td style={{...tdStyle,whiteSpace:'nowrap'}}>
                          <button onClick={()=>setEditCashItem(e)} title="Edit" style={{background:'transparent',border:'none',color:'#0891b2',cursor:'pointer',fontSize:13,marginRight:4}}>✏️</button>
                          <button onClick={()=>deleteCash(e.id)} title="Delete" style={{background:'transparent',border:'none',color:'#dc2626',cursor:'pointer',fontSize:13,opacity:0.7}}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════
          DSR MODAL
      ═══════════════════════════════════════════════════ */}
      {(dsrModal || editDsrItem) && (
        <DSRModal
          clinicId={clinicId}
          month={month}
          editItem={editDsrItem}
          onClose={()=>{setDsrModal(false);setEditDsrItem(null);}}
          onSaved={(row)=>{
            if (editDsrItem) setDsrData(prev=>prev.map(e=>e.id===row.id?row:e));
            else setDsrData(prev=>[row,...prev]);
            setDsrModal(false); setEditDsrItem(null);
          }}
        />
      )}

      {/* ═══════════════════════════════════════════════════
          PETTY CASH MODAL
      ═══════════════════════════════════════════════════ */}
      {(pettyModal || editPettyItem) && (
        <PettyCashModal
          clinicId={clinicId}
          month={month}
          editItem={editPettyItem}
          onClose={()=>{setPettyModal(false);setEditPettyItem(null);}}
          onSaved={(row)=>{
            if (editPettyItem) setPettyCashData(prev=>prev.map(e=>e.id===row.id?row:e));
            else setPettyCashData(prev=>[row,...prev]);
            setPettyModal(false); setEditPettyItem(null);
          }}
        />
      )}

      {/* ═══════════════════════════════════════════════════
          CASH DEPOSIT MODAL
      ═══════════════════════════════════════════════════ */}
      {(cashModal || editCashItem) && (
        <CashDepositModal
          clinicId={clinicId}
          month={month}
          editItem={editCashItem}
          onClose={()=>{setCashModal(false);setEditCashItem(null);}}
          onSaved={(row)=>{
            if (editCashItem) setCashDeposits(prev=>prev.map(e=>e.id===row.id?row:e));
            else setCashDeposits(prev=>[row,...prev]);
            setCashModal(false); setEditCashItem(null);
          }}
        />
      )}
      {visitNoteItem && (
        <VisitNoteModal
          item={visitNoteItem}
          onClose={()=>setVisitNoteItem(null)}
        />
      )}
    </div>
  );
}

function DSRModal({ clinicId, month, onClose, onSaved, editItem }) {
  const isEdit = !!editItem;
  const todayStr = new Date().toISOString().slice(0,10);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [date,setDate]=useState(isEdit?(editItem.entry_date||todayStr):todayStr);
  const [time,setTime]=useState(isEdit?(editItem.entry_time||''):'');
  const [name,setName]=useState(isEdit?(editItem.patient_name||''):'');
  const [phone,setPhone]=useState(isEdit?(editItem.phone||''):'');
  const [source,setSource]=useState(isEdit?(editItem.source||''):'');
  const [visitType,setVisitType]=useState(isEdit?(editItem.visit_type||'Clinic'):'Clinic');
  const [purpose,setPurpose]=useState(isEdit?(editItem.purpose||''):'');
  const [spoke,setSpoke]=useState(isEdit?(editItem.spoke||''):'');
  const [audiologist,setAudiologist]=useState(isEdit?(editItem.audiologist||''):'');
  const [invoiceNo,setInvoiceNo]=useState(isEdit?(editItem.invoice_no||''):'');
  const [amount,setAmount]=useState(isEdit?(editItem.amount||''):'');
  const [paymentMode,setPaymentMode]=useState(isEdit?(editItem.payment_mode||''):'');
  const [paymentType,setPaymentType]=useState(isEdit?(editItem.payment_type||''):'');
  const [nr,setNr]=useState(isEdit?!!parseInt(editItem.nr):false);
  const [remarks,setRemarks]=useState(isEdit?(editItem.remarks||''):'');

  const inp={width:'100%',padding:'9px 12px',background:'var(--inputbg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text1)',fontSize:12,marginBottom:10};
  const lbl={fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,display:'block',marginBottom:4,textTransform:'uppercase'};

  const save = async () => {
    if (!name.trim()) { setErr('Patient name is required'); return; }
    if (!purpose)     { setErr('Purpose is required'); return; }
    if (!date)        { setErr('Date is required'); return; }
    setBusy(true); setErr('');
    const body = { clinic_id:clinicId, entry_date:date, entry_time:time||null, patient_name:name.trim(), phone:phone.trim(), source, visit_type:visitType, purpose, spoke:spoke.trim(), audiologist:audiologist.trim(), invoice_no:invoiceNo.trim(), amount:amount?parseFloat(amount):0, payment_mode:paymentMode, payment_type:paymentType, nr:nr?1:0, remarks:remarks.trim() };
    try {
      const url = isEdit ? `${API_BASE}/dsr.php?id=${editItem.id}` : `${API_BASE}/dsr.php`;
      const res = await apiCall(url, { method: isEdit?'PUT':'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (data.error) { setErr(data.error); setBusy(false); return; }
      const saved = { ...(isEdit?editItem:{}), ...body, id: isEdit?editItem.id:data.id };
      onSaved(saved);
    } catch(e) { setErr(e.message||'Save failed'); }
    setBusy(false);
  };

  const DSR_PURPOSES = ['PTA & HAT','HAT Only','HA Inquiry','Follow Up','Counselling','Repair','Demo','PTA Only','BERA/OAE','Fine Tuning','Service','Other'];
  const DSR_SOURCES  = ['Walk-in','Google','Doctor Referral','Facebook / Instagram','Hear.com','Patient Referral','SoundLife App','Camp','Old Patient','Other'];
  const PAYMENT_MODES = ['Cash','Card','UPI','NEFT/RTGS','Cheque','Free'];

  return (
    <div style={{position:'fixed',inset:0,background:'#00000080',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={onClose}>
      <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:18,padding:26,maxWidth:600,width:'100%',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 64px #00000040'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
          <div style={{fontSize:15,fontWeight:700,color:'var(--text1)'}}>{isEdit?'Edit DSR Entry':'New DSR Entry'}</div>
          <button onClick={onClose} style={{background:'transparent',border:'none',fontSize:20,cursor:'pointer',color:'var(--muted)'}}>✕</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Date *</label><input type="date" style={inp} value={date} onChange={e=>setDate(e.target.value)}/></div>
          <div><label style={lbl}>Time</label><input type="time" style={inp} value={time} onChange={e=>setTime(e.target.value)}/></div>
        </div>

        <label style={lbl}>Patient Name *</label>
        <input style={inp} value={name} onChange={e=>setName(e.target.value)} placeholder="Full name"/>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Phone</label><input style={inp} value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Mobile number"/></div>
          <div><label style={lbl}>Source</label>
            <select style={inp} value={source} onChange={e=>setSource(e.target.value)}>
              <option value="">Select…</option>
              {DSR_SOURCES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Visit Type</label>
            <select style={inp} value={visitType} onChange={e=>setVisitType(e.target.value)}>
              {['Clinic','Home','Camp','Online'].map(v=><option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Purpose *</label>
            <select style={inp} value={purpose} onChange={e=>setPurpose(e.target.value)}>
              <option value="">Select…</option>
              {DSR_PURPOSES.map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Spoke To</label><input style={inp} value={spoke} onChange={e=>setSpoke(e.target.value)} placeholder="e.g. Patient, Family"/></div>
          <div><label style={lbl}>Audiologist</label><input style={inp} value={audiologist} onChange={e=>setAudiologist(e.target.value)} placeholder="Name"/></div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Invoice #</label><input style={inp} value={invoiceNo} onChange={e=>setInvoiceNo(e.target.value)} placeholder="Inv number"/></div>
          <div><label style={lbl}>Amount (₹)</label><input type="number" style={inp} value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0"/></div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Payment Mode</label>
            <select style={inp} value={paymentMode} onChange={e=>setPaymentMode(e.target.value)}>
              <option value="">Select…</option>
              {PAYMENT_MODES.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Payment Type</label>
            <select style={inp} value={paymentType} onChange={e=>setPaymentType(e.target.value)}>
              <option value="">Select…</option>
              {['Full','Advance','Balance','EMI'].map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <label style={lbl}>Remarks</label>
        <textarea style={{...inp,minHeight:56,resize:'vertical'}} value={remarks} onChange={e=>setRemarks(e.target.value)} placeholder="Any notes…" rows={2}/>

        <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',marginBottom:14,fontSize:12,color:'var(--text1)'}}>
          <input type="checkbox" checked={nr} onChange={e=>setNr(e.target.checked)} style={{width:'auto'}}/>
          NR / Repeat visit (exclude from revenue)
        </label>

        {err && <div style={{background:'#fee2e2',border:'1px solid #fecaca',borderRadius:8,padding:'9px 13px',marginBottom:12,fontSize:12,color:'#dc2626'}}>{err}</div>}

        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{...S.btnG,fontSize:12,padding:'9px 18px'}}>Cancel</button>
          <button className="btn-p" onClick={save} disabled={busy} style={{...S.btn,fontSize:12,padding:'9px 18px',opacity:busy?0.6:1}}>
            {busy?'Saving…':isEdit?'Save Changes':'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PettyCashModal({ clinicId, month, onClose, onSaved, editItem }) {
  const isEdit = !!editItem;
  const todayStr = new Date().toISOString().slice(0,10);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [date,setDate]=useState(isEdit?(editItem.entry_date||todayStr):todayStr);
  const [entryType,setEntryType]=useState(isEdit?(editItem.entry_type||'expense'):'expense');
  const [category,setCategory]=useState(isEdit?(editItem.category||''):'');
  const [description,setDescription]=useState(isEdit?(editItem.description||''):'');
  const [amount,setAmount]=useState(isEdit?(editItem.amount||''):'');

  const inp={width:'100%',padding:'9px 12px',background:'var(--inputbg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text1)',fontSize:12,marginBottom:10};
  const lbl={fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,display:'block',marginBottom:4,textTransform:'uppercase'};

  const PETTY_CATS = ['Stationery','Housekeeping','Transport','Food / Refreshments','Utilities','Repairs','Staff','Courier','Miscellaneous'];

  const save = async () => {
    if (!amount||parseFloat(amount)<=0){ setErr('Valid amount is required'); return; }
    if (entryType==='expense' && !category){ setErr('Category is required'); return; }
    if (!description.trim()){ setErr('Description is required'); return; }
    setBusy(true); setErr('');
    const body = { clinic_id:clinicId, entry_date:date, entry_type:entryType, category, description:description.trim(), amount:parseFloat(amount) };
    try {
      const url = isEdit ? `${API_BASE}/petty_cash.php?id=${editItem.id}` : `${API_BASE}/petty_cash.php`;
      const res = await apiCall(url, { method:isEdit?'PUT':'POST', body:JSON.stringify(body) });
      const data = await res.json();
      if (data.error) { setErr(data.error); setBusy(false); return; }
      const saved = isEdit ? { ...editItem, ...body } : { id:data.id, ...body };
      onSaved(saved);
    } catch(e) { setErr(e.message||'Save failed'); }
    setBusy(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'#00000080',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={onClose}>
      <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:18,padding:26,maxWidth:440,width:'100%',boxShadow:'0 24px 64px #00000040'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
          <div style={{fontSize:15,fontWeight:700,color:'var(--text1)'}}>{isEdit?'Edit Entry':'Add Petty Cash Entry'}</div>
          <button onClick={onClose} style={{background:'transparent',border:'none',fontSize:20,cursor:'pointer',color:'var(--muted)'}}>✕</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Date *</label><input type="date" style={inp} value={date} onChange={e=>setDate(e.target.value)}/></div>
          <div><label style={lbl}>Type</label>
            <select style={inp} value={entryType} onChange={e=>setEntryType(e.target.value)}>
              <option value="expense">Expense</option>
              <option value="carry_forward">Carry Forward</option>
            </select>
          </div>
        </div>

        {entryType==='expense' && (<>
          <label style={lbl}>Category *</label>
          <select style={inp} value={category} onChange={e=>setCategory(e.target.value)}>
            <option value="">Select category…</option>
            {PETTY_CATS.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </>)}

        <label style={lbl}>Description *</label>
        <input style={inp} value={description} onChange={e=>setDescription(e.target.value)} placeholder="What was purchased / paid for"/>

        <label style={lbl}>Amount (₹) *</label>
        <input type="number" style={inp} value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00"/>

        {err && <div style={{background:'#fee2e2',border:'1px solid #fecaca',borderRadius:8,padding:'9px 13px',marginBottom:12,fontSize:12,color:'#dc2626'}}>{err}</div>}

        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:4}}>
          <button onClick={onClose} style={{...S.btnG,fontSize:12,padding:'9px 18px'}}>Cancel</button>
          <button className="btn-p" onClick={save} disabled={busy} style={{...S.btn,fontSize:12,padding:'9px 18px',opacity:busy?0.6:1}}>
            {busy?'Saving…':isEdit?'Save Changes':'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CashDepositModal({ clinicId, month, onClose, onSaved, editItem }) {
  const isEdit = !!editItem;
  const todayStr = new Date().toISOString().slice(0,10);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [date,setDate]=useState(isEdit?(editItem.deposit_date||todayStr):todayStr);
  const [amount,setAmount]=useState(isEdit?(editItem.amount||''):'');
  const [particulars,setParticulars]=useState(isEdit?(editItem.particulars||editItem.remarks||''):'');
  const [reference,setReference]=useState(isEdit?(editItem.reference||''):'');
  const [bankName,setBankName]=useState(isEdit?(editItem.bank_name||editItem.bank||''):'');
  const [remarks,setRemarks]=useState(isEdit?(editItem.remarks||''):'');

  const inp={width:'100%',padding:'9px 12px',background:'var(--inputbg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text1)',fontSize:12,marginBottom:10};
  const lbl={fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,display:'block',marginBottom:4,textTransform:'uppercase'};

  const save = async () => {
    if (!amount||parseFloat(amount)<=0){ setErr('Valid amount is required'); return; }
    setBusy(true); setErr('');
    const body = { deposit_date:date, amount:parseFloat(amount), bank_name:bankName.trim(), reference:reference.trim(), remarks:remarks.trim(), particulars:particulars.trim() };
    try {
      const url = isEdit ? `${API_BASE}/cash_deposits.php?id=${editItem.id}` : `${API_BASE}/cash_deposits.php`;
      const res = await apiCall(url, { method:isEdit?'PUT':'POST', body:JSON.stringify({ clinic_id:clinicId, ...body }) });
      const data = await res.json();
      if (data.error) { setErr(data.error); setBusy(false); return; }
      const saved = isEdit ? { ...editItem, ...body } : { id:data.id, clinic_id:clinicId, ...body };
      onSaved(saved);
    } catch(e) { setErr(e.message||'Save failed'); }
    setBusy(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'#00000080',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={onClose}>
      <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:18,padding:26,maxWidth:440,width:'100%',boxShadow:'0 24px 64px #00000040'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
          <div style={{fontSize:15,fontWeight:700,color:'var(--text1)'}}>{isEdit?'Edit Cash Entry':'Add Cash Deposit'}</div>
          <button onClick={onClose} style={{background:'transparent',border:'none',fontSize:20,cursor:'pointer',color:'var(--muted)'}}>✕</button>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          <div><label style={lbl}>Date *</label><input type="date" style={inp} value={date} onChange={e=>setDate(e.target.value)}/></div>
          <div><label style={lbl}>Amount (₹) *</label><input type="number" style={inp} value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00"/></div>
        </div>

        <label style={lbl}>Patient / Particulars</label>
        <input style={inp} value={particulars} onChange={e=>setParticulars(e.target.value)} placeholder="Patient name / description"/>

        <label style={lbl}>Invoice / Reference (PN#)</label>
        <input style={inp} value={reference} onChange={e=>setReference(e.target.value)} placeholder="PN/xxxx/2026-27"/>

        <label style={lbl}>Bank Name</label>
        <input style={inp} value={bankName} onChange={e=>setBankName(e.target.value)} placeholder="e.g. HDFC Bank"/>

        <label style={lbl}>Remarks</label>
        <textarea style={{...inp,minHeight:52,resize:'vertical'}} value={remarks} onChange={e=>setRemarks(e.target.value)} placeholder="Any notes…" rows={2}/>

        {err && <div style={{background:'#fee2e2',border:'1px solid #fecaca',borderRadius:8,padding:'9px 13px',marginBottom:12,fontSize:12,color:'#dc2626'}}>{err}</div>}

        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:4}}>
          <button onClick={onClose} style={{...S.btnG,fontSize:12,padding:'9px 18px'}}>Cancel</button>
          <button className="btn-p" onClick={save} disabled={busy} style={{...S.btn,fontSize:12,padding:'9px 18px',opacity:busy?0.6:1}}>
            {busy?'Saving…':isEdit?'Save Changes':'Save Deposit'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Visit Note Modal (from DSR entry) ─────────────────────
function VisitNoteModal({ item, onClose }) {
  const note = [
    item.audiologist ? `Audiologist: ${item.audiologist}` : '',
    item.purpose     ? `Purpose: ${item.purpose}` : '',
    item.invoice_no  ? `Invoice: ${item.invoice_no}` : '',
    item.amount > 0  ? `Amount: ₹${Number(item.amount).toLocaleString('en-IN')} (${item.payment_mode||'—'})` : '',
    item.remarks     ? `Notes: ${item.remarks}` : '',
  ].filter(Boolean).join('\n');

  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(note).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); });
  };

  const inp={width:'100%',padding:'9px 12px',background:'var(--inputbg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text1)',fontSize:12};

  return (
    <div style={{position:'fixed',inset:0,background:'#00000080',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={onClose}>
      <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:18,padding:26,maxWidth:440,width:'100%',boxShadow:'0 24px 64px #00000040'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div style={{fontSize:15,fontWeight:700,color:'var(--text1)'}}>📝 Visit Note</div>
          <button onClick={onClose} style={{background:'transparent',border:'none',fontSize:20,cursor:'pointer',color:'var(--muted)'}}>✕</button>
        </div>

        <div style={{marginBottom:12,padding:'10px 14px',background:'var(--accentbg)',borderRadius:10,border:'1px solid var(--border)'}}>
          <div style={{fontSize:13,fontWeight:700,color:'var(--text1)'}}>{item.patient_name}</div>
          <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{item.entry_date} · {item.visit_type||'Clinic'}</div>
        </div>

        <label style={{fontSize:10,fontWeight:700,color:'var(--muted)',letterSpacing:0.8,display:'block',marginBottom:6,textTransform:'uppercase'}}>
          Visit Note — copy to patient profile
        </label>
        <textarea readOnly value={note} rows={6}
          style={{...inp,minHeight:100,resize:'vertical',fontFamily:'monospace',marginBottom:14}}
          onClick={e=>e.target.select()}
        />

        <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:8,padding:'8px 12px',marginBottom:14,fontSize:11,color:'#92400e'}}>
          💡 Search for <strong>{item.patient_name}</strong> → open patient profile → Visits tab → add visit note
        </div>

        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{...S.btnG,fontSize:12,padding:'9px 18px'}}>Close</button>
          <button onClick={copy} style={{...S.btn,fontSize:12,padding:'9px 18px',background:copied?'#059669':'var(--accent)'}}>
            {copied?'✓ Copied!':'Copy Note'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SHYAMAL STOCK VIEW
// ══════════════════════════════════════════════════════════
function ShyamalStockView({ user }) {
  const readOnly = !['sl-shyamal','sl-office'].includes(user.username) && user.role !== 'owner';
  const [stockTab,    setStockTab]    = useState('dashboard');
  const [allItems,    setAllItems]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [company,     setCompany]     = useState('all');
  const [search,      setSearch]      = useState('');
  const [showAdd,     setShowAdd]     = useState(false);
  const [stats,       setStats]       = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [editId,      setEditId]      = useState(null);
  const [editItem,    setEditItem]    = useState(null); // item to edit in modal
  const [drillStatus, setDrillStatus] = useState(null);
  const [category,    setCategory]    = useState('all');
  const [sortBy,      setSortBy]      = useState('newest');
  const [audiFilter,  setAudiFilter]  = useState('all');
  const thStyle = {padding:'9px 12px',fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,borderBottom:'2px solid var(--border)',background:'var(--card)',textAlign:'left',whiteSpace:'nowrap'};
  const tdStyle = {padding:'9px 12px',fontSize:12,color:'var(--text1)',borderBottom:'1px solid var(--border)',verticalAlign:'middle'};
  const Spinner = () => <div style={{textAlign:'center',padding:'50px',color:'var(--muted)'}}>Loading…</div>;

  const STATUSES = {
    in_stock:{ label:'In Clinic',    color:'#059669', bg:'#d1fae5' },
    on_trial:{ label:'H. Trial',     color:'#d97706', bg:'#fef3c7' },
    in_cabin:{ label:'In Cabin',     color:'#7c3aed', bg:'#ede9fe' },
    standby: { label:'Standby',      color:'#0891b2', bg:'#cffafe' },
    fitted:  { label:'Fitting Done', color:'#1d4ed8', bg:'#dbeafe' },
    missing: { label:'Missing',      color:'#ef4444', bg:'#fee2e2' },
    sent_ho: { label:'Sent to HO',   color:'#6b7280', bg:'#f3f4f6' },
    buyback: { label:'Buyback',      color:'#be185d', bg:'#fce7f3' },
  };

  const TABS = [
    { key:'dashboard', icon:'📊', label:'Dashboard' },
    { key:'stock',     icon:'📦', label:'In Stock' },
    { key:'moving',    icon:'🚪', label:'In Cabin' },
    { key:'standby',   icon:'🔄', label:'Standby' },
    { key:'trial',     icon:'🎧', label:'HA Trial' },
    { key:'fitting',   icon:'✅', label:'Fitting' },
    { key:'sentho',    icon:'🚚', label:'Sent to HO' },
  ];

  // Status sets per tab  (stock = physically at clinic; fitted/sent_ho live in their own tabs)
  const TAB_STATUS = {
    stock:   ['in_stock','missing','buyback'],
    moving:  ['in_cabin'],
    standby: ['standby'],
    trial:   ['on_trial'],
    fitting: ['fitted'],
    sentho:  ['sent_ho'],
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiCall(`${API_BASE}/stock.php`).then(r=>r.json()).catch(()=>[]),
      apiCall(`${API_BASE}/stock.php?stats=1`).then(r=>r.json()).catch(()=>({stats:[],companies:[]})),
    ]).then(([data, meta]) => {
      setAllItems(Array.isArray(data) ? data : []);
      if (Array.isArray(meta.stats))     setStats(meta.stats);
      if (Array.isArray(meta.companies)) setCompanies(meta.companies);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const statMap = {};
  stats.forEach(s => { statMap[s.status] = (statMap[s.status]||0) + parseInt(s.cnt); });
  const total = Object.values(statMap).reduce((a,b) => a+b, 0);

  const changeStatus = async (id, newStatus) => {
    if (readOnly) return;
    await apiCall(`${API_BASE}/stock.php?id=${id}`, { method:'PUT', body: JSON.stringify({ status: newStatus }) });
    setEditId(null);
    load();
  };

  const deleteItem = async (id, model) => {
    if (readOnly) return;
    if (!window.confirm(`Remove "${model}" from stock? This cannot be undone.`)) return;
    await apiCall(`${API_BASE}/stock.php?id=${id}`, { method:'DELETE' });
    load();
  };

  // Filter + sort items
  const visibleItems = useMemo(() => {
    let items = allItems;
    // status filter
    if (drillStatus === '__all__') { /* show all */ }
    else if (drillStatus) items = items.filter(i => i.status === drillStatus);
    else { const ss = TAB_STATUS[stockTab]; if (ss) items = items.filter(i => ss.includes(i.status)); }
    // category filter
    if (category !== 'all') items = items.filter(i => (i.category||'HA') === category);
    // brand filter
    if (company !== 'all') items = items.filter(i => (i.company||'').toLowerCase() === company.toLowerCase());
    // audiologist filter (In Cabin tab)
    if (audiFilter !== 'all') items = items.filter(i => (i.audiologist||'') === audiFilter);
    // search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(i =>
        (i.sn||'').toLowerCase().includes(q) ||
        (i.model||'').toLowerCase().includes(q) ||
        (i.patient_name||'').toLowerCase().includes(q) ||
        (i.audiologist||'').toLowerCase().includes(q) ||
        (i.company||'').toLowerCase().includes(q)
      );
    }
    // sort
    items = [...items].sort((a,b) => {
      if (sortBy === 'newest')  return new Date(b.added_at||0) - new Date(a.added_at||0);
      if (sortBy === 'oldest')  return new Date(a.added_at||0) - new Date(b.added_at||0);
      if (sortBy === 'brand')   return (a.company||'').localeCompare(b.company||'');
      if (sortBy === 'model')   return (a.model||'').localeCompare(b.model||'');
      if (sortBy === 'days')    return (b.outward_date||b.inward_date||'').localeCompare(a.outward_date||a.inward_date||'');
      return 0;
    });
    return items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, stockTab, company, search, drillStatus, category, audiFilter, sortBy]);

  // Audiologists present in current tab's items (for In Cabin filter)
  const tabAudiologists = useMemo(() => {
    const ss = TAB_STATUS[stockTab];
    const base = ss ? allItems.filter(i => ss.includes(i.status)) : allItems;
    return [...new Set(base.map(i=>i.audiologist).filter(Boolean))].sort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems, stockTab]);

  const inp = S.inp;

  // ── StockTable sub-component ──────────────────────────────────
  const StockTable = ({ rows }) => (
    <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,overflow:'auto',boxShadow:'var(--cardshadow)'}}>
      {rows.length === 0
        ? <div style={{textAlign:'center',padding:'50px 20px',color:'var(--muted)'}}>No items found.</div>
        : (
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
          <thead>
            <tr>
              {['Brand','Model','S/N','Pack','Status','Audiologist','Patient / Sent To','Days','Inward Date',''].map(h=>(
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(item => {
              const st = STATUSES[item.status] || {label:item.status||'—', color:'#6b7280', bg:'#f3f4f6'};
              return (
                <tr key={item.id} className="row-hover">
                  <td style={{...tdStyle,fontWeight:700,color:'var(--accent)',fontSize:11}}>{item.company}</td>
                  <td style={{...tdStyle,fontWeight:600}}>{item.model}</td>
                  <td style={{...tdStyle,fontFamily:'monospace',fontSize:11,color:'var(--muted2)'}}>{item.sn||'—'}</td>
                  <td style={{...tdStyle,fontSize:11}}>{item.pack_type||'—'}</td>
                  <td style={tdStyle}>
                    {!readOnly && editId===item.id ? (
                      <select value={item.status} autoFocus
                        onChange={e=>changeStatus(item.id,e.target.value)}
                        onBlur={()=>setEditId(null)}
                        style={{...inp,width:'auto',fontSize:11,padding:'4px 8px',marginBottom:0}}>
                        {Object.entries(STATUSES).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                      </select>
                    ) : (
                      <span onClick={()=>{if(!readOnly)setEditId(item.id);}}
                        style={{display:'inline-block',padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:600,background:st.bg,color:st.color,cursor:readOnly?'default':'pointer',border:`1px solid ${st.color}30`}}>
                        {st.label}
                      </span>
                    )}
                  </td>
                  <td style={{...tdStyle}}>
                    {item.audiologist
                      ? <span style={{fontSize:11,fontWeight:700,color:'var(--accent)'}}>{item.audiologist}</span>
                      : <span style={{color:'var(--muted)',fontSize:11}}>—</span>}
                    {item.notes && <div style={{fontSize:10,color:'var(--muted)',marginTop:1,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.notes}</div>}
                  </td>
                  <td style={tdStyle}>
                    {item.status === 'in_cabin'
                      ? <span style={{fontSize:11,fontWeight:700,color:'#7c3aed'}}>{item.outward_name||'—'}</span>
                      : item.patient_name||item.outward_name||'—'}
                  </td>
                  <td style={{...tdStyle,color:'var(--muted)',fontSize:11}}>
                    {(() => {
                      const ref = item.outward_date || item.inward_date;
                      if (!ref) return <span style={{color:'var(--muted)'}}>—</span>;
                      const days = Math.floor((Date.now() - new Date(ref+'T00:00:00')) / 86400000);
                      const col = days > 30 ? '#ef4444' : days > 14 ? '#d97706' : '#059669';
                      return <span style={{fontWeight:700,color:col}}>{days}d</span>;
                    })()}
                  </td>
                  <td style={{...tdStyle,color:'var(--muted)',fontSize:11}}>{item.inward_date ? new Date(item.inward_date+'T00:00:00').toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{...tdStyle,textAlign:'right',whiteSpace:'nowrap'}}>
                    {!readOnly && (<>
                      <button onClick={()=>setEditItem(item)}
                        style={{padding:'4px 10px',borderRadius:7,border:'1px solid var(--border)',background:'var(--card)',color:'var(--text1)',fontSize:11,fontWeight:600,cursor:'pointer',marginRight:5}}>
                        ✏️ Edit
                      </button>
                      <button onClick={()=>deleteItem(item.id, item.model)}
                        style={{padding:'4px 10px',borderRadius:7,border:'none',background:'#fee2e2',color:'#991b1b',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                        − Remove
                      </button>
                    </>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  // Navigate from dashboard card to filtered list
  const drillTo = (tab, statusKey) => {
    setStockTab(tab);
    setDrillStatus(statusKey);
    setSearch('');
    setCompany('all');
  };

  // ── Dashboard view ────────────────────────────────────────────
  const DashboardView = () => {
    const companyMap = {};
    allItems.forEach(i => {
      const co = i.company || 'Unknown';
      if (!companyMap[co]) companyMap[co] = { total:0 };
      companyMap[co].total++;
      companyMap[co][i.status] = (companyMap[co][i.status]||0) + 1;
    });
    const sorted = Object.entries(companyMap).sort((a,b)=>b[1].total-a[1].total);
    const maxBrandTotal = sorted.length ? sorted[0][1].total : 1;

    const CARDS = [
      { label:'Total',      val:total,               color:'#7c3aed', icon:'📦', tab:'stock',   drill:'__all__',  sub:'All devices' },
      { label:'In Clinic',  val:statMap.in_stock||0, color:'#059669', icon:'🏥', tab:'stock',   drill:null,       sub:'Available · reception' },
      { label:'In Cabin',   val:statMap.in_cabin||0,  color:'#7c3aed', icon:'🚪', tab:'moving',  drill:null, sub:'With audiologist in cabin' },
      { label:'Standby',    val:statMap.standby||0,   color:'#0891b2', icon:'🔄', tab:'standby', drill:null, sub:'Given to pt during repair' },
      { label:'H. Trial',   val:statMap.on_trial||0,  color:'#d97706', icon:'🎧', tab:'trial',   drill:null, sub:'Devices out with patients' },
      { label:'Fitted',     val:statMap.fitted||0,   color:'#1d4ed8', icon:'✅', tab:'fitting', drill:null,       sub:'Completed fittings' },
      { label:'Sent to HO', val:statMap.sent_ho||0,  color:'#6b7280', icon:'🚚', tab:'sentho',  drill:null,        sub:'Dispatched to HO' },
      { label:'Buyback',    val:statMap.buyback||0,  color:'#be185d', icon:'🔄', tab:'stock',   drill:'buyback',   sub:'Bought back · in clinic' },
      { label:'Missing',    val:statMap.missing||0,  color:'#ef4444', icon:'⚠️', tab:'stock',   drill:'missing',   sub:'Not located' },
    ];

    return (
      <div>
        {/* Stat cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:12,marginBottom:28}}>
          {CARDS.map((s,i) => {
            const pct = total > 0 ? Math.round((s.val/total)*100) : 0;
            return (
              <div key={s.label} onClick={()=>drillTo(s.tab, s.drill)}
                className="card-hover fadeUp"
                style={{animationDelay:`${i*0.04}s`,background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:16,padding:'18px 18px 14px',boxShadow:'var(--cardshadow)',cursor:'pointer',transition:'transform 0.18s,box-shadow 0.18s',position:'relative',overflow:'hidden'}}>
                {/* top accent bar */}
                <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:s.color,borderRadius:'16px 16px 0 0'}}/>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <span style={{fontSize:20}}>{s.icon}</span>
                  <span style={{fontSize:10,fontWeight:700,color:s.color,background:`${s.color}18`,padding:'2px 7px',borderRadius:20}}>{pct}%</span>
                </div>
                <div style={{fontSize:32,fontWeight:800,color:s.color,lineHeight:1,marginBottom:4}}>{s.val}</div>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text1)',marginBottom:2}}>{s.label}</div>
                <div style={{fontSize:10,color:'var(--muted)',marginBottom:10}}>{s.sub}</div>
                {/* progress bar */}
                <div style={{height:4,background:'var(--border)',borderRadius:4,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:s.color,borderRadius:4,transition:'width 0.8s ease'}}/>
                </div>
                {/* arrow hint */}
                <div style={{position:'absolute',bottom:12,right:14,fontSize:11,color:s.color,opacity:0.6}}>→</div>
              </div>
            );
          })}
        </div>

        {/* Brand breakdown */}
        <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:16,overflow:'hidden',boxShadow:'var(--cardshadow)'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontWeight:800,fontSize:14,color:'var(--text1)'}}>By Brand</span>
            <span style={{fontSize:11,color:'var(--muted)',marginLeft:'auto'}}>{sorted.length} brands · {total} total</span>
          </div>
          <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
            <thead>
              <tr>
                {[
                  {h:'Brand',     w:160},
                  {h:'Share',     w:120},
                  {h:'Total',     w:60},
                  {h:'In Clinic', w:80, c:'#059669'},
                  {h:'H. Trial',  w:72, c:'#d97706'},
                  {h:'Stand By',  w:72, c:'#7c3aed'},
                  {h:'Fitted',    w:64, c:'#1d4ed8'},
                  {h:'Sent HO',   w:72, c:'#6b7280'},
                  {h:'Missing',   w:72, c:'#ef4444'},
                ].map(col=>(
                  <th key={col.h} style={{...thStyle,width:col.w,fontSize:9,color:col.c||'var(--muted)'}}>{col.h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(([co,d])=>{
                const barW = Math.round((d.total/maxBrandTotal)*100);
                return (
                  <tr key={co} className="row-hover">
                    <td style={{...tdStyle,fontWeight:700,color:'var(--accent)',fontSize:12}}>{co}</td>
                    <td style={{...tdStyle,minWidth:120}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{flex:1,height:6,background:'var(--border)',borderRadius:4,overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${barW}%`,background:'linear-gradient(90deg,var(--accent),#9333ea)',borderRadius:4}}/>
                        </div>
                        <span style={{fontSize:10,color:'var(--muted)',minWidth:28,textAlign:'right'}}>{Math.round((d.total/total)*100)}%</span>
                      </div>
                    </td>
                    <td style={{...tdStyle,fontWeight:800,fontSize:13}}>{d.total}</td>
                    <td style={{...tdStyle,color:'#059669',fontWeight:600}}>{d.in_stock||0}</td>
                    <td style={{...tdStyle,color:'#d97706',fontWeight:600}}>{d.on_trial||0}</td>
                    <td style={{...tdStyle,color:'#7c3aed',fontWeight:600}}>{d.in_cabin||0}</td>
                    <td style={{...tdStyle,color:'#1d4ed8',fontWeight:600}}>{d.fitted||0}</td>
                    <td style={{...tdStyle,color:'#6b7280',fontWeight:600}}>{d.sent_ho||0}</td>
                    <td style={{...tdStyle,color:'#ef4444',fontWeight:600}}>{d.missing||0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <TopBar title="Stock Registry" sub="Shyamal · Hearing Aids & Accessories"/>
      <div className="main-pad" style={{padding:'20px 26px'}}>

        {/* ── Inner tab bar ── */}
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20}}>
          <div style={{display:'flex',gap:4,background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:12,padding:4}}>
            {TABS.map(t => (
              <button key={t.key} onClick={()=>{setStockTab(t.key);setSearch('');setCompany('all');setDrillStatus(null);setCategory('all');setAudiFilter('all');setSortBy('newest');}}
                style={{padding:'7px 16px',borderRadius:8,border:'none',background:stockTab===t.key?'var(--accent)':'transparent',color:stockTab===t.key?'#fff':'var(--text2)',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all 0.15s',display:'flex',alignItems:'center',gap:5}}>
                <span>{t.icon}</span>{t.label}
              </button>
            ))}
          </div>
          {!readOnly && (
            <button className="btn-p" onClick={()=>setShowAdd(stockTab)}
              style={{...S.btn,fontSize:12,padding:'8px 18px',marginLeft:'auto'}}>
              + Add Device
            </button>
          )}
        </div>

        {loading ? <Spinner/> : (
          <>
            {stockTab === 'dashboard' && <DashboardView/>}

            {stockTab !== 'dashboard' && (
              <>
                {/* ── Drill breadcrumb ── */}
                {drillStatus && (
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,padding:'8px 14px',background:'var(--accentbg)',border:'1px solid var(--border2)',borderRadius:10}}>
                    <span style={{fontSize:11,color:'var(--accent)',fontWeight:700}}>
                      {drillStatus === '__all__' ? '📦 All Devices' : `Filtered: ${STATUSES[drillStatus]?.label || drillStatus}`}
                    </span>
                    <span style={{fontSize:11,color:'var(--muted)'}}>· {visibleItems.length} items</span>
                    <button onClick={()=>setDrillStatus(null)}
                      style={{marginLeft:'auto',padding:'3px 10px',borderRadius:6,border:'1px solid var(--border2)',background:'transparent',color:'var(--accent)',fontSize:11,fontWeight:600,cursor:'pointer'}}>
                      ✕ Clear filter
                    </button>
                  </div>
                )}
                {/* ── Filter bar ── */}
                <div style={{background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,padding:'14px 16px',marginBottom:12,boxShadow:'var(--cardshadow)'}}>
                  {/* Row 1: Brand pills */}
                  <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:10,alignItems:'center'}}>
                    <span style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.5,marginRight:4}}>Brand</span>
                    {['all',...companies].map(c=>(
                      <button key={c} onClick={()=>setCompany(c)}
                        style={{padding:'4px 11px',borderRadius:20,border:`1.5px solid ${company===c?'var(--accent)':'var(--border)'}`,background:company===c?'var(--accent)':'transparent',color:company===c?'#fff':'var(--text1)',fontSize:11,fontWeight:600,cursor:'pointer',transition:'all 0.12s'}}>
                        {c==='all'?'All Brands':c}
                      </button>
                    ))}
                  </div>
                  {/* Row 2: Category + Audiologist + Sort + Search */}
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    {/* Category */}
                    <div style={{display:'flex',gap:4,alignItems:'center'}}>
                      <span style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.5}}>Type</span>
                      {[{v:'all',l:'All'},{v:'HA',l:'👂 HA'},{v:'Accessory',l:'🔌 Accessory'},{v:'Buyback',l:'🔄 Buyback'},{v:'Instrument',l:'🎛 Instrument'}].map(o=>(
                        <button key={o.v} onClick={()=>setCategory(o.v)}
                          style={{padding:'4px 10px',borderRadius:20,border:`1.5px solid ${category===o.v?'#0891b2':'var(--border)'}`,background:category===o.v?'#0891b2':'transparent',color:category===o.v?'#fff':'var(--text1)',fontSize:11,fontWeight:600,cursor:'pointer',transition:'all 0.12s'}}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                    {/* Audiologist filter (only when there are audis in this tab) */}
                    {tabAudiologists.length > 0 && (
                      <select value={audiFilter} onChange={e=>setAudiFilter(e.target.value)}
                        style={{...inp,width:'auto',fontSize:11,padding:'5px 10px',marginBottom:0,minWidth:140}}>
                        <option value="all">All Audiologists</option>
                        {tabAudiologists.map(a=><option key={a} value={a}>{a}</option>)}
                      </select>
                    )}
                    {/* Sort */}
                    <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
                      style={{...inp,width:'auto',fontSize:11,padding:'5px 10px',marginBottom:0,minWidth:130}}>
                      <option value="newest">↓ Newest first</option>
                      <option value="oldest">↑ Oldest first</option>
                      <option value="brand">Brand A–Z</option>
                      <option value="model">Model A–Z</option>
                      <option value="days">Days (longest first)</option>
                    </select>
                    {/* Search */}
                    <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search model, S/N, patient…"
                      style={{...inp,fontSize:11,marginBottom:0,marginLeft:'auto',minWidth:200}}/>
                    {/* Count */}
                    <span style={{fontSize:11,color:'var(--muted)',whiteSpace:'nowrap',fontWeight:600}}>{visibleItems.length} item{visibleItems.length!==1?'s':''}</span>
                  </div>
                </div>
                <StockTable rows={visibleItems}/>
              </>
            )}
          </>
        )}

        {showAdd  && <StockAddModal onClose={()=>setShowAdd(false)}  onSaved={()=>{setShowAdd(false);load();}}  user={user} defaultTab={showAdd}/>}
        {editItem && <StockAddModal onClose={()=>setEditItem(null)} onSaved={()=>{setEditItem(null);load();}} user={user} editItem={editItem}/>}
      </div>
    </div>
  );
}

function StockAddModal({ onClose, onSaved, user, editItem, defaultTab }) {
  const isEdit = !!editItem;
  const defaultStatus = defaultTab === 'moving' ? 'in_cabin' : defaultTab === 'trial' ? 'on_trial' : defaultTab === 'fitting' ? 'fitted' : defaultTab === 'sentho' ? 'sent_ho' : 'in_stock';
  const [form, setForm] = useState(isEdit ? {
    sn:           editItem.sn           || '',
    category:     editItem.category     || 'HA',
    model:        editItem.model        || '',
    company:      editItem.company      || 'SIGNIA',
    status:       editItem.status       || 'in_stock',
    inward_date:  editItem.inward_date  || '',
    outward_date: editItem.outward_date || '',
    patient_name: editItem.patient_name || '',
    audiologist:  editItem.audiologist  || '',
    outward_name: editItem.outward_name || '',
    pack_type:    editItem.pack_type    || 'OPEN',
    notes:        editItem.notes        || '',
  } : {
    sn:'', category:'HA', model:'', company:'SIGNIA',
    status: defaultStatus, inward_date:'', outward_date:'',
    patient_name:'', audiologist:'', outward_name:'', pack_type:'OPEN', notes:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const COMPANIES = ['SIGNIA','PHONAK','WIDEX','COSELGI','INTERTON','UNITRON','HORIZON','RESOUND','INSTRUMENTS','OTHER'];

  const needsPatient = ['on_trial','fitted','in_cabin','standby'].includes(form.status);

  const save = async () => {
    setError('');
    if (!form.model.trim()) { setError('Model / Product Name is required.'); return; }
    if (!form.company.trim()) { setError('Brand is required.'); return; }
    setSaving(true);
    try {
      const url = isEdit ? `${API_BASE}/stock.php?id=${editItem.id}` : `${API_BASE}/stock.php`;
      const method = isEdit ? 'PUT' : 'POST';
      const r = await apiCall(url, { method, body: JSON.stringify(form) });
      const d = await r.json();
      if (d.error) { setError(d.error); setSaving(false); return; }
      onSaved();
    } catch(e) { setError('Save failed. Please try again.'); }
    setSaving(false);
  };

  const lbl = {fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.6,marginBottom:6,display:'block'};
  const inp = {...S.inp, marginBottom:0, fontSize:13};
  const sec = {fontSize:10,fontWeight:700,color:'var(--accent)',textTransform:'uppercase',letterSpacing:1,marginBottom:12,marginTop:4,paddingBottom:6,borderBottom:'1px solid var(--border)'};

  const CATEGORY_OPTS = [
    {v:'HA',        label:'Hearing Aid (HA)',   icon:'👂'},
    {v:'Accessory', label:'Accessory',           icon:'🔌'},
    {v:'Buyback',   label:'Buyback',             icon:'🔄'},
    {v:'Instrument',label:'Instrument',          icon:'🎛'},
    {v:'Other',     label:'Other',               icon:'📦'},
  ];

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'16px'}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="fadeUp" style={{background:'var(--card)',borderRadius:24,width:'100%',maxWidth:560,boxShadow:'0 32px 100px rgba(0,0,0,0.22)',overflowY:'auto',maxHeight:'92vh'}}>

        {/* Header */}
        <div style={{padding:'24px 28px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:14}}>
          <div style={{width:42,height:42,borderRadius:12,background:'linear-gradient(135deg,var(--accent),#9333ea)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>{isEdit?'✏️':'📦'}</div>
          <div>
            <div style={{fontWeight:800,fontSize:18,color:'var(--text1)'}}>{isEdit ? `Edit: ${editItem.model}` : 'Add Device to Stock'}</div>
            <div style={{fontSize:12,color:'var(--muted)',marginTop:2}}>Shyamal Clinic · {isEdit ? `S/N: ${editItem.sn||'—'}` : 'Inventory'}</div>
          </div>
          <button onClick={onClose} style={{marginLeft:'auto',width:32,height:32,borderRadius:8,border:'none',background:'var(--bg3)',color:'var(--muted)',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
        </div>

        <div style={{padding:'24px 28px'}}>

          {/* Section 1 — Device Info */}
          <div style={sec}>Device Information</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>

            {/* Category — visual pill selector */}
            <div style={{gridColumn:'1/-1'}}>
              <span style={lbl}>Category</span>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {CATEGORY_OPTS.map(o=>(
                  <button key={o.v} onClick={()=>f('category',o.v)}
                    style={{padding:'7px 14px',borderRadius:20,border:`1.5px solid ${form.category===o.v?'var(--accent)':'var(--border)'}`,background:form.category===o.v?'var(--accent)':'transparent',color:form.category===o.v?'#fff':'var(--text2)',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all 0.15s',display:'flex',alignItems:'center',gap:5}}>
                    <span>{o.icon}</span>{o.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{gridColumn:'1/-1'}}>
              <label style={lbl}>Model / Product Name <span style={{color:'#ef4444'}}>*</span></label>
              <input value={form.model} onChange={e=>f('model',e.target.value)} style={inp}
                placeholder="e.g. Signia Pure C&G 7X, Move Pro 490…" autoFocus/>
            </div>

            <div>
              <label style={lbl}>Brand <span style={{color:'#ef4444'}}>*</span></label>
              <select value={form.company} onChange={e=>f('company',e.target.value)} style={inp}>
                {COMPANIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label style={lbl}>Pack Type</label>
              <div style={{display:'flex',gap:8}}>
                {['OPEN','PACK'].map(pt=>(
                  <button key={pt} onClick={()=>f('pack_type',pt)}
                    style={{flex:1,padding:'10px',borderRadius:10,border:`1.5px solid ${form.pack_type===pt?'var(--accent)':'var(--border)'}`,background:form.pack_type===pt?'var(--accentbg)':'transparent',color:form.pack_type===pt?'var(--accent)':'var(--text2)',fontSize:12,fontWeight:700,cursor:'pointer',transition:'all 0.15s'}}>
                    {pt}
                  </button>
                ))}
              </div>
            </div>

            <div style={{gridColumn:'1/-1'}}>
              <label style={lbl}>Serial No. <span style={{fontSize:10,color:'var(--muted)',fontWeight:400,textTransform:'none'}}>(leave blank for accessories)</span></label>
              <input value={form.sn} onChange={e=>f('sn',e.target.value)} style={{...inp,fontFamily:'monospace'}}
                placeholder="e.g. 2586129793"/>
            </div>
          </div>

          {/* Section 2 — Status & Dates */}
          <div style={sec}>Status & Dates</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
            <div style={{gridColumn:'1/-1'}}>
              <label style={lbl}>Current Status</label>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {[
                  {v:'in_stock', label:'In Clinic',    color:'#059669'},
                  {v:'on_trial', label:'H. Trial',     color:'#d97706'},
                  {v:'in_cabin', label:'In Cabin',      color:'#7c3aed'},
                  {v:'standby',  label:'Standby',      color:'#0891b2'},
                  {v:'fitted',   label:'Fitted',       color:'#1d4ed8'},
                  {v:'sent_ho',  label:'Sent to HO',   color:'#6b7280'},
                  {v:'missing',  label:'Missing',      color:'#ef4444'},
                  {v:'buyback',  label:'Buyback',      color:'#be185d'},
                ].map(s=>(
                  <button key={s.v} onClick={()=>f('status',s.v)}
                    style={{padding:'6px 14px',borderRadius:20,border:`1.5px solid ${form.status===s.v?s.color:'var(--border)'}`,background:form.status===s.v?s.color:'transparent',color:form.status===s.v?'#fff':'var(--text2)',fontSize:12,fontWeight:600,cursor:'pointer',transition:'all 0.15s'}}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={lbl}>Inward Date</label>
              <input type="date" value={form.inward_date} onChange={e=>f('inward_date',e.target.value)} style={inp}/>
            </div>
            <div>
              <label style={lbl}>Outward Date</label>
              <input type="date" value={form.outward_date} onChange={e=>f('outward_date',e.target.value)} style={inp}/>
            </div>
          </div>

          {/* Section 3 — Cabin / Patient details (conditional) */}
          {needsPatient && (
            <>
              <div style={sec}>{form.status === 'in_cabin' ? 'Cabin Assignment' : 'Patient Details'}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
                {form.status === 'in_cabin' && (
                  <div>
                    <label style={lbl}>Cabin No. <span style={{color:'#ef4444'}}>*</span></label>
                    <select value={form.outward_name} onChange={e=>f('outward_name',e.target.value)} style={inp}>
                      <option value="">Select cabin…</option>
                      {['Cabin 1','Cabin 2','Cabin 3','Cabin 4','Cabin 5','Cabin 6'].map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label style={lbl}>Audiologist <span style={{color:'#ef4444'}}>*</span></label>
                  <input value={form.audiologist} onChange={e=>f('audiologist',e.target.value)} style={inp} placeholder="e.g. Daxa Mam, Ajay Sir"/>
                </div>
                {['on_trial','fitted'].includes(form.status) && (
                  <div>
                    <label style={lbl}>Patient Name <span style={{color:'#ef4444'}}>*</span></label>
                    <input value={form.patient_name} onChange={e=>f('patient_name',e.target.value)} style={inp} placeholder="Full patient name"/>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Notes */}
          <div style={{marginBottom:20}}>
            <label style={lbl}>Notes <span style={{fontSize:10,color:'var(--muted)',fontWeight:400,textTransform:'none'}}>(optional)</span></label>
            <textarea value={form.notes} onChange={e=>f('notes',e.target.value)} rows={2}
              style={{...inp,resize:'vertical',fontFamily:'inherit'}} placeholder="Any additional notes…"/>
          </div>

          {/* Error */}
          {error && (
            <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:12,color:'#dc2626',fontWeight:600}}>
              ⚠ {error}
            </div>
          )}

          {/* Actions */}
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button onClick={onClose}
              style={{padding:'10px 22px',borderRadius:10,border:'1px solid var(--border)',background:'transparent',color:'var(--text1)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              style={{padding:'10px 26px',borderRadius:10,border:'none',background:'linear-gradient(135deg,var(--accent),#9333ea)',color:'#fff',fontSize:13,fontWeight:700,cursor:saving?'not-allowed':'pointer',opacity:saving?0.7:1,boxShadow:'0 4px 14px var(--accent)44',transition:'opacity 0.15s'}}>
              {saving ? 'Saving…' : isEdit ? '✓ Save Changes' : '+ Add to Inventory'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}


function Clinic({ user, store, onLogout, theme, toggleTheme }) {
  const [tab,setTab]=useState("home"); const [selected,setSel]=useState(null); const [lightbox,setLb]=useState(null);
  const myPts=store.patients.filter(p=>p.clinicId===user.clinic);
  const isBlr=true; // All clinics have appointments tab; check-in/checkout is gated per appointment
  const [apptBadge,setApptBadge]=useState(0);
  const [overduePopup, dismissOverdue] = useOverdueCheck(isBlr);
  useEffect(()=>{
    if(!isBlr) return;
    const today=fmtDateKey(new Date());
    apiCall(`${API_BASE}/appointments.php?start=${today}&end=${today}`)
      .then(r=>r.json())
      .then(data=>{if(Array.isArray(data)) setApptBadge(data.filter(a=>a.status==='pending').length);})
      .catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isBlr]);
  const showShyamal = THIS_MONTH_CLINICS.includes(user.clinic);
  const nav=[
    {id:"home",         icon:"⌂",  label:"Home",           active:tab==="home",         onClick:()=>setTab("home")},
    ...(isBlr?[{id:"appointments",icon:"📅",label:"Appointments",badge:apptBadge,active:tab==="appointments",onClick:()=>{setTab("appointments");setApptBadge(0);}}]:[]),
    {id:"search",       icon:"⌕",  label:"Search Patient", active:tab==="search",       onClick:()=>setTab("search")},
    {id:"add",          icon:"+",  label:"Add Patient",    active:tab==="add",          onClick:()=>setTab("add")},
    ...(showShyamal?[{id:"stock",   icon:"📦",label:"Stock",      active:tab==="stock",    onClick:()=>setTab("stock")}]:[]),
  ];
  return (
    <Shell user={user} navItems={nav} onLogout={onLogout} theme={theme} toggleTheme={toggleTheme} pageKey={tab}>
      <CmdKPalette patients={store.patients} nav={nav} onSelect={p=>{setSel(p);}} codeOnly />
      {tab==="home"         && <ClinicHome user={user} myPts={myPts} onAdd={()=>setTab("add")} onSearch={()=>setTab("search")}/>}
      {tab==="appointments" && isBlr && <AppointmentsView user={user} onPatientCreated={store.refresh}/>}
      {tab==="search"       && <SearchView store={store} onSelect={setSel} clinicId={user.clinic} user={user}/>}
      {tab==="add"          && <AddPatient user={user} store={store} onDone={()=>setTab("home")} onCancel={()=>setTab("home")}/>}
      {tab==="stock"        && showShyamal && <ShyamalStockView user={user}/>}
      {selected && <PatientModal p={selected} store={store} onClose={()=>setSel(null)} setLb={setLb} currentClinicId={user.clinic} user={user}/>}
      {lightbox  && <Lightbox {...lightbox} onClose={()=>setLb(null)}/>}
      <OverduePopup appts={overduePopup} onDismiss={dismissOverdue}/>
    </Shell>
  );
}

// ── ClinicHome: export buttons removed ────────────────────
function ClinicHome({ user, myPts, onAdd, onSearch }) {
  const acl="#7c3aed"; const c=CLINICS[user.clinic];
  const totalRec=myPts.reduce((a,p)=>a+(p.recordCount||p.records?.length||0),0);
  const today=myPts.filter(p=>new Date(p.createdAt).toDateString()===new Date().toDateString()).length;
  const thisMonth=myPts.filter(p=>{const d=new Date(p.createdAt);const n=new Date();return d.getMonth()===n.getMonth()&&d.getFullYear()===n.getFullYear();}).length;
  const quote=QUOTES[Math.floor(Date.now()/86400000)%QUOTES.length];
  const isBlr=true; // All clinics show appointment widgets on home
  const [dashAppts,setDashAppts]=useState([]);
  const [dashLoading,setDashLoading]=useState(false);
  useEffect(()=>{
    if(!isBlr) return;
    setDashLoading(true);
    const todayKey=fmtDateKey(new Date());
    // fetch today + last 14 days to catch overdue
    const past=fmtDateKey(new Date(Date.now()-14*86400000));
    apiCall(`${API_BASE}/appointments.php?start=${past}&end=${todayKey}`)
      .then(r=>r.json())
      .then(data=>{if(Array.isArray(data)) setDashAppts(data);})
      .catch(()=>{})
      .finally(()=>setDashLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isBlr]);
  const todayKey=fmtDateKey(new Date());
  const todayAppts=dashAppts.filter(a=>a.appointment_date===todayKey&&a.status==='pending');
  const overdueAppts=dashAppts.filter(a=>a.appointment_date<todayKey&&a.status==='pending');
  return (
    <div>
      {/* No export actions here — clinic staff do not have download authority */}
      <TopBar title={user.clinicLabel} sub={`${c?.city} · ${c?.region}`}
        actions={
          <button className="btn-p" onClick={onAdd} style={S.btn}>+ New Patient</button>
        }/>
      <div className="main-pad" style={{padding:"22px 26px"}}>
        <div className="grid-3" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
          {[[myPts.length,"Total Patients","👥"],[totalRec,"Records Uploaded","📋"],[today,"Registered Today","📅"],[thisMonth,"This Month","📆"]].map(([v,l,ic],i)=>(
            <div key={l} className="card-hover fadeUp" style={{animationDelay:`${i*0.07}s`,background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"18px 20px",boxShadow:"var(--cardshadow)",borderTop:`3px solid ${acl}`}}>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:8}}>{ic} {l}</div>
              <div style={{fontSize:30,fontWeight:800,color:acl}}>{v}</div>
            </div>
          ))}
        </div>
        <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:22}}>
          <button className="card-hover btn-p" onClick={onSearch} style={{background:"var(--card)",border:`2px dashed ${acl}40`,borderRadius:16,padding:"28px 24px",cursor:"pointer",textAlign:"left",boxShadow:"var(--cardshadow)"}}>
            <div style={{fontSize:28,marginBottom:10}}>🔍</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text1)",marginBottom:6}}>Search Patient</div>
            <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.5}}>Find by mobile number, case code, or name across all clinics.</div>
          </button>
          <button className="card-hover btn-p" onClick={onAdd} style={{background:`linear-gradient(135deg,${acl}18,${acl}08)`,border:`2px dashed ${acl}50`,borderRadius:16,padding:"28px 24px",cursor:"pointer",textAlign:"left",boxShadow:"var(--cardshadow)"}}>
            <div style={{fontSize:28,marginBottom:10}}>➕</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text1)",marginBottom:6}}>Register New Patient</div>
            <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.5}}>Auto-generated case code assigned instantly.</div>
          </button>
        </div>
        {isBlr&&(
          <div style={{display:"flex",flexDirection:"column",gap:14,marginBottom:22}}>
            {/* Today's Appointments */}
            <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"16px 18px",boxShadow:"var(--cardshadow)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:700,color:"var(--text1)"}}>📅 Today's Appointments</div>
                <span style={{background:"#fef3c7",color:"#92400e",padding:"2px 9px",borderRadius:99,fontSize:11,fontWeight:700}}>
                  {dashLoading?'…':todayAppts.length} pending
                </span>
              </div>
              {dashLoading?(
                <div style={{fontSize:12,color:"var(--muted)",padding:"8px 0"}}>Loading…</div>
              ):todayAppts.length===0?(
                <div style={{fontSize:12,color:"var(--muted)",padding:"8px 0"}}>No pending appointments today 🎉</div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {todayAppts.sort((a,b)=>a.appointment_time>b.appointment_time?1:-1).map(a=>(
                    <div key={a.id} className="row-hover" style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",borderRadius:8,background:"var(--bg)"}}>
                      <span style={{background:"#f59e0b",color:"#fff",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:700,flexShrink:0}}>
                        {fmtApptTime(a.appointment_time)}
                      </span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text1)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.patient_name}</div>
                        <div style={{fontSize:11,color:"var(--muted)"}}>{a.phone}{a.purpose?` · ${a.purpose}`:''}</div>
                      </div>
                      <span style={{background:a.visit_type==='home'?'#dbeafe':'#ede9fe',color:a.visit_type==='home'?'#1d4ed8':'#7c3aed',padding:"2px 6px",borderRadius:4,fontSize:10,fontWeight:600,flexShrink:0}}>
                        {a.visit_type==='home'?'🏠':'🏥'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Overdue Pending */}
            {(dashLoading||overdueAppts.length>0)&&(
              <div style={{background:"var(--card)",border:"1px solid #fecaca",borderRadius:14,padding:"16px 18px",boxShadow:"var(--cardshadow)"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--text1)"}}>⏰ Pending / Overdue</div>
                  <span style={{background:"#fee2e2",color:"#dc2626",padding:"2px 9px",borderRadius:99,fontSize:11,fontWeight:700}}>
                    {dashLoading?'…':overdueAppts.length}
                  </span>
                </div>
                {dashLoading?(
                  <div style={{fontSize:12,color:"var(--muted)",padding:"8px 0"}}>Loading…</div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {overdueAppts.sort((a,b)=>a.appointment_date<b.appointment_date?1:-1).map(a=>(
                      <div key={a.id} className="row-hover" style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",borderRadius:8,background:"#fff5f5"}}>
                        <span style={{background:"#ef4444",color:"#fff",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}>
                          {new Date(a.appointment_date+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'short'})}
                        </span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:"var(--text1)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.patient_name}</div>
                          <div style={{fontSize:11,color:"var(--muted)"}}>{a.phone}{a.purpose?` · ${a.purpose}`:''}</div>
                        </div>
                        <span style={{background:"#fee2e2",color:"#dc2626",padding:"2px 6px",borderRadius:4,fontSize:10,fontWeight:600,flexShrink:0}}>
                          {fmtApptTime(a.appointment_time)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{background:`linear-gradient(135deg,${acl}18,${acl}08)`,border:`1px solid ${acl}25`,borderRadius:14,padding:"18px 22px"}}>
          <div style={{fontSize:10,color:acl,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>Today's Thought</div>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:16,color:"var(--text1)",fontStyle:"italic",lineHeight:1.5}}>"{quote.q}"</div>
          <div style={{fontSize:11,color:"var(--muted)",marginTop:8}}>{quote.a}</div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// APPOINTMENTS (Bangalore clinics only)
// ══════════════════════════════════════════════════════════

const APPT_PURPOSES      = ['First Consultation','Follow-up','HA Trial','HA Fitting','Repair / Service','Home Demo','Other'];
const APPT_OUTCOMES      = ['PTA Done','HA Trial Done','Booked','Follow-up Scheduled','Not Interested','Other'];
const APPT_MISSED_REASONS= ['Time not confirmed','Patient rescheduled','Patient not reachable','Staff unavailable','Patient cancelled','Other'];

// Time slot dropdown — 10:00 AM to 8:00 PM in 30-min steps
const APPT_TIMES = (() => {
  const slots = [];
  for (let h = 10; h <= 20; h++) {
    for (let m = 0; m < 60; m += 30) {
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      const label = `${h12}:${String(m).padStart(2,'0')} ${ampm}`;
      const value = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
      slots.push({label, value});
    }
  }
  return slots;
})();

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function DateDropdown({ value, onChange, style }) {
  // value is "YYYY-MM-DD" or ""
  const parts = value ? value.split('-') : ['','',''];
  const [yr, setYr] = useState(parts[0]||'');
  const [mo, setMo] = useState(parts[1]||'');
  const [dy, setDy] = useState(parts[2]||'');

  const update = (y,m,d) => {
    if(y&&m&&d) onChange(`${y}-${m}-${d}`);
    else onChange('');
  };

  const now = new Date();
  const years = [now.getFullYear(), now.getFullYear()+1];
  const daysInMonth = (yr&&mo) ? new Date(parseInt(yr), parseInt(mo), 0).getDate() : 31;
  const days = Array.from({length:daysInMonth},(_,i)=>String(i+1).padStart(2,'0'));

  const sel = {...S.inp, flex:1, marginBottom:0, paddingRight:6};
  return (
    <div style={{display:'flex',gap:6,...style}}>
      <select style={sel} value={dy} onChange={e=>{setDy(e.target.value);update(yr,mo,e.target.value);}}>
        <option value="">Day</option>
        {days.map(d=><option key={d} value={d}>{parseInt(d)}</option>)}
      </select>
      <select style={sel} value={mo} onChange={e=>{setMo(e.target.value);update(yr,e.target.value,dy);}}>
        <option value="">Month</option>
        {MONTHS.map((m,i)=><option key={i} value={String(i+1).padStart(2,'0')}>{m}</option>)}
      </select>
      <select style={sel} value={yr} onChange={e=>{setYr(e.target.value);update(e.target.value,mo,dy);}}>
        <option value="">Year</option>
        {years.map(y=><option key={y} value={String(y)}>{y}</option>)}
      </select>
    </div>
  );
}

function apptColor(a) {
  if (a.status==='done') return '#22c55e';
  if (a.status==='missed') return '#ef4444';
  const now=new Date(); now.setHours(0,0,0,0);
  const d=new Date(a.appointment_date+'T00:00:00');
  const diff=Math.floor((d-now)/86400000);
  if (diff<0) return '#ef4444';   // overdue
  if (diff<=1) return '#f59e0b';  // today or tomorrow
  return 'var(--muted)';          // 3+ days
}

function getWeekMonday(date) {
  const d=new Date(date); d.setHours(0,0,0,0);
  const dow=d.getDay();
  d.setDate(d.getDate()-(dow===0?6:dow-1));
  return d;
}
function addDays(date,n){ const d=new Date(date); d.setDate(d.getDate()+n); return d; }
function fmtDateKey(d){ return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function fmtApptTime(t){
  if(!t) return '';
  const [h,m]=t.split(':');
  const hr=parseInt(h,10);
  return `${hr%12||12}:${m} ${hr>=12?'PM':'AM'}`;
}

// ══════════════════════════════════════════════════════════
// APPOINTMENTS MIS VIEW — read-only, all clinics, filter by clinic
// ══════════════════════════════════════════════════════════
function AppointmentsMISView() {
  const [date, setDate] = useState(fmtDateKey(new Date()));
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clinicFilter, setClinicFilter] = useState('all');
  const [refreshTick, setRefreshTick] = useState(0);
  const todayKey = fmtDateKey(new Date());

  useEffect(() => {
    setLoading(true);
    // Use range query (same start+end date) so carryover pending/missed are included
    apiCall(`${API_BASE}/appointments.php?start=${date}&end=${date}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setAppts(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date, refreshTick]);

  // Dropdown: show all clinics (all can view/add appointments; check-in is feature-gated)
  const activeClinics = Object.keys(CLINICS);

  const isToday = date === todayKey;
  const ownAppts = appts.filter(a => a.appointment_date === date);

  const displayed = clinicFilter === 'all'
    ? ownAppts
    : ownAppts.filter(a => a.clinic_id === clinicFilter);

  displayed.sort((a, b) => {
    if (a.clinic_id !== b.clinic_id) return a.clinic_id.localeCompare(b.clinic_id);
    return a.appointment_time.localeCompare(b.appointment_time);
  });

  const pendingCount = displayed.filter(a => a.status === 'pending').length;
  const doneCount    = displayed.filter(a => a.status === 'done').length;
  const missedCount  = displayed.filter(a => a.status === 'missed').length;

  function prevDay() { const d=new Date(date+'T00:00:00'); d.setDate(d.getDate()-1); setDate(fmtDateKey(d)); }
  function nextDay() { const d=new Date(date+'T00:00:00'); d.setDate(d.getDate()+1); setDate(fmtDateKey(d)); }

  const C = {background:'var(--card)',border:'1px solid var(--cardborder)',borderRadius:14,boxShadow:'var(--cardshadow)'};
  const displayLabel = new Date(date+'T00:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  return (
    <div>
      <TopBar title="Appointments" sub="All clinics · Read-only view"
        actions={<button onClick={()=>setRefreshTick(t=>t+1)} disabled={loading}
          style={{padding:'6px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--card)',color:'var(--text1)',fontSize:12,fontWeight:700,cursor:loading?'default':'pointer',opacity:loading?0.5:1}}>
          {loading?'⟳ Refreshing…':'⟳ Refresh'}
        </button>}/>
      <div className="main-pad" style={{padding:'22px 26px'}}>

        {/* Date nav + clinic filter */}
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18,flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,...C,padding:'8px 14px'}}>
            <button onClick={prevDay} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--accent)',lineHeight:1,padding:'0 4px'}}>‹</button>
            <span style={{fontSize:13,fontWeight:700,color:'var(--text1)',minWidth:200,textAlign:'center'}}>
              {isToday ? '📅 Today — ' : ''}{displayLabel}
            </span>
            <button onClick={nextDay} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--accent)',lineHeight:1,padding:'0 4px'}}>›</button>
            {!isToday && <button onClick={()=>setDate(todayKey)} style={{fontSize:10,fontWeight:700,color:'var(--accent)',background:'var(--accentbg)',border:'1px solid var(--accent)',borderRadius:6,padding:'2px 8px',cursor:'pointer',marginLeft:4}}>Today</button>}
          </div>

          {/* Clinic filter */}
          <select value={clinicFilter} onChange={e=>setClinicFilter(e.target.value)}
            style={{height:38,borderRadius:10,border:'1px solid var(--border)',background:'var(--card)',color:'var(--text1)',padding:'0 12px',fontSize:13,cursor:'pointer'}}>
            <option value="all">All Clinics</option>
            {activeClinics.map(c=>(
              <option key={c} value={c}>{CLINICS[c]?.label||c}</option>
            ))}
          </select>

          {/* Stats strip */}
          <div style={{display:'flex',gap:8,marginLeft:'auto'}}>
            {[{v:displayed.length,l:'Total',c:'#7c3aed'},{v:pendingCount,l:'Pending',c:'#f59e0b'},{v:doneCount,l:'Done',c:'#10b981'},{v:missedCount,l:'Missed',c:'#ef4444'}].map(s=>(
              <div key={s.l} style={{background:s.c+'18',border:`1px solid ${s.c}33`,borderRadius:10,padding:'6px 14px',textAlign:'center'}}>
                <div style={{fontSize:18,fontWeight:800,color:s.c,lineHeight:1}}>{s.v}</div>
                <div style={{fontSize:9,fontWeight:700,color:s.c,marginTop:2,textTransform:'uppercase',letterSpacing:0.5}}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Today at a glance */}
        {isToday && (() => {
          const inSession = displayed.filter(a => a.checkin_time && !a.checkout_time).length;
          const done      = displayed.filter(a => a.status === 'done').length;
          const missed    = displayed.filter(a => a.status === 'missed').length;
          const pending   = displayed.filter(a => a.status === 'pending' && !a.checkin_time).length;
          return (
            <div style={{...C, padding:'12px 20px', marginBottom:16, display:'flex', alignItems:'center', gap:16, flexWrap:'wrap'}}>
              <span style={{fontSize:13, fontWeight:700, color:'var(--text1)'}}>📅 Today at a glance</span>
              <span style={{fontSize:11, color:'var(--muted)', marginRight:4}}>{displayLabel}</span>
              {[
                {label:'Pending',  count:pending,   color:'#f59e0b'},
                {label:'In Session', count:inSession, color:'#7c3aed'},
                {label:'Done',     count:done,      color:'#10b981'},
                {label:'Missed',   count:missed,    color:'#ef4444'},
              ].map(p => (
                <div key={p.label} style={{display:'flex', alignItems:'center', gap:6, background:p.color+'18', border:`1px solid ${p.color}33`, borderRadius:20, padding:'4px 12px'}}>
                  <span style={{fontSize:16, fontWeight:800, color:p.color, lineHeight:1}}>{p.count}</span>
                  <span style={{fontSize:11, fontWeight:600, color:p.color}}>{p.label}</span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Appointments table */}
        <div style={{...C,overflow:'hidden'}}>
          <div style={{overflowY:'auto',maxHeight:'calc(100vh - 260px)'}}>
          {/* Header — sticky inside the scroll container */}
          <div style={{display:'grid',gridTemplateColumns:'1.6fr 110px 100px 130px 100px 110px 130px',gap:'0 12px',padding:'10px 18px',borderBottom:'1px solid var(--border)',background:'var(--accentbg)',position:'sticky',top:0,zIndex:2}}>
            {['Patient / Contact','Time · Type','Clinic','Purpose','Source','Status','Session'].map(h=>(
              <div key={h} style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.7}}>{h}</div>
            ))}
          </div>

          {loading && <div style={{padding:32,textAlign:'center',color:'var(--muted)'}}><span className="spin">⟳</span> Loading…</div>}
          {!loading && displayed.length===0 && (
            <div style={{padding:40,textAlign:'center',color:'var(--muted2)'}}>
              <div style={{fontSize:28,marginBottom:8}}>📅</div>
              <div style={{fontSize:14,fontWeight:600}}>No appointments for this day</div>
              {clinicFilter!=='all'&&<div style={{fontSize:12,marginTop:4}}>Try "All Clinics" to see all data</div>}
            </div>
          )}
          {!loading && displayed.map((a,i)=>{
            const statusColor = a.status==='done'?'#10b981':a.status==='missed'?'#ef4444':'#f59e0b';
            const statusBg    = a.status==='done'?'#f0fdf4':a.status==='missed'?'#fef2f2':'#fffbeb';
            const isHome      = a.visit_type==='home';
            const sessionMins = a.checkin_time && a.checkout_time
              ? Math.max(0, Math.round((new Date(a.checkout_time.replace(' ','T'))-new Date(a.checkin_time.replace(' ','T')))/60000))
              : null;
            return (
              <div key={a.id} style={{display:'grid',gridTemplateColumns:'1.6fr 110px 100px 130px 100px 110px 130px',gap:'0 12px',
                padding:'12px 18px',borderBottom:'1px solid var(--border)',
                background:i%2===0?'transparent':'var(--accentbg)',
                borderLeft:`3px solid ${statusColor}`}}>

                {/* Patient */}
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--text1)',marginBottom:2}}>{a.patient_name}</div>
                  <div style={{fontSize:11,color:'var(--muted2)'}}>
                    📞 {a.phone}
                    {a.age ? ` · ${a.age}y` : ''}
                    {a.gender ? ` · ${a.gender}` : ''}
                  </div>
                  {a.notes && (
                    <div style={{fontSize:10,color:'var(--muted)',fontStyle:'italic',marginTop:3,
                      background:'var(--accentbg)',borderRadius:5,padding:'2px 6px',display:'inline-block'}}>
                      💬 {a.notes}
                    </div>
                  )}
                  {isHome && a.home_address && (
                    <div style={{fontSize:10,color:'#6d28d9',marginTop:3}}>
                      📍 {a.home_address}{a.landmark?` · ${a.landmark}`:''}
                      {a.maps_link&&<a href={a.maps_link} target="_blank" rel="noopener noreferrer" style={{marginLeft:5,color:'#1d4ed8',fontWeight:600}}>Map ↗</a>}
                    </div>
                  )}
                </div>

                {/* Time + visit type */}
                <div style={{alignSelf:'center'}}>
                  <div style={{fontSize:13,color:'var(--text1)',fontWeight:700}}>{fmtApptTime(a.appointment_time)}</div>
                  <span style={{fontSize:9,fontWeight:700,borderRadius:4,padding:'2px 6px',marginTop:4,display:'inline-block',
                    background:isHome?'#dbeafe':'#ede9fe',color:isHome?'#1d4ed8':'#7c3aed'}}>
                    {isHome?'🏠 Home':'🏥 Clinic'}
                  </span>
                </div>

                {/* Clinic */}
                <div style={{alignSelf:'center'}}>
                  <span style={{fontSize:11,fontWeight:600,background:'var(--accentbg)',color:'var(--accent)',borderRadius:6,padding:'2px 7px'}}>
                    {CLINICS[a.clinic_id]?.label||a.clinic_id||'—'}
                  </span>
                </div>

                {/* Purpose */}
                <div style={{alignSelf:'center'}}>
                  <div style={{fontSize:12,color:'var(--text1)',fontWeight:500}}>{a.purpose||'—'}</div>
                  {a.purpose==='Other'&&a.purpose_other&&(
                    <div style={{fontSize:10,color:'var(--muted)',marginTop:2}}>{a.purpose_other}</div>
                  )}
                </div>

                {/* Source */}
                <div style={{alignSelf:'center'}}>
                  {a.source
                    ? <span style={{fontSize:10,fontWeight:700,background:'#ede9fe',color:'#7c3aed',borderRadius:6,padding:'3px 7px',display:'inline-block'}}>📌 {a.source}</span>
                    : <span style={{fontSize:10,color:'var(--muted2)'}}>—</span>
                  }
                </div>

                {/* Status + outcome */}
                <div style={{alignSelf:'center'}}>
                  <span style={{fontSize:11,fontWeight:700,borderRadius:6,padding:'3px 8px',background:statusBg,color:statusColor,display:'inline-block'}}>
                    {a.status?.toUpperCase()}
                  </span>
                  {a.outcome&&(
                    <div style={{fontSize:10,color:'var(--muted)',marginTop:3,fontWeight:500}}>{a.outcome}</div>
                  )}
                  {a.status==='missed'&&a.missed_reason&&(
                    <div style={{fontSize:10,color:'#dc2626',marginTop:3,fontWeight:600}}>↳ {a.missed_reason}</div>
                  )}
                </div>

                {/* Session check-in / check-out — only for APPT_CLINICS */}
                {(()=>{
                  if(!APPT_CLINICS.includes(a.clinic_id)) return <span style={{fontSize:10,color:'var(--muted2)'}}>—</span>;
                  const ciMap=a.checkin_lat&&a.checkin_lng?`https://www.google.com/maps?q=${a.checkin_lat},${a.checkin_lng}`:null;
                  const coMap=a.checkout_lat&&a.checkout_lng?`https://www.google.com/maps?q=${a.checkout_lat},${a.checkout_lng}`:null;
                  const lsMap=a.last_seen_lat&&a.last_seen_lng?`https://www.google.com/maps?q=${a.last_seen_lat},${a.last_seen_lng}`:null;
                  const leftMap=a.left_location_lat&&a.left_location_lng?`https://www.google.com/maps?q=${a.left_location_lat},${a.left_location_lng}`:null;
                  const resetCheckin=async()=>{
                    if(!window.confirm('Reset check-in/out for this appointment?')) return;
                    await apiCall(`${API_BASE}/appointments.php?id=${a.id}&action=reset_checkin`,{method:'PUT',body:JSON.stringify({})});
                    setRefreshTick(t=>t+1);
                  };
                  return (
                    <div style={{alignSelf:'center'}}>
                      {/* Left-location alert — highest priority */}
                      {a.left_location_at&&(
                        <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:6,padding:'4px 7px',marginBottom:4}}>
                          <div style={{fontSize:10,fontWeight:800,color:'#dc2626'}}>⚠️ Left at {fmtHHMM(a.left_location_at)}</div>
                          {leftMap&&<a href={leftMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:'#dc2626',textDecoration:'none'}}>📍 Where they went</a>}
                        </div>
                      )}
                      {sessionMins!==null?(
                        <>
                          <div style={{fontSize:11,fontWeight:700,color:'#065f46'}}>
                            🕐 {fmtHHMM(a.checkin_time)} → {fmtHHMM(a.checkout_time)}
                          </div>
                          <div style={{display:'flex',gap:5,alignItems:'center',marginTop:3,flexWrap:'wrap'}}>
                            <span style={{fontSize:10,fontWeight:700,background:'#d1fae5',color:'#065f46',borderRadius:99,padding:'1px 7px'}}>
                              {fmtDuration(sessionMins)}
                            </span>
                            {ciMap&&<a href={ciMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:'#1d4ed8',textDecoration:'none',background:'#dbeafe',padding:'1px 6px',borderRadius:99}}>📍 In</a>}
                            {coMap&&<a href={coMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:'#7c3aed',textDecoration:'none',background:'#ede9fe',padding:'1px 6px',borderRadius:99}}>📍 Out</a>}
                          </div>
                        </>
                      ):a.checkin_time?(
                        <>
                          <div style={{fontSize:11,fontWeight:700,color:'#92400e'}}>🟡 In Session</div>
                          <div style={{fontSize:10,color:'#78350f',marginTop:2}}>since {fmtHHMM(a.checkin_time)}</div>
                          <div style={{display:'flex',gap:5,marginTop:4,flexWrap:'wrap'}}>
                            {ciMap&&<a href={ciMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:'#1d4ed8',textDecoration:'none',background:'#dbeafe',padding:'1px 6px',borderRadius:99}}>📍 In</a>}
                            {lsMap&&<a href={lsMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:'#059669',textDecoration:'none',background:'#d1fae5',padding:'1px 6px',borderRadius:99}}>📍 Now · {fmtHHMM(a.last_seen_at)}</a>}
                          </div>
                        </>
                      ):(
                        <span style={{fontSize:10,color:'var(--muted2)'}}>—</span>
                      )}
                      {a.checkin_time&&(
                        <button onClick={resetCheckin}
                          style={{marginTop:5,fontSize:9,fontWeight:700,color:'#dc2626',background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:5,padding:'1px 7px',cursor:'pointer',display:'block'}}>
                          ✕ Reset
                        </button>
                      )}
                    </div>
                  );
                })()}

              </div>
            );
          })}
        </div>
        </div>{/* end scroll container */}

      </div>
    </div>
  );
}

function AppointmentsView({ user, onPatientCreated }) {
  const todayBase=new Date(); todayBase.setHours(0,0,0,0);
  const [selectedDay,setSelectedDay]=useState(todayBase);
  const [weekStart,setWeekStart]=useState(()=>getWeekMonday(todayBase));
  const [appts,setAppts]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showModal,setShowModal]=useState(false);
  const [editAppt,setEditAppt]=useState(null);

  const loadWeek=useCallback(async(ws)=>{
    setLoading(true);
    try{
      const start=fmtDateKey(ws), end=fmtDateKey(addDays(ws,6));
      const res=await apiCall(`${API_BASE}/appointments.php?start=${start}&end=${end}`);
      const data=await res.json();
      setAppts(Array.isArray(data)?data:[]);
    }catch(e){console.error(e);}
    setLoading(false);
  },[]);

  useEffect(()=>{loadWeek(weekStart);},[weekStart,loadWeek]);

  const refresh=()=>loadWeek(weekStart);
  const weekDays=Array.from({length:7},(_,i)=>addDays(weekStart,i));
  const todayKey=fmtDateKey(todayBase);
  const dayKey=fmtDateKey(selectedDay);

  const dayAppts = appts
    .filter(a => a.appointment_date === dayKey)
    .sort((a,b) => a.appointment_time.localeCompare(b.appointment_time));

  const todayCount=appts.filter(a=>a.appointment_date===todayKey).length;
  const pendingCount=appts.filter(a=>a.status==='pending').length;
  const doneWeek=appts.filter(a=>a.status==='done').length;
  const missedWeek=appts.filter(a=>a.status==='missed').length;

  const DOW_LABELS=['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div>
      <TopBar title="Appointments"
        sub={`${CLINICS[user.clinic]?.label||user.clinicLabel} · ${CLINICS[user.clinic]?.city||''}`}
        actions={
          <div style={{display:'flex',gap:8}}>
            <button onClick={refresh} disabled={loading}
              style={{padding:'6px 14px',borderRadius:8,border:'1px solid var(--border)',background:'var(--card)',color:'var(--text1)',fontSize:12,fontWeight:700,cursor:loading?'default':'pointer',opacity:loading?0.5:1}}>
              {loading?'⟳ Refreshing…':'⟳ Refresh'}
            </button>
            <button className="btn-p" onClick={()=>{setEditAppt(null);setShowModal(true);}} style={S.btn}>
              + Add Appointment
            </button>
          </div>
        }/>
      <div className="main-pad" style={{padding:"22px 26px"}}>

        {/* ── Summary cards ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:22}}>
          {[
            {label:"Today's Appointments", value:todayCount, color:"#7c3aed", icon:"📅"},
            {label:"Pending",              value:pendingCount,color:"#f59e0b", icon:"⏳"},
            {label:"Done This Week",       value:doneWeek,   color:"#22c55e", icon:"✅"},
            {label:"Missed This Week",     value:missedWeek, color:"#ef4444", icon:"❌"},
          ].map(({label,value,color,icon})=>(
            <div key={label} className="card-hover fadeUp"
              style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,
                      padding:"16px 18px",borderTop:`3px solid ${color}`,boxShadow:"var(--cardshadow)"}}>
              <div style={{fontSize:10,color:"var(--muted)",marginBottom:6}}>{icon} {label}</div>
              <div style={{fontSize:28,fontWeight:800,color}}>{value}</div>
            </div>
          ))}
        </div>

        {/* ── Week strip ── */}
        <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,
                     padding:"14px 16px",marginBottom:18,boxShadow:"var(--cardshadow)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <button onClick={()=>setWeekStart(w=>addDays(w,-7))}
              style={{background:"transparent",border:"1px solid var(--border)",borderRadius:7,
                      padding:"4px 10px",cursor:"pointer",color:"var(--muted)",fontSize:12}}>← Prev</button>
            <span style={{fontSize:12,fontWeight:600,color:"var(--text1)"}}>
              {weekStart.toLocaleDateString('en-IN',{day:'numeric',month:'short'})} –{' '}
              {addDays(weekStart,6).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
            </span>
            <button onClick={()=>setWeekStart(w=>addDays(w,7))}
              style={{background:"transparent",border:"1px solid var(--border)",borderRadius:7,
                      padding:"4px 10px",cursor:"pointer",color:"var(--muted)",fontSize:12}}>Next →</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6}}>
            {weekDays.map((d,i)=>{
              const dk=fmtDateKey(d);
              const isToday=dk===todayKey;
              const isSel=dk===dayKey;
              const cnt=appts.filter(a=>a.appointment_date===dk&&a.status==='pending').length;
              return (
                <button key={dk} onClick={()=>setSelectedDay(new Date(d))}
                  style={{background:isSel?'linear-gradient(135deg,#7c3aed,#6d28d9)':isToday?'#ede9fe':'transparent',
                          border:`1px solid ${isSel?'#7c3aed':isToday?'#c4b5fd':'var(--border)'}`,
                          borderRadius:10,padding:'8px 4px',cursor:'pointer',textAlign:'center',transition:'all 0.15s'}}>
                  <div style={{fontSize:9,fontWeight:600,color:isSel?'#fff':isToday?'#7c3aed':'var(--muted)',textTransform:'uppercase'}}>
                    {DOW_LABELS[i]}
                  </div>
                  <div style={{fontSize:16,fontWeight:700,color:isSel?'#fff':'var(--text1)',margin:'3px 0'}}>{d.getDate()}</div>
                  {cnt>0?(
                    <span style={{display:'inline-block',background:isSel?'#ffffff44':'#7c3aed',color:'#fff',
                                  borderRadius:99,padding:'1px 6px',fontSize:9,fontWeight:700}}>{cnt}</span>
                  ):<div style={{height:16}}/>}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Day header ── */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div>
            <div style={{fontSize:16,fontWeight:700,color:"var(--text1)"}}>
              {selectedDay.toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}
            </div>
            <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>
              {dayAppts.length} appointment{dayAppts.length!==1?'s':''}
            </div>
          </div>
          <button className="btn-p" onClick={()=>{setEditAppt(null);setShowModal(true);}}
            style={{...S.btn,fontSize:12,padding:"8px 14px"}}>+ Add</button>
        </div>

        {/* ── Appointment list ── */}
        {loading?(
          <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>
            <div style={{width:24,height:24,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",
                         borderRadius:"50%",animation:"spin 0.7s linear infinite",margin:"0 auto 10px"}}/>
            Loading…
          </div>
        ):dayAppts.length===0?(
          <div style={{textAlign:"center",padding:"60px 20px",color:"var(--muted)"}}>
            <div style={{fontSize:56,marginBottom:14}}>📅</div>
            <div style={{fontSize:15,fontWeight:600,color:"var(--text1)",marginBottom:6}}>No appointments for this day</div>
            <div style={{fontSize:12,color:"var(--muted)",marginBottom:20}}>Koi appointment nahi hai — add karo abhi!</div>
            <button className="btn-p" onClick={()=>{setEditAppt(null);setShowModal(true);}} style={S.btn}>
              + Add Appointment
            </button>
          </div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {dayAppts.map(a=>(
              <ApptCard key={a.id} appt={a} user={user}
                onEdit={()=>{setEditAppt(a);setShowModal(true);}}
                onRefresh={refresh}
                onPatientCreated={onPatientCreated}/>
            ))}
          </div>
        )}
      </div>

      {showModal&&(
        <ApptModal
          appt={editAppt}
          selectedDate={dayKey}
          user={user}
          weekAppts={appts}
          onClose={()=>setShowModal(false)}
          onSaved={()=>{setShowModal(false);refresh();}}/>
      )}
    </div>
  );
}

function fmtDuration(mins) {
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins/60), m = mins%60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
function fmtHHMM(dtStr) {
  if (!dtStr) return '';
  const d = new Date(dtStr.includes('T') ? dtStr : dtStr.replace(' ','T'));
  return d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
}

function ApptCard({ appt, user, onEdit, onRefresh, onPatientCreated }) {
  const [showDone,setShowDone]=useState(false);
  const [showMissed,setShowMissed]=useState(false);
  const [showReschedule,setShowReschedule]=useState(false);
  const [outcome,setOutcome]=useState('');
  const [ptStatus,setPtStatus]=useState('');
  const ptSource = appt.source || '';
  const [ptCreated,setPtCreated]=useState('');   // case code of newly created patient
  const [ptDupWarn,setPtDupWarn]=useState(false); // phone already exists
  const [ptError,setPtError]=useState('');        // creation error message
  const [reason,setReason]=useState('');
  const [reasonOther,setReasonOther]=useState('');
  const [reschedDate,setReschedDate]=useState('');
  const [reschedTime,setReschedTime]=useState('');
  const [busy,setBusy]=useState(false);
  const [ckBusy,setCkBusy]=useState(false);
  const [ckErr,setCkErr]=useState('');
  const [elapsed,setElapsed]=useState('');

  // Live elapsed timer when checked in but not checked out
  useEffect(()=>{
    if (!appt.checkin_time || appt.checkout_time) { setElapsed(''); return; }
    const tick=()=>{
      const diff=Math.floor((Date.now()-new Date(appt.checkin_time.replace(' ','T')))/60000);
      setElapsed(fmtDuration(diff));
    };
    tick();
    const id=setInterval(tick,30000);
    return ()=>clearInterval(id);
  },[appt.checkin_time,appt.checkout_time]);

  const getGPS=()=>new Promise((resolve)=>{
    if (!navigator.geolocation) return resolve({lat:null,lng:null});
    navigator.geolocation.getCurrentPosition(
      p=>resolve({lat:p.coords.latitude,lng:p.coords.longitude}),
      ()=>resolve({lat:null,lng:null}),
      {enableHighAccuracy:true,timeout:8000,maximumAge:0}
    );
  });

  const isHome=appt.visit_type==='home';

  const doCheckin=async()=>{
    setCkBusy(true); setCkErr('');
    const {lat,lng}=isHome?await getGPS():{lat:null,lng:null};
    try{
      await apiCall(`${API_BASE}/appointments.php?id=${appt.id}&action=checkin`,{
        method:'PUT',body:JSON.stringify({lat,lng})
      });
      onRefresh();
    }catch(e){ setCkErr('Check-in failed. Try again.'); }
    setCkBusy(false);
  };

  const doCheckout=async()=>{
    setCkBusy(true); setCkErr('');
    const {lat,lng}=isHome?await getGPS():{lat:null,lng:null};
    try{
      await apiCall(`${API_BASE}/appointments.php?id=${appt.id}&action=checkout`,{
        method:'PUT',body:JSON.stringify({lat,lng})
      });
      onRefresh();
    }catch(e){ setCkErr('Check-out failed. Try again.'); }
    setCkBusy(false);
  };

  const color=apptColor(appt);
  const isOverdue=appt.status==='pending'&&color==='#ef4444';
  const ckDurationMins = appt.checkin_time && appt.checkout_time
    ? Math.max(0, Math.round((new Date(appt.checkout_time.replace(' ','T'))-new Date(appt.checkin_time.replace(' ','T')))/60000))
    : null;

  const markDone=async()=>{
    if(!outcome||!ptStatus) return;
    setBusy(true);
    setPtCreated('');setPtDupWarn(false);setPtError('');
    try{
      // 1. Mark appointment as done (skip if already done)
      if(appt.status!=='done'){
        await apiCall(`${API_BASE}/appointments.php?id=${appt.id}`,{
          method:'PUT',body:JSON.stringify({status:'done',outcome}),
        });
      }
      // 2. Check for duplicate phone (global — across all clinics)
      const dupRes=await apiCall(`${API_BASE}/patients.php?phone=${encodeURIComponent(appt.phone)}`);
      const dupData=await dupRes.json();
      const exists=Array.isArray(dupData)&&dupData.length>0;
      if(exists){
        setPtDupWarn(true);
        setBusy(false);
        onPatientCreated?.(); // refresh store so existing patient is visible in search
        setTimeout(()=>{setShowDone(false);onRefresh();},3000);
        return;
      }
      // 3. Create new patient
      const cid=user?.clinic||appt.clinic_id||'';
      const code=genCode(cid);
      const ptRes=await apiCall(`${API_BASE}/patients.php`,{
        method:'POST',
        body:JSON.stringify({
          id:code,
          name:appt.patient_name,
          phone:appt.phone,
          age:appt.age||'',
          gender:appt.gender||'',
          source:ptSource,
          patientStatus:ptStatus,
          clinicId:cid,
        }),
      });
      const ptData=await ptRes.json();
      setBusy(false);
      if(ptData.error){
        // Patient creation failed — keep panel open so user can retry; don't auto-close
        setPtError(ptData.message||ptData.error||'Patient creation failed. Please try again.');
        onRefresh(); // refresh appointments only
      }else{
        setPtCreated(code);
        onPatientCreated?.(); // refresh patient store so search finds new patient immediately
        setTimeout(()=>{setShowDone(false);onRefresh();},3000);
      }
    }catch(e){
      console.error(e);
      setBusy(false);
      // Network/parse error — keep panel open so user knows something went wrong
      setPtError('Connection error. Appointment is marked done but patient may not have been created. Please refresh and check.');
    }
  };

  const markMissed=async()=>{
    const r=reason==='Other'?reasonOther.trim():reason;
    if(!r) return;
    setBusy(true);
    try{
      await apiCall(`${API_BASE}/appointments.php?id=${appt.id}`,{
        method:'PUT',body:JSON.stringify({status:'missed',missed_reason:r}),
      });
      onRefresh();
    }catch(e){console.error(e);}
    setBusy(false);setShowMissed(false);
  };

  const doDelete=async()=>{
    if(!window.confirm('Delete this appointment?')) return;
    try{
      await apiCall(`${API_BASE}/appointments.php?id=${appt.id}`,{method:'DELETE'});
      onRefresh();
    }catch(e){console.error(e);}
  };

  const doReschedule=async()=>{
    if(!reschedDate||!reschedTime) return;
    setBusy(true);
    try{
      await apiCall(`${API_BASE}/appointments.php?id=${appt.id}`,{
        method:'PUT',
        body:JSON.stringify({status:'pending',appointment_date:reschedDate,appointment_time:reschedTime}),
      });
      onRefresh();
    }catch(e){console.error(e);}
    setBusy(false);setShowReschedule(false);
  };

  return (
    <div className="card-hover fadeUp"
      style={{background:"var(--card)",border:"1px solid var(--cardborder)",
              borderLeft:`4px solid ${color}`,borderRadius:"0 14px 14px 0",
              padding:"14px 16px",
              boxShadow:isOverdue?`0 2px 16px ${color}22`:"var(--cardshadow)"}}>

      {/* Row 1: name + badges */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:6}}>
        <div style={{fontWeight:700,fontSize:14,color:"var(--text1)"}}>{appt.patient_name}</div>
        <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
          {!user?.clinic&&appt.clinic_id&&(
            <span style={{background:"#f3e8ff",color:"#7c3aed",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:700}}>
              {CLINICS[appt.clinic_id]?.label||appt.clinic_id}
            </span>
          )}
          <span style={{background:color,color:"#fff",padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700}}>
            {fmtApptTime(appt.appointment_time)}
          </span>
          <span style={{
            background:appt.visit_type==='home'?'#dbeafe':'#ede9fe',
            color:appt.visit_type==='home'?'#1d4ed8':'#7c3aed',
            padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700}}>
            {appt.visit_type==='home'?'🏠 Home':'🏥 Clinic'}
          </span>
        </div>
      </div>

      {/* Row 2: phone + purpose */}
      <div style={{fontSize:12,color:"var(--muted)",marginBottom:appt.visit_type==='home'?4:6}}>
        📱 {appt.phone}
        {appt.age?` · ${appt.age}y`:''}
        {appt.gender?` · ${appt.gender}`:''}
        &nbsp;·&nbsp;
        {appt.purpose}{appt.purpose==='Other'&&appt.purpose_other?` — ${appt.purpose_other}`:''}
        {appt.source&&<span style={{marginLeft:6,background:"#ede9fe",color:"#7c3aed",padding:"1px 7px",borderRadius:5,fontSize:10,fontWeight:700}}>📌 {appt.source}</span>}
      </div>

      {/* Row 3: address (home visit) */}
      {appt.visit_type==='home'&&appt.home_address&&(
        <div style={{fontSize:11,color:"var(--muted)",marginBottom:6}}>
          📍 {appt.home_address}{appt.landmark?` · Near ${appt.landmark}`:''}
          {appt.maps_link&&(
            <a href={appt.maps_link} target="_blank" rel="noopener noreferrer"
              style={{marginLeft:6,color:"#1d4ed8",fontSize:10,fontWeight:600}}>🗺 Map</a>
          )}
        </div>
      )}

      {/* Missed reason — visible to office/regional/mis */}
      {appt.status==='missed'&&appt.missed_reason&&(
        <div style={{fontSize:11,fontWeight:700,color:"#dc2626",background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:6,padding:"4px 10px",marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
          ❌ Missed — {appt.missed_reason}
        </div>
      )}

      {/* Notes */}
      {appt.notes&&(
        <div style={{fontSize:11,color:"var(--muted2)",fontStyle:"italic",marginBottom:8,
                     background:"var(--accentbg)",borderRadius:6,padding:"4px 8px"}}>
          {appt.notes}
        </div>
      )}

      {/* ── Check-in / Check-out strip — only for APPT_CLINICS ── */}
      {APPT_CLINICS.includes(appt.clinic_id)&&(()=>{
        const ci=appt.checkin_time, co=appt.checkout_time;
        const isHome=appt.visit_type==='home';
        const mapLink=(lat,lng)=>lat&&lng?`https://www.google.com/maps?q=${lat},${lng}`:null;
        const ciMap=isHome?mapLink(appt.checkin_lat,appt.checkin_lng):null;
        const coMap=isHome?mapLink(appt.checkout_lat,appt.checkout_lng):null;
        if(co){
          const overHours = ckDurationMins >= 120;
          return (
            <div style={{marginTop:8,background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"7px 10px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:10,fontWeight:700,color:"#15803d"}}>🕐 Session</span>
              <span style={{fontSize:11,color:"#065f46"}}>{fmtHHMM(ci)} → {fmtHHMM(co)}</span>
              <span style={{fontSize:10,fontWeight:700,background:overHours?"#fef3c7":"#d1fae5",color:overHours?"#92400e":"#065f46",padding:"1px 7px",borderRadius:99}}>{fmtDuration(ckDurationMins)}</span>
              {ciMap&&<a href={ciMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:"#1d4ed8",textDecoration:"none",background:"#dbeafe",padding:"1px 7px",borderRadius:99}}>📍 In</a>}
              {coMap&&<a href={coMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:"#7c3aed",textDecoration:"none",background:"#ede9fe",padding:"1px 7px",borderRadius:99}}>📍 Out</a>}
            </div>
          );
        }
        if(ci){
          return (
            <div style={{marginTop:8,background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:8,padding:"7px 10px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:10,fontWeight:700,color:"#92400e"}}>🟡 In Session</span>
              <span style={{fontSize:11,color:"#78350f"}}>since {fmtHHMM(ci)}</span>
              {elapsed&&<span style={{fontSize:10,fontWeight:700,background:"#fef3c7",color:"#92400e",padding:"1px 7px",borderRadius:99}}>⏱ {elapsed}</span>}
              {ciMap&&<a href={ciMap} target="_blank" rel="noopener noreferrer" style={{fontSize:10,fontWeight:700,color:"#1d4ed8",textDecoration:"none",background:"#dbeafe",padding:"1px 7px",borderRadius:99}}>📍 Location</a>}
              <button onClick={doCheckout} disabled={ckBusy}
                style={{marginLeft:"auto",padding:"4px 12px",borderRadius:6,border:"1px solid #f59e0b",background:ckBusy?"#fef3c7":"#f59e0b",color:"#fff",fontSize:11,fontWeight:700,cursor:ckBusy?"default":"pointer"}}>
                {ckBusy?"…":"✓ Check Out"}
              </button>
              {ckErr&&<span style={{fontSize:10,color:"#dc2626"}}>{ckErr}</span>}
            </div>
          );
        }
        return (
          <div style={{marginTop:8,display:"flex",alignItems:"center",gap:6}}>
            <button onClick={doCheckin} disabled={ckBusy}
              style={{padding:"4px 14px",borderRadius:6,border:"1px solid #6ee7b7",background:ckBusy?"#d1fae5":"#10b981",color:"#fff",fontSize:11,fontWeight:700,cursor:ckBusy?"default":"pointer"}}>
              {ckBusy?"Checking in…":"✓ Check In"}
            </button>
            {ckErr&&<span style={{fontSize:10,color:"#dc2626"}}>{ckErr}</span>}
          </div>
        );
      })()}

      {/* ── Actions: PENDING ── */}
      {appt.status==='pending'&&!showDone&&!showMissed&&(
        <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
          <button onClick={()=>setShowDone(true)} style={{padding:"5px 12px",borderRadius:7,border:"1px solid #bbf7d0",background:"#f0fdf4",color:"#15803d",fontSize:11,fontWeight:700,cursor:"pointer"}}>✅ Mark Done</button>
          <button onClick={()=>setShowMissed(true)} style={{padding:"5px 12px",borderRadius:7,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontSize:11,fontWeight:700,cursor:"pointer"}}>❌ Missed</button>
          <button onClick={onEdit} style={{padding:"5px 10px",borderRadius:7,border:"1px solid var(--border)",background:"transparent",color:"var(--muted)",fontSize:11,cursor:"pointer"}}>✏️ Edit</button>
          <button onClick={doDelete} style={{padding:"5px 8px",borderRadius:7,border:"none",background:"transparent",color:"#dc2626",fontSize:12,cursor:"pointer",opacity:0.5}}>🗑</button>
        </div>
      )}

      {/* Mark Done inline */}
      {appt.status==='pending'&&showDone&&(
        <div style={{marginTop:10,background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:10,padding:"12px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--text1)",marginBottom:8}}>Select Outcome:</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
            {APPT_OUTCOMES.map(o=>(
              <button key={o} onClick={()=>setOutcome(o)}
                style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${outcome===o?'#22c55e':'var(--border)'}`,
                        background:outcome===o?'#f0fdf4':'transparent',
                        color:outcome===o?'#15803d':'var(--muted)',
                        fontSize:11,cursor:"pointer",fontWeight:outcome===o?700:400}}>
                {o}
              </button>
            ))}
          </div>
          {outcome&&(
            <>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text1)",marginBottom:6}}>New Patient Details:</div>
              <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                <select style={{...S.inp,flex:1,minWidth:120,fontSize:11}} value={ptStatus} onChange={e=>setPtStatus(e.target.value)}>
                  <option value="">Patient Status…</option>
                  {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
                {ptSource&&<div style={{flex:1,minWidth:130,fontSize:11,padding:"6px 10px",background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:8,color:"var(--muted)"}}>📌 {ptSource}</div>}
              </div>
            </>
          )}
          {ptDupWarn&&(
            <div style={{fontSize:11,color:'#92400e',background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:6,padding:'5px 9px',marginBottom:8}}>
              ⚠️ Patient already exists with this phone — appointment marked done, no new patient created.
            </div>
          )}
          {ptCreated&&(
            <div style={{fontSize:11,color:'#065f46',background:'#d1fae5',border:'1px solid #6ee7b7',borderRadius:6,padding:'5px 9px',marginBottom:8}}>
              ✅ Patient created — <strong>{ptCreated}</strong>
            </div>
          )}
          {ptError&&(
            <div style={{fontSize:11,color:'#991b1b',background:'#fee2e2',border:'1px solid #fca5a5',borderRadius:6,padding:'5px 9px',marginBottom:8}}>
              ❌ {ptError}
            </div>
          )}
          <div style={{display:"flex",gap:6}}>
            <button onClick={markDone} disabled={!outcome||!ptStatus||busy}
              style={{padding:"6px 14px",borderRadius:7,border:"none",
                      background:(!outcome||!ptStatus||busy)?'#d1fae5':'#22c55e',color:"#fff",
                      fontSize:11,fontWeight:700,cursor:(!outcome||!ptStatus||busy)?"default":"pointer"}}>
              {busy?"Saving…":"✅ Confirm Done"}
            </button>
            <button onClick={()=>{setShowDone(false);setOutcome('');setPtStatus('');setPtCreated('');setPtDupWarn(false);setPtError('');}}
              style={{padding:"6px 10px",borderRadius:7,border:"1px solid var(--border)",background:"transparent",color:"var(--muted)",fontSize:11,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mark Missed inline */}
      {appt.status==='pending'&&showMissed&&(
        <div style={{marginTop:10,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,padding:"12px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--text1)",marginBottom:8}}>Reason:</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
            {APPT_MISSED_REASONS.map(r=>(
              <button key={r} onClick={()=>setReason(r)}
                style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${reason===r?'#ef4444':'#fecaca'}`,
                        background:reason===r?'#fee2e2':'transparent',
                        color:'#dc2626',fontSize:11,cursor:"pointer",fontWeight:reason===r?700:400}}>
                {r}
              </button>
            ))}
          </div>
          {reason==='Other'&&(
            <input style={{...S.inp,marginBottom:8,fontSize:11}} placeholder="Specify reason…"
              value={reasonOther} onChange={e=>setReasonOther(e.target.value)}/>
          )}
          <div style={{display:"flex",gap:6}}>
            <button onClick={markMissed} disabled={!reason||busy}
              style={{padding:"6px 14px",borderRadius:7,border:"none",
                      background:!reason||busy?'#fecaca':'#ef4444',color:"#fff",
                      fontSize:11,fontWeight:700,cursor:!reason||busy?"default":"pointer"}}>
              {busy?"Saving…":"Confirm Missed"}
            </button>
            <button onClick={()=>{setShowMissed(false);setReason('');setReasonOther('');}}
              style={{padding:"6px 10px",borderRadius:7,border:"1px solid #fecaca",background:"transparent",color:"#dc2626",fontSize:11,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* DONE state */}
      {appt.status==='done'&&!showDone&&(
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8,flexWrap:"wrap"}}>
          <span style={{background:"#d1fae5",color:"#065f46",padding:"3px 10px",borderRadius:6,fontSize:11,fontWeight:700}}>✅ Done</span>
          {appt.outcome&&<span style={{fontSize:11,color:"var(--muted)"}}>— {appt.outcome}</span>}
          {ckDurationMins!==null&&<span style={{background:"#ede9fe",color:"#7c3aed",padding:"2px 8px",borderRadius:6,fontSize:10,fontWeight:700}}>🕐 {fmtDuration(ckDurationMins)}</span>}
          <button onClick={async()=>{
            // Check if patient already exists before showing form
            const r=await apiCall(`${API_BASE}/patients.php?phone=${encodeURIComponent(appt.phone)}`);
            const d=await r.json();
            if(Array.isArray(d)&&d.length>0){
              alert(`Patient already registered as ${d[0].name} (${d[0].id})`);
            } else {
              setOutcome(appt.outcome||''); setPtStatus(''); setPtCreated(''); setPtDupWarn(false); setPtError('');
              setShowDone(true);
            }
          }} style={{padding:"3px 10px",borderRadius:6,border:"1px solid #6ee7b7",background:"#ecfdf5",color:"#059669",fontSize:11,fontWeight:700,cursor:"pointer"}}>+ Create Patient</button>
          <button onClick={onEdit} style={{marginLeft:"auto",background:"transparent",border:"none",color:"var(--muted)",fontSize:11,cursor:"pointer",textDecoration:"underline"}}>Edit</button>
          <button onClick={doDelete} style={{padding:"3px 8px",borderRadius:7,border:"none",background:"transparent",color:"#dc2626",fontSize:12,cursor:"pointer",opacity:0.5}}>🗑</button>
        </div>
      )}

      {/* MISSED state */}
      {appt.status==='missed'&&!showReschedule&&(
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8,flexWrap:"wrap"}}>
          <span style={{background:"#fee2e2",color:"#dc2626",padding:"3px 10px",borderRadius:6,fontSize:11,fontWeight:700}}>❌ Missed</span>
          {appt.missed_reason&&<span style={{fontSize:11,color:"var(--muted)"}}>— {appt.missed_reason}</span>}
          <button onClick={()=>{setReschedDate('');setReschedTime('');setShowReschedule(true);}} style={{marginLeft:"auto",padding:"3px 10px",borderRadius:6,border:"1px solid #c4b5fd",background:"#ede9fe",color:"#7c3aed",fontSize:11,fontWeight:700,cursor:"pointer"}}>📅 Reschedule</button>
          <button onClick={doDelete} style={{padding:"3px 8px",borderRadius:7,border:"none",background:"transparent",color:"#dc2626",fontSize:12,cursor:"pointer",opacity:0.5}}>🗑</button>
        </div>
      )}
      {appt.status==='missed'&&showReschedule&&(
        <div style={{marginTop:10,background:"#ede9fe",border:"1px solid #c4b5fd",borderRadius:10,padding:"12px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"var(--text1)",marginBottom:8}}>Pick new date &amp; time:</div>
          <DateDropdown value={reschedDate} onChange={setReschedDate} style={{marginBottom:8}}/>
          <select style={{...S.inp,marginBottom:10,fontSize:12}} value={reschedTime} onChange={e=>setReschedTime(e.target.value)}>
            <option value="">Select time…</option>
            {APPT_TIMES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div style={{display:"flex",gap:6}}>
            <button onClick={doReschedule} disabled={!reschedDate||!reschedTime||busy}
              style={{padding:"6px 14px",borderRadius:7,border:"none",
                      background:(!reschedDate||!reschedTime||busy)?'#c4b5fd':'#7c3aed',color:"#fff",
                      fontSize:11,fontWeight:700,cursor:(!reschedDate||!reschedTime||busy)?"default":"pointer"}}>
              {busy?"Saving…":"✅ Confirm Reschedule"}
            </button>
            <button onClick={()=>setShowReschedule(false)}
              style={{padding:"6px 10px",borderRadius:7,border:"1px solid #c4b5fd",background:"transparent",color:"#7c3aed",fontSize:11,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ApptModal({ appt, selectedDate, user, weekAppts = [], onClose, onSaved }) {
  const isRegional = user?.role === 'regional';
  const regionalClinics = (isRegional && user?.region) ? (REGIONS[user.region]||[]) : [];
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [dupWarn,setDupWarn]=useState(null);
  const skipDupCheckRef=useRef(false);
  const [clinicId,setClinicId]=useState(appt?.clinic_id||(regionalClinics[0]||''));
  const [name,setName]=useState(appt?.patient_name||'');
  const [phone,setPhone]=useState(appt?.phone||'');
  const [age,setAge]=useState(appt?.age||'');
  const [gender,setGender]=useState(appt?.gender||'');
  const [date,setDate]=useState(appt?.appointment_date||selectedDate||'');
  const [time,setTime]=useState(appt?.appointment_time?.slice(0,5)||'');
  const [visitType,setVisitType]=useState(appt?.visit_type||'clinic');
  const [address,setAddress]=useState(appt?.home_address||'');
  const [landmark,setLandmark]=useState(appt?.landmark||'');
  const [mapsLink,setMapsLink]=useState(appt?.maps_link||'');
  const [purpose,setPurpose]=useState(appt?.purpose||'');
  const [purposeOther,setPurposeOther]=useState(appt?.purpose_other||'');
  const [notes,setNotes]=useState(appt?.notes||'');
  const [source,setSource]=useState(appt?.source||'');

  const save=async()=>{
    if(isRegional&&!clinicId)                     {setErr('Please select a clinic.');return;}
    if(!name.trim())                              {setErr('Patient name is required.');return;}
    if(!phone.trim()||phone.replace(/\D/g,'').length<7){setErr('Valid phone number required.');return;}
    if(!date)                                     {setErr('Please select a date.');return;}
    if(!time)                                     {setErr('Please select appointment time.');return;}
    if(!purpose)                                  {setErr('Purpose is required.');return;}
    if(!source)                                   {setErr('Please select the source.');return;}
    if(visitType==='home'&&!address.trim())       {setErr('Home address is required for home visits.');return;}
    if(purpose==='Other'&&!purposeOther.trim())   {setErr('Please specify the purpose.');return;}
    // Duplicate check — only for new appointments, only if not bypassed
    if(!appt && !skipDupCheckRef.current) {
      const norm=phone.trim().replace(/\D/g,'');
      const conflict=weekAppts.find(a=>
        a.status==='pending' &&
        a.phone?.replace(/\D/g,'')===norm
      );
      if(conflict){
        setDupWarn(conflict);
        return;
      }
    }
    skipDupCheckRef.current=false;
    setBusy(true);setErr('');
    try{
      const body={
        patient_name:name.trim(),phone:phone.trim(),age:age.trim(),gender,
        appointment_date:date,appointment_time:time,visit_type:visitType,
        home_address:visitType==='home'?address.trim():'',
        landmark:visitType==='home'?landmark.trim():'',
        maps_link:visitType==='home'?mapsLink.trim():'',
        purpose,purpose_other:purpose==='Other'?purposeOther.trim():'',notes:notes.trim(),source,
        ...(isRegional&&clinicId?{clinic_id:clinicId}:{}),
      };
      let res;
      if(appt){
        res=await apiCall(`${API_BASE}/appointments.php?id=${appt.id}`,{method:'PUT',body:JSON.stringify(body)});
      }else{
        res=await apiCall(`${API_BASE}/appointments.php`,{method:'POST',body:JSON.stringify(body)});
      }
      const data=await res.json();
      if(data.error){setErr(data.error);setBusy(false);return;}
      onSaved();
    }catch(e){setErr(e.message||'Save failed.');}
    setBusy(false);
  };

  const inp={...S.inp,marginBottom:12};
  const lbl={...S.label,marginBottom:4};

  return (
    <div style={{position:"fixed",inset:0,background:"#00000080",zIndex:1000,display:"flex",
                 alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:18,
                   padding:28,maxWidth:560,width:"100%",maxHeight:"90vh",overflowY:"auto",
                   boxShadow:"0 24px 64px #00000040"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{fontSize:16,fontWeight:700,color:"var(--text1)"}}>{appt?'Edit Appointment':'New Appointment'}</div>
          <button onClick={onClose} style={{background:"transparent",border:"none",fontSize:20,cursor:"pointer",color:"var(--muted)"}}>✕</button>
        </div>

        {/* ── Clinic selector (regional users only) ── */}
        {isRegional&&regionalClinics.length>0&&(
          <div style={{marginBottom:16}}>
            <label style={lbl}>Clinic *</label>
            <select style={inp} value={clinicId} onChange={e=>setClinicId(e.target.value)}>
              <option value="">Select clinic…</option>
              {regionalClinics.map(c=><option key={c} value={c}>{CLINICS[c]?.label||c}</option>)}
            </select>
          </div>
        )}

        {/* ── Patient Details ── */}
        <div style={{fontSize:9,fontWeight:700,color:"var(--muted)",letterSpacing:1.4,textTransform:"uppercase",marginBottom:12}}>Patient Details</div>
        <label style={lbl}>Patient Name *</label>
        <input style={inp} value={name} onChange={e=>setName(e.target.value)} placeholder="Full name"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:0}}>
          <div>
            <label style={lbl}>Phone *</label>
            <input style={{...inp,marginBottom:0}} value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Mobile number"/>
          </div>
          <div>
            <label style={lbl}>Age</label>
            <input style={{...inp,marginBottom:0}} value={age} onChange={e=>setAge(e.target.value)} placeholder="Age" type="text"/>
          </div>
        </div>
        <label style={{...lbl,marginTop:12}}>Gender</label>
        <div style={{display:"flex",gap:8,marginBottom:18}}>
          {['Male','Female','Other'].map(g=>(
            <button key={g} onClick={()=>setGender(g)}
              style={{padding:"6px 14px",borderRadius:7,
                      border:`1px solid ${gender===g?"#7c3aed":"var(--border)"}`,
                      background:gender===g?"var(--accentbg)":"transparent",
                      color:gender===g?"#7c3aed":"var(--muted)",
                      fontSize:12,cursor:"pointer",fontWeight:gender===g?600:400,transition:"all 0.15s"}}>
              {g}
            </button>
          ))}
        </div>

        {/* ── Appointment Details ── */}
        <div style={{fontSize:9,fontWeight:700,color:"var(--muted)",letterSpacing:1.4,textTransform:"uppercase",marginBottom:12}}>Appointment Details</div>
        {/* Date — 3 dropdowns to avoid browser native validation entirely */}
        <label style={lbl}>Date *</label>
        <DateDropdown value={date} onChange={setDate} style={{marginBottom:12}}/>
        <label style={lbl}>Time *</label>
        <select style={{...inp,marginBottom:12}} value={time} onChange={e=>setTime(e.target.value)}>
          <option value="">Select time…</option>
          {APPT_TIMES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        <label style={{...lbl,marginTop:12}}>Visit Type *</label>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          {[['clinic','🏥 Clinic Visit'],['home','🏠 Home Visit']].map(([v,l])=>(
            <button key={v} onClick={()=>setVisitType(v)}
              style={{flex:1,padding:"9px",borderRadius:9,
                      border:`1px solid ${visitType===v?"#7c3aed":"var(--border)"}`,
                      background:visitType===v?"linear-gradient(135deg,#7c3aed,#6d28d9)":"transparent",
                      color:visitType===v?"#fff":"var(--muted)",
                      fontSize:12,cursor:"pointer",fontWeight:700,transition:"all 0.2s"}}>
              {l}
            </button>
          ))}
        </div>

        {visitType==='home'&&(
          <>
            <label style={lbl}>Full Address *</label>
            <textarea style={{...inp,minHeight:60,resize:"vertical"}} value={address}
              onChange={e=>setAddress(e.target.value)} placeholder="Full home address…"/>
            <label style={lbl}>Landmark</label>
            <input style={inp} value={landmark} onChange={e=>setLandmark(e.target.value)} placeholder="Landmark (optional)"/>
            <label style={lbl}>Google Maps Link</label>
            <input style={inp} value={mapsLink} onChange={e=>setMapsLink(e.target.value)} placeholder="Paste maps URL (optional)"/>
          </>
        )}

        <label style={lbl}>Purpose *</label>
        <select style={inp} value={purpose} onChange={e=>setPurpose(e.target.value)}>
          <option value="">Select purpose…</option>
          {APPT_PURPOSES.map(p=><option key={p} value={p}>{p}</option>)}
        </select>
        {purpose==='Other'&&(
          <>
            <label style={lbl}>Specify Purpose *</label>
            <input style={inp} value={purposeOther} onChange={e=>setPurposeOther(e.target.value)} placeholder="Describe the purpose…"/>
          </>
        )}

        <label style={lbl}>Source *</label>
        <select style={inp} value={source} onChange={e=>setSource(e.target.value)}>
          <option value="">How did patient hear about us?</option>
          {SOURCE_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
        </select>

        <label style={lbl}>Notes</label>
        <textarea style={{...inp,minHeight:72,resize:"vertical"}} value={notes}
          onChange={e=>setNotes(e.target.value)} placeholder="Any additional notes…" rows={3}/>

        {dupWarn&&(
          <div style={{background:"#fef3c7",border:"1px solid #f59e0b",borderRadius:10,padding:"14px 16px",marginBottom:14}}>
            <div style={{fontWeight:700,color:"#92400e",fontSize:13,marginBottom:6}}>⚠️ Duplicate Appointment Detected</div>
            <div style={{color:"#78350f",fontSize:12,marginBottom:12}}>
              This patient already has a <strong>pending</strong> appointment on{" "}
              <strong>{dupWarn.appointment_date}</strong> at{" "}
              <strong>{fmtApptTime(dupWarn.appointment_time)}</strong> — {dupWarn.purpose}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn-p"
                onClick={()=>{skipDupCheckRef.current=true;setDupWarn(null);save();}}
                style={{...S.btn,background:"linear-gradient(135deg,#d97706,#b45309)",fontSize:12,padding:"7px 14px"}}>
                Add Anyway
              </button>
              <button onClick={()=>setDupWarn(null)}
                style={{...S.btnG,fontSize:12,padding:"7px 14px"}}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {err&&<ErrBox msg={err}/>}

        {!dupWarn&&(
          <>
            <button className="btn-p" onClick={save} disabled={busy}
              style={{...S.btn,width:"100%",justifyContent:"center",marginTop:14,opacity:busy?0.7:1}}>
              {busy?<><Spin/> Saving…</>:(appt?'Update Appointment':'Save Appointment')}
            </button>
            <div style={{textAlign:"center",marginTop:10}}>
              <button onClick={onClose}
                style={{background:"transparent",border:"none",color:"var(--muted)",fontSize:12,cursor:"pointer",textDecoration:"underline"}}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SEARCH VIEW
// ══════════════════════════════════════════════════════════
function SearchView({ store, onSelect, isMIS, clinicId, user }) {
  const [q,setQ]=useState(""); const [type,setType]=useState("phone"); const [res,setRes]=useState(null);
  const [inlineLog,setInlineLog]=useState({}); const [inlineBy,setInlineBy]=useState({}); const [logDone,setLogDone]=useState({});
  const [showSug,setShowSug]=useState(false); const [searching,setSearching]=useState(false);
  const [liveSug,setLiveSug]=useState([]); const [sugLoading,setSugLoading]=useState(false);
  const minLen = type==="phone" ? 6 : 5;
  // Debounced live search — fires server query as user types
  useEffect(()=>{
    const qt=q.trim();
    if(qt.length<minLen){setLiveSug([]);return;}
    const timer=setTimeout(async()=>{
      setSugLoading(true);
      try{
        const r=await apiCall(`${API_BASE}/patients.php?search=${encodeURIComponent(qt)}&by=${type}`);
        const d=await r.json();
        setLiveSug(Array.isArray(d)?d.slice(0,8):[]);
      }catch{setLiveSug([]);}
      setSugLoading(false);
    },350);
    return()=>clearTimeout(timer);
  },[q,type]);
  const run=async()=>{
    if(q.trim().length<minLen){setRes(null);return;}
    setLiveSug([]);setShowSug(false);setSearching(true);
    try{
      const r=await apiCall(`${API_BASE}/patients.php?search=${encodeURIComponent(q.trim())}&by=${type}`);
      const data=await r.json();
      setRes(Array.isArray(data)?data:[]);
    }catch{setRes([]);}
    setSearching(false);setInlineLog({});setInlineBy({});setLogDone({});
  };
  const submitLog=async(pid)=>{
    const note=(inlineLog[pid]||"").trim();
    const by=(inlineBy[pid]||"").trim();
    if(!note||!by)return;
    await store.addVisit(pid,{note,attendedBy:by,clinicId:store.patients.find(x=>x.id===pid)?.clinicId||clinicId},user);
    setLogDone(x=>({...x,[pid]:true}));setInlineLog(x=>({...x,[pid]:""}));setInlineBy(x=>({...x,[pid]:""}));
  };
  return (
    <div>
      <TopBar title={isMIS?"Global Search":"Search Patient"} sub="Search across all clinics"/>
      <div className="main-pad" style={{padding:"22px 26px",maxWidth:700}}>
        <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"22px",boxShadow:"var(--cardshadow)"}}>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            {[["phone","📱 Phone"],["code","🔢 Code"]].map(([t,l])=>(
              <button key={t} onClick={()=>setType(t)} style={{padding:"7px 15px",borderRadius:8,border:`1px solid ${type===t?"var(--accent)":"var(--border)"}`,background:type===t?"var(--accentbg)":"transparent",color:type===t?"var(--accent)":"var(--muted)",fontSize:12,cursor:"pointer",fontWeight:600,transition:"all 0.2s"}}>{l}</button>
            ))}
          </div>
          <div style={{position:"relative"}}>
            <div style={{display:"flex",gap:10}}>
              <input style={{...S.inp,flex:1}} value={q}
                onChange={e=>{const v=e.target.value;setQ(v);setShowSug(true);if(v.trim().length<minLen)setRes(null);}}
                onKeyDown={e=>e.key==="Enter"&&run()}
                onBlur={()=>setTimeout(()=>{setShowSug(false);setLiveSug([]);},200)}
                placeholder={type==="phone"?"Mobile number…":"Case code e.g. SHY-123456…"}/>
              <button className="btn-p" onClick={run} disabled={searching||q.trim().length<minLen} style={{...S.btn,opacity:q.trim().length<minLen?0.45:1}}>{searching?<><Spin/> …</>:"Search"}</button>
            </div>
            {showSug&&(sugLoading||liveSug.length>0)&&(
              <div style={{position:"absolute",top:"100%",left:0,right:54,zIndex:100,background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,boxShadow:"0 8px 24px #7c3aed18",marginTop:4,overflow:"hidden"}}>
                {sugLoading&&<div style={{padding:"10px 14px",fontSize:11,color:"var(--muted)",display:"flex",alignItems:"center",gap:6}}><Spin/> Searching…</div>}
                {liveSug.map(p=>(
                  <div key={p.id} className="row-hover" onMouseDown={()=>{onSelect(p);setShowSug(false);setLiveSug([]);}} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",cursor:"pointer",borderBottom:"1px solid var(--border)"}}>
                    <div style={{width:30,height:30,borderRadius:8,background:"linear-gradient(135deg,#7c3aed,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:13,flexShrink:0}}>{p.name?.[0]?.toUpperCase()}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:600,color:"var(--text1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>📱 {p.phone} · {CLINICS[p.clinicId]?.label||p.clinicId} · {p.id}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {q.trim().length>0&&q.trim().length<minLen&&<div style={{marginTop:8,fontSize:11,color:"var(--muted2)",textAlign:"center"}}>Type at least {minLen} characters to search</div>}
          <div style={{marginTop:16}}>
            {/* render-time gate — show is null whenever query < minLen, no matter what res holds */}
            {(q.trim().length<minLen||res===null)&&<div style={{color:"var(--muted2)",fontSize:12,textAlign:"center",padding:18}}>Enter at least {minLen} digits and press Search</div>}
            {q.trim().length>=minLen&&res?.length===0&&<div style={{textAlign:"center",padding:"28px 16px",color:"var(--muted)"}}>
              <div style={{fontSize:28,marginBottom:8}}>🔍</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text1)",marginBottom:4}}>No patient found</div>
              <div style={{fontSize:12,color:"var(--muted2)"}}>Try a different number or case code</div>
            </div>}
            {q.trim().length>=minLen&&res?.map(p=>{
              const c=CLINICS[p.clinicId];
              const lastV=p.visits?.length ? p.visits.reduce((l,v)=>(!l||v.ts>l.ts)?v:l,null) : null;
              const daysSince=lastV ? Math.floor((Date.now()-new Date(lastV.ts).getTime())/86400000) : null;
              const lastVLabel=daysSince===null?'No visits yet':daysSince===0?'Visited today':`Last visit ${daysSince}d ago`;
              return (
                <div key={p.id} style={{background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:12,marginBottom:10,overflow:"hidden"}}>
                  <div className="row-hover" onClick={()=>onSelect(p)} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer"}}>
                    <div style={{width:36,height:36,borderRadius:10,background:`linear-gradient(135deg,#7c3aed,#6d28d9)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:14,flexShrink:0}}>{p.name?.[0]?.toUpperCase()}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,color:"var(--text1)",fontSize:13}}>{p.name}</div>
                      <div style={{fontSize:11,color:"var(--muted)",marginTop:1}}>📱 {p.phone} · {c?.label} · {c?.city}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <Cb code={p.id} color="#7c3aed"/>
                      <div style={{fontSize:9,color:"var(--muted2)",marginTop:3}}>{n(p.recordCount||p.records?.length||0)} records · {n(p.visitCount||p.visits?.length||0)} visits</div>
                      <div style={{fontSize:9,color:daysSince===null?'var(--muted2)':daysSince>30?'#ef4444':daysSince>7?'#f59e0b':'#22c55e',marginTop:2,fontWeight:600}}>{lastVLabel}</div>
                    </div>
                  </div>
                  {!isMIS&&(
                    <div style={{borderTop:"1px solid var(--border)",padding:"10px 14px",background:"var(--card)"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:0.8,textTransform:"uppercase",marginBottom:7}}>📝 Log Today's Visit</div>
                      {logDone[p.id]?(<div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"7px 12px",fontSize:12,color:"#15803d",fontWeight:600}}>✅ Visit logged!</div>):(
                        <div style={{display:"flex",flexDirection:"column",gap:7}}>
                          <input style={{...S.inp,fontSize:12}} placeholder="👨‍⚕️ Audiologist name (required)…" value={inlineBy[p.id]||""} onChange={e=>setInlineBy(x=>({...x,[p.id]:e.target.value}))}/>
                          <div style={{display:"flex",gap:8}}>
                            <textarea style={{...S.inp,flex:1,minHeight:42,resize:"vertical",fontSize:12}} placeholder="Visit note…" value={inlineLog[p.id]||""} onChange={e=>setInlineLog(x=>({...x,[p.id]:e.target.value}))}/>
                            <button className="btn-p" onClick={()=>submitLog(p.id)} style={{...S.btn,alignSelf:"flex-end",fontSize:11,padding:"8px 14px",whiteSpace:"nowrap"}}>+ Log</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ADD PATIENT
// ══════════════════════════════════════════════════════════
function AddPatient({ user, store, onDone, onCancel, allowedClinics }) {
  const isRegional = user.role === "regional";
  const regionClinicIds = allowedClinics && allowedClinics.length > 0 ? allowedClinics : (user.clinic ? [user.clinic] : []);
  const [selectedClinicId, setSelectedClinicId] = useState(regionClinicIds[0] || user.clinic || "");
  const [f,setF]=useState({name:"",phone:"",altPhone:"",age:"",gender:"Male",address:"",source:"",doctorName:"",patientStatus:"",notes:""});
  const [phoneType,setPhoneType]=useState("ind"); // "ind" | "int"
  const [altPhoneType,setAltPhoneType]=useState("ind");
  const [showAltPhone,setShowAltPhone]=useState(false);
  const [errs,setErrs]=useState({}); const [success,setSuccess]=useState(null); const [busy,setBusy]=useState(false);
  const [dupPatient,setDupPatient]=useState(null); const [dupNote,setDupNote]=useState(""); const [dupBy,setDupBy]=useState(""); const [dupLogDone,setDupLogDone]=useState(false);
  const set=(k,v)=>{setF(x=>({...x,[k]:v}));if(k==="phone")setDupPatient(null);};
  const validate=()=>{
    const e={};
    if(!f.name.trim())e.name="Patient name is required";
    if(phoneType==="ind"){if(!/^\d{10}$/.test(f.phone))e.phone="Enter a valid 10-digit mobile number";}
    else{if(f.phone.replace(/[\d\s\+\-\(\)]/g,'').length>0||f.phone.replace(/\D/g,'').length<7)e.phone="Enter a valid international number (min 7 digits)";}
    if(!f.age||isNaN(Number(f.age))||Number(f.age)<=0)e.age="Please enter the patient's age";
    if(!f.source)e.source="Please select how the patient heard about us";
    if(f.source==="Doctor Referral"&&!f.doctorName.trim())e.doctorName="Enter the referring doctor's name";
    if(f.source==="Hospital"&&!f.doctorName.trim())e.doctorName="Enter the hospital name";
    if(!f.patientStatus)e.patientStatus="Select a patient status to continue";
    if(isRegional&&!selectedClinicId)e.clinic="Please select a clinic";
    return e;
  };
  const acl="#7c3aed";
  const effectiveClinicId = isRegional ? selectedClinicId : user.clinic;
  const [submitErr,setSubmitErr]=useState("");
  const submit=async()=>{
    const e=validate();if(Object.keys(e).length){setErrs(e);return;}
    setBusy(true);setSubmitErr("");
    try{
      // Cross-clinic server dup check
      const dupRes=await apiCall(`${API_BASE}/patients.php?phone=${encodeURIComponent(f.phone)}`);
      const dupData=await dupRes.json();
      if(Array.isArray(dupData)&&dupData.length>0){setBusy(false);setDupPatient(dupData[0]);return;}
      const p=await store.addPatient({...f,altPhone:f.altPhone||null,clinicId:effectiveClinicId},user);
      setBusy(false);setSuccess(p);
    }catch(err){setBusy(false);setSubmitErr(err.message?.includes('already registered')?err.message:"Failed to save — check your connection and try again.");}
  };
  if(success)return(
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
      <div className="fadeUp" style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:18,padding:"36px 34px",textAlign:"center",maxWidth:480,width:"100%",boxShadow:"var(--cardshadow)"}}>
        <div style={{fontSize:48,marginBottom:10}}>✅</div>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:22,color:"var(--text1)",marginBottom:6}}>Patient Registered!</div>
        <div style={{color:"var(--muted)",fontSize:12,marginBottom:16}}>Auto-generated Case Code:</div>
        <div style={{fontSize:34,fontWeight:800,color:acl,letterSpacing:5,marginBottom:6,fontFamily:"monospace"}}>{success.id}</div>
        <div style={{color:"var(--muted2)",fontSize:11,marginBottom:20}}>Share this code with the patient for all future visits.</div>
        <div style={{display:"flex",gap:10,justifyContent:"center"}}>
          <button className="btn-p" onClick={()=>setSuccess(null)} style={S.btn}>Add Another</button>
          <button onClick={onDone} style={S.btnG}>Go to Home →</button>
        </div>
      </div>
    </div>
  );
  return (
    <div>
      <TopBar title="Register New Patient" sub={user.clinicLabel}/>
      <div className="main-pad" style={{padding:"22px 26px",maxWidth:660}}>
        {dupPatient&&(
          <div className="fadeUp" style={{background:"#fff7ed",border:"2px solid #fb923c",borderRadius:14,padding:"18px 20px",marginBottom:18}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <div style={{fontSize:22}}>⚠️</div>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:"#c2410c"}}>This number is already registered</div>
                <div style={{fontSize:12,color:"#9a3412",marginTop:2}}>At <strong>{CLINICS[dupPatient.clinicId]?.label}</strong>, {CLINICS[dupPatient.clinicId]?.city}.</div>
              </div>
            </div>
            <div style={{background:"var(--card)",border:"1px solid #fed7aa",borderRadius:10,padding:"12px 15px",marginBottom:14}}>
              <div style={{fontWeight:700,fontSize:13,color:"var(--text1)",marginBottom:4}}>{dupPatient.name}</div>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:10}}>📱 {dupPatient.phone} · <Cb code={dupPatient.id} color="#7c3aed"/> · {dupPatient.recordCount||dupPatient.records?.length||0} records · {dupPatient.visitCount||dupPatient.visits?.length||0} visits</div>
              {!dupLogDone?(
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  <input style={{...S.inp,fontSize:12}} placeholder="👨‍⚕️ Audiologist name (required)…" value={dupBy} onChange={e=>setDupBy(e.target.value)}/>
                  <div style={{display:"flex",gap:8}}>
                    <textarea style={{...S.inp,flex:1,minHeight:52,resize:"vertical",fontSize:12}} placeholder="Log today's visit note…" value={dupNote} onChange={e=>setDupNote(e.target.value)}/>
                    <button className="btn-p" onClick={async()=>{if(!dupNote.trim()||!dupBy.trim())return;await store.addVisit(dupPatient.id,{note:dupNote,attendedBy:dupBy.trim(),clinicId:user.clinic||dupPatient.clinicId},user);setDupNote("");setDupBy("");setDupLogDone(true);}}
                      style={{...S.btn,alignSelf:"flex-end",whiteSpace:"nowrap",fontSize:12,padding:"9px 16px"}}>+ Log</button>
                  </div>
                </div>
              ):(<div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"9px 13px",fontSize:12,color:"#15803d",fontWeight:600}}>✅ Visit logged!</div>)}
            </div>
            <button onClick={()=>setDupPatient(null)} style={S.btnG}>← Back to Form</button>
          </div>
        )}
        <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,padding:"26px",boxShadow:"var(--cardshadow)"}}>
          <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 18px"}}>
            {/* Name */}
            <div style={{marginBottom:16}}>
              <label style={S.label}>Full Name *</label>
              <input style={S.inp} type="text" placeholder="Patient's full name" value={f.name}
                onChange={e=>{set("name",e.target.value);setErrs(r=>({...r,name:undefined}));}}/>
              {errs.name&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.name}</div>}
            </div>
            {/* Phone with IND/INT toggle */}
            <div style={{marginBottom:16}}>
              <label style={S.label}>Mobile Number *</label>
              <div style={{display:"flex",gap:0,borderRadius:9,overflow:"hidden",border:`1px solid ${errs.phone?"var(--danger)":"var(--border)"}`,background:"var(--inputbg,var(--bg))"}}>
                <div style={{display:"flex",flexShrink:0}}>
                  {["ind","int"].map(t=>(
                    <button key={t} type="button" onClick={()=>{setPhoneType(t);setErrs(r=>({...r,phone:undefined}));}} style={{padding:"0 10px",border:"none",borderRight:"1px solid var(--border)",background:phoneType===t?"#7c3aed":"transparent",color:phoneType===t?"#fff":"var(--muted)",fontSize:10,fontWeight:700,cursor:"pointer",letterSpacing:.5,textTransform:"uppercase"}}>
                      {t==="ind"?"🇮🇳 IND":"🌐 INT"}
                    </button>
                  ))}
                </div>
                <input style={{...S.inp,border:"none",borderRadius:0,flex:1,margin:0,background:"transparent"}}
                  type="tel"
                  placeholder={phoneType==="ind"?"10-digit mobile":"e.g. +1 415 555 0123"}
                  value={f.phone}
                  maxLength={phoneType==="ind"?10:20}
                  onChange={e=>{const v=phoneType==="ind"?e.target.value.replace(/\D/g,'').slice(0,10):e.target.value.slice(0,20);set("phone",v);setDupPatient(null);setErrs(r=>({...r,phone:undefined}));}}
                  onBlur={async e=>{const ph=e.target.value.trim();if(phoneType==="ind"&&/^\d{10}$/.test(ph)){try{const r=await apiCall(`${API_BASE}/patients.php?phone=${encodeURIComponent(ph)}`);const d=await r.json();if(Array.isArray(d)&&d.length>0)setDupPatient(d[0]);}catch{}}}}/>
              </div>
              {errs.phone&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.phone}</div>}
              {/* Alt phone toggle */}
              {!showAltPhone?(
                <button type="button" onClick={()=>setShowAltPhone(true)} style={{marginTop:6,background:"none",border:"none",color:"var(--accent)",fontSize:11,fontWeight:600,cursor:"pointer",padding:0}}>+ Add alternative number</button>
              ):(
                <div style={{marginTop:8,display:"flex",gap:0,borderRadius:9,overflow:"hidden",border:"1px solid var(--border)",background:"var(--inputbg,var(--bg))"}}>
                  <div style={{display:"flex",flexShrink:0}}>
                    {["ind","int"].map(t=>(
                      <button key={t} type="button" onClick={()=>setAltPhoneType(t)} style={{padding:"0 10px",border:"none",borderRight:"1px solid var(--border)",background:altPhoneType===t?"#7c3aed":"transparent",color:altPhoneType===t?"#fff":"var(--muted)",fontSize:10,fontWeight:700,cursor:"pointer",letterSpacing:.5,textTransform:"uppercase"}}>
                        {t==="ind"?"🇮🇳":"🌐"}
                      </button>
                    ))}
                  </div>
                  <input style={{...S.inp,border:"none",borderRadius:0,flex:1,margin:0,background:"transparent"}}
                    type="tel" placeholder="Alternative number (optional)"
                    value={f.altPhone}
                    maxLength={altPhoneType==="ind"?10:20}
                    onChange={e=>{const v=altPhoneType==="ind"?e.target.value.replace(/\D/g,'').slice(0,10):e.target.value.slice(0,20);set("altPhone",v);}}/>
                  <button type="button" onClick={()=>{setShowAltPhone(false);set("altPhone","");}} style={{padding:"0 10px",border:"none",borderLeft:"1px solid var(--border)",background:"transparent",color:"var(--muted)",fontSize:14,cursor:"pointer"}}>✕</button>
                </div>
              )}
            </div>
            {/* Age */}
            <div style={{marginBottom:16}}>
              <label style={S.label}>Age *</label>
              <input style={S.inp} type="number" placeholder="Age in years" value={f.age}
                onChange={e=>{set("age",e.target.value);setErrs(r=>({...r,age:undefined}));}}/>
              {errs.age&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.age}</div>}
            </div>
            {/* Address */}
            <div style={{marginBottom:16}}>
              <label style={S.label}>Address</label>
              <input style={S.inp} type="text" placeholder="Area / City" value={f.address}
                onChange={e=>set("address",e.target.value)}/>
            </div>
            <div style={{marginBottom:16}}>
              <label style={S.label}>Gender</label>
              <select style={S.inp} value={f.gender} onChange={e=>set("gender",e.target.value)}><option>Male</option><option>Female</option><option>Other</option></select>
            </div>
            <div style={{marginBottom:16,gridColumn:"1/-1"}}>
              <label style={S.label}>📣 How did the patient hear about us? *</label>
              <select style={{...S.inp,borderColor:errs.source?"var(--danger)":undefined}} value={f.source} onChange={e=>{set("source",e.target.value);if(e.target.value!=="Doctor Referral"&&e.target.value!=="Hospital")set("doctorName","");setErrs(r=>({...r,source:undefined,doctorName:undefined}));}}>
                <option value="">Select source…</option>
                {SOURCE_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              {errs.source&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.source}</div>}
            </div>
            {f.source==="Doctor Referral"&&(
              <div style={{marginBottom:16,gridColumn:"1/-1"}}>
                <label style={S.label}>👨‍⚕️ Referred by which Doctor? *</label>
                <input style={{...S.inp,borderColor:errs.doctorName?"var(--danger)":undefined}} type="text" placeholder="Enter doctor's name…" value={f.doctorName} onChange={e=>{set("doctorName",e.target.value);setErrs(r=>({...r,doctorName:undefined}));}}/>
                {errs.doctorName&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.doctorName}</div>}
              </div>
            )}
            {f.source==="Hospital"&&(
              <div style={{marginBottom:16,gridColumn:"1/-1"}}>
                <label style={S.label}>🏥 Hospital Name *</label>
                <input style={{...S.inp,borderColor:errs.doctorName?"var(--danger)":undefined}} type="text" placeholder="Enter hospital name…" value={f.doctorName} onChange={e=>{set("doctorName",e.target.value);setErrs(r=>({...r,doctorName:undefined}));}}/>
                {errs.doctorName&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.doctorName}</div>}
              </div>
            )}
            <div style={{marginBottom:16,gridColumn:"1/-1"}}>
              <label style={S.label}>🩺 Patient Status *</label>
              <select style={{...S.inp,borderColor:errs.patientStatus?"var(--danger)":undefined}} value={f.patientStatus} onChange={e=>{set("patientStatus",e.target.value);setErrs(r=>({...r,patientStatus:undefined}));}}>
                <option value="">Select status…</option>
                {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              {errs.patientStatus&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.patientStatus}</div>}
            </div>
            <div style={{marginBottom:16,gridColumn:"1/-1"}}>
              <label style={S.label}>Chief Complaint / Initial Notes</label>
              <textarea style={{...S.inp,minHeight:76,resize:"vertical"}} placeholder="Symptoms, presenting complaint…" value={f.notes} onChange={e=>set("notes",e.target.value)}/>
            </div>
            {isRegional && (
              <div style={{marginBottom:16,gridColumn:"1/-1"}}>
                <label style={S.label}>🏥 Clinic *</label>
                <select style={S.inp} value={selectedClinicId} onChange={e=>setSelectedClinicId(e.target.value)}>
                  <option value="">Select clinic…</option>
                  {regionClinicIds.map(cid=><option key={cid} value={cid}>{CLINICS[cid]?.label} — {CLINICS[cid]?.city}</option>)}
                </select>
                {errs.clinic&&<div style={{color:"var(--danger)",fontSize:10,marginTop:3}}>{errs.clinic}</div>}
              </div>
            )}
          </div>
          {submitErr&&<div style={{color:"var(--danger)",fontSize:12,marginBottom:10,fontWeight:600}}>⚠️ {submitErr}</div>}
          <div style={{display:"flex",gap:10}}>
            <button className="btn-p" onClick={submit} disabled={busy} style={{...S.btn,background:busy?"var(--border)":"linear-gradient(135deg,#7c3aed,#6d28d9)",cursor:busy?"not-allowed":"pointer"}}>
              {busy?<><Spin/> Saving…</>:"Register & Generate Code"}
            </button>
            <button onClick={onCancel} style={S.btnG}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MULTI-CHECK DROPDOWN
// ══════════════════════════════════════════════════════════
function MultiCheckDropdown({ label, options, selected, onChange, displayFn }) {
  const [open,setOpen] = useState(false);
  const ref = useRef(null);
  const allSel = selected.length === options.length;
  const disp = displayFn || (x=>x);

  useEffect(()=>{
    function onClickOutside(e){ if(ref.current&&!ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown",onClickOutside);
    return ()=>document.removeEventListener("mousedown",onClickOutside);
  },[]);

  const toggleAll = () => onChange(allSel ? [] : [...options]);
  const toggle    = (opt) => onChange(selected.includes(opt) ? selected.filter(s=>s!==opt) : [...selected,opt]);

  const displayText = allSel
    ? `All ${label}`
    : selected.length === 0
      ? `None selected`
      : selected.length === 1
        ? disp(selected[0])
        : `${selected.length} ${label.toLowerCase()} selected`;

  return (
    <div ref={ref} style={{position:"relative"}}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{...S.inp,cursor:"pointer",display:"flex",justifyContent:"space-between",
                alignItems:"center",userSelect:"none",marginBottom:0,padding:"9px 12px"}}>
        <span style={{fontSize:12,color:selected.length===0?"var(--muted)":"var(--text1)"}}>{displayText}</span>
        <span style={{fontSize:9,color:"var(--muted)",marginLeft:6}}>{open?"▲":"▼"}</span>
      </div>
      {open&&(
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,
                     background:"var(--card)",border:"1px solid var(--cardborder)",
                     borderRadius:10,boxShadow:"0 8px 24px #00000020",zIndex:300,
                     maxHeight:220,overflowY:"auto"}}>
          <label style={{display:"flex",alignItems:"center",gap:9,padding:"8px 12px",
                         borderBottom:"1px solid var(--border)",cursor:"pointer",
                         background:"var(--accentbg)",fontWeight:700}}>
            <input type="checkbox" checked={allSel} onChange={toggleAll}
              style={{accentColor:"#7c3aed",width:14,height:14}}/>
            <span style={{fontSize:12,color:"var(--text1)"}}>All {label}</span>
          </label>
          {options.map(opt=>(
            <label key={opt} style={{display:"flex",alignItems:"center",gap:9,padding:"6px 12px",
                                     cursor:"pointer",borderBottom:"1px solid var(--border)"}}>
              <input type="checkbox" checked={selected.includes(opt)} onChange={()=>toggle(opt)}
                style={{accentColor:"#7c3aed",width:14,height:14}}/>
              <span style={{fontSize:12,color:"var(--text1)"}}>{disp(opt)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// EXPORT MODAL
// ══════════════════════════════════════════════════════════
function ExportModal({ allowedClinics, onClose, presetClinic }) {
  const clinicOpts     = allowedClinics && allowedClinics.length > 0 ? allowedClinics : Object.keys(CLINICS);
  const clinicOptsFull = clinicOpts.filter(id=>CLINICS[id]);
  const todayStr       = fmtDateKey(new Date());
  const firstOfMonth   = todayStr.slice(0,8) + "01";

  const [fromDate,setFromDate]     = useState(firstOfMonth);
  const [toDate,setToDate]         = useState(todayStr);
  const [clinicFilters,setClinicFs]= useState(presetClinic && presetClinic !== "all" && clinicOptsFull.includes(presetClinic) ? [presetClinic] : [...clinicOptsFull]);
  const [statusFilters,setStatFs]  = useState([...STATUS_OPTIONS]);
  const [sourceFilters,setSrcFs]   = useState([...SOURCE_OPTIONS]);
  const [doctorFilter,setDoctorF]  = useState("");
  const [matchCount,setMatchCount] = useState(null);
  const [counting,setCounting]     = useState(false);
  const [exporting,setExporting]   = useState("");

  // Build the filters object used for both count and export API calls
  const buildFilters = () => {
    const allClinics  = clinicFilters.length === clinicOptsFull.length;
    const allStatuses = statusFilters.length === STATUS_OPTIONS.length;
    const allSources  = sourceFilters.length === SOURCE_OPTIONS.length;
    return {
      from:     fromDate,
      to:       toDate,
      clinics:  allClinics  ? [] : clinicFilters,
      statuses: allStatuses ? [] : statusFilters,
      sources:  allSources  ? [] : sourceFilters,
      doctor:   doctorFilter,
    };
  };

  // Debounced live count — hits ?mode=count (instant COUNT(*), no data payload)
  useEffect(()=>{
    setCounting(true);
    setMatchCount(null);
    const timer = setTimeout(async()=>{
      try{
        const f = buildFilters();
        const p = new URLSearchParams({ mode:'count' });
        if(f.from)             p.set('from', f.from);
        if(f.to)               p.set('to',   f.to);
        f.clinics.forEach(c  => p.append('clinics[]',  c));
        f.statuses.forEach(s => p.append('statuses[]', s));
        f.sources.forEach(s  => p.append('sources[]',  s));
        if(f.doctor?.trim())   p.set('doctor', f.doctor.trim());
        const res  = await fetch(`${API_BASE}/patients.php?${p}`,{credentials:'include'});
        const data = await res.json();
        setMatchCount(typeof data.count==='number' ? data.count : null);
      }catch{ setMatchCount(null); }
      setCounting(false);
    }, 400);
    return ()=>clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[fromDate,toDate,clinicFilters,statusFilters,sourceFilters,doctorFilter]);

  const buildFilterLabel = () => {
    const parts = [`${fromDate} to ${toDate}`];
    if(clinicFilters.length < clinicOptsFull.length) parts.push(`Clinics: ${clinicFilters.map(id=>CLINICS[id]?.label||id).join(", ")}`);
    if(statusFilters.length < STATUS_OPTIONS.length) parts.push(`Status: ${statusFilters.join(", ")}`);
    if(sourceFilters.length < SOURCE_OPTIONS.length) parts.push(`Source: ${sourceFilters.join(", ")}`);
    if(doctorFilter.trim()) parts.push(`Doctor: ${doctorFilter.trim()}`);
    return parts.join(" · ");
  };

  const doExport=async(type)=>{
    if(matchCount===0) return;
    setExporting(type);
    try{
      // Fetch ONLY the filtered patients — server does the work, not the browser
      const data = await fetchFullPatients(buildFilters());
      if(!Array.isArray(data)||!data.length) { alert("No patients matched the filters."); setExporting(""); return; }
      const label = clinicFilters.length===clinicOptsFull.length ? "All" : clinicFilters.map(id=>CLINICS[id]?.label||id).join("_");
      const fl    = buildFilterLabel();
      if(type==="excel") await exportToExcel(data,`SoundLife_${label}`,fl);
      else               await exportToPDF(data,`SoundLife Export — ${label}`,fl);
    }catch(e){alert("Export failed: "+e.message);}
    setExporting("");
  };

  const sel={...S.inp,marginBottom:0};
  return(
    <div style={{position:"fixed",inset:0,background:"#00000080",zIndex:1100,display:"flex",
                 alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:18,
                   padding:28,maxWidth:520,width:"100%",maxHeight:"90vh",overflowY:"auto",
                   boxShadow:"0 24px 64px #00000040"}}
           onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div style={{fontSize:16,fontWeight:700,color:"var(--text1)"}}>Export Patients</div>
          <button onClick={onClose} style={{background:"transparent",border:"none",fontSize:20,cursor:"pointer",color:"var(--muted)"}}>✕</button>
        </div>

        {/* Date range */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div>
            <label style={{...S.label,marginBottom:4}}>From Date</label>
            <input type="date" style={sel} value={fromDate} onChange={e=>setFromDate(e.target.value)}/>
          </div>
          <div>
            <label style={{...S.label,marginBottom:4}}>To Date</label>
            <input type="date" style={sel} value={toDate} onChange={e=>setToDate(e.target.value)}/>
          </div>
        </div>

        {/* Clinic multi-select */}
        <div style={{marginBottom:12}}>
          <label style={{...S.label,marginBottom:4}}>Clinic</label>
          <MultiCheckDropdown
            label="Clinics"
            options={clinicOptsFull}
            selected={clinicFilters}
            onChange={setClinicFs}
            displayFn={id=>CLINICS[id]?.label||id}/>
        </div>

        {/* Status multi-select */}
        <div style={{marginBottom:12}}>
          <label style={{...S.label,marginBottom:4}}>Patient Status</label>
          <MultiCheckDropdown
            label="Statuses"
            options={STATUS_OPTIONS}
            selected={statusFilters}
            onChange={setStatFs}/>
        </div>

        {/* Source multi-select */}
        <div style={{marginBottom:12}}>
          <label style={{...S.label,marginBottom:4}}>Source</label>
          <MultiCheckDropdown
            label="Sources"
            options={SOURCE_OPTIONS}
            selected={sourceFilters}
            onChange={setSrcFs}/>
        </div>

        {/* Doctor name filter */}
        <div style={{marginBottom:20}}>
          <label style={{...S.label,marginBottom:4}}>Doctor Name</label>
          <input style={sel} placeholder="Filter by doctor name…" value={doctorFilter} onChange={e=>setDoctorF(e.target.value)}/>
        </div>

        {/* Match count */}
        <div style={{background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:9,
                     padding:"10px 14px",marginBottom:18,textAlign:"center"}}>
          {counting ? (
            <span style={{color:"var(--muted)",fontSize:13}}><Spin/> Counting…</span>
          ) : matchCount===null ? (
            <span style={{color:"var(--muted)",fontSize:13}}>—</span>
          ) : matchCount > 0 ? (
            <span style={{color:"#7c3aed",fontWeight:700,fontSize:14}}>{matchCount} patient{matchCount!==1?"s":""} match these filters</span>
          ) : (
            <span style={{color:"var(--danger)",fontWeight:600,fontSize:13}}>No patients match these filters</span>
          )}
        </div>

        {/* Export buttons */}
        <div style={{display:"flex",gap:10}}>
          <button className="btn-p" disabled={!!exporting||counting||matchCount===0}
            onClick={()=>doExport("excel")}
            style={{...S.btn,flex:1,justifyContent:"center",background:"linear-gradient(135deg,#059669,#047857)",
                    opacity:(exporting||counting||matchCount===0)?0.5:1,cursor:(matchCount===0||counting)?"not-allowed":"pointer"}}>
            {exporting==="excel"?<><Spin/> Preparing…</>:"⬇ Excel (2 sheets)"}
          </button>
          <button className="btn-p" disabled={!!exporting||counting||matchCount===0}
            onClick={()=>doExport("pdf")}
            style={{...S.btn,flex:1,justifyContent:"center",background:"linear-gradient(135deg,#dc2626,#b91c1c)",
                    opacity:(exporting||counting||matchCount===0)?0.5:1,cursor:(matchCount===0||counting)?"not-allowed":"pointer"}}>
            {exporting==="pdf"?<><Spin/> Preparing…</>:"⬇ PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ALL PATIENTS (MIS)
// ══════════════════════════════════════════════════════════
function AllPatients({ store, onSelect, allowedClinics, user }) {
  const clinicOptions = allowedClinics && allowedClinics.length > 0
    ? allowedClinics
    : Object.keys(CLINICS);
  const [filter,setFilter]=useState("all");
  const [page,setPage]=useState(1);
  const [showExportModal,setShowExportModal]=useState(false);
  const PER_PAGE=50;
  const list=filter==="all"?store.patients:store.patients.filter(p=>p.clinicId===filter);
  useEffect(()=>setPage(1),[filter]);
  const totalPages=Math.ceil(list.length/PER_PAGE);
  const paginated=[...list].slice((page-1)*PER_PAGE,page*PER_PAGE);
  return (
    <div>
      <TopBar title="All Patients" sub={`${list.length} shown`}
        actions={user?.role!=="regional"&&(
          <button className="btn-p" onClick={()=>setShowExportModal(true)}
            style={{...S.btn,background:"linear-gradient(135deg,#7c3aed,#6d28d9)",fontSize:12,padding:"8px 16px"}}>
            ⬇ Export
          </button>
        )}/>
      {/* ── Sticky filter bar ── */}
      <div style={{position:"sticky",top:57,zIndex:10,background:"var(--bg)",borderBottom:"1px solid var(--border)",padding:"10px 26px 8px",boxShadow:"0 2px 8px #0000000a"}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}><FBtn active={filter==="all"} onClick={()=>setFilter("all")}>All Clinics</FBtn></div>
        {Object.entries(REGIONS).filter(([,ids])=>ids.some(id=>clinicOptions.includes(id))).map(([region,ids])=>(
          <div key={region} style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}}>
            <span style={{fontSize:10,color:"var(--muted2)",minWidth:80,fontWeight:600}}>{region}:</span>
            {ids.filter(id=>clinicOptions.includes(id)&&CLINICS[id]).map(id=><FBtn key={id} active={filter===id} onClick={()=>setFilter(id)} color={CLINICS[id].color}>{CLINICS[id].label}</FBtn>)}
          </div>
        ))}
      </div>

      <div className="main-pad" style={{padding:"16px 26px"}}>
        <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:14,overflow:"hidden",boxShadow:"var(--cardshadow)"}}>
          <div style={{overflowY:"auto",maxHeight:"calc(100vh - 220px)"}}>
          {/* Sticky table header */}
          <div className="table-row" style={{display:"grid",gridTemplateColumns:"2fr 1.3fr 1.2fr 1.4fr 1.2fr 0.7fr 1fr",padding:"10px 18px",background:"var(--accentbg)",fontSize:9,fontWeight:700,color:"var(--muted)",letterSpacing:1.2,textTransform:"uppercase",borderBottom:"1px solid var(--border)",position:"sticky",top:0,zIndex:2}}>
            <div>Patient</div><div>Code</div><div>Phone</div><div>Clinic</div><div>Status</div><div>Recs</div><div>Date</div>
          </div>
          {list.length===0&&<Empty msg="No patients found"/>}
          {paginated.map((p,i)=>{const c=CLINICS[p.clinicId];const sc=p.patientStatus?STATUS_COLORS[p.patientStatus]:null;const isNew=new Date(p.createdAt).toDateString()===new Date().toDateString();

            const isFollowUp=p.patientStatus==='Follow Up';
            const daysSinceReg=Math.floor((Date.now()-new Date(p.createdAt))/86400000);
            const needsRevisit=isFollowUp&&daysSinceReg>=4;
            return(
            <div key={p.id} className="row-hover table-row" onClick={()=>onSelect(p)} style={{display:"grid",gridTemplateColumns:"2fr 1.3fr 1.2fr 1.4fr 1.2fr 0.7fr 1fr",padding:"11px 15px 11px 15px",borderTop:"1px solid var(--border)",cursor:"pointer",fontSize:12,alignItems:"center",borderLeft:`3px solid ${c?.color||"var(--border)"}`,animation:`rowFadeIn 0.25s ease both`,animationDelay:`${Math.min(i*0.03,0.3)}s`}}>
              <div style={{fontWeight:600,color:"var(--text1)",display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                {p.name}
                {isNew&&<span style={{background:"#dcfce7",color:"#15803d",borderRadius:4,fontSize:8,fontWeight:800,padding:"1px 5px",letterSpacing:0.3,flexShrink:0}}>NEW</span>}
                {needsRevisit&&<span title={`Follow Up — registered ${daysSinceReg}d ago, follow-up pending`} style={{background:"#fef3c7",color:"#92400e",borderRadius:4,fontSize:8,fontWeight:800,padding:"1px 5px",flexShrink:0}}>⚠ Revisit due</span>}
              </div>
              <div><Cb code={p.id} color={c?.color}/></div>
              <div style={{color:"var(--muted)"}}>{p.phone}</div>
              <div><span style={{background:c?.color+"18",color:c?.color,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:600}}>{c?.label}</span></div>
              <div>{p.patientStatus&&sc?<span style={{background:sc.bg,color:sc.color,padding:"2px 7px",borderRadius:5,fontSize:9,fontWeight:700}}>{p.patientStatus}</span>:<span style={{color:"var(--muted2)",fontSize:10}}>—</span>}</div>
              <div style={{color:"var(--muted)"}}>{n(p.recordCount||p.records?.length||0)}</div>
              <div style={{color:"var(--muted)"}}>{new Date(p.createdAt).toLocaleDateString("en-IN")}</div>
            </div>
          );})}
          </div>{/* end scroll container */}
          {totalPages>1&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"14px 18px",borderTop:"1px solid var(--border)"}}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{padding:"5px 11px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:page===1?"var(--muted2)":"var(--muted)",fontSize:10,cursor:page===1?"default":"pointer",fontWeight:600,opacity:page===1?0.5:1}}>← Prev</button>
              <span style={{fontSize:11,color:"var(--muted)"}}>Page {page} of {totalPages} · {list.length} total patients</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{padding:"5px 11px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:page===totalPages?"var(--muted2)":"var(--muted)",fontSize:10,cursor:page===totalPages?"default":"pointer",fontWeight:600,opacity:page===totalPages?0.5:1}}>Next →</button>
            </div>
          )}
        </div>
      </div>
      {showExportModal&&(
        <ExportModal allowedClinics={clinicOptions} onClose={()=>setShowExportModal(false)} presetClinic={filter==="all"?"all":filter}/>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// GALLERY
// ══════════════════════════════════════════════════════════
const GALLERY_PAGE = 24;
function Gallery({ store, setLb, allowedClinics }) {
  const visibleClinics = allowedClinics && allowedClinics.length > 0
    ? allowedClinics.filter(id => CLINICS[id])
    : Object.keys(CLINICS);
  const [cf,setCf]=useState("all");
  const [galleryData,setGalleryData]=useState([]);
  const [offset,setOffset]=useState(0);
  const [hasMore,setHasMore]=useState(true);
  const [loading,setLoading]=useState(false);
  const [fileViewer,setFileViewer]=useState(null);

  const fetchPage = (off, clinic) => {
    setLoading(true);
    const clinicParam = clinic !== "all" ? `&clinic=${encodeURIComponent(clinic)}` : "";
    fetch(`${API_BASE}/patients.php?mode=gallery&limit=${GALLERY_PAGE}&offset=${off}${clinicParam}`,{credentials:'include'})
      .then(r=>r.json())
      .then(data=>{
        if(!Array.isArray(data)) return;
        setGalleryData(prev => off===0 ? data : [...prev, ...data]);
        setHasMore(data.length === GALLERY_PAGE);
      })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(()=>{ setOffset(0); setGalleryData([]); fetchPage(0, cf); },[cf]);// eslint-disable-line

  const loadMore = () => {
    const next = offset + GALLERY_PAGE;
    setOffset(next);
    fetchPage(next, cf);
  };

  const filtered = galleryData;
  const imgs = filtered.filter(r=>r.type==="image").map(r=>({...r,patient:{name:r.patient_name,clinicId:r.clinic_id}}));
  const docs = filtered.filter(r=>r.type!=="image" && !(r.name||"").toLowerCase().endsWith(".jpg")).map(r=>({...r,patient:{name:r.patient_name,clinicId:r.clinic_id}}));

  return (
    <div>
      <TopBar title="Records Gallery" sub={`${imgs.length}${hasMore?"+":""} images · ${docs.length}${hasMore?"+":""} documents`}/>
      <div className="main-pad" style={{padding:"22px 26px"}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
          <FBtn active={cf==="all"} onClick={()=>setCf("all")}>All</FBtn>
          {visibleClinics.map(id=><FBtn key={id} active={cf===id} onClick={()=>setCf(id)} color={CLINICS[id].color}>{CLINICS[id].label}</FBtn>)}
        </div>
        {!loading&&imgs.length===0&&docs.length===0&&<Empty msg="No records uploaded yet"/>}
        {imgs.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:12,marginBottom:docs.length>0?24:0}}>
          {imgs.map(r=><RCard key={r.rid} r={r} onView={()=>setLb({src:imgSrc(r.data),name:r.name,patient:r.patient})} showClinic/>)}
        </div>}
        {docs.length>0&&(
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"var(--muted)",letterSpacing:0.5,textTransform:"uppercase",marginBottom:10}}>📄 Documents &amp; Reports</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:12}}>
              {docs.map(r=>{
                const isPdf=(r.name||"").match(/\.pdf$/i);
                const isTxt=(r.name||"").match(/\.txt$/i);
                const isWord=(r.name||"").match(/\.docx?$/i);
                const isExcel=(r.name||"").match(/\.xlsx?$/i);
                return(
                <div key={r.rid} onClick={()=>{if(isPdf)setFileViewer({url:imgSrc(r.data),name:r.name,kind:"pdf"});else if(isTxt)setFileViewer({url:imgSrc(r.data),name:r.name,kind:"txt"});}}
                  style={{borderRadius:11,overflow:"hidden",border:"1px solid var(--border)",background:"var(--card)",cursor:(isPdf||isTxt)?"pointer":"default"}}>
                  <div style={{height:100,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:28,color:"var(--muted)"}}>
                    {isPdf?"📕":isTxt?"📝":isWord?"📝":isExcel?"📊":"📄"}
                    <div style={{fontSize:10,color:"var(--muted2)",marginTop:5,textAlign:"center",padding:"0 6px"}}>{r.name}</div>
                    {(isPdf||isTxt)&&<div style={{fontSize:9,color:"#7c3aed",marginTop:2}}>Tap to view</div>}
                  </div>
                  <div style={{padding:"7px 9px",borderTop:"1px solid var(--border)"}}>
                    <div style={{fontSize:10,fontWeight:600,color:"var(--text1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
                    <div style={{fontSize:9,color:"var(--muted2)",marginTop:2}}>{r.patient?.name} · {new Date(r.ts).toLocaleDateString("en-IN")}</div>
                  </div>
                </div>
              );})}
            </div>
          </div>
        )}
        {loading&&<div style={{textAlign:"center",padding:"24px 0",color:"var(--muted)",fontSize:13}}><Spin/> Loading…</div>}
        {!loading&&hasMore&&(
          <div style={{textAlign:"center",marginTop:20}}>
            <button onClick={loadMore} style={{...S.btnG,fontSize:12,padding:"8px 24px"}}>Load more</button>
          </div>
        )}
      </div>
      {fileViewer&&<FileViewerModal {...fileViewer} onClose={()=>setFileViewer(null)}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ANALYTICS
// ══════════════════════════════════════════════════════════

// ── HoverTooltip — owns hover state so MasterAnalytics doesn't re-render on every mouse move ──
function HoverTooltip({ svgRef, pts, data, values, metric, metricKey, VW, VH, padT, chartH }) {
  const [hover, setHover] = useState(null);
  const onMouseMove = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (VW / rect.width);
    let near = 0, minD = Infinity;
    pts.forEach(([x], i) => { const d = Math.abs(x - mx); if (d < minD) { minD = d; near = i; } });
    setHover(prev => prev === near ? prev : near);
  }, [svgRef, pts, VW]);

  const tooltipX = hover !== null
    ? (pts[hover][0] > VW * 0.68 ? pts[hover][0] - 118 : pts[hover][0] + 14)
    : 0;
  const tooltipY = hover !== null
    ? (pts[hover][1] > VH * 0.65 ? pts[hover][1] - 62 : pts[hover][1] - 8)
    : 0;

  return (
    <>
      <rect x={0} y={0} width={VW} height={VH} fill="transparent"
        onMouseMove={onMouseMove} onMouseLeave={() => setHover(null)}
        style={{ cursor:"crosshair" }}/>
      {hover !== null && (
        <>
          <line x1={pts[hover][0]} y1={padT} x2={pts[hover][0]} y2={padT+chartH}
            stroke={metric.color} strokeWidth="1" strokeDasharray="4,4" opacity="0.45"/>
          <circle cx={pts[hover][0]} cy={pts[hover][1]} r="5.5"
            fill={metric.color} stroke="white" strokeWidth="2.5"/>
          <foreignObject x={tooltipX} y={tooltipY} width="112" height="58" style={{ overflow:"visible" }}>
            <div xmlns="http://www.w3.org/1999/xhtml" style={{ background:"var(--card)", border:`1.5px solid ${metric.color}55`, borderRadius:10, padding:"7px 11px", boxShadow:"0 6px 20px rgba(0,0,0,0.13)", pointerEvents:"none", whiteSpace:"nowrap" }}>
              <div style={{ fontSize:10, color:"var(--muted)", fontWeight:600 }}>{data[hover].label}</div>
              <div style={{ fontSize:18, fontWeight:800, color:metric.color, lineHeight:1.2 }}>{metricKey==="convRate" ? `${values[hover]}%` : values[hover]}</div>
              <div style={{ fontSize:9, color:"var(--muted2)" }}>{metric.label}</div>
            </div>
          </foreignObject>
        </>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════
// HEATMAP CALENDAR
// ══════════════════════════════════════════════════════════
function HeatmapCalendar({ patients }) {
  const counts = useMemo(() => {
    const map = {};
    patients.forEach(p => {
      if (!p.createdAt) return;
      const day = p.createdAt.slice(0, 10);
      map[day] = (map[day] || 0) + 1;
    });
    return map;
  }, [patients]);

  const cellColor = c => {
    if (!c) return 'var(--border)';
    if (c <= 3)  return '#c4b5fd';
    if (c <= 7)  return '#8b5cf6';
    if (c <= 14) return '#7c3aed';
    return '#5b21b6';
  };

  const months = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() - 5 + i);
      const yr = d.getFullYear();
      const mo = d.getMonth();
      const label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      const daysInMonth = new Date(yr, mo + 1, 0).getDate();
      const days = Array.from({ length: daysInMonth }, (_, d) => {
        const day = new Date(yr, mo, d + 1);
        const key = `${yr}-${String(mo+1).padStart(2,'0')}-${String(d+1).padStart(2,'0')}`;
        const dow = (day.getDay() + 6) % 7; // Mon=0, Sun=6
        return { key, d: d + 1, dow };
      });
      return { label, yr, mo, days };
    });
  }, []);

  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:20, padding:'4px 0' }}>
      {months.map(({ label, days }) => {
        const firstDow = days[0].dow;
        const cells = [];
        for (let i = 0; i < firstDow; i++) cells.push(null);
        days.forEach(d => cells.push(d));
        const cols = Math.ceil(cells.length / 7);
        return (
          <div key={label}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', marginBottom:6, textTransform:'uppercase', letterSpacing:0.6 }}>{label}</div>
            <div style={{ display:'grid', gridTemplateRows:'repeat(7,12px)', gridTemplateColumns:`repeat(${cols},12px)`, gap:2, gridAutoFlow:'column' }}>
              {cells.map((cell, idx) => (
                <div key={idx}
                  title={cell ? `${cell.key}: ${counts[cell.key] || 0} patient${(counts[cell.key]||0)!==1?'s':''}` : ''}
                  style={{ width:12, height:12, borderRadius:2, background: cell ? cellColor(counts[cell.key] || 0) : 'transparent' }}
                />
              ))}
            </div>
          </div>
        );
      })}
      <div style={{ width:'100%', display:'flex', gap:10, alignItems:'center', marginTop:4 }}>
        <span style={{ fontSize:11, color:'var(--muted)' }}>Less</span>
        {['var(--border)','#c4b5fd','#8b5cf6','#7c3aed','#5b21b6'].map(c => (
          <div key={c} style={{ width:12, height:12, borderRadius:2, background:c }} title={c}/>
        ))}
        <span style={{ fontSize:11, color:'var(--muted)' }}>More</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MASTER ANALYTICS
// ══════════════════════════════════════════════════════════
function MasterAnalytics({ store, allowedClinics }) {
  const visibleClinics = allowedClinics && allowedClinics.length > 0
    ? allowedClinics.filter(id => CLINICS[id])
    : Object.keys(CLINICS);
  const clinicOptions = [{ id:"all", label:"All Clinics" }, ...visibleClinics.map(id=>({ id, label:CLINICS[id].label }))];
  const METRICS = [
    { key:"total",       label:"Total Patients", color:"#7c3aed" },
    { key:"followup",    label:"Follow Up",      color:"#6366f1" },
    { key:"haTrial",     label:"HA Trial",       color:"#f59e0b" },
    { key:"booked",      label:"Booked",         color:"#10b981" },
    { key:"fittingDone", label:"Fitting Done",   color:"#3b82f6" },
    { key:"convRate",    label:"Conv %",         color:"#ec4899" },
  ];
  const COMPARE_METRICS = [
    { key:"total",       label:"Total Patients" },
    { key:"convRate",    label:"Conv Rate % (Booked+FD / HA Trial)" },
    { key:"bookingRate", label:"Booking Rate % (Booked)" },
    { key:"haTrialRate", label:"HA Trial Rate %" },
  ];
  const REGION_COLORS = { Gujarat:"#7c3aed", Karnataka:"#f97316", Maharashtra:"#3b82f6", Odisha:"#f59e0b" };

  // ── Existing state ────────────────────────────────────────
  const [clinic, setClinic]         = useState("all");
  const [metricKey, setMetricKey]   = useState("total");
  const pathRef = useRef(null);
  const svgRef  = useRef(null);

  // ── New state ─────────────────────────────────────────────
  const [chartTab, setChartTab]           = useState("trend");
  const [compareMetric, setCompareMetric] = useState("total");
  const [barHover, setBarHover]           = useState(null);
  const [barProg, setBarProg]             = useState(1);
  const [scope, setScope]                 = useState("all");
  const barAnimRef = useRef(null);

  const metric = METRICS.find(m => m.key === metricKey);

  // ── Existing: area chart data ─────────────────────────────
  const data = useMemo(() => {
    const patients = clinic === "all" ? store.patients : store.patients.filter(p => p.clinicId === clinic);
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 5 + i);
      const yr = d.getFullYear(), mo = d.getMonth();
      const label = d.toLocaleDateString("en-IN", { month:"short", year:"2-digit" });
      const inMonth = patients.filter(p => { const pd = new Date(p.createdAt); return pd.getFullYear()===yr && pd.getMonth()===mo; });
      const total = inMonth.length;
      const followup   = inMonth.filter(p => p.patientStatus==="Follow Up").length;
      const haTrial    = inMonth.filter(p => p.patientStatus==="HA Trial").length;
      const booked     = inMonth.filter(p => p.patientStatus==="Booked").length;
      const fittingDone= inMonth.filter(p => p.patientStatus==="Fitting Done").length;
      // Conv % = (Booked + Fitting Done) / (Follow-up + HA Trial + Booked + Fitting Done)
      const pipelineAll = followup + haTrial + booked + fittingDone;
      const convRate   = pipelineAll > 0 ? Math.round(((booked + fittingDone) / pipelineAll) * 100) : 0;
      return { label, total, followup, haTrial, booked, fittingDone, convRate };
    });
  }, [store.patients, clinic]);

  // ── SVG layout constants ──────────────────────────────────
  const VW=900, VH=280, padL=44, padR=20, padT=24, padB=36;
  const chartW = VW - padL - padR;
  const chartH = VH - padT - padB;

  // ── buildLine — stable across renders ────────────────────
  const buildLine = useCallback((points) => {
    if (points.length < 2) return "";
    let d = `M ${points[0][0]},${points[0][1]}`;
    for (let i = 0; i < points.length - 1; i++) {
      const cpx = (points[i][0] + points[i+1][0]) / 2;
      d += ` C ${cpx},${points[i][1]} ${cpx},${points[i+1][1]} ${points[i+1][0]},${points[i+1][1]}`;
    }
    return d;
  }, []);

  // ── Chart computations — memoized so hover re-renders skip these ──
  const { values, pts, linePath, areaPath, yTicks } = useMemo(() => {
    const values  = data.map(d => d[metricKey]);
    const maxVal  = Math.max(...values, 1);
    const minVal  = Math.min(...values, 0);
    const range   = maxVal - minVal || 1;
    const xOf = i => padL + (i / (data.length - 1)) * chartW;
    const yOf = v => padT + chartH - ((v - minVal) / range) * chartH;
    const pts = data.map((d, i) => [xOf(i), yOf(d[metricKey])]);
    const linePath = buildLine(pts);
    const areaPath = linePath + ` L ${pts[pts.length-1][0]},${padT+chartH} L ${pts[0][0]},${padT+chartH} Z`;
    const yTicks = Array.from({ length: 5 }, (_, i) => {
      const frac = i / 4;
      return { y: padT + chartH * frac, v: Math.round(maxVal - frac * range) };
    });
    return { values, pts, linePath, areaPath, yTicks };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metricKey, buildLine]);

  // ── Existing: animate line on metric/clinic change ────────
  useEffect(() => {
    const p = pathRef.current;
    if (!p) return;
    const len = p.getTotalLength();
    p.style.transition = "none";
    p.style.strokeDasharray = len;
    p.style.strokeDashoffset = len;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      p.style.transition = "stroke-dashoffset 0.6s ease-out";
      p.style.strokeDashoffset = "0";
    }));
  }, [metricKey, clinic, linePath]);

  const gradId = `mag-${metricKey}`;
  const hVal   = values[values.length - 1];
  const hLabel = data[data.length - 1].label;

  // ── Scope filter for compare tab ─────────────────────────
  const scopedPatients = useMemo(() => {
    if (scope === 'all') return store.patients;
    const now = new Date();
    const cutoff = scope === 'quarter'
      ? new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    return store.patients.filter(p => new Date(p.createdAt) >= cutoff);
  }, [store.patients, scope]);

  // ── New: bar chart clinic data ────────────────────────────
  const clinicBarData = useMemo(() => {
    return Object.entries(CLINICS).map(([id, info]) => {
      const pats = scopedPatients.filter(p => p.clinicId === id);
      const total = pats.length;
      if (!total) return null;
      const followup   = pats.filter(p => p.patientStatus==="Follow Up").length;
      const haTrial    = pats.filter(p => p.patientStatus==="HA Trial").length;
      const booked     = pats.filter(p => p.patientStatus==="Booked").length;
      const fittingDone= pats.filter(p => p.patientStatus==="Fitting Done").length;
      const pipelineAll = followup + haTrial + booked + fittingDone;
      const convRate   = pipelineAll > 0 ? Math.round(((booked + fittingDone) / pipelineAll) * 100) : 0;
      const bookingRate= Math.round((booked / total) * 100);
      const haTrialRate= Math.round((haTrial / total) * 100);
      return { id, label:info.label, region:info.region, total, followup, booked, fittingDone, convRate, bookingRate, haTrialRate };
    }).filter(Boolean);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedPatients]);

  const getBarVal = useCallback(d => {
    if (compareMetric==="total")        return d.total;
    if (compareMetric==="convRate")     return d.convRate;
    if (compareMetric==="bookingRate")  return d.bookingRate;
    return d.haTrialRate;
  }, [compareMetric]);

  const isPercent     = compareMetric !== "total";
  const sortedBarData = useMemo(() => [...clinicBarData].sort((a,b)=>getBarVal(b)-getBarVal(a)), [clinicBarData, getBarVal]);
  const uniqueRegions = [...new Set(sortedBarData.map(d=>d.region))];

  // ── New: bar SVG dims ─────────────────────────────────────
  const VBW=900, VBH=300, bPadL=44, bPadR=20, bPadT=20, bPadB=56;
  const bChartW  = VBW - bPadL - bPadR;
  const bChartH  = VBH - bPadT - bPadB;
  const barMaxVal= Math.max(...sortedBarData.map(d=>getBarVal(d)), 1);
  const barCount = sortedBarData.length || 1;
  const barSlot  = bChartW / barCount;
  const barW     = Math.max(Math.min(barSlot * 0.6, 36), 6);
  const bXOf = i => bPadL + barSlot * i + barSlot / 2;
  const bYOf = v => bPadT + bChartH - (v / barMaxVal) * bChartH * barProg;
  const bHOf = v => (v / barMaxVal) * bChartH * barProg;
  const bYTicks  = Array.from({length:5}, (_,i) => {
    const frac = i/4;
    return { y: bPadT + bChartH * frac, v: Math.round(barMaxVal * (1-frac)) };
  });

  // ── New: bar animation on tab/metric switch ───────────────
  useEffect(() => {
    if (chartTab !== "compare") return;
    cancelAnimationFrame(barAnimRef.current);
    setBarProg(0);
    let start = null;
    const ease = t => 1 - Math.pow(1-t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts-start)/500, 1);
      setBarProg(ease(p));
      if (p < 1) barAnimRef.current = requestAnimationFrame(step);
    };
    barAnimRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(barAnimRef.current);
  }, [chartTab, compareMetric]);

  return (
    <div>
      <TopBar title="Master Analytics" sub="Monthly trends by clinic"/>
      <div className="main-pad" style={{ padding:"22px 26px" }}>

        {/* Tab toggle + controls */}
        <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
          <FBtn active={chartTab==="trend"}   onClick={()=>setChartTab("trend")}>📈 Trend</FBtn>
          <FBtn active={chartTab==="compare"} onClick={()=>setChartTab("compare")}>🏥 Clinic Compare</FBtn>
          <FBtn active={chartTab==="heatmap"} onClick={()=>setChartTab("heatmap")}>📅 Heatmap</FBtn>
          <div style={{ width:1, height:18, background:"var(--border)", margin:"0 2px" }}/>
          {chartTab==="trend" && <>
            <select value={clinic} onChange={e=>setClinic(e.target.value)} style={{ ...S.inp, maxWidth:220, fontWeight:600 }}>
              {clinicOptions.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select value={metricKey} onChange={e=>setMetricKey(e.target.value)} style={{ ...S.inp, maxWidth:180, fontWeight:600 }}>
              {METRICS.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </>}
          {chartTab==="compare" && <>
            <select value={compareMetric} onChange={e=>setCompareMetric(e.target.value)} style={{ ...S.inp, maxWidth:220, fontWeight:600 }}>
              {COMPARE_METRICS.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
            <div style={{width:1,height:18,background:"var(--border)",margin:"0 2px"}}/>
            {[['all','All Time'],['month','This Month'],['quarter','This Quarter']].map(([s,label])=>(
              <button key={s} onClick={()=>setScope(s)} style={{fontSize:11,fontWeight:700,padding:"5px 12px",borderRadius:20,border:`1px solid ${scope===s?"#7c3aed":"var(--border)"}`,background:scope===s?"#7c3aed":"var(--card)",color:scope===s?"#fff":"var(--muted)",cursor:"pointer",transition:"all 0.15s"}}>{label}</button>
            ))}
          </>}
          {chartTab==="heatmap" && (
            <select value={clinic} onChange={e=>setClinic(e.target.value)} style={{ ...S.inp, maxWidth:220, fontWeight:600 }}>
              {clinicOptions.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          )}
        </div>

        {/* ── EXISTING area chart card (unchanged) ── */}
        {chartTab==="trend" && (
          <div className="fadeUp" style={{ background:"var(--card)", border:"1px solid var(--cardborder)", borderRadius:16, padding:"20px 20px 10px", boxShadow:"var(--cardshadow)", marginBottom:18 }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:8 }}>
              <div>
                <div style={{ fontSize:11, color:"var(--muted)", fontWeight:600, textTransform:"uppercase", letterSpacing:0.8 }}>{metric.label}</div>
                <div style={{ fontSize:32, fontWeight:800, color:metric.color, lineHeight:1.1, marginTop:2 }}>
                  {metricKey==="convRate" ? `${hVal}%` : hVal}
                  <span style={{ fontSize:12, color:"var(--muted)", fontWeight:500, marginLeft:8 }}>{hLabel}</span>
                </div>
              </div>
              <div style={{ fontSize:11, color:"var(--muted2)" }}>Last 6 months</div>
            </div>
            <svg ref={svgRef} viewBox={`0 0 ${VW} ${VH}`} style={{ width:"100%", height:280, display:"block" }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={metric.color} stopOpacity="0.4"/>
                  <stop offset="100%" stopColor={metric.color} stopOpacity="0"/>
                </linearGradient>
              </defs>
              {yTicks.map(({y,v})=>(
                <g key={v}>
                  <line x1={padL} y1={y} x2={VW-padR} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray="3,6"/>
                  <text x={padL-6} y={y+4} textAnchor="end" fontSize="11" fill="var(--muted2)" fontFamily="DM Sans,sans-serif">{v}</text>
                </g>
              ))}
              {pts.map(([x],i)=>(
                <text key={i} x={x} y={VH-6} textAnchor="middle" fontSize="11" fill="var(--muted2)" fontFamily="DM Sans,sans-serif">{data[i].label}</text>
              ))}
              <path d={areaPath} fill={`url(#${gradId})`}/>
              <path ref={pathRef} d={linePath} fill="none" stroke={metric.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <HoverTooltip svgRef={svgRef} pts={pts} data={data} values={values} metric={metric} metricKey={metricKey} VW={VW} VH={VH} padT={padT} chartH={chartH}/>
            </svg>
          </div>
        )}

        {/* ── NEW: Clinic comparison bar chart ── */}
        {chartTab==="compare" && (<>
          <div className="fadeUp" style={{ background:"var(--card)", border:"1px solid var(--cardborder)", borderRadius:16, padding:"20px 20px 10px", boxShadow:"var(--cardshadow)", marginBottom:18 }}>
            {/* Region legend */}
            <div style={{ display:"flex", gap:16, marginBottom:12, flexWrap:"wrap" }}>
              {uniqueRegions.map(r=>(
                <div key={r} style={{ display:"flex", alignItems:"center", gap:5 }}>
                  <div style={{ width:10, height:10, borderRadius:2, background:REGION_COLORS[r]||"#999" }}/>
                  <span style={{ fontSize:11, color:"var(--muted)", fontWeight:600 }}>{r}</span>
                </div>
              ))}
            </div>
            <svg viewBox={`0 0 ${VBW} ${VBH}`} style={{ width:"100%", height:300, display:"block", cursor:"crosshair" }} onMouseLeave={()=>setBarHover(null)}>
              <defs>
                {Object.entries(REGION_COLORS).map(([region,color])=>(
                  <linearGradient key={region} id={`bg-${region.replace(/\s/g,"")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={color} stopOpacity="1"/>
                    <stop offset="100%" stopColor={color} stopOpacity="0.35"/>
                  </linearGradient>
                ))}
              </defs>
              {/* Y ticks */}
              {bYTicks.map(({y,v})=>(
                <g key={v}>
                  <line x1={bPadL} y1={y} x2={VBW-bPadR} y2={y} stroke="var(--border)" strokeWidth="1" strokeDasharray="3,6"/>
                  <text x={bPadL-6} y={y+4} textAnchor="end" fontSize="11" fill="var(--muted2)" fontFamily="DM Sans,sans-serif">{isPercent?`${v}%`:v}</text>
                </g>
              ))}
              {/* Baseline */}
              <line x1={bPadL} y1={bPadT+bChartH} x2={VBW-bPadR} y2={bPadT+bChartH} stroke="var(--border)" strokeWidth="1"/>
              {/* Bars */}
              {sortedBarData.map((d,i)=>{
                const x    = bXOf(i);
                const val  = getBarVal(d);
                const h    = Math.max(bHOf(val), 0);
                const y    = bYOf(val);
                const color= REGION_COLORS[d.region]||"#7c3aed";
                const isHov= barHover===i;
                const ttX  = x > VBW*0.7 ? x-134 : x+10;
                const ttY  = Math.max(y-10, bPadT);
                return (
                  <g key={d.id} style={{ cursor:"pointer" }}
                    onMouseEnter={()=>setBarHover(i)}
                    onClick={()=>{ setChartTab("trend"); setClinic(d.id); }}
                  >
                    <rect x={x-barW/2} y={y} width={barW} height={h}
                      fill={`url(#bg-${d.region.replace(/\s/g,"")})`} rx="3"
                      opacity={isHov?1:0.8} stroke={isHov?color:"none"} strokeWidth="1.5"/>
                    <text x={x} y={bPadT+bChartH+14} textAnchor="middle" fontSize="9"
                      fill={isHov?"var(--accent)":"var(--muted2)"}
                      fontFamily="DM Sans,sans-serif" fontWeight={isHov?"700":"400"}>
                      {d.label.length>9 ? d.label.slice(0,9)+"…" : d.label}
                    </text>
                    {isHov && (
                      <foreignObject x={ttX} y={ttY} width="148" height="100" style={{ overflow:"visible" }}>
                        <div xmlns="http://www.w3.org/1999/xhtml" style={{ background:"var(--card)", border:`1.5px solid ${color}55`, borderRadius:10, padding:"8px 12px", boxShadow:"0 6px 20px rgba(0,0,0,0.15)", pointerEvents:"none" }}>
                          <div style={{ fontSize:10, color:"var(--muted)", fontWeight:600 }}>{d.label} · {d.region}</div>
                          <div style={{ fontSize:20, fontWeight:800, color, lineHeight:1.2, margin:"2px 0" }}>{isPercent?`${val}%`:val}</div>
                          <div style={{ display:"flex", gap:8, marginTop:4 }}>
                            <span style={{ fontSize:9, color:"var(--muted2)" }}>Total <b style={{color:"var(--text1)"}}>{n(d.total)}</b></span>
                            <span style={{ fontSize:9, color:"var(--muted2)" }}>Booked <b style={{color:"#10b981"}}>{n(d.booked)}</b></span>
                            <span style={{ fontSize:9, color:"var(--muted2)" }}>FD <b style={{color:"#3b82f6"}}>{n(d.fittingDone)}</b></span>
                          </div>
                          <div style={{ fontSize:9, color:"var(--muted2)", marginTop:3 }}>Conv {d.convRate}% · Booking {d.bookingRate}%</div>
                          <div style={{ fontSize:9, color:"var(--muted2)", marginTop:2 }}>Click to view trend →</div>
                        </div>
                      </foreignObject>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Ranked clinic table */}
          <div style={{ background:"var(--card)", border:"1px solid var(--cardborder)", borderRadius:16, overflow:"hidden", boxShadow:"var(--cardshadow)", marginBottom:18 }}>
            <div style={{ display:"grid", gridTemplateColumns:"36px 1fr 100px 80px", background:"var(--accentbg)", borderBottom:"1px solid var(--border)", padding:"10px 16px" }}>
              {["#","Clinic","Region","Value"].map(h=>(
                <div key={h} style={{ fontSize:11, fontWeight:700, color:"var(--accent)", textTransform:"uppercase", letterSpacing:0.6 }}>{h}</div>
              ))}
            </div>
            {sortedBarData.map((d,i)=>{
              const val  = getBarVal(d);
              const color= REGION_COLORS[d.region]||"#7c3aed";
              return (
                <div key={d.id} className="row-hover" style={{ display:"grid", gridTemplateColumns:"36px 1fr 100px 80px", padding:"9px 16px", borderBottom:i<sortedBarData.length-1?"1px solid var(--border)":"none", cursor:"pointer" }}
                  onClick={()=>{ setChartTab("trend"); setClinic(d.id); }}>
                  <div style={{ fontSize:12, color:"var(--muted2)", fontWeight:700 }}>{i+1}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:color, flexShrink:0 }}/>
                    <span style={{ fontSize:12, fontWeight:600, color:"var(--text1)" }}>{d.label}</span>
                  </div>
                  <div style={{ fontSize:11, color:"var(--muted)" }}>{d.region}</div>
                  <div style={{ fontSize:13, fontWeight:800, color }}>{isPercent?`${val}%`:val}</div>
                </div>
              );
            })}
          </div>
        </>)}

        {/* ── Heatmap calendar ── */}
        {chartTab==="heatmap" && (
          <div className="fadeUp" style={{ background:"var(--card)", border:"1px solid var(--cardborder)", borderRadius:16, padding:"20px 24px", boxShadow:"var(--cardshadow)" }}>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--text1)", marginBottom:16 }}>Patient registrations — last 6 months</div>
            <HeatmapCalendar patients={clinic==="all" ? store.patients : store.patients.filter(p=>p.clinicId===clinic)} />
          </div>
        )}

        {/* ── EXISTING summary table (trend tab only, unchanged) ── */}
        {chartTab==="trend" && (
          <div style={{ background:"var(--card)", border:"1px solid var(--cardborder)", borderRadius:16, overflow:"hidden", boxShadow:"var(--cardshadow)" }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 1fr 1fr", background:"var(--accentbg)", borderBottom:"1px solid var(--border)", padding:"10px 16px" }}>
              {["Month","Total","Follow Up","HA Trial","Booked","Fitting Done","Conv %"].map(h=>(
                <div key={h} style={{ fontSize:11, fontWeight:700, color:"var(--accent)", textTransform:"uppercase", letterSpacing:0.6 }}>{h}</div>
              ))}
            </div>
            {data.map((d,i)=>(
              <div key={i} className="row-hover" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 1fr 1fr", padding:"10px 16px", borderBottom:i<data.length-1?"1px solid var(--border)":"none" }}>
                <div style={{ fontSize:12, fontWeight:600, color:"var(--text1)" }}>{d.label}</div>
                <div style={{ fontSize:12, color:"#7c3aed", fontWeight:700 }}>{n(d.total)}</div>
                <div style={{ fontSize:12, color:"#6366f1", fontWeight:700 }}>{n(d.followup)}</div>
                <div style={{ fontSize:12, color:"#f59e0b", fontWeight:700 }}>{n(d.haTrial)}</div>
                <div style={{ fontSize:12, color:"#10b981", fontWeight:700 }}>{n(d.booked)}</div>
                <div style={{ fontSize:12, color:"#3b82f6", fontWeight:700 }}>{n(d.fittingDone)}</div>
                <div style={{ fontSize:12, color:d.convRate>=50?"#10b981":d.convRate>=20?"#f59e0b":"var(--muted)", fontWeight:700 }}>{d.convRate}%</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// CMD+K COMMAND PALETTE
// ─────────────────────────────────────────────────────────────
function CmdKPalette({ patients, nav, onSelect, codeOnly=false }) {
  const [open, setOpen]   = useState(false);
  const [q, setQ]         = useState('');
  const [idx, setIdx]     = useState(0);
  const [liveResults, setLiveResults] = useState([]);
  const inputRef          = useRef(null);

  useEffect(() => {
    const handler = e => {
      if ((e.metaKey||e.ctrlKey) && e.key==='k') { e.preventDefault(); setOpen(o=>!o); setQ(''); setIdx(0); setLiveResults([]); }
      if (e.key==='Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => { if (open) setTimeout(()=>inputRef.current?.focus(), 40); }, [open]);

  // codeOnly: live API search (cross-clinic, phone/code, 6-char min)
  useEffect(() => {
    if (!codeOnly) return;
    const qt = q.trim();
    if (qt.length < 6) { setLiveResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await apiCall(`${API_BASE}/patients.php?search=${encodeURIComponent(qt)}&by=phone`);
        const d = await r.json();
        setLiveResults(Array.isArray(d) ? d.slice(0, 8).map(p => ({ type:'patient', label:p.name||'—', sub:`${p.phone||'No phone'} · ${CLINICS[p.clinicId]?.label||p.clinicId||''}`, data:p })) : []);
      } catch { setLiveResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, codeOnly]);

  const results = useMemo(() => {
    if (codeOnly) return liveResults;
    if (!q.trim()) return [];
    const lq = q.toLowerCase();
    return patients
      .filter(p => p.name?.toLowerCase().includes(lq) || (p.phone||'').includes(q))
      .slice(0, 8)
      .map(p => ({ type:'patient', label:p.name||'—', sub:`${p.phone||'No phone'} · ${CLINICS[p.clinicId]?.label||p.clinicId||''}`, data:p }));
  }, [q, patients, codeOnly, liveResults]);

  useEffect(()=>{ setIdx(0); }, [results]);

  const go = r => {
    setOpen(false);
    if (r.type==='patient') onSelect(r.data);
    if (r.type==='nav')     r.data.onClick();
  };

  const onKey = e => {
    if (e.key==='ArrowDown') { e.preventDefault(); setIdx(i=>Math.min(i+1,results.length-1)); }
    if (e.key==='ArrowUp')   { e.preventDefault(); setIdx(i=>Math.max(i-1,0)); }
    if (e.key==='Enter' && results[idx]) go(results[idx]);
  };

  if (!open) return null;
  return (
    <div onClick={()=>setOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:99999, display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:100 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--card)', borderRadius:16, width:'100%', maxWidth:540, boxShadow:'0 24px 60px rgba(0,0,0,.25)', overflow:'hidden' }}>
        {/* Search input */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px', borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontSize:18, color:'var(--muted)', flexShrink:0 }}>⌕</span>
          <input
            ref={inputRef}
            value={q}
            onChange={e=>setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder={codeOnly ? "Search by case code or phone number…" : "Search patients by name or number…"}
            style={{ flex:1, border:'none', outline:'none', fontSize:15, background:'transparent', color:'var(--text1)' }}
          />
          <kbd style={{ fontSize:10, color:'var(--muted2)', background:'var(--bg3)', borderRadius:4, padding:'2px 7px', border:'1px solid var(--border)', flexShrink:0 }}>ESC</kbd>
        </div>
        {/* Results */}
        {results.length > 0 ? (
          <div style={{ maxHeight:340, overflowY:'auto' }}>
            {results.map((r,i)=>(
              <div key={i} onClick={()=>go(r)} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 18px', cursor:'pointer', background:i===idx?'var(--accentbg)':'transparent', borderLeft:`3px solid ${i===idx?'var(--accent)':'transparent'}`, transition:'background 0.1s' }}
                onMouseEnter={()=>setIdx(i)}>
                <div style={{ width:32, height:32, borderRadius:8, background:'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                  {r.type==='patient'?'👤':r.type==='nav'?r.icon:'🏥'}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.label}</div>
                  <div style={{ fontSize:11, color:'var(--muted2)', marginTop:1 }}>{r.sub}</div>
                </div>
                {i===idx && <span style={{ fontSize:11, color:'var(--muted2)', flexShrink:0 }}>↵</span>}
              </div>
            ))}
          </div>
        ) : q ? (
          <div style={{ padding:'28px 18px', textAlign:'center', color:'var(--muted2)', fontSize:13 }}>
            {codeOnly && q.trim().length < 6 ? `Enter ${6 - q.trim().length} more character${6 - q.trim().length > 1 ? 's' : ''}…` : `No results for "${q}"`}
          </div>
        ) : (
          <div style={{ padding:'14px 18px' }}>
            <div style={{ fontSize:10, color:'var(--muted2)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>Quick navigation</div>
            {nav.map((n,i)=>(
              <div key={n.id} onClick={()=>{n.onClick();setOpen(false);}} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', cursor:'pointer', borderRadius:8, background:i===idx?'var(--accentbg)':'transparent', transition:'background 0.1s' }}
                onMouseEnter={()=>setIdx(i)}>
                <span style={{ fontSize:15, flexShrink:0 }}>{n.icon}</span>
                <span style={{ fontSize:13, color:'var(--text1)' }}>{n.label}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ padding:'8px 18px', borderTop:'1px solid var(--border)', display:'flex', gap:14, fontSize:10, color:'var(--muted2)' }}>
          <span>↑↓ navigate</span><span>↵ open</span><span>ESC close</span>
          <span style={{ marginLeft:'auto' }}>⌘K to toggle</span>
        </div>
      </div>
    </div>
  );
}

function Sparkline({ data, color='#7c3aed', width=80, height=28 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((v,i) => `${(i/(data.length-1))*width},${height-(v/max)*height}`).join(' ');
  const fillPts = `0,${height} ${pts} ${width},${height}`;
  return (
    <svg width={width} height={height} style={{display:'block',overflow:'visible'}}>
      <polygon points={fillPts} fill={color} opacity={0.12}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" opacity={0.75}/>
    </svg>
  );
}

function Analytics({ store, allowedClinics, onSelect }) {
  const visibleClinics = allowedClinics && allowedClinics.length > 0
    ? allowedClinics.filter(id => CLINICS[id])
    : Object.keys(CLINICS);

  const [scope, setScope] = useState('all');
  const [showNotInterestedModal, setShowNotInterestedModal] = useState(false);
  const [funnelStageModal, setFunnelStageModal] = useState(null); // {label, patients[]}

  const [galleryData, setGalleryData] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/patients.php?mode=gallery`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setGalleryData(data); })
      .catch(() => {});
  }, []);

  const pts = store.patients.filter(p => visibleClinics.includes(p.clinicId));
  const totalRec = galleryData.filter(r => visibleClinics.includes(r.clinic_id)).length;

  // ── Date helpers (always absolute, not scoped) ──
  const now = new Date();
  const thisMonth = pts.filter(p => { const d = new Date(p.createdAt); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }).length;
  const prevD = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = pts.filter(p => { const d = new Date(p.createdAt); return d.getMonth() === prevD.getMonth() && d.getFullYear() === prevD.getFullYear(); }).length;
  const growthNum = lastMonth > 0 ? (thisMonth - lastMonth) / lastMonth * 100 : null;
  const growthUp = growthNum !== null && growthNum >= 0;

  // ── Scope filter (applied to funnel / region / source) ──
  const scopedPts = useMemo(() => {
    if (scope === 'all') return pts;
    const qStart = scope === 'quarter'
      ? new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    return pts.filter(p => new Date(p.createdAt) >= qStart);
  }, [pts, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopeTotal = scopedPts.length;

  // ── Funnel (scoped) — single pass, memoized ──
  const { fittingDone, hotPipeline, haTrial, booked, notInterested, directFitted, directFitPatients, funnelStages } = useMemo(() => {
    const haTBucket = [], bookedBucket = [], fittedBucket = [], directFitBucket = [], inquiryBucket = [];
    let hotPipeline = 0, notInterested = 0;
    for (const p of scopedPts) {
      const s = p.patientStatus;
      if (s === 'Inquiry')       { inquiryBucket.push(p); }
      else if (s === 'HA Trial') { haTBucket.push(p);     hotPipeline++; }
      else if (s === 'Booked')   { bookedBucket.push(p);  hotPipeline++; }
      else if (s === 'Follow Up'){ hotPipeline++; }
      else if (s === 'Fitting Done') {
        if (p.trialStarted) fittedBucket.push(p);   // went through HA Trial first
        else directFitBucket.push(p);               // registered directly as Fitting Done
      }
      else if (s === 'Not Interested') notInterested++;
    }
    const stages = [
      { label:'Total Patients', count:scopedPts.length,    color:'#7c3aed', patients: scopedPts },
    ];
    if (inquiryBucket.length > 0) {
      stages.push({ label:'Inquiry', count:inquiryBucket.length, color:'#8b5cf6', patients: inquiryBucket });
    }
    stages.push(
      { label:'HA Trial',     count:haTBucket.length,    color:'#f59e0b', patients: haTBucket },
      { label:'Booked',       count:bookedBucket.length, color:'#10b981', patients: bookedBucket },
      { label:'Fitting Done', count:fittedBucket.length, color:'#3b82f6', patients: fittedBucket },
    );
    return {
      fittingDone:        fittedBucket.length,
      directFitted:       directFitBucket.length,
      directFitPatients:  directFitBucket,
      hotPipeline,
      haTrial:            haTBucket.length,
      booked:             bookedBucket.length,
      notInterested,
      funnelStages:       stages,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedPts]);
  const total = scopeTotal;
  const followUpCount = scopedPts.filter(p => p.patientStatus === 'Follow Up').length;
  const pipelineAll = followUpCount + haTrial + booked + fittingDone;
  const convRate = pipelineAll > 0 ? Math.round((booked + fittingDone) / pipelineAll * 100) : 0;

  // ── Monthly trend (12 months) ──
  const months12 = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    const count = pts.filter(p => { const pd = new Date(p.createdAt); return pd.getMonth() === d.getMonth() && pd.getFullYear() === d.getFullYear(); }).length;
    return { label: d.toLocaleDateString('en-IN', { month: 'short' }), count, isCurrent: i === 11 };
  });
  const maxM = Math.max(...months12.map(m => m.count), 1);
  const avg3 = Math.round(months12.slice(-4, -1).reduce((a, m) => a + m.count, 0) / 3);

  // ── Region performance (scoped) ──
  const regionPerf = Object.entries(REGIONS)
    .filter(([, ids]) => ids.some(id => visibleClinics.includes(id)))
    .map(([r, ids]) => {
      const rPts = scopedPts.filter(p => ids.includes(p.clinicId));
      const rTotal   = rPts.length;
      const rFittedTrial  = rPts.filter(p => p.patientStatus === 'Fitting Done' && p.trialStarted).length;
      const rDirectFitted = rPts.filter(p => p.patientStatus === 'Fitting Done' && !p.trialStarted).length;
      const rFitted       = rFittedTrial + rDirectFitted;
      const rBooked       = rPts.filter(p => p.patientStatus === 'Booked').length;
      const rHaTrial      = rPts.filter(p => p.patientStatus === 'HA Trial').length;
      const rFollowUp     = rPts.filter(p => p.patientStatus === 'Follow Up').length;
      const rPipe         = rFollowUp + rHaTrial + rBooked + rFitted;
      const rPipeAll      = rFollowUp + rHaTrial + rBooked + rFittedTrial;
      const rConv         = rPipeAll > 0 ? Math.round((rBooked + rFittedTrial) / rPipeAll * 100) : 0;
      return { r, total: rTotal, fitted: rFitted, directFitted: rDirectFitted, pipeline: rPipe, conv: rConv, patients: rPts };
    })
    .sort((a, b) => b.total - a.total);
  const maxReg = Math.max(...regionPerf.map(x => x.total), 1);

  // ── Source quality (scoped) ──
  const sourceQ = SOURCE_OPTIONS.map(s => {
    const sPts          = scopedPts.filter(p => p.source === s);
    const sTotal        = sPts.length;
    const sFittedTrial  = sPts.filter(p => p.patientStatus === 'Fitting Done' && p.trialStarted).length;
    const sDirectFitted = sPts.filter(p => p.patientStatus === 'Fitting Done' && !p.trialStarted).length;
    const sBooked       = sPts.filter(p => p.patientStatus === 'Booked').length;
    const sHaTrial      = sPts.filter(p => p.patientStatus === 'HA Trial').length;
    const sFollowUp     = sPts.filter(p => p.patientStatus === 'Follow Up').length;
    const sPipeAll      = sFollowUp + sHaTrial + sBooked + sFittedTrial;
    const sConv         = sPipeAll > 0 ? Math.round((sBooked + sFittedTrial) / sPipeAll * 100) : 0;
    const sPct          = total > 0 ? Math.round(sTotal / total * 100) : 0;
    return { s, total: sTotal, conv: sConv, pct: sPct, directFitted: sDirectFitted, patients: sPts };
  }).filter(x => x.total > 0).sort((a, b) => b.total - a.total);
  const maxSQ  = Math.max(...sourceQ.map(x => x.total), 1);
  const unknown = scopedPts.filter(p => !p.source).length;

  const C = { background:'var(--card)', border:'1px solid var(--cardborder)', borderRadius:14, boxShadow:'var(--cardshadow)' };

  const scopeLabel = scope==='all'?'All Time':scope==='month'?'This Month':'This Quarter';

  return (
    <div>
      <TopBar title="Analytics" sub="Decision-making dashboard"/>
      <div className="main-pad" style={{padding:"22px 26px"}}>

        {/* ── Scope Toggle ── */}
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:18,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:"var(--muted)",fontWeight:600,marginRight:4}}>Showing:</span>
          {[['all','All Time'],['month','This Month'],['quarter','This Quarter']].map(([s,label])=>(
            <button key={s} onClick={()=>setScope(s)} style={{fontSize:11,fontWeight:700,padding:"5px 14px",borderRadius:20,border:`1px solid ${scope===s?"#7c3aed":"var(--border)"}`,background:scope===s?"#7c3aed":"var(--card)",color:scope===s?"#fff":"var(--muted)",cursor:"pointer",transition:"all 0.15s"}}>{label}</button>
          ))}
          {scope!=='all'&&<span style={{fontSize:10,color:"var(--muted2)",marginLeft:4}}>Funnel, Region &amp; Source reflect {scopeLabel} registrations</span>}
        </div>

        {/* ── KPI Cards ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:22}}>
          {[
            { v:total,        l:"Total Patients",   sub:`${totalRec} records uploaded`,                                                                                          icon:"👥", dc:false, sfx:"" },
            { v:thisMonth,    l:"This Month",        sub:growthNum!==null?`${growthUp?"▲":"▼"} ${Math.abs(growthNum).toFixed(1)}% vs last month`:`${lastMonth} last month`,       icon:"📅", dc:growthNum!==null, sfx:"" },
            { v:convRate,     l:"Conversion Rate",   sub:`${booked + fittingDone} converted · ${haTrial} HA trials`,                                                            icon:"🎯", dc:false, sfx:"%" },
            { v:hotPipeline,  l:"Hot Pipeline",      sub:"HA Trial + Booked + Follow Up",                                                                                icon:"🔥", dc:false, sfx:"" },
          ].map(({ v, l, sub, icon, dc, sfx }, i) => (
            <div key={l} className="card-hover fadeUp" style={{...C, animationDelay:`${i*0.07}s`, padding:"18px 20px", borderTop:"3px solid var(--accent)"}}>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:8}}>{icon} {l}</div>
              <div style={{fontSize:28,fontWeight:800,color:"var(--accent)",lineHeight:1}}><CountUpNum value={v} suffix={sfx}/></div>
              <div style={{fontSize:11,marginTop:6,color: dc&&growthNum!==null ? (growthUp?"#059669":"#dc2626") : "var(--muted2)"}}>{sub}</div>
              <div style={{marginTop:8}}><Sparkline data={months12.slice(-6).map(m=>m.count)} width={80} height={24}/></div>
            </div>
          ))}
        </div>

        {/* ── Row 2: Funnel + Monthly Trend ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1.5fr",gap:18,marginBottom:18}}>

          {/* Conversion Funnel */}
          <div style={{...C, padding:"20px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontSize:13,fontWeight:700,color:"var(--text1)"}}>🔻 Conversion Funnel</div>
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                {directFitted>0&&<button onClick={()=>setFunnelStageModal({label:'Direct Fittings',color:'#2563eb',patients:directFitPatients})} style={{fontSize:10,fontWeight:600,background:"#eff6ff",color:"#2563eb",borderRadius:6,padding:"2px 8px",border:"1px solid #bfdbfe",cursor:"pointer"}}>⚡ {directFitted} direct fitting{directFitted>1?'s':''} ↗</button>}
                {notInterested>0&&<button onClick={()=>setShowNotInterestedModal(true)} style={{fontSize:10,fontWeight:600,background:"#fef2f2",color:"#dc2626",borderRadius:6,padding:"2px 8px",border:"1px solid #fecaca",cursor:"pointer"}}>⚠ {notInterested} not interested ↗</button>}
              </div>
            </div>
            <div style={{fontSize:9,color:"var(--muted2)",marginBottom:14}}>Trial conv. rate excludes direct fittings · {scopeLabel}</div>
            {funnelStages.map((stage, i) => {
              const prev = funnelStages[i - 1];
              const pct  = total > 0 ? Math.round(stage.count / total * 100) : 0;
              const dropPct = prev && prev.count > 0 ? Math.round((1 - stage.count / prev.count) * 100) : null;
              return (
                <div key={stage.label}>
                  {dropPct !== null && (
                    <div style={{display:"flex",alignItems:"center",gap:6,margin:"4px 0 4px 8px"}}>
                      <div style={{width:1,height:12,background:"#e5e7eb"}}/>
                      <span style={{fontSize:10,color:"#dc2626",fontWeight:700}}>▼ {dropPct}% drop-off</span>
                    </div>
                  )}
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:2}}>
                    <div onClick={()=>stage.count>0&&onSelect&&setFunnelStageModal({label:stage.label,color:stage.color,patients:stage.patients})} style={{flex:1,position:"relative",height:36,cursor:stage.count>0&&onSelect?"pointer":"default"}}>
                      <div style={{position:"absolute",inset:0,background:"var(--accentbg)",borderRadius:8,overflow:"hidden"}}>
                        <div style={{
                          width:`${pct}%`, height:"100%",
                          background:stage.color,
                          borderRadius:8, minWidth:stage.count?40:0,
                          display:"flex", alignItems:"center", paddingLeft:10, gap:6,
                          transition:"width 1s cubic-bezier(.16,1,.3,1)",
                        }}>
                          {stage.count > 0 && <>
                            <span style={{fontSize:14,fontWeight:800,color:"#fff",whiteSpace:"nowrap"}}>{stage.count}</span>
                            <span style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.85)"}}>{pct}%</span>
                          </>}
                        </div>
                      </div>
                    </div>
                    <div style={{minWidth:90,textAlign:"right"}}>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--text1)"}}>{stage.label}</div>
                      {i > 0 && <div style={{fontSize:10,color:"var(--muted2)",marginTop:1}}>{pct}% of total</div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Monthly Trend */}
          <div style={{...C, padding:"20px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"var(--text1)"}}>📈 Monthly Registrations</div>
                <div style={{fontSize:11,color:"var(--muted2)",marginTop:2}}>12-month view</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:10,color:"var(--muted2)"}}>3-mo avg</div>
                <div style={{fontSize:20,fontWeight:800,color:"var(--accent)"}}>{avg3}</div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",gap:4,height:120}}>
              {months12.map(m => (
                <div key={m.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                  {m.count > 0 && <div style={{fontSize:9,fontWeight:700,color:m.isCurrent?"#059669":"var(--accent)"}}>{m.count}</div>}
                  <div style={{
                    width:"100%", borderRadius:"4px 4px 0 0",
                    background:m.isCurrent?"linear-gradient(0deg,#059669,#34d399)":"linear-gradient(0deg,#7c3aed,#a78bfa)",
                    height:`${Math.max((m.count/maxM)*100,2)}%`, minHeight:2,
                    transition:"height 1s cubic-bezier(.16,1,.3,1)", opacity:m.count===0?0.25:1,
                  }}/>
                  <div style={{fontSize:9,color:m.isCurrent?"var(--accent)":"var(--muted)",fontWeight:m.isCurrent?700:400}}>{m.label}</div>
                </div>
              ))}
            </div>
            {growthNum !== null && (
              <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border)",display:"flex",gap:20}}>
                <div><div style={{fontSize:10,color:"var(--muted2)"}}>This month</div><div style={{fontSize:16,fontWeight:800,color:"var(--text1)"}}>{thisMonth}</div></div>
                <div><div style={{fontSize:10,color:"var(--muted2)"}}>Last month</div><div style={{fontSize:16,fontWeight:800,color:"var(--text1)"}}>{lastMonth}</div></div>
                <div><div style={{fontSize:10,color:"var(--muted2)"}}>MoM growth</div><div style={{fontSize:16,fontWeight:800,color:growthUp?"#059669":"#dc2626"}}>{growthUp?"▲":"▼"} {Math.abs(growthNum).toFixed(1)}%</div></div>
              </div>
            )}
          </div>
        </div>

        {/* ── Row 3: Region Performance + Source Quality ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1.2fr",gap:18}}>

          {/* Region Performance */}
          <div style={{...C, padding:"20px"}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text1)",marginBottom:14}}>🌍 Region Performance</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 44px 56px 48px",gap:"0 8px",marginBottom:6}}>
              {["Region","Pts","Pipeline","Conv%"].map(h=>(
                <div key={h} style={{fontSize:10,color:"var(--muted2)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,paddingBottom:8,borderBottom:"1px solid var(--border)"}}>{h}</div>
              ))}
            </div>
            {regionPerf.map(({ r, total:rT, pipeline:rP, conv:rC, directFitted:rD, patients:rPats }) => (
              <div key={r} onClick={()=>setFunnelStageModal({label:r,color:"#7c3aed",patients:rPats})} style={{display:"grid",gridTemplateColumns:"1fr 44px 56px 48px",gap:"0 8px",alignItems:"center",padding:"9px 0",borderBottom:"1px solid var(--border)",cursor:"pointer"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:"var(--text1)",marginBottom:3}}>{r} ↗</div>
                  {rD>0&&<div style={{fontSize:9,fontWeight:600,color:"#2563eb",marginBottom:3}}>⚡ {rD} direct</div>}
                  <div style={{height:4,background:"var(--accentbg)",borderRadius:99}}>
                    <div style={{width:`${(rT/maxReg)*100}%`,height:"100%",background:"linear-gradient(90deg,#7c3aed,#a78bfa)",borderRadius:99,minWidth:rT?4:0}}/>
                  </div>
                </div>
                <div style={{fontSize:13,fontWeight:700,color:"var(--accent)",textAlign:"right"}}>{rT}</div>
                <div style={{textAlign:"right"}}>
                  <span style={{fontSize:11,fontWeight:600,borderRadius:5,padding:"2px 7px",color:rP>0?"#d97706":"var(--muted2)",background:rP>0?"#fffbeb":"transparent"}}>{rP}</span>
                </div>
                <div style={{textAlign:"right"}}>
                  <span style={{fontSize:11,fontWeight:700,borderRadius:5,padding:"2px 7px",
                    color:rC>=71?"#059669":rC>=51?"#16a34a":rC>=21?"#d97706":rC>0?"#dc2626":"var(--muted2)",
                    background:rC>=71?"#f0fdf4":rC>=51?"#dcfce7":rC>=21?"#fffbeb":rC>0?"#fef2f2":"transparent"
                  }}>{rC}%</span>
                </div>
              </div>
            ))}
          </div>

          {/* Source Quality */}
          <div style={{...C, padding:"20px"}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text1)",marginBottom:14}}>📣 Source Quality</div>
            {sourceQ.length===0&&<Empty msg="No source data yet"/>}
            {sourceQ.map(({ s, total:sT, conv:sC, pct, directFitted:sDirect, patients:sPats }) => (
              <div key={s} style={{marginBottom:11,cursor:"pointer"}} onClick={()=>setFunnelStageModal({label:s,color:"#7c3aed",patients:sPats})}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:12,color:"var(--muted)"}}>{s} ↗</span>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{fontSize:10,color:"var(--muted2)"}}>{sT} · {pct}%</span>
                    {sDirect > 0 && <span style={{fontSize:10,fontWeight:600,background:"#eff6ff",color:"#2563eb",borderRadius:5,padding:"1px 6px",border:"1px solid #bfdbfe"}}>⚡ {sDirect} direct</span>}
                    <span style={{fontSize:10,fontWeight:700,borderRadius:5,padding:"1px 6px",
                      color:sC>=71?"#059669":sC>=51?"#16a34a":sC>=21?"#d97706":"#dc2626",
                      background:sC>=71?"#f0fdf4":sC>=51?"#dcfce7":sC>=21?"#fffbeb":"#fef2f2",
                    }}>{sC}% conv</span>
                  </div>
                </div>
                <div style={{background:"var(--accentbg)",borderRadius:99,height:7}}>
                  <div style={{width:`${(sT/maxSQ)*100}%`,background:"linear-gradient(90deg,#7c3aed,#a78bfa)",height:"100%",borderRadius:99,transition:"width 1s cubic-bezier(.16,1,.3,1)",minWidth:sT?4:0}}/>
                </div>
              </div>
            ))}
            {unknown>0&&<div style={{fontSize:11,color:"var(--muted2)",marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)"}}>⚠️ {unknown} patient{unknown>1?"s":""} without source recorded</div>}
          </div>
        </div>


      </div>

      {/* ── Funnel Stage Patient List Modal ── */}
      {funnelStageModal && (
        <div onClick={()=>setFunnelStageModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--card)",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 48px rgba(0,0,0,0.18)"}}>
            <div style={{padding:"18px 22px 14px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:funnelStageModal.color}}>{funnelStageModal.label}</div>
                <div style={{fontSize:11,color:"var(--muted2)",marginTop:2}}>{funnelStageModal.patients.length} patient{funnelStageModal.patients.length!==1?"s":""} · {scopeLabel} · click to open</div>
              </div>
              <button onClick={()=>setFunnelStageModal(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--muted)",lineHeight:1,padding:"4px 8px"}}>×</button>
            </div>
            <div style={{overflowY:"auto",flex:1}}>
              {funnelStageModal.patients.length===0 && <div style={{padding:32,textAlign:"center",color:"var(--muted2)",fontSize:13}}>No patients in this stage.</div>}
              {funnelStageModal.patients.slice(0,200).map((p,i)=>(
                <div key={p.id} onClick={()=>{onSelect(p);setFunnelStageModal(null);}} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 22px",borderBottom:"1px solid var(--border)",background:i%2===0?"transparent":"var(--accentbg)",cursor:"pointer",transition:"background 0.1s"}} onMouseEnter={e=>{e.currentTarget.style.background="#f5f3ff";}} onMouseLeave={e=>{e.currentTarget.style.background=i%2===0?"transparent":"var(--accentbg)";}}>
                  <div style={{width:34,height:34,borderRadius:"50%",background:"var(--accentbg)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:`2px solid ${funnelStageModal.color}`}}>
                    <span style={{fontSize:14,color:funnelStageModal.color,fontWeight:700}}>{(p.name||"?")[0].toUpperCase()}</span>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text1)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name||"—"}</div>
                    <div style={{fontSize:11,color:"var(--muted2)",marginTop:1}}>{p.phone||"No phone"} · {CLINICS[p.clinicId]?.label||p.clinicId||"—"}</div>
                  </div>
                  <div style={{fontSize:10,color:"var(--muted2)",flexShrink:0,textAlign:"right"}}>
                    {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : ""}
                    <div style={{fontSize:9,color:"#7c3aed",marginTop:2}}>View →</div>
                  </div>
                </div>
              ))}
              {funnelStageModal.patients.length>200&&<div style={{padding:"10px 22px",fontSize:11,color:"var(--muted2)",textAlign:"center",borderTop:"1px solid var(--border)"}}>Showing first 200 of {funnelStageModal.patients.length}. Use Search for specific patients.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Not Interested Patient List Modal ── */}
      {showNotInterestedModal && (()=>{
        const niPts = scopedPts.filter(p => p.patientStatus === 'Not Interested');
        return (
          <div onClick={()=>setShowNotInterestedModal(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--card)",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 48px rgba(0,0,0,0.18)"}}>
              {/* Header */}
              <div style={{padding:"18px 22px 14px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:"#dc2626"}}>⚠ Not Interested Patients</div>
                  <div style={{fontSize:11,color:"var(--muted2)",marginTop:2}}>{niPts.length} patient{niPts.length!==1?"s":""} · {scopeLabel}</div>
                </div>
                <button onClick={()=>setShowNotInterestedModal(false)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--muted)",lineHeight:1,padding:"4px 8px"}}>×</button>
              </div>
              {/* List */}
              <div style={{overflowY:"auto",flex:1}}>
                {niPts.length===0 && <div style={{padding:32,textAlign:"center",color:"var(--muted2)",fontSize:13}}>No patients found.</div>}
                {niPts.map((p,i)=>(
                  <div key={p.id} onClick={onSelect?()=>{onSelect(p);setShowNotInterestedModal(false);}:undefined} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 22px",borderBottom:"1px solid var(--border)",background:i%2===0?"transparent":"var(--accentbg)",cursor:onSelect?"pointer":"default",transition:"background 0.1s"}} onMouseEnter={onSelect?e=>{e.currentTarget.style.background="#f5f3ff";}:undefined} onMouseLeave={onSelect?e=>{e.currentTarget.style.background=i%2===0?"transparent":"var(--accentbg)";}:undefined}>
                    <div style={{width:34,height:34,borderRadius:"50%",background:"#fee2e2",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <span style={{fontSize:14,color:"#dc2626",fontWeight:700}}>{(p.name||"?")[0].toUpperCase()}</span>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text1)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name||"—"}</div>
                      <div style={{fontSize:11,color:"var(--muted2)",marginTop:1}}>{p.phone||"No phone"} · {CLINICS[p.clinicId]?.label||p.clinicId||"—"}</div>
                    </div>
                    <div style={{fontSize:10,color:"var(--muted2)",flexShrink:0,textAlign:"right"}}>
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : ""}
                      {onSelect&&<div style={{fontSize:9,color:"#7c3aed",marginTop:2}}>View →</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PATIENT MODAL — with Edit tab
// ══════════════════════════════════════════════════════════
function PatientModal({ p, store, onClose, readOnly, setLb, currentClinicId, user }) {
  const [tab,setTab]=useState("info");
  const [note,setNote]=useState("");
  const [attendedBy,setAttendedBy]=useState("");
  const [confirmDelete,setConfirmDelete]=useState(false);
  const [showPrintCard,setShowPrintCard]=useState(false);
  const [fileViewer,setFileViewer]=useState(null); // {url, name, kind:'pdf'|'txt'}
  const fileRef=useRef();
  const [busy,setBusy]=useState(false);
  const [uploadMsg,setUploadMsg]=useState(null); // {ok:n, fail:[names]}
  const [editF,setEditF]=useState({});
  const [editBusy,setEditBusy]=useState(false);
  const [editDone,setEditDone]=useState(false);
  const [fullData,setFullData]=useState(null);
  const [loadingFull,setLoadingFull]=useState(false);
  const [repairHistory,setRepairHistory]=useState(null);
  const [repairLoading,setRepairLoading]=useState(false);
  const [dsrHistory,setDsrHistory]=useState(null);
  const [dsrHistLoading,setDsrHistLoading]=useState(false);
  const [editingVid,setEditingVid]=useState(null);
  const [editVNote,setEditVNote]=useState("");
  const [editVBy,setEditVBy]=useState("");
  const [visitBusy,setVisitBusy]=useState(false);

  const live=store.patients.find(x=>x.id===p.id)||p;
  const display=fullData||live;

  useEffect(()=>{
    if(((tab==='records'||tab==='visits'||tab==='info')||showPrintCard)&&fullData===null&&!loadingFull){
      setLoadingFull(true);
      fetch(`${API_BASE}/patients.php?id=${live.id}`,{credentials:'include'})
        .then(r=>r.json())
        .then(data=>{setFullData(data);setLoadingFull(false);})
        .catch(()=>setLoadingFull(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab,fullData,loadingFull,live.id,showPrintCard]);
  const c=CLINICS[live.clinicId];
  const acl="#7c3aed";
  // Returns true if the current user may edit/delete this visit
  const canModifyVisit = (v) => {
    if (readOnly) return false;
    if (user?.role === 'clinic') return v.clinicId === user.clinic;
    if (user?.role === 'regional') return !!(REGIONS[user.region]?.includes(v.clinicId));
    return false;
  };

  const startEdit = () => {
    setEditF({ name:live.name, phone:live.phone, age:live.age, gender:live.gender||"Male", address:live.address||"", source:live.source||"", doctorName:live.doctorName||"", patientStatus:live.patientStatus||"", notes:live.notes||"" });
  };

  const saveEdit = async () => {
    if(!editF.name?.trim()) return;
    if(editF.source==="Doctor Referral"&&!editF.doctorName?.trim()) return;
    if(editF.source==="Hospital"&&!editF.doctorName?.trim()) return;
    setEditBusy(true);
    const updates = {...editF, doctorName: (editF.source==="Doctor Referral"||editF.source==="Hospital") ? editF.doctorName.trim() : null};
    await store.editPatient(live.id, updates, user);
    setEditBusy(false); setTab("info"); setEditDone(true);
    setTimeout(() => setEditDone(false), 3000);
  };

  const upload=async(e)=>{
    const files=Array.from(e.target.files);if(!files.length)return;
    setBusy(true);setUploadMsg(null);
    let ok=0;const fail=[];
    for(const f of files){
      try{await store.addRecord(live.id,{type:f.type.startsWith("image/")?"image":"document",name:f.name,file:f},user);ok++;}
      catch{fail.push(f.name);}
    }
    setBusy(false);setFullData(null);
    setUploadMsg({ok,fail});
    setTimeout(()=>setUploadMsg(null),8000);
  };

  // Fetch repair history when tab is opened
  useEffect(()=>{
    if(tab==='repairs'&&repairHistory===null&&!repairLoading&&live.phone){
      setRepairLoading(true);
      fetch(`${API_BASE}/patients.php?repair_history=1&phone=${encodeURIComponent(live.phone)}`,{credentials:'include'})
        .then(r=>r.json()).then(d=>{ setRepairHistory(Array.isArray(d)?d:[]); setRepairLoading(false); })
        .catch(()=>{ setRepairHistory([]); setRepairLoading(false); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);

  // Fetch DSR history when visits tab is opened (by phone)
  useEffect(()=>{
    if(tab==='visits'&&dsrHistory===null&&!dsrHistLoading&&live.phone){
      setDsrHistLoading(true);
      fetch(`${API_BASE}/dsr.php?phone=${encodeURIComponent(live.phone.replace(/\D/g,''))}`,{credentials:'include'})
        .then(r=>r.json()).then(d=>{ setDsrHistory(Array.isArray(d)?d:[]); setDsrHistLoading(false); })
        .catch(()=>{ setDsrHistory([]); setDsrHistLoading(false); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tab]);

  const tabs = readOnly
    ? ["info","records","audiometry","visits","repairs"]
    : ["info","edit","records","audiometry","visits","repairs"];

  return (
    <div className="fadeIn" style={{position:"fixed",inset:0,background:"#00000066",zIndex:200,display:"flex",alignItems:"stretch",justifyContent:"flex-end",backdropFilter:"blur(5px)"}} onClick={onClose}>
      <div className="slide-in-right" style={{background:"var(--card)",border:`1px solid ${acl}30`,borderRadius:"18px 0 0 18px",width:"min(720px,100vw)",height:"100%",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"-8px 0 40px #00000040"}} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{padding:"18px 22px",borderBottom:"1px solid var(--border)",background:"var(--accentbg)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:48,height:48,borderRadius:13,background:`linear-gradient(135deg,#7c3aed,#6d28d9)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:20}}>{live.name?.[0]?.toUpperCase()}</div>
            <div>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:19,color:"var(--text1)"}}>{live.name}</div>
              <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
                <CopyableCb code={live.id} color={acl}/>
                <span style={{background:acl+"18",color:acl,padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:600}}>{c?.label} · {c?.city}</span>
                {live.source&&<span style={{background:"#ede9fe",color:"#7c3aed",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:600}}>📣 {live.source}</span>}
                {live.patientStatus&&STATUS_COLORS[live.patientStatus]&&<span style={{background:STATUS_COLORS[live.patientStatus].bg,color:STATUS_COLORS[live.patientStatus].color,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700}}>🩺 {live.patientStatus}</span>}
                {editDone&&<span style={{background:"#f0fdf4",color:"#15803d",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:600}}>✅ Saved!</span>}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={()=>setShowPrintCard(true)} style={{padding:"6px 12px",background:"var(--accentbg2)",border:"1px solid var(--border2)",borderRadius:8,color:"var(--accent)",fontSize:11,cursor:"pointer",fontWeight:600}}>🪪 Print Card</button>
            {user?.role==="owner"&&!confirmDelete&&(
              <button onClick={()=>setConfirmDelete(true)} style={{padding:"6px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,color:"#dc2626",fontSize:11,cursor:"pointer",fontWeight:600}}>🗑 Delete Patient</button>
            )}
            {user?.role==="owner"&&confirmDelete&&(
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:"#dc2626",fontWeight:600}}>Sure? This is permanent!</span>
                <button onClick={async()=>{await store.deletePatient(live.id,user);onClose();}} style={{padding:"5px 11px",background:"#dc2626",border:"none",borderRadius:7,color:"#fff",fontSize:11,cursor:"pointer",fontWeight:700}}>Yes, Delete</button>
                <button onClick={()=>setConfirmDelete(false)} style={{padding:"5px 10px",background:"transparent",border:"1px solid var(--border)",borderRadius:7,color:"var(--muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
              </div>
            )}
            <button onClick={onClose} style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:8,width:32,height:32,fontSize:14,cursor:"pointer",color:"var(--muted)",flexShrink:0}}>✕</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"1px solid var(--border)",background:"var(--card)",padding:"0 22px",gap:4}}>
          {tabs.map(t=>(
            <button key={t} onClick={()=>{setTab(t);if(t==="edit")startEdit();}} style={{padding:"11px 14px",background:"none",border:"none",borderBottom:`2px solid ${tab===t?acl:"transparent"}`,color:tab===t?acl:"var(--muted)",fontSize:12,cursor:"pointer",fontWeight:tab===t?700:400,marginBottom:-1,transition:"all 0.15s",textTransform:"capitalize"}}>
              {t==="records"?`Records (${display.records?.length||0})`:t==="visits"?`Visits (${(display.visits?.length||0)+(dsrHistory?.length||0)})`:t==="edit"?"✏️ Edit":t==="info"?"Patient Info":t==="repairs"?`🔧 Service${repairHistory?.length?` (${repairHistory.length})`:''}`:t==="audiometry"?"🎧 Audiometry":t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{flex:1,overflow:"auto",padding:"20px 22px",background:"var(--bg)"}}>

          {/* INFO TAB */}
          {tab==="info"&&(
            <>
            <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[["Full Name",live.name],["Mobile",live.phone],live.altPhone?["Alt. Number",live.altPhone]:null,["Age",live.age?`${live.age} yrs`:"—"],["Gender",live.gender||"—"],["Address",live.address||"—"],["Clinic",c?.label],["City",c?.city],["Case Code",live.id],["Registered",new Date(live.createdAt).toLocaleDateString("en-IN")],["Patient Status",live.patientStatus||"—"]].filter(Boolean).map(([k,v])=>(
                <div key={k} style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:9,padding:"10px 14px"}}>
                  <div style={{fontSize:9,color:"var(--muted2)",fontWeight:700,letterSpacing:1.2,textTransform:"uppercase"}}>{k}</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text1)",marginTop:4}}>{v||"—"}</div>
                </div>
              ))}
              <div style={{gridColumn:"1/-1",background:"var(--card)",border:"1px solid var(--border)",borderRadius:9,padding:"10px 14px",borderLeft:"3px solid #7c3aed"}}>
                <div style={{fontSize:9,color:"var(--muted2)",fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",marginBottom:4}}>📣 Patient Source</div>
                <div style={{fontSize:13,fontWeight:600,color:live.source?"var(--accent)":"var(--muted)"}}>
                  {live.source||<span style={{fontStyle:"italic",fontWeight:400}}>Not recorded</span>}
                </div>
                {live.source==="Doctor Referral"&&live.doctorName&&(
                  <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>👨‍⚕️ Referred by: <strong style={{color:"var(--text1)"}}>{live.doctorName}</strong></div>
                )}
                {live.source==="Hospital"&&live.doctorName&&(
                  <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>🏥 Hospital: <strong style={{color:"var(--text1)"}}>{live.doctorName}</strong></div>
                )}
              </div>
              {live.notes&&(
                <div style={{gridColumn:"1/-1",background:"var(--card)",border:"1px solid var(--border)",borderRadius:9,padding:"10px 14px"}}>
                  <div style={{fontSize:9,color:"var(--muted2)",fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>Initial Notes</div>
                  <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.6}}>{live.notes}</div>
                </div>
              )}
            </div>
            {/* Journey Timeline */}
            {loadingFull&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"16px 0",color:"var(--muted)",fontSize:12}}><span style={{width:12,height:12,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Loading visit history…</div>}
            {!loadingFull&&display.visits&&display.visits.length>0&&(
              <div style={{marginTop:14}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>📅 Patient Journey ({display.visits.length} visit{display.visits.length>1?'s':''})</div>
                <div style={{position:"relative",paddingLeft:22}}>
                  <div style={{position:"absolute",left:8,top:6,bottom:6,width:2,background:"var(--border)",borderRadius:2}}/>
                  {[...(display.visits||[])].reverse().map((v)=>(
                    <div key={v.vid} style={{position:"relative",marginBottom:10}}>
                      <div style={{position:"absolute",left:-14,top:5,width:10,height:10,borderRadius:"50%",background:"#7c3aed",border:"2px solid var(--card)",boxShadow:"0 0 0 2px #7c3aed40"}}/>
                      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,padding:"9px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:4,marginBottom:4}}>
                          <div style={{fontSize:9,color:"var(--muted2)"}}>{new Date(v.ts).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</div>
                          {v.clinicId&&CLINICS[v.clinicId]&&<div style={{background:CLINICS[v.clinicId].color+"18",color:CLINICS[v.clinicId].color,padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>{CLINICS[v.clinicId].label}</div>}
                        </div>
                        {v.attendedBy&&<div style={{fontSize:11,fontWeight:700,color:"var(--accent)",marginBottom:3}}>👨‍⚕️ {v.attendedBy}</div>}
                        <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.5}}>{v.note}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
          )}

          {/* EDIT TAB */}
          {tab==="edit"&&(
            <div>
              <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:12,padding:"20px",boxShadow:"var(--cardshadow)"}}>
                <div style={{fontSize:12,color:"var(--muted)",marginBottom:18,background:"var(--accentbg)",padding:"9px 13px",borderRadius:8,border:"1px solid var(--border)"}}>
                  ✏️ Edit patient details below. Case code <strong style={{color:"var(--accent)"}}>{live.id}</strong> and registration date cannot be changed.
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 18px"}}>
                  {[{k:"name",l:"Full Name *",t:"text"},{k:"phone",l:"Mobile Number",t:"tel"},{k:"age",l:"Age",t:"number"},{k:"address",l:"Address",t:"text"}].map(x=>(
                    <div key={x.k} style={{marginBottom:14}}>
                      <label style={S.label}>{x.l}</label>
                      <input style={S.inp} type={x.t} value={editF[x.k]||""} onChange={e=>setEditF(f=>({...f,[x.k]:e.target.value}))}/>
                    </div>
                  ))}
                  <div style={{marginBottom:14}}>
                    <label style={S.label}>Gender</label>
                    <select style={S.inp} value={editF.gender||"Male"} onChange={e=>setEditF(f=>({...f,gender:e.target.value}))}>
                      <option>Male</option><option>Female</option><option>Other</option>
                    </select>
                  </div>
                  <div style={{marginBottom:14,gridColumn:"1/-1"}}>
                    <label style={S.label}>Patient Source</label>
                    <select style={S.inp} value={editF.source||""} onChange={e=>{const v=e.target.value;setEditF(f=>({...f,source:v,doctorName:(v!=="Doctor Referral"&&v!=="Hospital")?"":f.doctorName}));}}>
                      <option value="">Select source…</option>
                      {SOURCE_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  {editF.source==="Doctor Referral"&&(
                    <div style={{marginBottom:14,gridColumn:"1/-1"}}>
                      <label style={S.label}>👨‍⚕️ Referred by which Doctor? *</label>
                      <input style={S.inp} type="text" placeholder="Enter doctor's name…" value={editF.doctorName||""} onChange={e=>setEditF(f=>({...f,doctorName:e.target.value}))}/>
                    </div>
                  )}
                  {editF.source==="Hospital"&&(
                    <div style={{marginBottom:14,gridColumn:"1/-1"}}>
                      <label style={S.label}>🏥 Hospital Name *</label>
                      <input style={S.inp} type="text" placeholder="Enter hospital name…" value={editF.doctorName||""} onChange={e=>setEditF(f=>({...f,doctorName:e.target.value}))}/>
                    </div>
                  )}
                  <div style={{marginBottom:14,gridColumn:"1/-1"}}>
                    <label style={S.label}>🩺 Patient Status</label>
                    <select style={S.inp} value={editF.patientStatus||""} onChange={e=>setEditF(f=>({...f,patientStatus:e.target.value}))}>
                      <option value="">Select status…</option>
                      {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={{marginBottom:14,gridColumn:"1/-1"}}>
                    <label style={S.label}>Notes</label>
                    <textarea style={{...S.inp,minHeight:72,resize:"vertical"}} value={editF.notes||""} onChange={e=>setEditF(f=>({...f,notes:e.target.value}))}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button className="btn-p" onClick={saveEdit} disabled={editBusy} style={{...S.btn,cursor:editBusy?"not-allowed":"pointer",background:editBusy?"var(--border)":"linear-gradient(135deg,#7c3aed,#6d28d9)"}}>
                    {editBusy?<><Spin/> Saving…</>:"💾 Save Changes"}
                  </button>
                  <button onClick={()=>setTab("info")} style={S.btnG}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* RECORDS TAB */}
          {tab==="records"&&(
            <div>
              {!readOnly&&(
                <div style={{marginBottom:14}}>
                  <button className="btn-p" onClick={()=>fileRef.current.click()} disabled={busy} style={{...S.btn,background:busy?"var(--border)":"linear-gradient(135deg,#7c3aed,#6d28d9)",cursor:busy?"not-allowed":"pointer"}}>
                    {busy?<><Spin/> Uploading…</>:"📎 Upload Image / Report"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*,application/pdf,text/plain,.doc,.docx,.xls,.xlsx" multiple style={{display:"none"}} onChange={upload}/>
                  {uploadMsg&&(
                    <div style={{marginTop:8,padding:"8px 12px",borderRadius:8,fontSize:11,fontWeight:600,
                      background:uploadMsg.fail.length?"#fef2f2":"#f0fdf4",
                      color:uploadMsg.fail.length?"#dc2626":"#15803d",
                      border:`1px solid ${uploadMsg.fail.length?"#fecaca":"#bbf7d0"}`}}>
                      {uploadMsg.ok>0&&<div>✅ {uploadMsg.ok} file{uploadMsg.ok>1?"s":""} uploaded successfully.</div>}
                      {uploadMsg.fail.length>0&&<div>❌ Failed: {uploadMsg.fail.join(", ")}</div>}
                    </div>
                  )}
                </div>
              )}
              {loadingFull&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"24px 0",color:"var(--muted)",fontSize:12}}><span style={{width:14,height:14,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Loading…</div>}
              {!loadingFull&&(!display.records||display.records.length===0)&&<Empty msg="No records uploaded yet"/>}
              {!loadingFull&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))",gap:10}}>
                {(display.records||[]).map(r=>{
                  const isBroken=r.type!=="image"&&(r.name||"").toLowerCase().endsWith(".jpg");
                  const canView=!isBroken&&(r.type==="image"||(r.name||"").match(/\.(pdf|txt)$/i));
                  return(
                  <div key={r.rid} className="rec-card" style={{borderRadius:11,overflow:"hidden",border:`1px solid ${isBroken?"#fca5a5":"var(--border)"}`,background:"var(--card)",cursor:canView?"pointer":"default",position:"relative"}} onClick={()=>{
                    if(isBroken)return;
                    if(r.type==="image"){setLb({src:imgSrc(r.data),name:r.name});}
                    else if((r.name||"").match(/\.pdf$/i)){setFileViewer({url:imgSrc(r.data),name:r.name,kind:"pdf"});}
                    else if((r.name||"").match(/\.txt$/i)){setFileViewer({url:imgSrc(r.data),name:r.name,kind:"txt"});}
                  }}>
                    {isBroken?<div style={{height:100,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:22,background:"#fef2f2"}}>⚠️<div style={{fontSize:9,color:"#dc2626",fontWeight:700,marginTop:4,textAlign:"center",padding:"0 6px"}}>Re-upload needed</div><div style={{fontSize:8,color:"#9ca3af",marginTop:2,textAlign:"center",padding:"0 6px"}}>Saved with wrong extension</div></div>
                    :r.type==="image"?<img src={imgSrc(r.data,true)} onContextMenu={e=>e.preventDefault()} width="100%" height={115} style={{width:"100%",height:115,objectFit:"cover",display:"block",WebkitUserDrag:"none"}} alt={r.name}/>:<div style={{height:100,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:26,color:"var(--muted)"}}>{(r.name||"").match(/\.pdf$/i)?"📕":(r.name||"").match(/\.docx?$/i)?"📝":(r.name||"").match(/\.xlsx?$/i)?"📊":(r.name||"").match(/\.txt$/i)?"📝":"📄"}<div style={{fontSize:10,color:"var(--muted2)",marginTop:5,textAlign:"center",padding:"0 6px"}}>{r.name}</div><div style={{fontSize:9,color:"#7c3aed",marginTop:3}}>{(r.name||"").match(/\.(pdf|txt)$/i)?"Tap to view":""}</div></div>}
                    <div style={{padding:"7px 9px",borderTop:"1px solid var(--border)"}}>
                      <div style={{fontSize:10,fontWeight:600,color:"var(--text1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
                      <div style={{fontSize:9,color:"var(--muted2)",marginTop:2}}>{new Date(r.ts).toLocaleDateString("en-IN")}</div>
                    </div>
                    {["sl-office","sl-bangalore","sl-mumbai"].includes(user?.username)&&<button onClick={e=>{e.stopPropagation();if(!window.confirm("Delete this record? This cannot be undone."))return;store.deleteRecord(live.id,r.rid,user);setFullData(null);}} style={{width:"100%",padding:"5px",background:"#fef2f2",border:"none",borderTop:"1px solid var(--border)",color:"#dc2626",fontSize:10,cursor:"pointer"}}>🗑 Delete</button>}
                  </div>
                );})}
              </div>}
            </div>
          )}

          {/* AUDIOMETRY TAB */}
          {tab==="audiometry"&&(
            <AudiometryTab patient={live} readOnly={readOnly} user={user}/>
          )}

          {/* VISITS TAB */}
          {tab==="visits"&&(
            <div>
              {!readOnly&&(
                <div style={{background:"var(--card)",border:"1px solid var(--cardborder)",borderRadius:11,padding:"14px",marginBottom:14,boxShadow:"var(--cardshadow)"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:0.8,textTransform:"uppercase",marginBottom:10}}>📝 Log Today's Visit</div>
                  <div style={{marginBottom:10}}>
                    <label style={S.label}>👨‍⚕️ Attended By (Audiologist Name) *</label>
                    <input style={S.inp} placeholder="e.g. Dr. Priya Shah" value={attendedBy} onChange={e=>setAttendedBy(e.target.value)}/>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <textarea style={{...S.inp,flex:1,minHeight:54,resize:"vertical"}} placeholder="Visit note / observations…" value={note} onChange={e=>setNote(e.target.value)}/>
                    <button className="btn-p" onClick={async()=>{if(!note.trim()||!attendedBy.trim())return;await store.addVisit(live.id,{note,attendedBy:attendedBy.trim(),clinicId:currentClinicId||live.clinicId},user);setNote("");setAttendedBy("");setFullData(null);}} style={{...S.btn,alignSelf:"flex-end",whiteSpace:"nowrap",fontSize:12}}>+ Log Visit</button>
                  </div>
                  {(!note.trim()||!attendedBy.trim())&&<div style={{fontSize:10,color:"var(--muted2)",marginTop:6}}>⚠️ Both audiologist name and visit note are required.</div>}
                </div>
              )}
              {(loadingFull||dsrHistLoading)&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"24px 0",color:"var(--muted)",fontSize:12}}><span style={{width:14,height:14,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Loading…</div>}
              {!loadingFull&&!dsrHistLoading&&(!display.visits||display.visits.length===0)&&(!dsrHistory||dsrHistory.length===0)&&<Empty msg="No visit logs yet"/>}
              {/* DSR entries merged in */}
              {!dsrHistLoading&&(dsrHistory||[]).map(e=>{
                const cc=CLINICS[e.clinic_id];
                return(
                <div key={`dsr-${e.id}`} style={{background:"var(--card)",border:"1px solid var(--border)",borderLeft:"3px solid #7c3aed",borderRadius:9,padding:"11px 14px",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                    <span style={{fontSize:9,color:"var(--muted2)"}}>{e.entry_date}{e.entry_time?` · ${e.entry_time}`:''}</span>
                    {cc&&<span style={{background:cc.color+"18",color:cc.color,padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:700}}>{cc.label}</span>}
                    {e.purpose&&<span style={{background:"#ede9fe",color:"#7c3aed",padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:600}}>{e.purpose}</span>}
                    {e.amount>0&&<span style={{background:"#d1fae5",color:"#065f46",padding:"1px 7px",borderRadius:4,fontSize:10,fontWeight:700}}>₹{parseFloat(e.amount).toLocaleString('en-IN')}</span>}
                    {e.payment_mode&&<span style={{background:"#f1f5f9",color:"#475569",padding:"1px 6px",borderRadius:4,fontSize:9,fontWeight:600}}>{e.payment_mode}</span>}
                    {parseInt(e.nr)?<span style={{background:"#fef3c7",color:"#92400e",padding:"1px 5px",borderRadius:4,fontSize:9,fontWeight:700}}>NR</span>:null}
                    <span style={{marginLeft:"auto",fontSize:8,color:"var(--muted2)",fontWeight:600,letterSpacing:0.5}}>DSR</span>
                  </div>
                  {e.audiologist&&<div style={{fontSize:11,fontWeight:700,color:"var(--accent)",marginBottom:3}}>👨‍⚕️ {e.audiologist}</div>}
                  {e.remarks&&<div style={{fontSize:12,color:"var(--muted)",lineHeight:1.6}}>{e.remarks}</div>}
                  {e.invoice_no&&<div style={{fontSize:10,color:"var(--muted2)",marginTop:3}}>Inv: {e.invoice_no}</div>}
                </div>
                );
              })}
              {!loadingFull&&[...(display.visits||[])].reverse().map(v=>{
                const vDate = new Date(v.ts).toDateString();
                const sameDayImgs = (display.records||[]).filter(r=>r.type==="image"&&new Date(r.ts).toDateString()===vDate);
                const isEditing = editingVid === v.vid;
                const canMod = canModifyVisit(v);
                return (
                <div key={v.vid} style={{background:"var(--card)",border:`1px solid ${isEditing?"var(--accent)":"var(--border)"}`,borderRadius:9,padding:"11px 14px",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5,flexWrap:"wrap",gap:4}}>
                    <div style={{fontSize:9,color:"var(--muted2)"}}>{new Date(v.ts).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</div>
                    <div style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
                      {v.clinicId&&CLINICS[v.clinicId]&&(<div style={{background:CLINICS[v.clinicId].color+"18",color:CLINICS[v.clinicId].color,padding:"2px 8px",borderRadius:5,fontSize:9,fontWeight:700}}>🏥 {CLINICS[v.clinicId].label} · {CLINICS[v.clinicId].city}</div>)}
                      {canMod&&!isEditing&&(<>
                        <button onClick={()=>{setEditingVid(v.vid);setEditVNote(v.note);setEditVBy(v.attendedBy||"");}} style={{padding:"2px 8px",background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:5,fontSize:9,color:"var(--accent)",cursor:"pointer",fontWeight:600}}>✏️ Edit</button>
                        <button onClick={async()=>{if(visitBusy)return;setVisitBusy(true);await store.deleteVisit(live.id,v.vid,user);setFullData(null);setVisitBusy(false);}} disabled={visitBusy} style={{padding:"2px 8px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:5,fontSize:9,color:"#dc2626",cursor:"pointer",fontWeight:600}}>🗑 Remove</button>
                      </>)}
                    </div>
                  </div>
                  {isEditing ? (
                    <div style={{marginTop:6}}>
                      <div style={{marginBottom:8}}>
                        <label style={{...S.label,fontSize:10}}>👨‍⚕️ Attended By</label>
                        <input style={{...S.inp,fontSize:12}} value={editVBy} onChange={e=>setEditVBy(e.target.value)} placeholder="Audiologist name"/>
                      </div>
                      <div style={{marginBottom:8}}>
                        <label style={{...S.label,fontSize:10}}>Visit Note *</label>
                        <textarea style={{...S.inp,minHeight:54,resize:"vertical",fontSize:12}} value={editVNote} onChange={e=>setEditVNote(e.target.value)} placeholder="Visit note…"/>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn-p" onClick={async()=>{
                          if(!editVNote.trim()||visitBusy)return;
                          setVisitBusy(true);
                          await store.editVisit(live.id,v.vid,{note:editVNote.trim(),attendedBy:editVBy.trim()},user);
                          setEditingVid(null);setFullData(null);setVisitBusy(false);
                        }} disabled={visitBusy||!editVNote.trim()} style={{...S.btn,fontSize:12,padding:"6px 16px"}}>
                          {visitBusy?"Saving…":"Save"}
                        </button>
                        <button onClick={()=>setEditingVid(null)} style={{...S.btnG,fontSize:12,padding:"6px 14px"}}>Cancel</button>
                      </div>
                    </div>
                  ) : (<>
                    {v.attendedBy&&<div style={{fontSize:11,fontWeight:700,color:"var(--accent)",marginBottom:4}}>👨‍⚕️ {v.attendedBy}</div>}
                    <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.6}}>{v.note}</div>
                  </>)}
                  {sameDayImgs.length>0&&(
                    <div style={{marginTop:8,paddingTop:8,borderTop:"1px dashed var(--border)"}}>
                      <div style={{fontSize:9,color:"var(--muted2)",fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",marginBottom:6}}>📷 Records from this visit</div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {sameDayImgs.map(r=>(
                          <button key={r.rid} onClick={()=>setLb({src:imgSrc(r.data),name:r.name})}
                            style={{padding:"4px 10px",background:"var(--accentbg)",border:"1px solid var(--border2)",borderRadius:6,color:"var(--accent)",fontSize:10,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
                            🔍 View · {r.name.length>18?r.name.slice(0,18)+"…":r.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* REPAIRS TAB — 360 view: service portal history */}
          {tab==="repairs"&&(
            <div>
              {repairLoading&&(
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"24px 0",color:"var(--muted)",fontSize:13}}>
                  <span style={{width:14,height:14,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>
                  Loading service repair history…
                </div>
              )}
              {!repairLoading&&repairHistory!==null&&repairHistory.length===0&&(
                <div style={{textAlign:"center",padding:"48px 24px"}}>
                  <div style={{fontSize:36,marginBottom:12}}>🔧</div>
                  <div style={{fontSize:15,fontWeight:700,color:"var(--text1)",marginBottom:6}}>No service repairs</div>
                  <div style={{fontSize:12,color:"var(--muted)"}}>This patient has no repair tickets in the service portal yet.</div>
                </div>
              )}
              {!repairLoading&&repairHistory&&repairHistory.length>0&&(()=>{
                const SVC_STATUS_COLOR={
                  'Form Submitted':'#6b7280','Courier Sent':'#2563eb','Received at HO':'#0891b2',
                  'Sent to Company':'#7c3aed','Estimate Received':'#d97706','Patient Informed':'#f59e0b',
                  'Patient Approved':'#059669','Under Repair':'#0891b2','Dispatched to Branch':'#7c3aed',
                  'Received at Clinic':'#2563eb','Delivered to Patient':'#059669','Patient Rejected':'#dc2626',
                };
                const SVC_STATUS_ICON={
                  'Form Submitted':'📋','Courier Sent':'📦','Received at HO':'🏢','Sent to Company':'🏭',
                  'Estimate Received':'💰','Patient Informed':'📞','Patient Approved':'✅',
                  'Under Repair':'🔧','Dispatched to Branch':'🚚','Received at Clinic':'📥',
                  'Delivered to Patient':'🎉','Patient Rejected':'❌',
                };
                return (
                  <div>
                    {/* Summary strip */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:20}}>
                      {[
                        {icon:"🔧",label:"Total Repairs",value:repairHistory.length,col:"#7c3aed"},
                        {icon:"✅",label:"Delivered",value:repairHistory.filter(r=>r.status==='Delivered to Patient').length,col:"#059669"},
                        {icon:"🎁",label:"FOC Repairs",value:repairHistory.filter(r=>r.company_report_type==='FOC').length,col:"#2563eb"},
                        {icon:"📅",label:"First Repair",value:repairHistory[repairHistory.length-1]?.repair_date?.slice(0,7)||'—',col:"#d97706"},
                      ].map(s=>(
                        <div key={s.label} style={{background:"var(--card)",border:`1px solid ${s.col}22`,borderRadius:10,padding:"12px 14px",borderLeft:`3px solid ${s.col}`}}>
                          <div style={{fontSize:18,marginBottom:4}}>{s.icon}</div>
                          <div style={{fontSize:18,fontWeight:900,color:s.col,lineHeight:1}}>{s.value}</div>
                          <div style={{fontSize:10,color:"var(--muted)",marginTop:3,fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {/* Repair cards */}
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      {repairHistory.map((r,i)=>{
                        const sc=SVC_STATUS_COLOR[r.status]||'#6b7280';
                        const si=SVC_STATUS_ICON[r.status]||'🔧';
                        const isDelivered=r.status==='Delivered to Patient';
                        return (
                          <div key={r.id} style={{background:"var(--card)",border:`1px solid var(--border)`,borderRadius:12,overflow:"hidden",borderLeft:`4px solid ${sc}`}}>
                            <div style={{padding:"13px 16px",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
                                  <span style={{fontFamily:"monospace",fontSize:12,fontWeight:800,color:"#7c3aed"}}>{r.repair_no}</span>
                                  <span style={{background:`${sc}18`,color:sc,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700}}>{si} {r.status}</span>
                                  {r.company_report_type==='FOC'&&<span style={{background:"#dcfce7",color:"#15803d",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:700}}>🎁 FOC</span>}
                                  {r.company_report_type==='Estimate'&&<span style={{background:"#fef3c7",color:"#b45309",padding:"2px 7px",borderRadius:5,fontSize:10,fontWeight:700}}>💰 Paid</span>}
                                </div>
                                <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:12,color:"var(--muted)"}}>
                                  {r.ha_brand&&<span style={{fontWeight:600,color:"var(--text1)"}}>{r.ha_brand}{r.ha_model?` · ${r.ha_model}`:''}</span>}
                                  {r.ha_type&&<span>{r.ha_type}</span>}
                                  {r.warranty_type&&<span style={{color:r.warranty_type==='Under Warranty'?'#059669':'#d97706',fontWeight:600}}>{r.warranty_type==='Under Warranty'?'🛡️ Under Warranty':'⚠️ '+r.warranty_type}</span>}
                                </div>
                                {r.outcome_note&&<div style={{marginTop:8,padding:"7px 10px",background:"#f0fdf4",borderRadius:7,fontSize:11,color:"#166534",fontWeight:600,border:"1px solid #bbf7d0"}}>📋 {r.outcome_note}</div>}
                              </div>
                              <div style={{textAlign:"right",flexShrink:0}}>
                                <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{r.clinic_label||'—'}</div>
                                <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>{r.repair_date}</div>
                                {isDelivered&&r.delivered_to_patient&&<div style={{fontSize:10,color:"#059669",marginTop:2,fontWeight:600}}>✓ Del. {r.delivered_to_patient?.slice(0,10)}</div>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

        </div>
      </div>
      {showPrintCard && <PrintCardModal p={display} onClose={()=>setShowPrintCard(false)}/>}
      {fileViewer && <FileViewerModal {...fileViewer} onClose={()=>setFileViewer(null)}/>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// AUDIOMETRY TAB — Pure Tone Audiometry plotter
// ══════════════════════════════════════════════════════════
const AU_FREQS=[125,250,500,750,1000,1500,2000,3000,4000,6000,8000];
const AU_LABELS=['125','250','500','750','1k','1.5k','2k','3k','4k','6k','8k'];
const AU_DBS=[-10,0,10,20,30,40,50,60,70,80,90,100,110,120];
// const AU_PTA_F=[500,1000,2000,4000]; // kept for reference
const AU_ML=54,AU_MT=52;
const AU_H=380,AU_CW=46,AU_RH=22;
function auFx(i){return AU_ML+i*AU_CW;}
function auFreqX(f){return auFx(AU_FREQS.indexOf(f));}
function auDbY(db){return AU_MT+((db-AU_DBS[0])/10)*AU_RH;}
function auIsR(t){return t.includes('-r');}
function auIsAC(t){return t.startsWith('ac');}
function auClr(t){return auIsR(t)?'#dc2626':'#2563eb';}
function auSameSlot(p,type,freq){return p.freq===freq&&auIsR(p.type)===auIsR(type)&&auIsAC(p.type)===auIsAC(type);}
function auSnap(svgX,svgY){
  const fi=Math.max(0,Math.min(AU_FREQS.length-1,Math.round((svgX-AU_ML)/AU_CW)));
  const db=Math.max(AU_DBS[0],Math.min(AU_DBS[AU_DBS.length-1],Math.round((AU_DBS[0]+(svgY-AU_MT)/AU_RH*10)/5)*5));
  return{freq:AU_FREQS[fi],db};
}
function auAutoNote(pts){
  function thr(ear,mod,freq){
    const isR=(ear==='right');
    // same ear, same modality (AC or BC); prefer unmasked, fall back to masked
    const ep=pts.filter(p=>auIsR(p.type)===isR&&auIsAC(p.type)===(mod==='ac'));
    const pt=ep.find(p=>p.freq===freq&&!p.type.endsWith('-m'))||ep.find(p=>p.freq===freq&&p.type.endsWith('-m'));
    return pt?pt.db:null;
  }
  // Raw (unrounded) PTA for display
  function ptaRaw(ear){
    const vals=[500,1000,2000].map(f=>thr(ear,'ac',f)).filter(v=>v!==null);
    if(vals.length<2)return null;
    return vals.reduce((a,b)=>a+b,0)/vals.length;
  }
  // Rounded PTA for classification
  function pta(ear){const v=ptaRaw(ear);return v!==null?Math.round(v):null;}
  function bcPta(ear){
    const vals=[500,1000,2000].map(f=>thr(ear,'bc',f)).filter(v=>v!==null);
    if(vals.length<2)return null;
    return Math.round(vals.reduce((a,b)=>a+b,0)/vals.length);
  }
  function sev(v){
    if(v===null)return null;
    if(v<=25)return'NORMAL';if(v<=40)return'MILD';if(v<=55)return'MODERATE';
    if(v<=70)return'MODERATELY SEVERE';if(v<=90)return'SEVERE';return'PROFOUND';
  }
  function lossType(ear){
    const acV=pta(ear);if(acV===null)return null;
    if(acV<=25)return'normal';
    const bcV=bcPta(ear);
    if(bcV===null)return'hl';
    const gap=acV-bcV;
    if(bcV<=25&&gap>10)return'conductive';
    if(bcV>25&&gap>10)return'mixed';
    return'snhl';
  }
  function config(ear){
    const f=[250,500,1000,1500,2000,3000,4000,6000,8000];
    const t=f.map(fr=>({f:fr,v:thr(ear,'ac',fr)})).filter(x=>x.v!==null);
    if(t.length<3)return null;
    const get=fr=>{const x=t.find(x=>x.f===fr);return x?x.v:null;};
    const t250=get(250),t500=get(500),t1k=get(1000),t2k=get(2000),t3k=get(3000),t4k=get(4000),t8k=get(8000); // eslint-disable-line no-unused-vars
    if(t500!==null&&t1k!==null&&t2k!==null&&t4k!==null){
      const lowNorm=t500<=25&&t1k<=25&&t2k<=30;
      const notch4k=t4k>=40&&t4k-t2k>=20;
      const recovery=t8k!==null&&t8k<t4k-10;
      if(lowNorm&&notch4k)return recovery?'4kHz notch with recovery at 8kHz':'4kHz high-frequency notch';
    }
    if(t1k!==null&&t2k!==null&&t4k!==null&&t1k<=25&&t2k<=30&&t4k>=40&&(t3k===null||t3k>=35)){
      return'noise-induced notch (3–4kHz)';
    }
    if(t500!==null&&t4k!==null&&t500<=25&&t4k>25){
      if(t2k!==null&&t2k<=25&&t4k>=30)return'high-frequency loss (beyond 2kHz)';
      return'sloping high-frequency loss';
    }
    const vals=t.map(x=>x.v);
    const range=Math.max(...vals)-Math.min(...vals);
    if(range<=15)return'flat';
    if(t250!==null&&t2k!==null&&t250-t2k>=20)return'rising (low-frequency) configuration';
    if(t1k!==null&&t500!==null&&t4k!==null&&t1k>Math.max(t500,t4k)+15)return'mid-frequency (cookie-bite) configuration';
    const first=t[0].v,last=t[t.length-1].v;
    if(last-first>20)return'sloping';
    return null;
  }
  // Per-ear diagnosis line (all caps, structured)
  function diagLine(ear){
    const ac=pta(ear);if(ac===null)return null;
    const sv=sev(ac);const lt=lossType(ear);const cfg=config(ear);
    if(sv==='NORMAL'){
      if(cfg&&cfg.includes('noise'))return'NORMAL HEARING SENSITIVITY WITH NOISE-INDUCED NOTCH AT 3–4kHz.';
      if(cfg&&cfg.includes('notch'))return'NORMAL HEARING SENSITIVITY WITH HIGH-FREQUENCY NOTCH DIP AT 4kHz.';
      return'NORMAL HEARING SENSITIVITY.';
    }
    let typeStr='';
    if(lt==='conductive')typeStr=' CONDUCTIVE HEARING LOSS';
    else if(lt==='mixed')typeStr=' MIXED HEARING LOSS';
    else if(lt==='snhl')typeStr=' SENSORINEURAL HEARING LOSS';
    else typeStr=' HEARING LOSS';
    let cfgStr='';
    if(cfg==='sloping high-frequency loss'||cfg==='high-frequency loss (beyond 2kHz)')cfgStr=', SLOPING HIGH-FREQUENCY';
    else if(cfg==='4kHz high-frequency notch'||cfg==='4kHz notch with recovery at 8kHz')cfgStr=', 4kHz NOTCH PATTERN';
    else if(cfg==='noise-induced notch (3–4kHz)')cfgStr=', NOISE-INDUCED 3–4kHz NOTCH';
    else if(cfg==='flat')cfgStr=', FLAT CONFIGURATION';
    else if(cfg==='rising (low-frequency) configuration')cfgStr=', RISING LOW-FREQUENCY CONFIGURATION';
    else if(cfg==='mid-frequency (cookie-bite) configuration')cfgStr=', MID-FREQUENCY (COOKIE-BITE) PATTERN';
    else if(cfg==='sloping')cfgStr=', SLOPING CONFIGURATION';
    return sv+typeStr+cfgStr+'.';
  }
  // Rinne test (512 Hz → 500 Hz; fallback 1000 Hz)
  function rinne(ear){
    let ac=thr(ear,'ac',500),bc=thr(ear,'bc',500);
    if(ac===null){ac=thr(ear,'ac',1000);bc=thr(ear,'bc',1000);}
    if(ac===null)return null;
    if(bc===null)return'Positive (BC not recorded)';
    if(ac-bc>=15)return'Negative (BC > AC — conductive component)';
    if(ac-bc>=5)return'Weakly Positive';
    return'Positive (AC > BC — normal / SNHL)';
  }
  // Weber test — whichever ear has lower BC threshold hears it louder
  function weber(){
    let bcR=thr('right','bc',500),bcL=thr('left','bc',500);
    if(bcR===null)bcR=thr('right','bc',1000);
    if(bcL===null)bcL=thr('left','bc',1000);
    if(bcR===null&&bcL===null)return null;
    if(bcR===null)return'Lateralizes to LEFT ear';
    if(bcL===null)return'Lateralizes to RIGHT ear';
    if(Math.abs(bcR-bcL)<=5)return'Midline — no lateralization (symmetric BC)';
    if(bcR<bcL)return'Lateralizes to RIGHT ear';
    return'Lateralizes to LEFT ear';
  }
  const rRaw=ptaRaw('right');const lRaw=ptaRaw('left');
  const rDiag=diagLine('right');const lDiag=diagLine('left');
  if(!rDiag&&!lDiag)return'';
  const rRinne=rinne('right'),lRinne=rinne('left'),weberRes=weber();
  const rinneLine=[rRinne?'RIGHT: '+rRinne:null,lRinne?'LEFT: '+lRinne:null].filter(Boolean).join(' | ');
  let out='OTOSCOPY EVALUATION:\nRIGHT EAR: \nLEFT EAR: \n';
  out+='\nTUNING FORK TEST:\nRINNE TEST: '+(rinneLine||'—')+'\nWEBER TEST: '+(weberRes||'—')+'\n';
  out+='\nPURE TONE AVERAGE:\n';
  if(rRaw!==null)out+='RIGHT EAR: '+rRaw.toFixed(2)+' dBHL\n';
  if(lRaw!==null)out+='LEFT EAR: '+lRaw.toFixed(2)+' dBHL\n';
  out+='\nPROVISIONAL DIAGNOSIS:\n';
  if(rDiag)out+='RIGHT EAR: '+rDiag+'\n';
  if(lDiag)out+='LEFT EAR: '+lDiag;
  return out.trim();
}
function computeAutoMasking(pts){
  // Returns Set of base-"type_freq" keys that need masking per clinical cross-hearing rules.
  // AC rules (IA ≈ 40 dB supra-aural): mask if test AC − opposite BC ≥ 40 dB
  // BC rules (IA ≈ 0 dB):              mask if opposite AC − test BC ≤ 10 dB
  const m={};
  pts.forEach(p=>{
    if(!p.nr){
      const bt=p.type.endsWith('-m')?p.type.slice(0,-2):p.type; // normalise stored type
      m[bt+'_'+p.freq]=p.db;
    }
  });
  const masked=new Set();
  AU_FREQS.forEach(f=>{
    const acr=m['ac-r_'+f],acl=m['ac-l_'+f],bcr=m['bc-r_'+f],bcl=m['bc-l_'+f];
    if(acr!==undefined&&bcl!==undefined&&acr-bcl>=40)masked.add('ac-r_'+f);
    if(acl!==undefined&&bcr!==undefined&&acl-bcr>=40)masked.add('ac-l_'+f);
    if(bcr!==undefined&&acl!==undefined&&acl-bcr<=10)masked.add('bc-r_'+f);
    if(bcl!==undefined&&acr!==undefined&&acr-bcl<=10)masked.add('bc-l_'+f);
  });
  return masked;
}
// Resolve the effective rendering type for a point, respecting explicit overrides.
// maskOverride===false → always unmasked (user chose base symbol explicitly)
// maskOverride===true  → always masked   (user chose masked symbol explicitly)
// maskOverride===null/undefined → auto (apply computeAutoMasking result; for legacy saved data)
function auEffType(p,maskedSet){
  const bt=p.type.endsWith('-m')?p.type.slice(0,-2):p.type;
  if(p.maskOverride===false)return bt;
  if(p.maskOverride===true)return bt+'-m';
  // auto: legacy data or table entries — apply rules
  return maskedSet.has(bt+'_'+p.freq)?bt+'-m':bt;
}
function auDrawSym(parent,x,y,type,nr,sz,pt,onErase,eraserOn){
  const ns='http://www.w3.org/2000/svg';
  sz=sz||7;const c=auClr(type);const g=document.createElementNS(ns,'g');
  if(auIsAC(type)){
    if(auIsR(type)){
      if(type==='ac-r-m'){const r=sz,tri=document.createElementNS(ns,'polygon');tri.setAttribute('points',x+','+(y-r)+' '+(x+r*0.866)+','+(y+r*0.5)+' '+(x-r*0.866)+','+(y+r*0.5));tri.setAttribute('fill','none');tri.setAttribute('stroke',c);tri.setAttribute('stroke-width','2');g.appendChild(tri);}
      else{const cl=document.createElementNS(ns,'circle');cl.setAttribute('cx',x);cl.setAttribute('cy',y);cl.setAttribute('r',sz);cl.setAttribute('fill','none');cl.setAttribute('stroke',c);cl.setAttribute('stroke-width','2');g.appendChild(cl);}
    } else {
      if(type==='ac-l-m'){const rc=document.createElementNS(ns,'rect');rc.setAttribute('x',x-sz+1);rc.setAttribute('y',y-sz+1);rc.setAttribute('width',(sz-1)*2);rc.setAttribute('height',(sz-1)*2);rc.setAttribute('fill','none');rc.setAttribute('stroke',c);rc.setAttribute('stroke-width','2');g.appendChild(rc);}
      else{const l1=document.createElementNS(ns,'line'),l2=document.createElementNS(ns,'line');l1.setAttribute('x1',x-sz);l1.setAttribute('y1',y-sz);l1.setAttribute('x2',x+sz);l1.setAttribute('y2',y+sz);l1.setAttribute('stroke',c);l1.setAttribute('stroke-width','2.5');l1.setAttribute('stroke-linecap','round');l2.setAttribute('x1',x+sz);l2.setAttribute('y1',y-sz);l2.setAttribute('x2',x-sz);l2.setAttribute('y2',y+sz);l2.setAttribute('stroke',c);l2.setAttribute('stroke-width','2.5');l2.setAttribute('stroke-linecap','round');g.appendChild(l1);g.appendChild(l2);}
    }
  } else {
    const isMasked=type.endsWith('-m');const p=document.createElementNS(ns,'path');
    if(auIsR(type)){p.setAttribute('d',isMasked?'M'+(x+sz-1)+','+(y-sz)+' L'+(x-sz+2)+','+(y-sz)+' L'+(x-sz+2)+','+(y+sz)+' L'+(x+sz-1)+','+(y+sz):'M'+(x+sz)+','+(y-sz)+' L'+(x-sz+2)+','+y+' L'+(x+sz)+','+(y+sz));}
    else{p.setAttribute('d',isMasked?'M'+(x-sz+1)+','+(y-sz)+' L'+(x+sz-2)+','+(y-sz)+' L'+(x+sz-2)+','+(y+sz)+' L'+(x-sz+1)+','+(y+sz):'M'+(x-sz)+','+(y-sz)+' L'+(x+sz-2)+','+y+' L'+(x-sz)+','+(y+sz));}
    p.setAttribute('fill','none');p.setAttribute('stroke',c);p.setAttribute('stroke-width','2.5');p.setAttribute('stroke-linecap','round');p.setAttribute('stroke-linejoin','round');g.appendChild(p);
  }
  if(nr){
    // Downward diagonal arrow (standard audiometric NR: right ear → lower-left, left ear → lower-right)
    const r_r=type.includes('-r');const dir=r_r?-1:1;
    const tx1=x,ty1=y+sz+1,tx2=x+dir*(sz+6),ty2=y+sz+18;
    const tail=document.createElementNS(ns,'line');
    tail.setAttribute('x1',tx1);tail.setAttribute('y1',ty1);tail.setAttribute('x2',tx2);tail.setAttribute('y2',ty2);
    tail.setAttribute('stroke',c);tail.setAttribute('stroke-width','2');tail.setAttribute('stroke-linecap','round');g.appendChild(tail);
    // Arrowhead at tip
    const ang=Math.atan2(ty2-ty1,tx2-tx1);const as=6;
    const ah=document.createElementNS(ns,'path');
    ah.setAttribute('d','M'+tx2+','+ty2+' L'+(tx2-as*Math.cos(ang-0.45))+','+(ty2-as*Math.sin(ang-0.45))+' M'+tx2+','+ty2+' L'+(tx2-as*Math.cos(ang+0.45))+','+(ty2-as*Math.sin(ang+0.45)));
    ah.setAttribute('fill','none');ah.setAttribute('stroke',c);ah.setAttribute('stroke-width','2');ah.setAttribute('stroke-linecap','round');g.appendChild(ah);
  }
  const hit=document.createElementNS(ns,'circle');hit.setAttribute('cx',x);hit.setAttribute('cy',y);hit.setAttribute('r',String(sz+5));hit.setAttribute('fill','rgba(0,0,0,0)');hit.setAttribute('pointer-events','all');g.appendChild(hit);
  // Only intercept click for erasing — when eraser is OFF let click bubble to SVG for plotting
  if(pt&&onErase){g.addEventListener('click',function(e){if(eraserOn){e.stopPropagation();onErase(pt,g);}});}
  parent.appendChild(g);
}
function auBuildGrid(svgEl){
  const ns='http://www.w3.org/2000/svg';svgEl.innerHTML='';
  const zones=[{lo:-10,hi:25,fill:'rgba(34,197,94,.07)'},{lo:25,hi:40,fill:'rgba(234,179,8,.09)'},{lo:40,hi:55,fill:'rgba(249,115,22,.09)'},{lo:55,hi:70,fill:'rgba(239,68,68,.08)'},{lo:70,hi:90,fill:'rgba(220,38,38,.1)'},{lo:90,hi:120,fill:'rgba(185,28,28,.12)'}];
  zones.forEach(z=>{const r=document.createElementNS(ns,'rect');r.setAttribute('x',AU_ML);r.setAttribute('y',auDbY(z.lo));r.setAttribute('width',auFx(AU_FREQS.length-1)-AU_ML);r.setAttribute('height',auDbY(z.hi)-auDbY(z.lo));r.setAttribute('fill',z.fill);r.setAttribute('pointer-events','none');svgEl.appendChild(r);});
  AU_DBS.forEach(db=>{const y=auDbY(db);const line=document.createElementNS(ns,'line');line.setAttribute('x1',AU_ML);line.setAttribute('x2',auFx(AU_FREQS.length-1));line.setAttribute('y1',y);line.setAttribute('y2',y);line.setAttribute('stroke',db===0?'#9ca3af':'#e5e7eb');line.setAttribute('stroke-width',db===0?1.2:0.8);line.setAttribute('pointer-events','none');svgEl.appendChild(line);const lbl=document.createElementNS(ns,'text');lbl.setAttribute('x',AU_ML-6);lbl.setAttribute('y',y+4);lbl.setAttribute('text-anchor','end');lbl.setAttribute('font-size','10');lbl.setAttribute('fill','#6b7280');lbl.setAttribute('pointer-events','none');lbl.textContent=db;svgEl.appendChild(lbl);});
  AU_FREQS.forEach((f,i)=>{const x=auFx(i);const line=document.createElementNS(ns,'line');line.setAttribute('x1',x);line.setAttribute('x2',x);line.setAttribute('y1',AU_MT);line.setAttribute('y2',auDbY(120));line.setAttribute('stroke','#e5e7eb');line.setAttribute('stroke-width',0.8);line.setAttribute('pointer-events','none');svgEl.appendChild(line);const lbl=document.createElementNS(ns,'text');lbl.setAttribute('x',x);lbl.setAttribute('y',AU_MT-8);lbl.setAttribute('text-anchor','middle');lbl.setAttribute('font-size','10');lbl.setAttribute('fill','#6b7280');lbl.setAttribute('pointer-events','none');lbl.textContent=AU_LABELS[i];svgEl.appendChild(lbl);});
  const border=document.createElementNS(ns,'rect');border.setAttribute('x',AU_ML);border.setAttribute('y',AU_MT);border.setAttribute('width',auFx(AU_FREQS.length-1)-AU_ML);border.setAttribute('height',auDbY(120)-AU_MT);border.setAttribute('fill','none');border.setAttribute('stroke','#9ca3af');border.setAttribute('stroke-width',1.5);border.setAttribute('pointer-events','none');svgEl.appendChild(border);
  const gcy=AU_MT+(auDbY(120)-AU_MT)/2;const yl=document.createElementNS(ns,'text');yl.setAttribute('x',10);yl.setAttribute('y',gcy);yl.setAttribute('text-anchor','middle');yl.setAttribute('font-size','10');yl.setAttribute('fill','#6b7280');yl.setAttribute('transform','rotate(-90,10,'+gcy+')');yl.setAttribute('pointer-events','none');yl.textContent='dB HL';svgEl.appendChild(yl);
  const xl=document.createElementNS(ns,'text');xl.setAttribute('x',AU_ML+(auFx(AU_FREQS.length-1)-AU_ML)/2);xl.setAttribute('y',AU_H-5);xl.setAttribute('text-anchor','middle');xl.setAttribute('font-size','10');xl.setAttribute('fill','#6b7280');xl.setAttribute('pointer-events','none');xl.textContent='Frequency (Hz)';svgEl.appendChild(xl);
}
function auRedraw(svgEl,pts,onErase,eraserOn){
  auBuildGrid(svgEl);const ns='http://www.w3.org/2000/svg';const masked=computeAutoMasking(pts);
  const grps={acr:{pts:[],s:'#dc2626',d:''},acl:{pts:[],s:'#2563eb',d:''},bcr:{pts:[],s:'#dc2626',d:'6,4'},bcl:{pts:[],s:'#2563eb',d:'6,4'}};
  pts.forEach(p=>{const k=(auIsAC(p.type)?'ac':'bc')+(auIsR(p.type)?'r':'l');grps[k].pts.push(p);});
  Object.values(grps).forEach(grp=>{
    const sorted=[...grp.pts].sort((a,b)=>AU_FREQS.indexOf(a.freq)-AU_FREQS.indexOf(b.freq));
    // Draw main line through non-NR points only (segments; stop before NR, restart after)
    let d='',seg=false;
    sorted.forEach(p=>{
      const x=auFreqX(p.freq),y=auDbY(p.db);
      if(p.nr){seg=false;return;} // NR point: break the main line
      d+=(seg?' L':'M')+x+','+y;seg=true;
    });
    if(d){const path=document.createElementNS(ns,'path');path.setAttribute('d',d);path.setAttribute('fill','none');path.setAttribute('stroke',grp.s);path.setAttribute('stroke-width','1.8');if(grp.d)path.setAttribute('stroke-dasharray',grp.d);path.setAttribute('stroke-linecap','round');path.setAttribute('pointer-events','none');svgEl.appendChild(path);}
  });
  pts.forEach(p=>{const et=auEffType(p,masked);auDrawSym(svgEl,auFreqX(p.freq),auDbY(p.db),et,p.nr,7,p,onErase,eraserOn);});
}
function auRptSym(cx,cy,type,nr,sz){
  sz=sz||6;const c=auIsR(type)?'#dc2626':'#2563eb';const r_ac=type.startsWith('ac'),r_r=type.includes('-r'),r_m=type.endsWith('-m');let s='';
  if(r_ac){if(r_r){if(r_m){const r=sz;s='<polygon points="'+cx+','+(cy-r)+' '+(cx+r*0.866)+','+(cy+r*0.5)+' '+(cx-r*0.866)+','+(cy+r*0.5)+'" fill="none" stroke="'+c+'" stroke-width="1.8"/>';}else s='<circle cx="'+cx+'" cy="'+cy+'" r="'+sz+'" fill="none" stroke="'+c+'" stroke-width="1.8"/>';}else{if(r_m)s='<rect x="'+(cx-sz+1)+'" y="'+(cy-sz+1)+'" width="'+((sz-1)*2)+'" height="'+((sz-1)*2)+'" fill="none" stroke="'+c+'" stroke-width="1.8"/>';else s='<line x1="'+(cx-sz)+'" y1="'+(cy-sz)+'" x2="'+(cx+sz)+'" y2="'+(cy+sz)+'" stroke="'+c+'" stroke-width="2" stroke-linecap="round"/><line x1="'+(cx+sz)+'" y1="'+(cy-sz)+'" x2="'+(cx-sz)+'" y2="'+(cy+sz)+'" stroke="'+c+'" stroke-width="2" stroke-linecap="round"/>';}}
  else{if(r_r){if(r_m)s='<path d="M'+(cx+sz-1)+','+(cy-sz)+' L'+(cx-sz+2)+','+(cy-sz)+' L'+(cx-sz+2)+','+(cy+sz)+' L'+(cx+sz-1)+','+(cy+sz)+'" fill="none" stroke="'+c+'" stroke-width="2" stroke-linecap="round"/>';else s='<path d="M'+(cx+sz)+','+(cy-sz)+' L'+(cx-sz+2)+','+cy+' L'+(cx+sz)+','+(cy+sz)+'" fill="none" stroke="'+c+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';}else{if(r_m)s='<path d="M'+(cx-sz+1)+','+(cy-sz)+' L'+(cx+sz-2)+','+(cy-sz)+' L'+(cx+sz-2)+','+(cy+sz)+' L'+(cx-sz+1)+','+(cy+sz)+'" fill="none" stroke="'+c+'" stroke-width="2" stroke-linecap="round"/>';else s='<path d="M'+(cx-sz)+','+(cy-sz)+' L'+(cx+sz-2)+','+cy+' L'+(cx-sz)+','+(cy+sz)+'" fill="none" stroke="'+c+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';}}
  // NR arrow: right ear → points LEFT, left ear → points RIGHT (standard audiometric convention)
  if(nr){const dir=r_r?-1:1;const tx2=cx+dir*(sz+5),ty2=cy+sz+15,ang=Math.atan2(ty2-(cy+sz+1),tx2-cx),as=5;s+='<line x1="'+cx+'" y1="'+(cy+sz+1)+'" x2="'+tx2+'" y2="'+ty2+'" stroke="'+c+'" stroke-width="1.5" stroke-linecap="round"/><path d="M'+tx2+','+ty2+' L'+(tx2-as*Math.cos(ang-0.45)).toFixed(1)+','+(ty2-as*Math.sin(ang-0.45)).toFixed(1)+' M'+tx2+','+ty2+' L'+(tx2-as*Math.cos(ang+0.45)).toFixed(1)+','+(ty2-as*Math.sin(ang+0.45)).toFixed(1)+'" fill="none" stroke="'+c+'" stroke-width="1.5" stroke-linecap="round"/>';}
  return s;
}
function auGenAudiogram(ear,pts){
  const rML=52,rMT=24,rMR=10,rMB=42,rW=420,rH=480,nF=AU_FREQS.length,nD=AU_DBS.length;
  const rCW=(rW-rML-rMR)/(nF-1),rRH=(rH-rMT-rMB)/(nD-1);
  function rfx(i){return rML+i*rCW;}function rfqX(f){return rfx(AU_FREQS.indexOf(f));}function rdbY(db){return rMT+((db-AU_DBS[0])/10)*rRH;}
  const ep=pts.filter(p=>ear==='right'?auIsR(p.type):!auIsR(p.type));let svg='';
  svg+='<rect x="'+rML+'" y="'+rMT+'" width="'+(rfx(nF-1)-rML)+'" height="'+(rdbY(120)-rMT)+'" fill="#fff"/>';
  // Horizontal dB grid lines
  AU_DBS.forEach(db=>{const y=rdbY(db),mj=(db%20===0);svg+='<line x1="'+rML+'" x2="'+rfx(nF-1)+'" y1="'+y+'" y2="'+y+'" stroke="'+(mj?'#bbb':'#ddd')+'" stroke-width="'+(mj?'0.8':'0.5')+'"'+(mj?'':' stroke-dasharray="3,2"')+'/>';svg+='<text x="'+(rML-4)+'" y="'+(y+3.5)+'" text-anchor="end" font-size="9" fill="#555">'+db+'</text>';});
  // Vertical frequency grid lines — octave freqs darker, mid freqs lighter
  const STD_FREQS=[250,500,1000,2000,4000,8000];
  AU_FREQS.forEach((f,i)=>{
    const x=rfx(i),isStd=STD_FREQS.includes(f);
    svg+='<line x1="'+x+'" x2="'+x+'" y1="'+rMT+'" y2="'+rdbY(120)+'" stroke="'+(isStd?'#999':'#e0e0e0')+'" stroke-width="'+(isStd?'0.9':'0.4')+'"'+(isStd?'':' stroke-dasharray="3,3"')+'/>';
    // Frequency labels below graph: number on first line, Hz on second
    svg+='<text x="'+x+'" y="'+(rdbY(120)+12)+'" text-anchor="middle" font-size="8.5" fill="#444">'+AU_LABELS[i]+'</text>';
    svg+='<text x="'+x+'" y="'+(rdbY(120)+22)+'" text-anchor="middle" font-size="7.5" fill="#777">Hz</text>';
  });
  // "Frequency (Hz)" axis title below all labels
  svg+='<text x="'+(rML+(rfx(nF-1)-rML)/2)+'" y="'+(rdbY(120)+36)+'" text-anchor="middle" font-size="9" font-weight="bold" fill="#444">Frequency (Hz)</text>';
  svg+='<rect x="'+rML+'" y="'+rMT+'" width="'+(rfx(nF-1)-rML)+'" height="'+(rdbY(120)-rMT)+'" fill="none" stroke="#666" stroke-width="1.2"/>';
  const gmy=rMT+(rdbY(120)-rMT)/2;svg+='<text x="10" y="'+gmy+'" text-anchor="middle" font-size="9" fill="#555" transform="rotate(-90,10,'+gmy+')">Hearing Level (dB HL)</text>';
  ['ac','bc'].forEach(mod=>{
    const mp=ep.filter(p=>mod==='ac'?p.type.startsWith('ac'):!p.type.startsWith('ac'));
    const so=mp.slice().sort((a,b)=>AU_FREQS.indexOf(a.freq)-AU_FREQS.indexOf(b.freq));
    const lc=ear==='right'?'#dc2626':'#2563eb';const dash=mod==='bc'?' stroke-dasharray="6,3"':'';
    // Main line through non-NR points only
    let d='',seg=false;so.forEach(p=>{if(p.nr){seg=false;return;}const x=rfqX(p.freq),y=rdbY(p.db);d+=(seg?' L':'M')+x+','+y;seg=true;});
    if(d)svg+='<path d="'+d+'" fill="none" stroke="'+lc+'" stroke-width="1.8"'+dash+' stroke-linecap="round" stroke-linejoin="round"/>';
  });
  const masked=computeAutoMasking(pts);ep.forEach(p=>{const x=rfqX(p.freq),y=rdbY(p.db);svg+=auRptSym(x,y,auEffType(p,masked),p.nr,7);});
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 '+rW+' '+rH+'" style="display:block;width:100%;height:auto;">'+svg+'</svg>';
}
function auBuildThreshTable(pts){
  const rows=[{l:'AC Right Threshold',t:'ac-r',c:'#dc2626'},{l:'AC Left Threshold',t:'ac-l',c:'#2563eb'},{l:'BC Right Threshold',t:'bc-r',c:'#dc2626'},{l:'BC Left Threshold',t:'bc-l',c:'#2563eb'}];
  function gv(mt,f){const pt=pts.find(p=>auSameSlot(p,mt,f)&&p.freq===f);return pt?String(pt.db):'';}
  const SF=[250,500,750,1000,1500,2000,3000,4000,6000,8000],SL=['250','500','750','1k','1.5k','2k','3k','4k','6k','8k'];
  let h='<table style="border-collapse:collapse;font-size:7.5pt;width:100%;"><tr><th style="background:#f3f4f6;border:1px solid #ccc;padding:2px 6px;font-size:7pt;text-align:left;white-space:nowrap;min-width:65px;">Hz</th>';
  SF.forEach((f,i)=>{h+='<th style="background:#f3f4f6;border:1px solid #ccc;padding:2px 3px;font-size:7pt;text-align:center;">'+SL[i]+'</th>';});h+='</tr>';
  rows.forEach(row=>{h+='<tr><td style="border:1px solid #ccc;padding:2px 6px;font-weight:700;color:'+row.c+';white-space:nowrap;">'+row.l+'</td>';SF.forEach(f=>{h+='<td style="border:1px solid #ccc;padding:2px 3px;text-align:center;">'+gv(row.t,f)+'</td>';});h+='</tr>';});
  h+='</table>';return h;
}
function auNotesHtml(text){
  if(!text)return'';
  const esc=s=>String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  return text.split('\n').map(line=>{
    const t=line.trim();
    if(!t)return'<div><br></div>';
    const et=esc(t);
    // Section header: all-caps words ending with colon only (e.g. "TUNING FORK TEST:")
    if(/^[A-Z][A-Z\s/\-]+:$/.test(t))return'<div><strong style="letter-spacing:.3px">'+et+'</strong></div>';
    // Sub-label line: "RINNE TEST: value"
    const m=t.match(/^([A-Z][A-Z\s/\-]+:)(.*)/);
    if(m)return'<div><strong>'+esc(m[1])+'</strong>'+esc(m[2])+'</div>';
    return'<div>'+et+'</div>';
  }).join('');
}
function auBuildReport(entry,patientName,patientId,logoUrl){
  const pts=entry.points||[],notes=entry.notes||'';
  const ex=entry.extra||{};
  const age=ex.age||'';const complaint=ex.complaint||'';const ref=ex.reference||'';
  const now=entry.date instanceof Date?entry.date:new Date(entry.date||Date.now());
  const dd=String(now.getDate()).padStart(2,'0'),mm=String(now.getMonth()+1).padStart(2,'0'),yyyy=now.getFullYear();
  const hh=String(now.getHours()).padStart(2,'0'),mi=String(now.getMinutes()).padStart(2,'0');
  const ds=dd+'-'+mm+'-'+yyyy,ts=hh+':'+mi;
  const rs=auGenAudiogram('right',pts),ls2=auGenAudiogram('left',pts),th=auBuildThreshTable(pts);
  const nh=auNotesHtml(notes);
  const esc=s=>s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  function lsi(type){const inner=auRptSym(10,10,type,false,5);return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" style="display:inline-block;vertical-align:middle;">'+inner+'</svg>';}
  function lsiNR(type){const inner=auRptSym(10,6,type,true,5);return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 28" width="18" height="26" style="display:inline-block;vertical-align:middle;">'+inner+'</svg>';}
  // Complaint + Reference row (only show if filled)
  const crRow=(complaint||ref)?'<div style="font-size:8.5pt;margin-bottom:6px;line-height:1.7;">'+(complaint?'<strong>Chief Complaint:</strong> '+esc(complaint)+'&nbsp;&nbsp;':'')+(ref?'<strong>Ref. By:</strong> '+esc(ref):'')+'</div>':'';
  return '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Audiometry Report</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,Helvetica,sans-serif;font-size:9pt;color:#111;background:#e5e7eb;}.toolbar{background:#fff;border-bottom:1px solid #d1d5db;padding:7px 16px;display:flex;gap:10px;align-items:center;}@media print{.toolbar{display:none!important;}body{background:#fff;margin:0;}.page{box-shadow:none;margin:0;}}.toolbar button{border:1px solid #d1d5db;background:#f9fafb;border-radius:5px;padding:5px 14px;font-size:12px;cursor:pointer;font-weight:600;}.page{width:210mm;min-height:297mm;margin:10px auto;padding:8mm 12mm 10mm;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.15);}hr.main{border:none;border-top:1.5px solid #111;margin:3px 0 4px;}hr.thin{border:none;border-top:.7px solid #aaa;margin:3px 0;}.lhr{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:4px;}.lhr img{height:160px;object-fit:contain;display:block;}.ci{text-align:right;font-size:8pt;color:#555;line-height:1.5;}.ci strong{font-size:9pt;color:#111;display:block;}.ar{display:flex;gap:6px;align-items:flex-start;margin-bottom:3px;}.ac{flex:1;min-width:0;}.acl{font-size:8.5pt;font-weight:700;text-align:center;margin-bottom:3px;color:#111;letter-spacing:.5px;}.lc{width:90px;flex-shrink:0;padding-top:18px;}.lt{border-collapse:collapse;font-size:7pt;width:100%;}.lt th,.lt td{border:1px solid #ccc;padding:3px 2px;text-align:center;line-height:1;}.lt th{background:#f3f4f6;font-weight:700;font-size:6.5pt;}.ts{margin-bottom:5px;}.cs{border-top:1.5px solid #111;padding-top:4px;margin-bottom:5px;}.ch{text-align:center;font-size:9pt;font-weight:700;letter-spacing:1px;margin-bottom:5px;}.cb{background:#f3f4f6;border:1px solid #bbb;min-height:40px;padding:5px 10px;font-size:8.5pt;font-family:Arial,sans-serif;line-height:1.55;outline:none;color:#111;}.ft{border-top:1px solid #aaa;padding-top:4px;margin-top:4px;display:flex;align-items:center;justify-content:space-between;font-size:7.5pt;color:#666;}</style></head><body>'+
  '<div class="toolbar"><button onclick="window.print()">&#128424;&nbsp;Print</button><button onclick="window.close()">&#10005;&nbsp;Close</button></div>'+
  '<div class="page"><div class="lhr"><div style="overflow:hidden;line-height:0;max-height:105px;"><img src="'+logoUrl+'" alt="SoundLife" onerror="this.style.display=\'none\'"></div><div class="ci">Pure Tone Audiometry Report</div></div>'+
  '<hr class="main"><div style="font-size:9pt;margin-bottom:2px;line-height:1.8;"><strong>PATIENT:</strong> '+patientName+'&nbsp;&nbsp;<strong>ID:</strong> '+patientId+(age?'&nbsp;&nbsp;<strong>AGE:</strong> '+esc(String(age))+' yrs':'')+' &nbsp;&nbsp;<strong>DATE:</strong> '+ds+'&nbsp;&nbsp;<strong>TIME:</strong> '+ts+'</div><hr class="thin">'+
  crRow+
  '<div style="font-size:8.5pt;color:#444;margin-bottom:4px;">Test: Pure Tone Audiometry</div>'+
  '<div class="ar"><div class="ac"><div class="acl">RIGHT EAR</div>'+rs+'</div>'+
  '<div class="lc"><table class="lt"><thead><tr><th></th><th style="color:#dc2626;">R</th><th style="color:#2563eb;">L</th></tr></thead><tbody>'+
  '<tr><td style="text-align:left;font-weight:700;padding-left:3px;">AC</td><td>'+lsi('ac-r')+'</td><td>'+lsi('ac-l')+'</td></tr>'+
  '<tr><td style="text-align:left;font-weight:700;padding-left:3px;">AC M</td><td>'+lsi('ac-r-m')+'</td><td>'+lsi('ac-l-m')+'</td></tr>'+
  '<tr><td style="text-align:left;font-weight:700;padding-left:3px;">BC</td><td>'+lsi('bc-r')+'</td><td>'+lsi('bc-l')+'</td></tr>'+
  '<tr><td style="text-align:left;font-weight:700;padding-left:3px;">BC M</td><td>'+lsi('bc-r-m')+'</td><td>'+lsi('bc-l-m')+'</td></tr>'+
  '<tr><td style="text-align:left;font-weight:700;padding-left:3px;">NR (AC)</td><td>'+lsiNR('ac-r')+'</td><td>'+lsiNR('ac-l')+'</td></tr>'+
  '<tr><td style="text-align:left;font-weight:700;padding-left:3px;">NR (BC)</td><td>'+lsiNR('bc-r')+'</td><td>'+lsiNR('bc-l')+'</td></tr>'+
  '</tbody></table></div>'+
  '<div class="ac"><div class="acl">LEFT EAR</div>'+ls2+'</div></div>'+
  '<div class="ts">'+th+'</div>'+
  '<div class="cs"><div class="ch">COMMENTS</div><div class="cb" contenteditable="true">'+nh+'</div></div>'+
  '<div class="ft"><span>Print Date: '+ds+' '+ts+'&nbsp;&nbsp;Page 1/1</span></div>'+
  '</div></body></html>';
}

function AudiometryTab({patient,readOnly,user}){
  const acl='#7c3aed';
  const [pts,setPts]=useState([]);
  const [tool,setTool]=useState('ac-r');
  const [noResp,setNoResp]=useState(false);
  const [eraser,setEraser]=useState(false);
  const [notes,setNotes]=useState('');
  const [noteManual,setNoteManual]=useState(false);
  const noteManualRef=useRef(false);
  const [saved,setSaved]=useState([]);
  const [savedLoading,setSavedLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [tblVals,setTblVals]=useState({});
  const age=String(patient.age||'');
  const [complaint,setComplaint]=useState('');
  const [refSrc,setRefSrc]=useState('');
  // Undo/Redo — use refs so we can push inside event handlers without stale closures
  const historyRef=useRef([]);const futureRef=useRef([]);
  const [histLen,setHistLen]=useState(0);const [futLen,setFutLen]=useState(0);
  const svgRef=useRef(null);const notesRef=useRef(null);const notesExtRef=useRef(false);
  const toolRef=useRef('ac-r');const noRespRef=useRef(false);const eraserRef=useRef(false);const ptsRef=useRef([]);
  // Sets notes from an external source (auto-calc, load, clear) → triggers innerHTML update
  const setNotesAuto=useCallback((text)=>{notesExtRef.current=true;setNotes(text);},[]);
  useEffect(()=>{toolRef.current=tool;},[tool]);
  useEffect(()=>{noRespRef.current=noResp;},[noResp]);
  useEffect(()=>{eraserRef.current=eraser;},[eraser]);
  useEffect(()=>{ptsRef.current=pts;},[pts]);

  const pushHist=useCallback(()=>{
    historyRef.current=[...historyRef.current,[...ptsRef.current]];
    futureRef.current=[];
    setHistLen(historyRef.current.length);setFutLen(0);
  },[]);
  const undo=useCallback(()=>{
    if(!historyRef.current.length)return;
    futureRef.current=[...futureRef.current,[...ptsRef.current]];
    const prev=historyRef.current[historyRef.current.length-1];
    historyRef.current=historyRef.current.slice(0,-1);
    setHistLen(historyRef.current.length);setFutLen(futureRef.current.length);
    setPts(prev);
  },[]);
  const redo=useCallback(()=>{
    if(!futureRef.current.length)return;
    historyRef.current=[...historyRef.current,[...ptsRef.current]];
    const next=futureRef.current[futureRef.current.length-1];
    futureRef.current=futureRef.current.slice(0,-1);
    setHistLen(historyRef.current.length);setFutLen(futureRef.current.length);
    setPts(next);
  },[]);

  // Fetch saved audiograms from server on mount / patient change
  const fetchSaved=useCallback(()=>{
    setSavedLoading(true);
    fetch(`${API_BASE}/audiometry.php?pid=${encodeURIComponent(patient.id)}`,{credentials:'include'})
      .then(r=>r.json())
      .then(data=>{setSaved(Array.isArray(data)?data:[]);setSavedLoading(false);})
      .catch(()=>setSavedLoading(false));
  },[patient.id]);
  useEffect(()=>{fetchSaved();},[fetchSaved]);

  // Keyboard shortcuts: Ctrl+Z undo, Ctrl+Y / Ctrl+Shift+Z redo
  useEffect(()=>{
    const h=(e)=>{
      if((e.ctrlKey||e.metaKey)&&e.key==='z'&&!e.shiftKey){e.preventDefault();undo();}
      if((e.ctrlKey||e.metaKey)&&(e.key==='y'||(e.key==='z'&&e.shiftKey))){e.preventDefault();redo();}
    };
    window.addEventListener('keydown',h);return()=>window.removeEventListener('keydown',h);
  },[undo,redo]);

  useEffect(()=>{if(svgRef.current)auBuildGrid(svgRef.current);},[]);
  // Sync formatted HTML into contentEditable when notes updated externally
  useEffect(()=>{if(notesRef.current&&notesExtRef.current){notesRef.current.innerHTML=auNotesHtml(notes);notesExtRef.current=false;}},[notes]);
  const onErase=useCallback((pt)=>{
    if(!eraserRef.current)return;
    pushHist();
    setPts(prev=>prev.filter(p=>!(p.freq===pt.freq&&p.type===pt.type)));
  },[pushHist]);
  // Redraw when pts change OR when eraser is toggled (to add/remove click handlers)
  useEffect(()=>{
    if(!svgRef.current)return;
    auRedraw(svgRef.current,pts,onErase,eraser);
    const tv={};['ac-r','ac-l','bc-r','bc-l'].forEach(type=>{AU_FREQS.forEach(f=>{const pt=pts.find(p=>auSameSlot(p,type,f)&&p.freq===f);tv[type+'_'+f]=pt!==undefined?String(pt.db):'';});});
    setTblVals(tv);
    if(!noteManualRef.current)setNotesAuto(auAutoNote(pts));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[pts,onErase,eraser]);
  const onSvgClick=useCallback((e)=>{
    if(eraserRef.current)return;
    const svg=svgRef.current;if(!svg)return;
    const rect=svg.getBoundingClientRect();const vb=svg.viewBox.baseVal;
    const sx=(e.clientX-rect.left)*(vb.width/rect.width),sy=(e.clientY-rect.top)*(vb.height/rect.height);
    const{freq,db}=auSnap(sx,sy);
    pushHist();
    setPts(prev=>{const next=prev.filter(p=>!auSameSlot(p,toolRef.current,freq));const isMT=toolRef.current.endsWith('-m');next.push({freq,db,type:toolRef.current,nr:noRespRef.current,maskOverride:isMT?true:false});return next;});
  },[pushHist]);
  const clearAll=()=>{
    if(ptsRef.current.length>0)pushHist();
    setPts([]);setTblVals({});setNotesAuto('');noteManualRef.current=false;setNoteManual(false);setEraser(false);setNoResp(false);
    setComplaint('');setRefSrc('');
  };
  const saveAudiogram=async()=>{
    if(pts.length===0||saving)return;
    setSaving(true);
    try{
      await apiCall(`${API_BASE}/audiometry.php`,{method:'POST',body:JSON.stringify({case_code:patient.id,points:pts,notes,extra:{age,complaint,reference:refSrc}})});
      clearAll();
      fetchSaved();
    }catch(e){alert('Save failed: '+e.message);}
    setSaving(false);
  };
  const deleteEntry=async(id)=>{
    if(!window.confirm('Delete this audiogram? This cannot be undone.'))return;
    await apiCall(`${API_BASE}/audiometry.php?id=${id}`,{method:'DELETE',body:JSON.stringify({})});
    fetchSaved();
  };
  const logoUrl=window.location.origin+'/SL_logo.png';
  const printEntry=(entry)=>{
    const e={...entry,date:entry.created_at?new Date(entry.created_at):new Date()};
    const html=auBuildReport(e,patient.name||'—',patient.id,logoUrl);
    const win=window.open('','_blank','width=900,height=720');win.document.open();win.document.write(html);win.document.close();
  };
  const printCurrent=()=>{printEntry({id:Date.now(),created_at:new Date().toISOString(),points:JSON.parse(JSON.stringify(pts)),notes,extra:{age,complaint,reference:refSrc}});};
  const applyTable=()=>{
    const newPts=[];
    ['ac-r','ac-l','bc-r','bc-l'].forEach(type=>{AU_FREQS.forEach(f=>{const val=tblVals[type+'_'+f];if(val!==undefined&&val!==''){const db=Math.max(-10,Math.min(120,Math.round(parseInt(val,10)/5)*5));if(!isNaN(db))newPts.push({freq:f,db,type,nr:false});}});});
    noteManualRef.current=false;setPts(newPts);setNoteManual(false);
  };
  const auAdmins=['sl-office','sl-bangalore','sl-mumbai','sl-gujarat'];
  const canSave=(user?.role==='clinic')||(user?.role==='owner')||(user?.role==='regional')||(user&&auAdmins.includes(user.username));
  const canDelete=(user?.role==='clinic')||(user&&auAdmins.includes(user.username))||(user?.role==='owner')||(user?.role==='regional');
  return(
    <div style={{fontFamily:"inherit"}}>
      {/* ── Tools ── */}
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:12,padding:"12px 14px",marginBottom:10,boxShadow:"var(--cardshadow)"}}>
        <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:.8,textTransform:"uppercase",marginBottom:10}}>Plotting Tool</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
          {/* RIGHT EAR */}
          <div style={{borderRadius:9,overflow:"hidden",border:"1.5px solid #fca5a5"}}>
            <div style={{background:"linear-gradient(135deg,#dc2626,#b91c1c)",color:"#fff",fontSize:9,fontWeight:800,textAlign:"center",padding:"5px 0",letterSpacing:1.2}}>RIGHT EAR</div>
            <div style={{padding:"6px",display:"flex",flexDirection:"column",gap:3,background:"#fff5f5"}}>
              {[{id:'ac-r',label:'AC',sym:'○',desc:'Air Cond.'},{id:'bc-r',label:'BC',sym:'‹',desc:'Bone Cond.'},{id:'ac-r-m',label:'AC Masked',sym:'△',desc:'Masked AC'},{id:'bc-r-m',label:'BC Masked',sym:'[',desc:'Masked BC'}].map(td=>(
                <button key={td.id} onClick={()=>{setTool(td.id);setEraser(false);}}
                  style={{padding:"6px 8px",borderRadius:7,border:`1.5px solid ${tool===td.id&&!eraser?'#dc2626':'#fecaca'}`,background:tool===td.id&&!eraser?'#dc2626':'transparent',color:tool===td.id&&!eraser?'#fff':'#dc2626',fontSize:11,cursor:"pointer",fontWeight:700,display:"flex",alignItems:"center",gap:7,transition:"all .12s",textAlign:"left"}}>
                  <span style={{fontSize:15,width:18,textAlign:"center",lineHeight:1}}>{td.sym}</span>
                  <span style={{flex:1}}>{td.label}</span>
                  {tool===td.id&&!eraser&&<span style={{fontSize:8,opacity:.8,marginLeft:"auto"}}>●</span>}
                </button>
              ))}
            </div>
          </div>
          {/* LEFT EAR */}
          <div style={{borderRadius:9,overflow:"hidden",border:"1.5px solid #93c5fd"}}>
            <div style={{background:"linear-gradient(135deg,#2563eb,#1d4ed8)",color:"#fff",fontSize:9,fontWeight:800,textAlign:"center",padding:"5px 0",letterSpacing:1.2}}>LEFT EAR</div>
            <div style={{padding:"6px",display:"flex",flexDirection:"column",gap:3,background:"#eff6ff"}}>
              {[{id:'ac-l',label:'AC',sym:'×',desc:'Air Cond.'},{id:'bc-l',label:'BC',sym:'›',desc:'Bone Cond.'},{id:'ac-l-m',label:'AC Masked',sym:'□',desc:'Masked AC'},{id:'bc-l-m',label:'BC Masked',sym:']',desc:'Masked BC'}].map(td=>(
                <button key={td.id} onClick={()=>{setTool(td.id);setEraser(false);}}
                  style={{padding:"6px 8px",borderRadius:7,border:`1.5px solid ${tool===td.id&&!eraser?'#2563eb':'#bfdbfe'}`,background:tool===td.id&&!eraser?'#2563eb':'transparent',color:tool===td.id&&!eraser?'#fff':'#2563eb',fontSize:11,cursor:"pointer",fontWeight:700,display:"flex",alignItems:"center",gap:7,transition:"all .12s",textAlign:"left"}}>
                  <span style={{fontSize:15,width:18,textAlign:"center",lineHeight:1}}>{td.sym}</span>
                  <span style={{flex:1}}>{td.label}</span>
                  {tool===td.id&&!eraser&&<span style={{fontSize:8,opacity:.8,marginLeft:"auto"}}>●</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Modifiers row */}
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>setNoResp(v=>!v)} style={{flex:1,padding:"7px 10px",borderRadius:7,border:`1.5px solid ${noResp?"#d97706":"var(--border)"}`,background:noResp?"#fef3c7":"transparent",color:noResp?"#92400e":"var(--muted)",fontSize:10,cursor:"pointer",fontWeight:700,transition:"all .12s"}}>↯ No Response {noResp?"ON":"OFF"}</button>
          <button onClick={()=>setEraser(v=>!v)} style={{flex:1,padding:"7px 10px",borderRadius:7,border:`1.5px solid ${eraser?"#7c3aed":"var(--border)"}`,background:eraser?"#f5f3ff":"transparent",color:eraser?"#6d28d9":"var(--muted)",fontSize:10,cursor:"pointer",fontWeight:700,transition:"all .12s"}}>⌫ Eraser {eraser?"ON":"OFF"}</button>
        </div>
      </div>
      {/* ── Patient details ── */}
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 14px",marginBottom:10,boxShadow:"var(--cardshadow)"}}>
        <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:.8,textTransform:"uppercase",marginBottom:8}}>Patient Details</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <label style={{fontSize:9,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Age (yrs)</label>
            <div style={{width:68,padding:"5px 8px",background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:6,color:"var(--text1)",fontSize:12,fontWeight:700,minHeight:30,display:"flex",alignItems:"center"}}>{age||'—'}</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3,flex:1,minWidth:130}}>
            <label style={{fontSize:9,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Chief Complaint</label>
            <input type="text" value={complaint} onChange={e=>setComplaint(e.target.value)} placeholder="e.g. hearing loss, tinnitus…"
              style={{width:"100%",padding:"5px 8px",background:"var(--inputbg)",border:"1px solid var(--border)",borderRadius:6,color:"var(--text1)",fontSize:12,outline:"none"}}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:3,flex:1,minWidth:130}}>
            <label style={{fontSize:9,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Reference</label>
            <input type="text" value={refSrc} onChange={e=>setRefSrc(e.target.value)} placeholder="Dr. name / source…"
              style={{width:"100%",padding:"5px 8px",background:"var(--inputbg)",border:"1px solid var(--border)",borderRadius:6,color:"var(--text1)",fontSize:12,outline:"none"}}/>
          </div>
        </div>
      </div>
      {/* ── Manual table ── */}
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 12px",marginBottom:10,boxShadow:"var(--cardshadow)",overflowX:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:.8,textTransform:"uppercase"}}>Manual Entry</div>
          <button onClick={applyTable} style={{padding:"4px 12px",background:"linear-gradient(135deg,#7c3aed,#6d28d9)",border:"none",borderRadius:6,color:"#fff",fontSize:10,cursor:"pointer",fontWeight:600}}>↵ Apply to Graph</button>
        </div>
        <table style={{borderCollapse:"collapse",fontSize:10,minWidth:"100%"}}>
          <thead><tr>
            <th style={{padding:"3px 6px",border:"1px solid var(--border)",background:"var(--accentbg)",fontSize:9,textAlign:"left",whiteSpace:"nowrap"}}>Type</th>
            {AU_FREQS.map(f=><th key={f} style={{padding:"3px 4px",border:"1px solid var(--border)",background:"var(--accentbg)",fontSize:9,textAlign:"center",minWidth:36}}>{AU_LABELS[AU_FREQS.indexOf(f)]}</th>)}
          </tr></thead>
          <tbody>
            {['ac-r','ac-l','bc-r','bc-l'].map(type=>(
              <tr key={type}>
                <td style={{padding:"3px 6px",border:"1px solid var(--border)",fontWeight:700,color:type.includes('-r')?'#dc2626':'#2563eb',fontSize:9,whiteSpace:"nowrap"}}>{type==='ac-r'?'AC Right':type==='ac-l'?'AC Left':type==='bc-r'?'BC Right':'BC Left'}</td>
                {AU_FREQS.map(f=>(
                  <td key={f} style={{padding:"2px",border:"1px solid var(--border)"}}>
                    <input type="number" min="-10" max="120" step="5" value={tblVals[type+'_'+f]||''} onChange={e=>setTblVals(tv=>({...tv,[type+'_'+f]:e.target.value}))}
                      style={{width:"100%",border:"none",background:"transparent",textAlign:"center",fontSize:10,color:"var(--text1)",padding:"2px 0",outline:"none"}}/>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ── SVG Audiogram ── */}
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"8px",marginBottom:10,boxShadow:"var(--cardshadow)"}}>
        <svg ref={svgRef} viewBox="0 0 560 380" xmlns="http://www.w3.org/2000/svg"
          style={{display:"block",width:"100%",cursor:eraser?"cell":"crosshair",borderRadius:6}}
          onClick={onSvgClick}/>
      </div>
      {/* ── Diagnosis Notes ── */}
      <div style={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:10,padding:"12px 14px",marginBottom:12,boxShadow:"var(--cardshadow)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:.8,textTransform:"uppercase"}}>Diagnosis Notes</div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <span style={{background:noteManual?"#fef3c7":"var(--accentbg)",color:noteManual?"#b45309":acl,padding:"2px 8px",borderRadius:5,fontSize:9,fontWeight:600}}>{noteManual?"edited":"auto"}</span>
            <button onClick={()=>{noteManualRef.current=false;setNoteManual(false);setNotesAuto(auAutoNote(pts));}} style={{padding:"3px 10px",background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:5,color:acl,fontSize:9,cursor:"pointer",fontWeight:600}}>↺ Recalc</button>
          </div>
        </div>
        <div ref={notesRef} contentEditable suppressContentEditableWarning
          onInput={e=>{noteManualRef.current=true;setNoteManual(true);setNotes(e.currentTarget.innerText);}}
          data-placeholder="Auto-generated after plotting…"
          style={{minHeight:200,background:"var(--inputbg)",border:"1px solid var(--border)",borderRadius:7,color:"var(--text1)",fontSize:12,padding:"8px 10px",lineHeight:1.7,fontFamily:"inherit",outline:"none",whiteSpace:"pre-wrap",wordBreak:"break-word"}}
        />
      </div>
      {/* ── Save / Print actions ── */}
      {canSave&&(
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
          <button onClick={saveAudiogram} disabled={pts.length===0||saving}
            style={{padding:"9px 20px",background:pts.length===0||saving?"var(--accentbg)":"linear-gradient(135deg,#7c3aed,#6d28d9)",border:pts.length===0||saving?"1px solid var(--border)":"none",borderRadius:9,color:pts.length===0||saving?"var(--muted)":"#fff",fontSize:13,cursor:pts.length===0||saving?"not-allowed":"pointer",fontWeight:700,boxShadow:pts.length===0||saving?"none":"0 4px 12px #7c3aed33",display:"flex",alignItems:"center",gap:6}}>
            {saving?<><span style={{width:12,height:12,border:"2px solid #ffffff44",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Saving…</>:<>💾 Save Audiogram</>}
          </button>
          <button onClick={printCurrent} disabled={pts.length===0}
            style={{padding:"9px 18px",background:"transparent",border:`1px solid ${pts.length===0?"var(--border)":"var(--border2)"}`,borderRadius:9,color:pts.length===0?"var(--muted)":acl,fontSize:13,cursor:pts.length===0?"not-allowed":"pointer",fontWeight:600}}>
            🖨 Print Report
          </button>
          <button onClick={undo} disabled={histLen===0}
            style={{padding:"9px 13px",background:"transparent",border:`1px solid ${histLen===0?"var(--border)":"var(--border2)"}`,borderRadius:9,color:histLen===0?"var(--muted)":"var(--text1)",fontSize:13,cursor:histLen===0?"not-allowed":"pointer",fontWeight:600}}
            title="Undo (Ctrl+Z)">↩ Undo</button>
          <button onClick={redo} disabled={futLen===0}
            style={{padding:"9px 13px",background:"transparent",border:`1px solid ${futLen===0?"var(--border)":"var(--border2)"}`,borderRadius:9,color:futLen===0?"var(--muted)":"var(--text1)",fontSize:13,cursor:futLen===0?"not-allowed":"pointer",fontWeight:600}}
            title="Redo (Ctrl+Y)">↪ Redo</button>
          <button onClick={clearAll} style={{padding:"9px 14px",background:"transparent",border:"1px solid var(--border)",borderRadius:9,color:"var(--muted)",fontSize:13,cursor:"pointer",fontWeight:600}}>⌫ Clear</button>
          {pts.length===0&&<span style={{fontSize:10,color:"var(--muted2)",alignSelf:"center"}}>Plot points to enable</span>}
        </div>
      )}

      {/* ── Saved Audiograms (thumbnail grid, like Records tab) ── */}
      <div>
        <div style={{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:.8,textTransform:"uppercase",marginBottom:10}}>
          🎧 Saved Audiograms {!savedLoading&&`(${saved.length})`}
        </div>
        {savedLoading&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"18px 0",color:"var(--muted)",fontSize:12}}><span style={{width:13,height:13,border:"2px solid #c4b5fd",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>Loading…</div>}
        {!savedLoading&&saved.length===0&&<Empty msg="No saved audiograms yet — plot and save to build history"/>}
        {!savedLoading&&saved.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
            {saved.map((entry)=>{
              const d=new Date(entry.created_at||0);
              const dl=d.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"});
              const tl=d.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
              const diagLines=(entry.notes||'').split('\n').filter(l=>l.startsWith('RIGHT EAR:')||l.startsWith('LEFT EAR:')).slice(0,2).join(' | ');
              const noteSnip=diagLines.length>60?diagLines.slice(0,58)+'…':diagLines;
              const rMini=auGenAudiogram('right',entry.points||[]);
              const lMini=auGenAudiogram('left',entry.points||[]);
              return(
                <div key={entry.id} style={{background:"var(--card)",border:"1.5px solid var(--border)",borderRadius:11,overflow:"hidden",transition:"border-color .15s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=acl}
                  onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
                  {/* Mini audiogram thumbnail — two ears side by side */}
                  <div style={{display:"flex",gap:1,padding:"6px 6px 2px",background:"var(--accentbg)",cursor:"pointer"}}
                    onClick={()=>{if(ptsRef.current.length>0&&!window.confirm('Load this audiogram? Unsaved changes will be lost.'))return;noteManualRef.current=true;setNoteManual(true);setNotesAuto(entry.notes||'');setPts(JSON.parse(JSON.stringify(entry.points)));const ex=entry.extra||{};setComplaint(ex.complaint||'');setRefSrc(ex.reference||'');}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:6.5,fontWeight:800,color:"#dc2626",textAlign:"center",marginBottom:1,letterSpacing:.5}}>RIGHT</div>
                      <div dangerouslySetInnerHTML={{__html:rMini.replace('style="display:block;width:100%;height:auto;"','style="display:block;width:100%;height:auto;max-height:72px;"')}}/>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:6.5,fontWeight:800,color:"#2563eb",textAlign:"center",marginBottom:1,letterSpacing:.5}}>LEFT</div>
                      <div dangerouslySetInnerHTML={{__html:lMini.replace('style="display:block;width:100%;height:auto;"','style="display:block;width:100%;height:auto;max-height:72px;"')}}/>
                    </div>
                  </div>
                  {/* Card footer */}
                  <div style={{padding:"6px 9px",borderTop:"1px solid var(--border)"}}>
                    <div style={{fontSize:10,fontWeight:700,color:"var(--text1)",marginBottom:1}}>{dl}</div>
                    <div style={{fontSize:9,color:"var(--muted2)",marginBottom:noteSnip?4:6}}>{tl}</div>
                    {noteSnip&&<div style={{fontSize:8.5,color:"var(--muted)",marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{noteSnip}</div>}
                    <div style={{display:"flex",gap:4}}>
                      <button onClick={()=>{if(ptsRef.current.length>0&&!window.confirm('Load this audiogram? Unsaved changes will be lost.'))return;noteManualRef.current=true;setNoteManual(true);setNotesAuto(entry.notes||'');setPts(JSON.parse(JSON.stringify(entry.points)));const ex=entry.extra||{};setComplaint(ex.complaint||'');setRefSrc(ex.reference||'');}}
                        style={{flex:1,padding:"4px 0",background:"var(--accentbg)",border:"1px solid var(--border2)",borderRadius:6,color:acl,fontSize:9,cursor:"pointer",fontWeight:600}}>✏️ Load</button>
                      <button onClick={()=>printEntry(entry)}
                        style={{flex:1,padding:"4px 0",background:"var(--accentbg)",border:"1px solid var(--border2)",borderRadius:6,color:"#059669",fontSize:9,cursor:"pointer",fontWeight:600}}>🖨 Print</button>
                      {canDelete&&<button onClick={()=>deleteEntry(entry.id)}
                        style={{padding:"4px 8px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:6,color:"#dc2626",fontSize:9,cursor:"pointer",fontWeight:600}}>✕</button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// FILE VIEWER (PDF / TXT)
// ══════════════════════════════════════════════════════════
function FileViewerModal({ url, name, kind, onClose }) {
  const [txtContent, setTxtContent] = useState(null);
  const [txtError, setTxtError] = useState(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
  useEffect(() => {
    if (kind === 'txt') {
      fetch(url, { credentials: 'include' })
        .then(r => r.ok ? r.text() : Promise.reject(r.status))
        .then(t => setTxtContent(t))
        .catch(e => setTxtError('Could not load file: ' + e));
    }
  }, [url, kind]);
  useEffect(() => {
    const h = e => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fadeIn" style={{position:'fixed',inset:0,zIndex:500,background:'#000000ee',display:'flex',flexDirection:'column',backdropFilter:'blur(8px)'}} onClick={onClose}>
      <div style={{padding:'12px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#0d0b1a',borderBottom:'1px solid #2a2250',flexShrink:0}} onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:13,fontWeight:600,color:'#e8e4ff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'60%'}}>{name}</div>
        <div style={{display:'flex',gap:8,flexShrink:0}}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{...S.ibtn,textDecoration:'none',display:'inline-flex',alignItems:'center'}}>↗ Open</a>
          <button onClick={onClose} style={{...S.ibtn,marginLeft:6}}>✕ Close</button>
        </div>
      </div>
      <div style={{flex:1,overflow:'auto',background:'#1a1630',display:'flex',alignItems:'stretch',justifyContent:'center'}} onClick={e=>e.stopPropagation()}>
        {kind==='pdf' && (
          isMobile
            ? <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:20,padding:32}}>
                <div style={{fontSize:48}}>📄</div>
                <div style={{fontSize:14,color:'#c4b5fd',fontWeight:600,textAlign:'center'}}>{name}</div>
                <div style={{fontSize:12,color:'#6b7280',textAlign:'center'}}>PDFs can&apos;t be embedded on mobile browsers.</div>
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{background:'linear-gradient(135deg,#7c3aed,#6d28d9)',color:'#fff',padding:'12px 28px',borderRadius:10,fontSize:14,fontWeight:700,textDecoration:'none',display:'inline-flex',alignItems:'center',gap:8}}>
                  ↗ Open PDF
                </a>
              </div>
            : <iframe src={url} title={name} style={{flex:1,width:'100%',border:'none',minHeight:0}}/>
        )}
        {kind==='txt' && (
          txtError
            ? <div style={{padding:24,color:'#ef4444',fontSize:13}}>{txtError}</div>
            : txtContent===null
              ? <div style={{padding:24,color:'#a78bfa',fontSize:13}}>Loading…</div>
              : <pre style={{flex:1,margin:0,padding:'20px 24px',color:'#e8e4ff',fontSize:12,fontFamily:"'Courier New',monospace",lineHeight:1.7,whiteSpace:'pre-wrap',wordBreak:'break-word',overflowY:'auto'}}>{txtContent}</pre>
        )}
      </div>
      {!isMobile && <div style={{textAlign:'center',padding:'8px',fontSize:10,color:'#3d3070',background:'#0d0b1a'}}>Esc to close</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PRINT PATIENT CARD
// ══════════════════════════════════════════════════════════
function PrintCardModal({ p, onClose }) {
  const c = CLINICS[p.clinicId];
  const recentVisits = [...(p.visits||[])].reverse().slice(0,5);
  const cardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const visitsHtml = recentVisits.length === 0
    ? `<div style="color:#888;font-size:11px;font-style:italic">No visits recorded yet</div>`
    : recentVisits.map(v=>`
        <div style="border-left:3px solid #7c3aed;padding:6px 10px;margin-bottom:6px;background:#fafafa;border-radius:0 6px 6px 0">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px">
            <span style="font-size:10px;color:#666">${new Date(v.ts).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
            ${v.clinicId&&CLINICS[v.clinicId]?`<span style="font-size:9px;background:#ede9fe;color:#7c3aed;padding:2px 7px;border-radius:4px;font-weight:700">${CLINICS[v.clinicId].label}</span>`:""}
          </div>
          ${v.attendedBy?`<div style="font-size:10px;font-weight:700;color:#7c3aed;margin-bottom:2px">👨‍⚕️ ${v.attendedBy}</div>`:""}
          <div style="font-size:11px;color:#444;line-height:1.5">${v.note}</div>
        </div>`).join("");

  const cardHtml = `<div style="background:#fff;border-radius:18px;width:480px;box-shadow:0 8px 40px rgba(124,58,237,0.13);overflow:hidden;border:1px solid #ddd6fe;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif">
    <div style="background:linear-gradient(135deg,#4c1d95,#7c3aed);padding:22px 24px;color:#fff">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">
        <img src="/logo192.png" alt="SoundLife" style="height:38px;width:auto;filter:brightness(0) invert(1);display:block" crossorigin="anonymous"/>
        <div style="margin-left:auto;text-align:right">
          <div style="font-size:9px;color:#c4b5fd;font-weight:700;letter-spacing:1px;text-transform:uppercase">Case Code</div>
          <div style="font-size:20px;font-weight:800;letter-spacing:3px;font-family:monospace;color:#fff">${p.id}</div>
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:14px">
        <div style="width:52px;height:52px;border-radius:14px;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:700;color:#fff;flex-shrink:0">${(p.name?.[0]||"").toUpperCase()}</div>
        <div>
          <div style="font-size:22px;color:#fff;line-height:1.2">${p.name}</div>
          <div style="font-size:11px;color:#c4b5fd;margin-top:4px">📱 ${p.phone}${p.age?` · ${p.age} yrs`:""}${p.gender?` · ${p.gender}`:""}</div>
        </div>
      </div>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid #ede9fe">
      <div style="padding:12px 18px;border-right:1px solid #ede9fe">
        <div style="font-size:9px;color:#8b7ec8;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px">Clinic</div>
        <div style="font-size:13px;font-weight:600;color:#1e1b4b">${c?.label||p.clinicId}</div>
        <div style="font-size:11px;color:#7c3aed">${c?.city||""} · ${c?.region||""}</div>
      </div>
      <div style="padding:12px 18px">
        <div style="font-size:9px;color:#8b7ec8;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px">Registered</div>
        <div style="font-size:13px;font-weight:600;color:#1e1b4b">${new Date(p.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}</div>
        <div style="font-size:11px;color:#7c3aed">${p.records?.length||0} records · ${p.visits?.length||0} visits</div>
      </div>
    </div>
    ${p.address?`<div style="padding:10px 18px;border-bottom:1px solid #ede9fe;font-size:11px;color:#555">📍 ${p.address}</div>`:""}
    ${p.source?`<div style="padding:8px 18px;border-bottom:1px solid #ede9fe;font-size:11px;color:#7c3aed;background:#faf9ff">📣 Source: ${p.source}</div>`:""}
    ${p.patientStatus?`<div style="padding:8px 18px;border-bottom:1px solid #ede9fe;font-size:11px;font-weight:700;color:${STATUS_COLORS[p.patientStatus]?.color||"#7c3aed"};background:${STATUS_COLORS[p.patientStatus]?.bg||"#faf9ff"}">🩺 Status: ${p.patientStatus}</div>`:""}
    <div style="padding:16px 18px">
      <div style="font-size:10px;font-weight:700;color:#8b7ec8;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:10px">Recent Visit History (last 5)</div>
      ${visitsHtml}
    </div>
    <div style="padding:10px 18px;background:#faf9ff;border-top:1px solid #ede9fe;display:flex;justify-content:space-between;align-items:center">
      <div style="font-size:9px;color:#a89fd4">SoundLife Speech &amp; Hearing Clinic · Confidential</div>
    </div>
  </div>`;

  const doShareWhatsApp = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      // Try Web Share API with file (mobile browsers, Chrome Android, Safari iOS)
      if (navigator.canShare) {
        const blob = await new Promise(res => canvas.toBlob(res, "image/png"));
        const file = new File([blob], `${p.name.replace(/\s+/g,"-")}-card.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Patient Card — ${p.name}` });
          setSharing(false);
          return;
        }
      }

      // Desktop fallback: download PNG + open WhatsApp Web
      const link = document.createElement("a");
      link.download = `${p.name.replace(/\s+/g,"-")}-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setTimeout(() => window.open("https://web.whatsapp.com", "_blank"), 400);
    } catch (e) {
      alert("Could not generate image: " + e.message);
    }
    setSharing(false);
  };

  const doPrint = () => {
    const w = window.open("","_blank","width=800,height=600");
    const logoSrc = "data:image/png;base64,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";
    const visitsHtml = recentVisits.length === 0
      ? `<div style="color:#888;font-size:11px;font-style:italic">No visits recorded yet</div>`
      : recentVisits.map(v=>`
          <div style="border-left:3px solid #7c3aed;padding:6px 10px;margin-bottom:6px;background:#fafafa;border-radius:0 6px 6px 0">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px">
              <span style="font-size:10px;color:#666">${new Date(v.ts).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
              ${v.clinicId&&CLINICS[v.clinicId]?`<span style="font-size:9px;background:#ede9fe;color:#7c3aed;padding:2px 7px;border-radius:4px;font-weight:700">${CLINICS[v.clinicId].label}</span>`:""}
            </div>
            ${v.attendedBy?`<div style="font-size:10px;font-weight:700;color:#7c3aed;margin-bottom:2px">👨‍⚕️ ${v.attendedBy}</div>`:""}
            <div style="font-size:11px;color:#444;line-height:1.5">${v.note}</div>
          </div>`).join("");

    w.document.write(`<!DOCTYPE html><html><head><title>Patient Card — ${p.name}</title>
    <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700&display=swap" rel="stylesheet">
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'DM Sans',sans-serif;background:#f4f3ff;display:flex;align-items:center;justify-content:center;min-height:100vh;padding:24px;}
      .card{background:#fff;border-radius:18px;width:100%;max-width:480px;box-shadow:0 8px 40px #7c3aed22;overflow:hidden;border:1px solid #ddd6fe;}
      @media print{body{background:white;padding:0;}  .card{box-shadow:none;border-radius:0;max-width:100%;} .no-print{display:none!important;}}
    </style></head><body>
    <div class="card">
      <!-- Header -->
      <div style="background:linear-gradient(135deg,#4c1d95,#7c3aed);padding:22px 24px;color:#fff">
        <div style="display:flex;align-items:center;gap:10;margin-bottom:14px">
          <img src="${logoSrc}" alt="SoundLife" style="height:38px;width:auto;filter:brightness(0) invert(1);display:block"/>
          <div style="margin-left:auto;text-align:right">
            <div style="font-size:9px;color:#c4b5fd;font-weight:700;letter-spacing:1px;text-transform:uppercase">Case Code</div>
            <div style="font-size:20px;font-weight:800;letter-spacing:3px;font-family:monospace;color:#fff">${p.id}</div>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:14">
          <div style="width:52px;height:52px;border-radius:14px;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:700;color:#fff;flex-shrink:0">${p.name?.[0]?.toUpperCase()}</div>
          <div>
            <div style="font-family:'DM Serif Display',serif;font-size:22px;color:#fff;line-height:1.2">${p.name}</div>
            <div style="font-size:11px;color:#c4b5fd;margin-top:4px">📱 ${p.phone}${p.age?` &nbsp;·&nbsp; ${p.age} yrs`:""} ${p.gender?`&nbsp;·&nbsp; ${p.gender}`:""}</div>
          </div>
        </div>
      </div>

      <!-- Clinic Info -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border-bottom:1px solid #ede9fe">
        <div style="padding:12px 18px;border-right:1px solid #ede9fe">
          <div style="font-size:9px;color:#8b7ec8;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px">Clinic</div>
          <div style="font-size:13px;font-weight:600;color:#1e1b4b">${c?.label||p.clinicId}</div>
          <div style="font-size:11px;color:#7c3aed">${c?.city||""} · ${c?.region||""}</div>
        </div>
        <div style="padding:12px 18px">
          <div style="font-size:9px;color:#8b7ec8;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px">Registered</div>
          <div style="font-size:13px;font-weight:600;color:#1e1b4b">${new Date(p.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}</div>
          <div style="font-size:11px;color:#7c3aed">${p.records?.length||0} records · ${p.visits?.length||0} visits</div>
        </div>
      </div>

      ${p.address?`<div style="padding:10px 18px;border-bottom:1px solid #ede9fe;font-size:11px;color:#555">📍 ${p.address}</div>`:""}
      ${p.source?`<div style="padding:8px 18px;border-bottom:1px solid #ede9fe;font-size:11px;color:#7c3aed;background:#faf9ff">📣 Source: ${p.source}</div>`:""}
      ${p.patientStatus?`<div style="padding:8px 18px;border-bottom:1px solid #ede9fe;font-size:11px;font-weight:700;color:${STATUS_COLORS[p.patientStatus]?.color||"#7c3aed"};background:${STATUS_COLORS[p.patientStatus]?.bg||"#faf9ff"}">🩺 Status: ${p.patientStatus}</div>`:""}

      <!-- Visit History -->
      <div style="padding:16px 18px">
        <div style="font-size:10px;font-weight:700;color:#8b7ec8;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:10px">Recent Visit History (last 5)</div>
        ${visitsHtml}
      </div>

      <!-- Footer -->
      <div style="padding:10px 18px;background:#faf9ff;border-top:1px solid #ede9fe;display:flex;justify-content:space-between;align-items:center">
        <div style="font-size:9px;color:#a89fd4">Printed: ${new Date().toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}</div>
        <div style="font-size:9px;color:#a89fd4">SoundLife Speech & Hearing Clinic · Confidential</div>
      </div>
    </div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <>
    <div className="fadeIn" style={{position:"fixed",inset:0,background:"#00000077",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(6px)"}} onClick={onClose}>
      <div style={{background:"var(--card)",borderRadius:18,padding:28,maxWidth:440,width:"100%",border:"1px solid var(--cardborder)",boxShadow:"0 32px 80px #00000040"}} onClick={e=>e.stopPropagation()}>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,color:"var(--text1)",marginBottom:6}}>🪪 Print Patient Card</div>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:20,lineHeight:1.6}}>A printable card will open with patient details, clinic info, and last 5 visit logs.</div>

        {/* Preview */}
        <div style={{background:"var(--accentbg)",border:"1px solid var(--border)",borderRadius:12,padding:"14px 16px",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#7c3aed,#6d28d9)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:17}}>{p.name?.[0]?.toUpperCase()}</div>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:"var(--text1)"}}>{p.name}</div>
              <div style={{fontSize:11,color:"var(--muted)"}}>📱 {p.phone}{p.age?` · ${p.age} yrs`:""}</div>
            </div>
            <div style={{marginLeft:"auto",textAlign:"right"}}>
              <Cb code={p.id} color="#7c3aed"/>
            </div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <span style={{background:"#7c3aed18",color:"#7c3aed",padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:600}}>🏥 {CLINICS[p.clinicId]?.label}</span>
            <span style={{background:"#7c3aed18",color:"#7c3aed",padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:600}}>📋 {p.records?.length||0} records</span>
            <span style={{background:"#7c3aed18",color:"#7c3aed",padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:600}}>📝 {p.visits?.length||0} visits</span>
          </div>
        </div>

        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button className="btn-p" onClick={doPrint} style={S.btn}>🖨️ Open & Print</button>
          <button className="btn-p" onClick={doShareWhatsApp} disabled={sharing} style={{...S.btn,background:"linear-gradient(135deg,#25D366,#128C7E)",opacity:sharing?0.7:1}}>{sharing?"Generating…":"💬 Share on WhatsApp"}</button>
          <button onClick={onClose} style={S.btnG}>Cancel</button>
        </div>
      </div>
    </div>
    {/* Hidden card for html2canvas capture */}
    <div style={{position:"fixed",left:-9999,top:-9999,width:480,pointerEvents:"none",opacity:0,zIndex:-1}}>
      <div ref={cardRef} dangerouslySetInnerHTML={{ __html: cardHtml }}/>
    </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════
// LIGHTBOX
// ══════════════════════════════════════════════════════════
function Lightbox({ src, name, patient, onClose }) {
  const [zoom,setZoom]=useState(1); const [pos,setPos]=useState({x:0,y:0}); const drag=useRef(null);
  const [displaySrc,setDisplaySrc]=useState(null);
  const [converting,setConverting]=useState(false);
  useEffect(()=>{const h=e=>e.key==="Escape"&&onClose();window.addEventListener("keydown",h);return()=>window.removeEventListener("keydown",h);},[onClose]);
  useEffect(()=>{
    const isHeic=/\.(heic|heif)$/i.test(name||"");
    if(!isHeic){setDisplaySrc(src);return;}
    let objectUrl=null;
    setConverting(true);
    fetch(src,{credentials:'include'})
      .then(r=>r.blob())
      .then(async blob=>{const {default:heic2any}=await import("heic2any");return heic2any({blob,toType:"image/jpeg",quality:0.92});})
      .then(result=>{const b=Array.isArray(result)?result[0]:result;objectUrl=URL.createObjectURL(b);setDisplaySrc(objectUrl);})
      .catch(()=>setDisplaySrc(src))
      .finally(()=>setConverting(false));
    return()=>{if(objectUrl)URL.revokeObjectURL(objectUrl);};
  },[src,name]);
  const onWheel=e=>{e.preventDefault();setZoom(z=>Math.min(Math.max(z-e.deltaY*0.002,0.3),5));};
  const onMD=e=>{drag.current={x:e.clientX-pos.x,y:e.clientY-pos.y};};
  const onMM=e=>{if(!drag.current)return;setPos({x:e.clientX-drag.current.x,y:e.clientY-drag.current.y});};
  const onMU=()=>{drag.current=null;};
  return (
    <div className="fadeIn" style={{position:"fixed",inset:0,zIndex:500,background:"#000000ee",display:"flex",flexDirection:"column",backdropFilter:"blur(10px)"}} onClick={onClose}>
      <div style={{padding:"12px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#0d0b1a",borderBottom:"1px solid #2a2250",flexShrink:0}} onClick={e=>e.stopPropagation()}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:"#e8e4ff"}}>{name}</div>
          {patient&&<div style={{fontSize:10,color:"#6d5fa8",marginTop:2}}>{patient.name} · {CLINICS[patient.clinicId]?.label}</div>}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setZoom(z=>Math.max(z-0.25,0.3))} style={S.ibtn}>−</button>
          <span style={{fontSize:11,color:"#6d5fa8",minWidth:38,textAlign:"center"}}>{Math.round(zoom*100)}%</span>
          <button onClick={()=>setZoom(z=>Math.min(z+0.25,5))} style={S.ibtn}>+</button>
          <button onClick={()=>{setZoom(1);setPos({x:0,y:0});}} style={S.ibtn}>⟳</button>
          <button onClick={onClose} style={{...S.ibtn,marginLeft:6}}>✕ Close</button>
        </div>
      </div>
      <div style={{flex:1,overflow:"hidden",position:"relative",cursor:zoom>1?"grab":"zoom-in"}} onClick={e=>{e.stopPropagation();if(zoom===1)setZoom(2);}} onWheel={onWheel} onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU}>
        {converting&&<div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,color:"#a78bfa"}}>
          <span style={{width:28,height:28,border:"3px solid #3d3070",borderTopColor:"#7c3aed",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>
          <span style={{fontSize:12}}>Converting HEIC…</span>
        </div>}
        {displaySrc&&<img src={displaySrc} alt={name} onContextMenu={e=>e.preventDefault()} width="auto" height="auto" style={{position:"absolute",top:"50%",left:"50%",transform:`translate(calc(-50% + ${pos.x}px),calc(-50% + ${pos.y}px)) scale(${zoom})`,maxWidth:"92vw",maxHeight:"82vh",objectFit:"contain",borderRadius:8,boxShadow:"0 24px 70px #000",transition:drag.current?"none":"transform 0.2s",userSelect:"none",pointerEvents:"none",WebkitUserDrag:"none"}}/>}
      </div>
      <div style={{textAlign:"center",padding:"8px",fontSize:10,color:"#3d3070",background:"#0d0b1a"}}>Click to zoom · Scroll to zoom · Drag to pan · Esc to close</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ATOMS
// ══════════════════════════════════════════════════════════
function SLogo({ white }) {
  const src = "data:image/png;base64,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";
  return (
    <img
      src={src}
      alt="SoundLife Speech & Hearing Clinic"
      width={160}
      height={44}
      style={{
        height: white ? 44 : 40,
        width: "auto",
        objectFit: "contain",
        filter: white ? "brightness(0) invert(1)" : "none",
        display: "block",
      }}
    />
  );
}
function Spin(){return <span style={{width:13,height:13,border:"2px solid #ffffff44",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite",marginRight:6}}/>;}
function ErrBox({msg}){return <div style={{background:"#fef2f2",border:"1px solid #fecaca",color:"#dc2626",borderRadius:9,padding:"9px 13px",fontSize:12,marginTop:12}}>{msg}</div>;}
function PRow({p,onClick}){const c=CLINICS[p.clinicId];return(<div className="row-hover" onClick={onClick} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:10,cursor:"pointer",marginBottom:5,background:"var(--accentbg)",border:"1px solid var(--border)"}}><div style={{width:36,height:36,borderRadius:10,background:`linear-gradient(135deg,#7c3aed,#6d28d9)`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:14,flexShrink:0}}>{p.name?.[0]?.toUpperCase()}</div><div style={{flex:1,minWidth:0}}><div style={{fontWeight:600,color:"var(--text1)",fontSize:13}}>{p.name}</div><div style={{fontSize:11,color:"var(--muted)",marginTop:1}}>📱 {p.phone} · {c?.label} · {c?.city}</div></div><div style={{textAlign:"right",flexShrink:0}}><Cb code={p.id} color="#7c3aed"/><div style={{fontSize:9,color:"var(--muted2)",marginTop:3}}>{p.records?.length||0} records</div></div></div>);}
function RCard({r,onView,showClinic}){const c=r.patient?CLINICS[r.patient.clinicId]:null;return(<div className="rec-card" onClick={onView} style={{borderRadius:12,overflow:"hidden",border:"1px solid var(--border)",background:"var(--card)"}}><div style={{position:"relative"}}><img src={imgSrc(r.data,true)} onContextMenu={e=>e.preventDefault()} loading="lazy" width="100%" height={130} style={{width:"100%",height:130,objectFit:"cover",display:"block",WebkitUserDrag:"none"}} alt={r.name}/>{showClinic&&c&&<div style={{position:"absolute",top:7,right:7,background:"#7c3aedee",borderRadius:5,padding:"2px 7px",fontSize:9,fontWeight:700,color:"#fff"}}>{c.label}</div>}</div><div style={{padding:"8px 10px"}}>{r.patient&&<div style={{fontSize:11,fontWeight:600,color:"var(--text1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.patient.name}</div>}<div style={{fontSize:9,color:"var(--muted)",marginTop:2}}>{r.name}</div><div style={{fontSize:9,color:"var(--muted2)",marginTop:1}}>{new Date(r.ts).toLocaleDateString("en-IN")}</div></div></div>);}
function Cb({code,color="#7c3aed"}){return <span style={{background:color+"18",color,padding:"2px 8px",borderRadius:5,fontSize:10,fontWeight:700,letterSpacing:0.5,fontFamily:"monospace"}}>{code}</span>;}
function CopyableCb({code,color="#7c3aed"}){
  const [copied,setCopied]=useState(false);
  const doCopy=(e)=>{e.stopPropagation();navigator.clipboard?.writeText(code).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2200);}).catch(()=>{});};
  return(
    <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
      <span style={{background:color+"18",color,padding:"3px 10px",borderRadius:6,fontSize:13,fontWeight:800,letterSpacing:1,fontFamily:"monospace"}}>{code}</span>
      <button onClick={doCopy} style={{background:copied?"#f0fdf4":"var(--accentbg)",border:`1px solid ${copied?"#86efac":"var(--border)"}`,borderRadius:6,padding:"3px 8px",fontSize:10,color:copied?"#16a34a":color,cursor:"pointer",fontWeight:700,transition:"all 0.2s",flexShrink:0}}>
        {copied?"✓ Copied!":"Copy"}
      </button>
    </span>
  );
}
function FBtn({active,onClick,children,color="#7c3aed"}){return(<button onClick={onClick} style={{padding:"5px 11px",borderRadius:6,border:`1px solid ${active?color:"var(--border)"}`,background:active?color+"18":"transparent",color:active?color:"var(--muted)",fontSize:10,cursor:"pointer",fontWeight:600,transition:"all 0.15s"}}>{children}</button>);}
function Empty({msg}){return <div style={{color:"var(--muted2)",fontSize:12,textAlign:"center",padding:"22px 0"}}>{msg}</div>;}

const S={
  label:{fontSize:10,fontWeight:700,color:"var(--muted)",letterSpacing:0.8,display:"block",marginBottom:6,textTransform:"uppercase"},
  inp:{width:"100%",padding:"10px 13px",background:"var(--inputbg)",border:"1px solid var(--border)",borderRadius:9,color:"var(--text1)",fontSize:13,transition:"all 0.2s"},
  btn:{padding:"10px 20px",background:"linear-gradient(135deg,#7c3aed,#6d28d9)",border:"none",borderRadius:9,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",boxShadow:"0 4px 12px #7c3aed33",display:"inline-flex",alignItems:"center",gap:6},
  btnG:{padding:"10px 20px",background:"transparent",border:"1px solid var(--border)",borderRadius:9,color:"var(--muted)",fontSize:13,fontWeight:600,cursor:"pointer"},
  ibtn:{padding:"6px 11px",background:"#1e1a38",border:"1px solid #3d3070",borderRadius:7,color:"#c4b5fd",fontSize:12,cursor:"pointer"},
};